// ============================================================================
// main.js — Custom Weight Designer V2 entry point + orchestrator (Phase D1)
// ============================================================================
// Mounts into `#weight-designer-root` on the real Shopify page. Per V2 plan
// §5 guardrail #3 (graceful script failure): if the mount target is missing or
// setup throws, we bail out and leave whatever the Liquid section already
// rendered (fallback hero / product links / contact CTA) untouched.
//
// This file is the lean COORDINATOR (crossover §3 "god object" convention,
// minus the no-bundler machinery): it owns the single live state bag, builds
// the DOM skeleton (stage + glass chrome), instantiates the step controllers,
// and wires the shared context (render / history / autosave / announcements /
// step navigation). All pure design logic lives in ./ui/state.js (DOM-free,
// node-tested); all DOM lives in ./ui/*.
//
// SECTION MAP:
//   1. imports + constants
//   2. boot / mount (graceful-failure contract)
//   3. skeleton builder (stage region, glass panel, step tabs, status bar)
//   4. shared context (render, history, autosave, navigation, refresh)
//   5. chrome sync + keyboard + lifecycle (autosave interval, beforeunload)
// ============================================================================

// 1. imports + constants ------------------------------------------------------
import './styles/weight-designer.css';
import './styles/shell.css'; // reusable `.ds-*` designer-shell chrome (GD-dark)
import './styles/fonts.css'; // generated @font-face for the full library (2D DOM)

import { h, media } from './ui/dom.js';
import { wdTrack, WD_EVENTS, captureUtm } from './analytics.js';
import { awaitMobileLaunch, setFullScreenMenuItems, setFullScreenMenuOnOpen, isPhone, openViewportLock, closeViewportLock } from './ui/mobile-launch.js';
import { createStepTabs } from './ui/shell/step-tabs.js';
import { createSidePanel } from './ui/shell/side-panel.js';
import * as artworkHold from './ui/artwork-hold.js';
import { createToolCluster } from './ui/shell/tool-cluster.js';
import { createZoomPopover } from './ui/shell/zoom-popover.js';
import { createPoweredBy } from './ui/shell/powered-by.js';
import { createProductDownTab } from './ui/shell/product-down-tab.js';
import { installButtonHaptics } from './ui/haptics.js';
import {
  createPaneNav, navTargets, navPhrase, stepGate,
  CHEVRON_L as STEPNAV_CHEVRON_L, CHEVRON_R as STEPNAV_CHEVRON_R,
  DESIGN_STEP, QUOTE_STEP,
} from './ui/step-nav.js';
import {
  createState,
  record,
  undo as undoState,
  redo as redoState,
  canUndo,
  canRedo,
  toAutosave,
  fromAutosave,
  resetHistory,
  isBlackBody,
  applyCrossProductContrastRemap,
  inkContextKey,
  rememberInk,
  arriveOnConfiguration,
  rememberBodyColor,
  bodyColorFor,
  weightNudgeKey,
  rememberWeightNudge,
  restoreSnapshot,
  setLayoutPreset,
  setWeight,
  AUTOSAVE_KEY,
  isReadableEnvelopeVersion,
  instanceWeight,
  addProductInstance,
  removeProductInstance,
  markBodyColorUserSet,
} from './ui/state.js';
import { createDemoProfile } from './ui/sample.js';
import { createStatusBar } from './ui/status-bar.js';
import { labelWithDots } from './ui/busy-dots.js';
import { watchKeyboardOpen } from './ui/keyboard-inset.js';
import { createPreview } from './ui/preview.js';
import { renderDesignToSVGString, weightFocusPoint, arcTextFocusPoint, logoFocusPoint } from './render-engine.js';
import { bandConflictView, bandViewOptions, bandRescuePool, contextualFillSwap, ghostedPieceSwaps, remapLogoDataUrl, addOutlineDataUrl } from './logo-outline.js';
import { OUTLINES_MAX } from './design-profile.js';
import { DEFAULT_BODY_COLOR, DEFAULT_LAYOUT_PRESET, normalizeHex, createDefaultProfile, displayWeightText, serializeProfile, identityKey, resolveLogoAspect } from './design-profile.js';
import { measureInkHull, inkHullKey } from './logo-ink-hull.js'; // CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-09-26)
import { getLayoutPreset, layoutPresetProduct, layoutPresetsForProduct, FAMILY_DEFAULT_PRESET, LAYOUT_PRESET_IDS, isPlaceholderText } from './layouts.js';
import { productSupportsBodyColor, productSupportsMultiConfig, baseProductKey, modelFor, layoutFamilyFor, geomOptsFor, productKey, parseProductKey, categoryLabelOne, defaultWeightFor, designableWeights, productSpec, PRODUCT_BRAND, PRODUCTS, CATEGORIES } from './products.js';
import { createLogoStep, ensureLogoCensus } from './ui/logo-step.js';
import { createDesignPanel } from './ui/design-panel.js';
import { createProductStep } from './ui/product-step.js';
import { createPickWeightsPanel } from './ui/pick-weights-panel.js';
import { openGate } from './ui/gate.js';
import { createTheatre, installWheelLock } from './ui/theatre.js';
import { syncMarquee, observeMarquee } from './ui/shell/marquee.js';
import { injectLiquidFilter } from './ui/liquid-glass.js';
import { createShowcase } from './ui/showcase.js';
import { createPresentation } from './ui/present.js';
import { downloadAllDesignImages, ICON_GRID, downloadDesignImage, emailDesign, copyDesignerLink, syncChainShare, syncSoloShare, reviseMyDesign, ICON_SAVE_BACK, fetchSharedDesign, downloadDesignSheet, SHARE_PARAM, ICON_DL, ICON_MAIL, ICON_LINK, ICON_SHARE_VIA, canWebShare, shareViaSystem, warmShareLink } from './ui/share.js';
import { orderedDenomination } from './ui/quote-summary.js';
import { isRepMode, getRep } from './rep-mode.js';
/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — adopting a customer's share as -A needs an
   authenticated call; `portalApi` is the one that refreshes the 30-minute token. */
import { portalApi, isExternalPortalUser } from './portal-api.js';
import { createHelpAssist, ICON_HELP } from './ui/help-assist.js';
import { clearDesignIdentity, readDesignIdentity } from './design-identity.js';
import { clearTray } from './ui/project-tray.js';
import { pdfItems, PDF_TYPES, openPdfDialog } from './ui/pdf-sheets.js';
import { createDesignsPanel, ICON_DESIGNS_ROW } from './ui/designs-menu.js';
import { projectItems } from './ui/project-push.js';
import { showcaseItems } from './ui/showcase-admin.js';
import { openModal } from './ui/modal.js';
import { createCanvasActions } from './ui/shell/canvas-actions.js';

const MOUNT_SELECTOR = '#weight-designer-root';
const HISTORY_DEBOUNCE_MS = 500; // debounce meaningful-change snapshots (crossover §3)
const AUTOSAVE_INTERVAL_MS = 30000; // periodic flush (crossover §5)
// "Select Weights" leads (2026-07-14 landing redesign) — Logo/Design/Product are
// gated behind it (Design + Product additionally gated behind a logo — see
// refreshControls' stepTabs.setDisabled calls).
/* ⚠ THE LAST STEP IS NAMED FOR WHO IS STANDING ON IT (Ryan 2026-08-30). A buyer REQUESTS a quote;
   a rep CREATES one, and on the portal that step now carries the Workstation handoff. Read once at
   module load, which is safe: the portal's Liquid section sets `window.__wdPortalMode` before this
   bundle is fetched, and `isRepMode()` caches its first read anyway. */
const STEP_LABELS = ['Pick Weights', 'Add Logo', 'Customize Design', isRepMode() ? 'Create Quote' : 'Request Quote'];
// `QUOTE_STEP` (4) is imported from ui/step-nav.js — the nav model keys off it, and a second local
// copy is one edit away from disagreeing with it.
// SHORT forms, used ONLY by the phone's PREV/NEXT buttons (Ryan 2026-07-28). Those buttons carry a
// prefix the tabs and the sheet title do not — "NEXT: Customize Design" is the step name plus four
// characters of scaffolding, and it is the widest thing competing for a third of a 390px header.
// Everywhere the step is named on its own — the tabs, the sheet title, the gate — keeps the full
// label, so nothing the buyer navigates BY gets shortened; only the signpost pointing at it.
//   "Finalize" rather than a trimmed "Request Quote": the truncations are of a redundant qualifier
// ("Customize Design" → the design is the only thing to customize), but "Request" and "Quote" both
// carry weight, so that one is re-worded instead of clipped. Sparse on purpose — anything absent
// falls through to the full label.
const STEP_NAV_LABELS = { 3: 'Customize', 4: 'Finalize' };
// (The pills' chevrons moved to ui/step-nav.js 2026-08-06, so the pane footer's arrows and these
// are provably one drawing — they are imported above under their old names.)

// The beat between landing on the bare studio and the chrome sliding in. Lifted
// from the GLD (`custom-room-designer.js:277`), which waits exactly this long
// between removing its loading overlay and adding `.rd-ui-ready` — Ryan's
// "tasteful short pause" is a real number, not a feel. See the reveal CSS.
// The beat on the bare studio before the weight rolls in (and before the no-3D chrome
// reveal). Ryan's "tasteful short pause" (2026-07-15); 200ms is what the GLD waits
// between clearing its loading overlay and revealing (custom-room-designer.js:277).
const UI_REVEAL_PAUSE_MS = 200;
// The chrome now waits for the WEIGHT to roll in and settle before it slides in
// (Ryan 2026-07-17 — re-sequenced from chrome-first). This is only a SAFETY NET for the
// one case the roll-in promise can't cover: the Three.js chunk import hangs/fails so
// buildReveal never reaches the roll (a WebGL-unsupported device is handled separately,
// in buildReveal). In normal operation the roll-in's own promise drives the chrome, so
// this is set generously — the roll settles ~2s even on a cold load, well inside this,
// so it never pre-empts the intended order; it only rescues a broken load.
const UI_REVEAL_CHROME_FALLBACK_MS = 4500;
// …and a SECOND, shorter net armed once the roll is actually running (see buildReveal). Deliberately
// slack rather than exact: the roll's real duration lives in reveal-3d.js and a fallback that tracked
// it would be a duplicate of a number it does not own. It only has to outlast a roll that resolves
// normally, which this comfortably does.
const UI_ROLL_CHROME_FALLBACK_MS = 3400;
// How long to hold the ⅓mm relief back after a PRODUCT SWITCH so its shader recompile
// lands on the settled plate, not mid-entrance-spin (reveal-3d HANDOFF_ENTER_MS is 1000;
// +150 clears the settle). See buildReveal — WD-18 jitter fix (Ryan 2026-07-17).
const HANDOFF_SETTLE_MS = 1150;
const MOBILE_MQ = '(max-width: 768px) and (pointer: coarse)'; // VERBATIM (crossover §4 / §8)

// FAMILY_DEFAULT_PRESET (each layout family's flagship) lives in layouts.js —
// state.js's smartStart needs the same answer on logo upload, and the two must
// agree.

// (A local `productSupportsBodyColor` mirror lived here, carrying "update
// alongside reveal-3d.js's own definition" — it existed only so the Three.js-heavy
// reveal module could stay dynamically imported. products.js is pure, tiny and
// dependency-free, so BOTH sides import the one MODELS table instead and the
// mirror is gone.)

// 2. boot / mount -------------------------------------------------------------

function boot() {
  const target = document.querySelector(MOUNT_SELECTOR);
  if (!target) return; // no mount point — do nothing, touch nothing.
  /* ⛔ AT BOOT, NOT INSIDE `mount()`. It was installed alongside the switcher, which is built well
     into the tool's init — so the WELCOME SCREEN was already on screen and clickable before the
     listener existed, and its cards and Next button felt nothing (Ryan 2026-08-09). One delegated
     listener on `document` covers anything added later, so the earliest possible attach is also the
     cheapest and the only one that cannot be outrun by the UI it is meant to serve. */
  installButtonHaptics();
  mount(target).catch((err) => {
    // Graceful failure (V2 §5 #3): leave the Liquid fallback in place.
    // eslint-disable-next-line no-console
    console.error('[weight-designer] mount failed, fallback content left in place:', err);
  });
}

async function mount(target) {
  // PHONES: show a card and wait for the tap (Ryan 2026-07-26). Nothing below this line runs until
  // the buyer asks for the tool — so the 3D chunk, the font library and the texture pipeline stop
  // loading for everyone who merely scrolls past on a phone. Resolves instantly everywhere else.
  /* …EXCEPT WHEN THE URL ALREADY NAMES A DESIGN. A `?d=` visitor did not scroll past anything, they
     followed a link to one specific plate — so the card's whole premise (don't load 3D for someone
     who is passing by) does not describe them, and making them tap "Design Yours" to see a design
     that was sent to them is the feature failing on the device it was built for. The card is still
     OPENED rather than skipped, so full-screen mode keeps its scroll lock and its back-button
     history entry — see `awaitMobileLaunch`. */
  await awaitMobileLaunch(target, { autoOpen: !!readShareId() });

  const state = createState();

  /* ⚠ EXPOSED FROM HERE, NOT FROM THE `__wdDbg` BLOCK FURTHER DOWN, because the gate is what most
     of these probes are about and that block does not run until the 3D has mounted — by which time
     every picking decision has already been made and cannot be observed.
       ⛔ AND A PROBE MUST MEASURE STATE, NOT CLASSES. `probe-config-adder` exists because a `+`
     that adds a configuration and then toggles the whole product off underneath it looks EXACTLY
     like a dead handler on screen: same pixels, opposite bug. The picked set is the only observable
     that tells them apart. Stripped from the production bundle by the `DEV` guard. */
  if (import.meta.env && import.meta.env.DEV) {
    window.__wdState = state;
    window.__wdDefaultWeightFor = defaultWeightFor;
    window.__wdSerializeProfile = serializeProfile;
    /* Hoisted — `switchProduct` is a function DECLARATION further down this same scope. A probe
       needs a way to PARK on a known configuration before it starts measuring hops; the hops
       themselves are still driven through the switcher's own arrows, because what is under test is
       the seam and not this shortcut. */
    window.__wdSwitchTo = (k) => switchProduct(k);
    /* The ORDER the buyer's picks land in — the switcher's pill order, the down-tab's cycle order
       and which product the tool opens on, all of which are `pickedProducts()` read at different
       moments. Exposed because ordering is now a RULE with an owner (heaviest first within a kind,
       Ryan 2026-08-10) rather than a by-product of instance numbering, and a rule needs something
       that can assert it. Hoisted for the same reason `__wdSwitchTo` is. */
    window.__wdPickOrder = () => pickedProducts().map((p) => ({ key: p.key, w: weightForVisit(p.key) }));
    /* THE CAPTURED PER-PRODUCT DESIGNS, which is the only observable that can tell a working
       showcase set from the one 267 shipped. The envelopes themselves are megabytes of logo, so
       this reports the RECORD without them — product, family, layout, weight, and whether the
       identity still matches — which is exactly what `gatherFamilies` folds. Hoisted like the two
       above; `ctx` is a `const` further down this same scope. See tools/probe-showcase-set.mjs. */
    window.__wdCaptured = () => {
      /* ⚠ NULL MEANS "NOT READY YET", NOT "NOTHING CAPTURED", and the two must not be confused by a
         probe asserting an empty set. This block runs long before `ctx` is initialised — it is
         hoisted up here on purpose so the GATE can be observed (see the note above) — so a probe
         that measures during boot reaches `ctx` in its temporal dead zone. */
      let live;
      try { live = ctx.liveDesign(); } catch { return null; }   // once — each call serialises the design
      return [
        ...ctx.capturedDesigns().map((c) => ({ ...c, env: c.env.length, live: false })),
        ...(live ? [{ ...live, env: live.env.length, live: true }] : []),
      ];
    };
    /* ⭐ REDRAW WITHOUT RE-DERIVING (WD 453). A probe that writes a design decision straight onto
       the profile — "give the weight number this exact outline stack, so Ryan can see that option
       rendered" — had no way to make the plate show it. The only redraw a probe could reach was
       `__wdSwitchTo`, and switching away and back runs the whole cascade, which rebuilds the stack
       from the logo's bands and throws the written one away. Measured: the "remove the ring the
       logo has not got" option came back byte-identical to the shipped plate on every plate it was
       tried on, so the decision page would have shown two identical pictures under two different
       captions.
         `requestRender` is the commit the design panel itself uses for a value edit
       (`ctx.requestRender()`, NEVER `structuralCommit()` — design-panel.js says so at the one place
       that matters). Same TDZ guard as `__wdCaptured` above, and DEV-only like the rest of this
       block, so nothing here reaches a buyer. */
    window.__wdRerender = () => {
      try { ctx.requestRender(); } catch { return false; }
      return true;
    };
  }

  // --- restore autosave, else start on a clean, unbranded BLACK plate (Ryan
  //     2026-07-12: load 100% design-less; the user seeds a layout in the Design
  //     step or drops a logo, and colors wash the stage even on a blank plate). ---
  // Autosave RESTORE is never silent (Fable §3.2): we read any saved design but
  // start on a blank plate and offer a Resume gate on the intro. `resumeData` is
  // the last-known-good populated save (or null).
  const resumeData = readResume();
  seedBlank(state);

  target.classList.add('weight-designer');
  target.replaceChildren(); // clear the Liquid fallback now that we own the mount
  /* WD 492 (Ryan 11-01): a wheel over the designer window never scrolls the page (every screen, storefront and portal);
     and the theatre, desktop storefront only. See ui/theatre.js. */
  installWheelLock(target);
  const theatre = createTheatre({
    root: target,
    enabled: () => !isPhone() && !isRepMode() && !readShareId() && !target.classList.contains('wd-fs'),
  });

  // --- a SHARED design (?d=<id>) — Phase D2 ---------------------------------
  // Someone was sent this link. Fetch the design, restore it, and skip BOTH gates: the
  // recipient is not resuming their own work and is not picking products, they were sent one
  // specific plate to look at. Everything else mounts exactly as usual, which is the point of
  // routing a share through this app instead of a separate view-only page.
  //   A failed or expired id falls through to the normal opening, having said so — better a
  // working designer than a dead end, and a two-year-old link genuinely can expire.
  const sharedId = readShareId();
  let sharedNow = false;
  /* Declared HERE rather than beside its readers further down, because it is SET in the block below
     — and a `let` written before its declaration has been evaluated throws. See `buildCurrentTexture`
     for what it does. */
  let sharedRevealPending = false;
  if (sharedId) {
    /* ⛔ THE WINDOW IS TAKEN BEFORE THE FETCH, NOT AFTER IT (Ryan 2026-08-16, twice — first that it
       waited for the models, then that it still took a beat).
         The only thing needed to know this is a shared boot is the URL, and that is already in hand.
       Waiting for the envelope meant waiting on a multi-MB download of somebody's logo — measured at
       ~4.2s on a fast connection — during which a customer who followed a link about their own plates
       was looking at a marketing hero. Locking here puts them on the dark stage with "Opening this
       design…" for that whole time, which is the tool loading rather than the wrong page sitting there.
         Released below if the id turns out to be dead, and by `revealChrome` if the presentation
       cannot open — the lock never outlives the thing it was taken for. */
    if (!isPhone()) {
      try { target.scrollIntoView({ block: 'start', inline: 'nearest' }); } catch { /* older browsers */ }
      openViewportLock(target);
    }
    const note = h('div', { class: 'wd-share-loading' }, 'Opening this design…');
    target.appendChild(note);
    const record = await fetchSharedDesign(sharedId);
    note.remove();
    if (record && record.design) {
      /* ⭐ THE LINK OPENS ON THE FIRST MODEL OF THE SET, NOT ON WHICHEVER ONE THE SENDER HAPPENED TO
         BE LOOKING AT (Ryan 2026-08-16: *"on load, the presentation will always start on the first
         weight (furthest dot on the left)"*).
           `record.design` is the stage the sender pressed Share from — which is a fact about THEIR
         afternoon, not about the set. Opening on it lit the third dot of five for no reason a
         recipient could see, and made "first" mean something different on every link.
           ⛔ DECIDED HERE, BEFORE THE ONE `fromAutosave`, rather than corrected once the presentation
         is up. The alternative is a product switch between the roll-in and the reveal — a second
         model build the visitor watches, on the one screen this feature exists to make composed.
         The set arrives in catalogue order (`orderSharedSet`), so `[0]` is already the left dot. */
      const first = (record.set || [])[0];
      const opening = (first && first.design) || record.design;
      try { sharedNow = fromAutosave(opening, state).restored; } catch { sharedNow = false; }
      /* A set entry is another envelope from the same writer, so this should not happen — but the
         link is still perfectly good without it, and falling back to the design the record was
         built around beats refusing the share over the order of its dots. */
      if (!sharedNow && opening !== record.design) {
        try { sharedNow = fromAutosave(record.design, state).restored; } catch { sharedNow = false; }
      }
      /* WD 506 - a link minted before the census rode the envelope: re-measure it from the artwork
         BEFORE anything reads the recolour rules (the first composite, the presentation's hops). */
      if (sharedNow) { try { await ensureLogoCensus(state); } catch { /* the design still opens */ } }
      /* The first model was PICKED but never opened, so it carries no envelope of its own: put it
         on the stage wearing the design the link came with. Exactly what a flip to a designless
         slide does (`present.js` `go`), and what the tool does natively on a product switch — the
         boot-time version is just the key, because there is no 3D to switch yet. */
      if (sharedNow && first && !first.design && first.product && first.product !== state.ui.product) {
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 507, Ryan 2026-11-04) - AND IT ARRIVES THERE, it does not just
           take the name. Renaming the product left the stage wearing the design's LIVE plate colour and ink
           on a configuration that remembers its own (a 45 lb navy first slide opened yellow when the sender
           saved on the yellow plate); the second pass round was right because the hop machinery had run by
           then. This is `openConfiguration`'s sequence on the live state: stamp the envelope's values under
           its own key, move, arrive - the same three calls the presentation's hops make. A first slide of a
           different FAMILY still only takes the key: the layout rebuild is `switchProduct`'s and the 3D is
           not up yet, exactly as `openConfiguration` declines it. */
        const _from = state.ui.product;
        const _sameFamily = baseProductKey(first.product) === baseProductKey(_from);
        if (_sameFamily) {
          rememberBodyColor(state, _from);
          rememberInk(state, inkContextKey(state));
          rememberWeightNudge(state, weightNudgeKey(state));
        }
        // ONE assignment site, on purpose: test/ui-logic.smoke.mjs counts these (five, this is :314).
        state.ui.product = first.product;
        if (_sameFamily) {
          const _pk = parseProductKey(first.product);
          state.ui.productCategory = _pk.category;
          state.ui.productKind = _pk.kind;
          arriveOnConfiguration(state, first.product);
        }
      }
      /* ⭐ AND IT OPENS ON A WEIGHT THEY ACTUALLY ORDERED (Ryan 2026-08-30: *"if someone only wants
         to buy 50-100 lb set of dumbbells, when they reopen their link later to see their design, it
         shouldn't default to 25 lb pair"*).
           The rule and the reason it is a DISPLAY rule rather than a restore fix both live on
         `orderedDenomination`. What belongs here is the ORDER OF OPERATIONS: after `fromAutosave`,
         because that is what puts the design-time weight on the profile in the first place; after
         the `state.ui.product` line above, because the answer is per product and that line is what
         settles which product this is; and before anything renders, because `sharedRevealPending`
         still holds the stage on a stock plate and the 3D has not been built.
           ⚠ SAFE TO WRITE `weightValue` DIRECTLY, and that was checked rather than assumed. Ink is
         keyed on BODY COLOUR, not weight (`inkContextKey`), so no colour memory is invalidated; the
         mark's box is recomputed live from (product, weight) on every render rather than stored; and
         `savedSizes` is keyed by layout family. Nothing downstream is holding a weight-shaped
         assumption this could falsify.
           ⚠ THE LADDER IS PASSED so a rung that can be QUOTED but not DRAWN is never chosen — the
         two lists are not the same, which is the whole reason `weightForVisit` re-validates. */
      if (sharedNow && record.ordered && record.ordered.length) {
        state.ui.sharedOrdered = record.ordered;
        const w = orderedDenomination(
          record.ordered, state.ui.product, state.designProfile.weightValue, designableWeights(state.ui.product));
        if (Number.isFinite(w) && w > 0) state.designProfile.weightValue = w;
      }
    }
    if (sharedNow) {
      state.ui.isSharedView = true;
      state.ui.isDemo = false;
      /* THE REST OF THE SET — every model the sender designed, not just the one the link opens on.
         Parked on `ui` rather than in the envelope: the envelope is one design by definition, and
         it is also the autosave, the quote payload and the showcase slide format (see
         `design-identity.js` on why things that are ABOUT a design do not travel inside it). */
      state.ui.sharedSet = record.set || [];
      // The rep this link belongs to, for the contact line under the presented plate (or null).
      state.ui.sharedContact = record.contact || null;
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ AND THE ID, which the boot used to
         drop on the floor. `sharedId` was a local in `mount()`, so by the time a rep pressed Save
         nothing knew the design on screen had come from a customer's link — it saved as an
         anonymous new design with a fresh WEIGHT- ref and no way back to the submission. Adopting
         it as `-A` of a chain needs exactly this one string. */
      state.ui.sharedId = sharedId;
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE RECORD A BUYER MAY WRITE BACK TO,
         and only ever their own. `ordered` is written by the quote handoff alone, so a share
         carrying it IS the customer's submission; a link we minted of OUR versions never has one
         and can never be revised from the public tool.
         ⚠️ It moves when they flip to a rep-made slide and back — see `leave()` in ui/present.js,
         which reads the slide's own `origin`. Set here so a link opened directly on their design
         (the one in their first email, which carries no set at all) works with no slide to consult. */
      state.ui.sharedOrigin = (Array.isArray(record.ordered) && record.ordered.length) ? sharedId : '';
      // Hold the stage on a stock plate until the presentation spins the design onto it.
      sharedRevealPending = true;
    } else {
      // A dead or expired id: nothing is going to be presented, so give the page back.
      closeViewportLock(target);
      // Remember it for the status bar to mention once the shell exists — there is nowhere to
      // put a message this early, and an alert() on a marketing page is worse than the fault.
      state.ui.sharedLinkFailed = true;
    }
  }

  // The SVG refraction filter `.wd-liquid-glass` references. Must exist before
  // the gate paints its cards, or Chromium resolves url(#wd-liquid) to nothing
  // and they render un-refracted for a frame.
  injectLiquidFilter();

  // --- Landing gate (2026-07-14 redesign) -----------------------------------
  // Nothing else mounts until this resolves: no side panel, no 3D, no tabs —
  // just the blank stage + a centered popup (Ryan: "I hate the current page-
  // load UX"). Resume takes priority when a save exists; "Start fresh" / ✕
  // falls through to the normal product-selection gate below. Either exit
  // ("design" or "showcase") proceeds to build the shell exactly as before.
  let startAction = 'design'; // 'design' | 'showcase' — decides the post-shell landing behavior
  let resumedNow = false;
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-02) — THE PORTAL SKIPS THE INTRO ENTIRELY.
     *"reps don't need the intro, especially when 50% of the time they just need to load a
     pre-saved design… have it skip the intro and load on the designer 'Pick Weights' tab, all
     categories closed."*

     "The intro" is `openGate` — the two-beat "What are you designing?" → "Which ones?", which since
     2026-08-09 also carries the Resume pill the old modal used to ask about. It is the buyer's
     onboarding —
     the rep already knows what the tool is, and their real first move is either the Pick Weights
     accordion or My Designs, both of which are one click away inside the tool and neither of which
     the intro leads to.

     ⚠ A `?d=` SHARE LINK IS UNAFFECTED — `sharedNow` already skips both gates for everyone, and a
     rep opening a shared design should land on that design, not on an empty picker. Every rep
     branch below is therefore gated on `!sharedNow` as well. */
  const repMode = isRepMode();
  /* ⛔ THE RESUME MODAL IS GONE (Ryan 2026-08-09). It used to run RIGHT HERE, before the landing
     gate and therefore before the visitor had seen anything at all: a full-viewport scrim, a body
     scroll-lock and a focus trap over the whole storefront page, whatever they came for. Ryan:
     *"should we make the resume/start fresh pop-up self-contained to the window/editor instead of
     it hijacking the entire page? … perhaps just going to the page for another reason."*
       Resume is now a pill ON the gate's own welcome screen (ui/gate.js), which is why nothing
     happens here any more: the gate reports `'resume'` and the restore happens where the gate's
     result is read, a few dozen lines below.
       ⚠ THE `#wd-kb-` EXEMPTION WENT WITH IT, and that is the tell that this was the right shape.
     It existed for one reason — to stop the modal ambushing someone who had followed a Help & Guide
     deep link — and a gate that renders inline as ordinary page content ambushes nobody. A special
     case that dissolves when the design is fixed was a special case standing in for the fix. */

  /* The rep's unsaved work, offered back WITHOUT a gate (see `revealChrome`, far below).
     Skipping the resume gate must not mean silently discarding an afternoon's work: the autosave
     is still in localStorage, so the boot keeps a handle on it and the status bar offers a
     one-click Restore once there is somewhere to say so.

     ⚠ THE IDENTITY IS CAPTURED HERE, BEFORE THE LINE BELOW CLEARS IT. The stored `WEIGHT-` ref is
     what stops a second push minting a second reference number, and it is keyed to the autosave
     this offer restores — so the two travel together or not at all. Clearing the STORED copy is
     still right and still happens: until the rep accepts, what is on the stage is a blank plate,
     and a blank plate carrying the last design's reference number is the worse half of the bug
     `design-identity.js` exists to prevent. */
  const repResumeRaw = (repMode && resumeData && !sharedNow) ? resumeData.raw : null;
  const repResumeIdentity = repResumeRaw ? readDesignIdentity() : null;
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31) — WHO IS THIS DESIGN? Decided once, here,
     because this is the only place that knows whether the artwork about to be mounted is the rep's
     own resumed work or something else entirely.

     A remembered `WEIGHT-` ref is what stops a second push from minting a second reference number
     (design-identity.js). But the same memory pointed at the WRONG artwork is the worse fault: it
     would file brand-new work — or a design someone else shared — under an old reference number,
     silently, in a customer's proposal. `resumedNow` is true ONLY when this browser's own autosave
     was restored after the rep chose Resume, so this one line covers all three ways the identity
     goes stale: they chose "Start fresh", they arrived on a `?d=` share link, or there was nothing
     to resume at all.

     ⚠ MUST RUN BEFORE `createDesignsPanel` (far below), which seeds itself from this key. It does,
     by construction — both are statements in this same `mount()`.

     ⚠ AND IT DELIBERATELY DOES NOT CLEAR THE STAGING TRAY, even though "+ New" does. The asymmetry
     is intentional, so please don't "fix" it: "+ New" is an explicit, confirmed action whose dialog
     names the plates it is about to discard, whereas "Start fresh" is a two-button gate at boot
     that a rep can hit on reflex — and the tray can hold an afternoon's colour sweep. Nothing is
     lost by keeping it either: the ref is new (this line), and Review & send lists every staged
     plate, with its picture and product, before anything is sent. Visible beats tidy. */
  /* ⚠ MOVED BELOW THE GATE on 2026-08-09, and it had to be. `resumedNow` is no longer known at
     this point in the boot: the Resume choice is made ON the gate now, forty lines further down,
     so reading it here would ask the question before the buyer had answered it and clear the ref
     of every design that was about to be resumed. See `clearDesignIdentity` after `openGate`. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-02) — THE STAGE OPENS EMPTY, and that is a state
     the app already has rather than a new one: `clearStage()` produces exactly this whenever the
     buyer deselects their last pick, and `pickWeightsPanel`'s `onChange` already knows how to come
     back from it (`if (!reveal) mountFreshProduct(...)`). Booting into it is the same state reached
     one step earlier.

     The alternative was to mount `DEFAULT_PRODUCT_KEY` so there is something to look at, and it
     is wrong twice over: it shows a Competition Urethane bumper the rep did not choose, and it
     pays for a full geometry build (205ms on a grip plate) that is torn down the instant they load
     a saved design — which is half of what they open this page to do.

     ⚠ NOT `hasAnySelection(state)`. That reads like the more general guard and it silently changes
     the BUYER's path too: an old envelope can restore with an empty `selectedProducts` (see
     `state.js`'s "pre-fix design opens Pick Weights completely blank"), and such a resume mounts a
     model today. This flag says what it means — the rep's fresh portal boot, and nothing else. */
  const bootBlank = repMode && !sharedNow && !resumedNow;
  /**
   * The buyer's picks as [{key, label}], LEFT-MOST FIRST — the switcher's list,
   * and the gate's seed. One function because they must agree: the product the
   * tool opens on is the switcher's first pill.
   *
   * "Left-most on screen, not first-clicked." Ryan, 2026-07-15: "if they chose
   * products from 2 of 3 cards, the product on the top left-most card is the one
   * we start them on." `selectedProducts` is append-ordered BY CLICK, so picking
   * Dumbbells before Bumper Plates used to open on the dumbbell while the bumper
   * card sat further left. Sorted by CATEGORIES order — which IS the on-screen order.
   *
   * ⚠ AND BY KIND WITHIN THE CATEGORY (Ryan 2026-08-02). This sorted by category alone and
   * left click order to break the tie, which the previous note called falling out "for free"
   * — it did not. Ryan: *"I already had the comp urethane and training rubber models loaded.
   * I then added the comp rubber… it then got added 3rd, after the training rubber. Would
   * prefer if it stayed in the order it appears in on the pick weights drop-downs — and this
   * should be the case for all models of all categories."*
   *   `cat.kinds` IS that drop-down's order: `pick-weights-panel` renders `cat.kinds.map(...)`
   * straight through. So sorting on the same array is not "a matching order", it is the same
   * fact read twice — which is the only version of this that cannot drift when a kind is added.
   *
   * Filtered to DESIGNABLE: the gate can't offer anything else today, but Step 3's
   * quote picker can still set a category, and a key with no model would build the
   * wrong product on the stage — the exact fault this whole change removes.
   */
  function pickedProducts() {
    const catOrder = CATEGORIES.map((c) => c.id);
    // The kind's position in ITS category's own `kinds` array — the same array the Pick Weights
    // accordion iterates. An unknown kind sorts last rather than to the front, so a stray pick
    // can never displace a real one.
    const kindIdx = (p) => {
      const cat = CATEGORIES.find((c) => c.id === p.category);
      const i = cat ? cat.kinds.indexOf(p.kind) : -1;
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    /* ⚠ …AND BY INSTANCE WITHIN THE KIND (2026-08-09, multi-configuration). Category then kind was
       a TOTAL order while every pick was unique; a product picked twice makes it a partial one, and
       the tie would then fall back to `sort`'s stability — i.e. to click order, the exact thing the
       kind tie-break was added to stop. The instance number is allocated ascending and never
       renumbered, so ordering on it puts the white set before the green set it was added before,
       permanently. It is never PRINTED: the switcher shows two identically-named pills, told apart
       by the plate colour they carry, which is the only difference the buyer made. */
    const instOf = (p) => { const n = Math.trunc(Number(p.instance)); return Number.isFinite(n) && n > 1 ? n : 1; };
    /* ⭐ LIGHTEST FIRST WITHIN A KIND (Ryan 2026-08-10, second pass: *"the SIZE cards go 10 → 55, but
       the order of the models goes big to small when you load into the designer. We should probably
       have the model order mirror that of the size cards — so if someone loads in with 10, 25, 55, it
       starts them at 10"*).
         It shipped heaviest-first this morning, on the reading that a switcher should open on the
       headline plate. That was decided before the Size ladder started carrying units, colours and
       ownership — and now that a buyer meets the ladder ascending on the gate, ascending on the quote
       form and ascending in Customize Design, a switcher counting DOWN is the only surface in the
       tool reading the other way. Mirroring it costs one term and removes the odd one out.
         Instance number is not a proxy for either direction any more: configurations used to claim
       rungs down the ladder, so #1 held the 45 and #2 the 35, but the gate's circles broke that —
       displace the pair to 25 and 10 and instance #1 holds the 10.
       ⚠ WITHIN A KIND ONLY. Category and kind order are untouched — Ryan 2026-08-02 asked for those
       to mirror the Pick Weights drop-downs "for all models of all categories", and sorting the
       whole set by weight would put a 55 lb bumper's sibling ahead of a grip plate.
       ⚠ INSTANCE STAYS AS THE FINAL TIE-BREAK, because two configurations CAN hold the same rung
       (nothing stops it until the quote form, where a double-count costs money) and this comparator
       must stay total — otherwise the tie falls through to `sort` stability, i.e. to click order,
       which is the exact thing the kind tie-break was added to stop. */
    const rungOf = (p) => {
      const w = Number(instanceWeight(state, p.category, p.kind, instOf(p)));
      return Number.isFinite(w) ? w : 0;
    };
    const picks = [...state.ui.selectedProducts]
      .filter((p) => PRODUCTS[productKey(p.category, p.kind)])
      .sort((a, b) => (catOrder.indexOf(a.category) - catOrder.indexOf(b.category))
        || (kindIdx(a) - kindIdx(b))
        || (rungOf(a) - rungOf(b))
        || (instOf(a) - instOf(b)));
    // "Brand · Kind · Category", singular — "Ballast Strength · Premium Urethane ·
    // Grip Plate" (Ryan, 2026-07-17). The brand leads (buyers want to see the
    // product line they're branding), then the kind that changes as you cycle,
    // then the category that grounds it. The brand IS constant across the set —
    // earlier it was left off for exactly that reason — but Ryan wants it shown;
    // the switcher drops it first on the narrowest screens (CSS, see the bar).
    //
    // The category is named UNCONDITIONALLY now. This used to print the kind
    // alone and only reach for the category where a kind was ambiguous among the
    // buyer's own picks (the grip plate's kind used to collide with the
    // dumbbell's) — a rule that read fine on a pill listing every pick side by
    // side, where the set gave you the context. The down-tab shows ONE product at
    // a time, so there's no set to read it against: it has to say what it is.
    // Two words, not one string: the down-tab renders them as separate elements
    // so the narrow breakpoint can drop the category without a JS re-render.
    /* Which {category, kind} pairs appear MORE THAN ONCE — the only ones that need a plate-colour
       dot to be told apart. Counted over the filtered list so a pick with no model behind it can't
       make a lone product think it has a twin. */
    const seen = new Map();
    for (const p of picks) {
      const k = productKey(p.category, p.kind);
      seen.set(k, (seen.get(k) || 0) + 1);
    }
    return picks.map((p) => {
      const key = productKey(p.category, p.kind, instOf(p));
      return {
        key,
        brand: PRODUCT_BRAND,
        kind: p.kind,
        category: categoryLabelOne(p.category),
        /* ⭐ The dot the switcher wears, and null for every set that has ever existed before today.
           Two configurations of one bumper carry the identical brand, kind and category, so the
           colour is the entire distinguishing fact — see the swatch note in `product-down-tab.js`
           for why it must not be rendered when there is nothing to distinguish. */
        swatch: seen.get(productKey(p.category, p.kind)) > 1 ? bodyColorFor(state, key) : null,
      };
    });
  }

  /* ── A NEWLY PICKED PRODUCT MUST NOT BE UPSTREAM OF WHERE YOU LAND (Ryan 2026-08-08) ────────────
   * *"someone is working on a dumbbell only, then they go to the Pick Weights tab and pick a weight
   * that appears BEFORE dumbbells on the list, like a comp bumper plate. Then they go back to the
   * Customize Design tab, it'll take them to the dumbbell, skipping over the comp bumper they just
   * added… they have to go forward to CD tab then back."*
   *
   * The design step remembers which product you were on, which is right for every other way of
   * arriving at it and wrong for this one: `pickedProducts()` sorts by category then kind, so a pick
   * can land ANYWHERE in the list — including behind you. The forward button only ever walks
   * forward, so a product added upstream is reachable solely through the switcher, and on a phone
   * that is a control the buyer has to notice first.
   *
   * ⚠ THE JUMP IS CONDITIONAL, and Ryan drew the line himself: *"they're on the training bumper,
   * then they add a dumbbell… we don't need them to jump back to the comp bumper."* A pick that
   * lands AHEAD of you is already on the path — Next reaches it — so nothing should move. Only a
   * pick BEHIND you needs the app to do something.
   *
   * ⚠ AND IT AIMS AT THE EARLIEST NEW PICK, not at the first product in the list. Ryan said "the
   * first weight in their list", and in both of his examples the two are the same answer. They part
   * only when the buyer is midway through a set and adds something behind them but ahead of the
   * start — where "first in the list" would drag them back past products they had already walked
   * through and decided about. Going to the thing they just added is the smaller move and the one
   * that matches why they went to Pick Weights at all.
   */
  let lastPickKeys = new Set();
  const pendingPickKeys = new Set();

  /** Diff the new pick list against the last one we saw; remember what ARRIVED. */
  function notePickAdditions(picks) {
    const now = new Set(picks.map((p) => p.key));
    for (const key of now) if (!lastPickKeys.has(key)) pendingPickKeys.add(key);
    // A pick that is REMOVED stops being pending — un-ticking it is the buyer saying they did not
    // want it, and a stale entry would jump the stage to a product that is no longer in the set.
    for (const key of [...pendingPickKeys]) if (!now.has(key)) pendingPickKeys.delete(key);
    lastPickKeys = now;
  }

  /**
   * Consume the pending picks on arrival at the design step, moving the stage back to the earliest
   * one that sits behind the buyer. Always consumes, jump or no jump: the visit is what the buyer
   * needed the reminder FOR, and leaving them armed would fire the jump on some later, unrelated
   * arrival (the exception case above ends here, having moved nothing).
   */
  function applyPendingPickJump() {
    if (!pendingPickKeys.size) return;
    const order = pickedProducts().map((p) => p.key);
    const here = order.indexOf(state.ui.product);
    // Seeded at the current position, so each candidate has to beat it AND every earlier candidate:
    // what falls out is the earliest pending pick that is genuinely behind us. A current product no
    // longer in the set (they un-ticked the one they were on) counts as "past the end", which lets
    // the earliest pending pick win — the same left-most rule `onChange` falls back to.
    let best = here < 0 ? order.length : here;
    let target = null;
    for (const key of pendingPickKeys) {
      const i = order.indexOf(key);
      if (i >= 0 && i < best) { best = i; target = key; }
    }
    pendingPickKeys.clear();
    /* Un-awaited, and deliberately BEFORE the pane fills. `switchProduct` adopts the product state
       synchronously and only the 3D hand-off is async — so by the time `goToStep` builds the design
       panel a few lines later, it builds the incoming product's controls ONCE rather than building
       the outgoing product's and correcting them. The pane fade and the panel rebuild are the
       switch's own machinery (`setBodyBusy` → `runDeferredPanelRefresh`), which is why this must not
       be re-implemented here: landing this way is the same set-piece as tapping the product in the
       switcher, and there is no second vocabulary for "the model changed". */
    if (target) switchProduct(target);
  }

  // `sharedNow` skips this: the picks that came with the shared design ARE the product context,
  // and asking a viewer to choose products before showing them the plate they were sent is the
  // wrong first question. `repMode` skips it per the note at the top of mount().
  if (!sharedNow && !repMode) {
    // stepLabels: the gate's ghost tab strip mirrors the real step row so the
    // canvas top can't shift on handoff (see gate.js).
    /* `resume` puts the Resume pill on beat 1 and is absent for the great majority of visitors, who
       have nothing to resume. It carries the LOGO URL only — `readResume` already has the parsed
       envelope in hand, so this costs one property read rather than a second parse of a multi-MB
       string at first paint. See the note on the pill in ui/gate.js for why it is the logo and not
       a render of the plate. */
    startAction = await openGate({
      state,
      stepLabels: STEP_LABELS,
      resume: resumeData ? { logoUrl: resumeData.logoUrl } : null,
    }, target);
    /* ⭐ THE RESUME PILL'S ANSWER, applied HERE rather than inside the gate. The gate reports a
       choice; it does not own the buyer's state. That split is what lets the same restore path serve
       the modal that used to sit at boot and the pill that replaced it, and it is why `openGate`
       still needs to know nothing about autosave envelopes. */
    if (startAction === 'resume' && resumeData) {
      const res = fromAutosave(resumeData.raw, state);
      resumedNow = res.restored;
      // WD 506 - same as the share boot: an autosave written before the census rode the envelope.
      if (resumedNow) { try { await ensureLogoCensus(state); } catch { /* the design still resumes */ } }
    }
    /* ⛔ EVERYTHING BELOW IS THE FRESH-START SEED, so a resume must not run it. It picks the
       left-most product, resets the weight to that product's sample and re-seeds the layout — which
       on a restored design would overwrite the buyer's own product, denomination and arrangement
       with defaults, silently, at the moment they asked to get their work back. `restorePicks` has
       already put all three where they belong. */
    // Seed the live product from the buyer's left-most pick so the 3D that first
    // appears already matches (mirrors switchProduct's own category sync).
    const first = resumedNow ? null : pickedProducts()[0];
    if (first) {
      const { category, kind, instance } = parseProductKey(first.key);
      state.ui.product = first.key;
      state.ui.productCategory = category;
      state.ui.productKind = kind;
      /* Open on this product's SAMPLE weight (45 lb plate / 25 lb dumbbell / 60 lb bar) — there's
         no denomination picker in the DESIGNER (Ryan 2026-07-20).
         ⭐ …but there is one on the GATE now (2026-08-10), so ask what this configuration actually
         claims rather than what its instance number implies. `instanceWeight` falls straight back to
         `defaultWeightFor` when the buyer never touched a circle, so every design that predates the
         feature opens exactly where it always did — but a buyer who chose 55s and 10s must not be
         handed a 45 on the stage they just asked not to have. */
      setWeight(state, instanceWeight(state, category, kind, instance));
      // Seed the LAYOUT to match, or a buyer opening on a grip plate gets the
      // bumper's default ring preset painted straight across their hand cutouts.
      const fam = layoutFamilyFor(first.key);
      if (layoutPresetProduct(getLayoutPreset(state.designProfile.layoutPreset)) !== fam) {
        setLayoutPreset(state, FAMILY_DEFAULT_PRESET[fam]);
        // …but the OPENING canvas stays artless. setLayoutPreset seeds the preset's
        // weight/text elements (the grip's two "45 -LB-"); those must not show on the
        // untouched plate — smartStart re-seeds them on logo upload. The bumper never
        // reaches this branch (its family already matches the default), so it opened
        // blank and the grip didn't (Ryan 2026-07-16).
        seedBlank(state);
      }
    }
  }
  /* WHO IS THIS DESIGN? — see the long note further up, where this line used to live. It waits for
     the gate because `resumedNow` is the whole question: a remembered `WEIGHT-` ref pointed at the
     WRONG artwork would file brand-new work under an old reference number in a customer's proposal.
     True only when THIS browser's own autosave was restored, which covers all four ways the identity
     goes stale — they left the Resume pill alone, they arrived on a `?d=` share link, the restore
     failed, or there was nothing to resume.
     ⚠ Still before `createDesignsPanel`, which seeds itself from this key. Both are statements in
     this same `mount()`, in this order. */
  if (!resumedNow) clearDesignIdentity();
  /* The picks the session STARTED with are not additions — whether they came from the gate, a
     resumed autosave, a share link or a rep's saved design. Seeded here, once, after every one of
     those paths has had its say, so the first thing `notePickAdditions` ever diffs against is a
     truthful baseline. Without it the whole opening set reads as "just added" and the first visit to
     the design step would jump the buyer to the start of their own set. */
  lastPickKeys = new Set(pickedProducts().map((p) => p.key));

  // The buyer is past the landing gate and into the tool — the funnel's denominator (Phase D1's
  // conversion instrumentation, finally built 2026-07-26). Deliberately NOT counted for a
  // SHARED-link visitor: they were sent a finished plate to look at, and folding them into
  // "designs started" would inflate the top of the funnel with people who never intended to
  // design anything, making every downstream rate look worse than it is.
  if (!sharedNow) wdTrack(WD_EVENTS.STARTED);

  // …and the campaign that sent them, captured HERE rather than at submit time — a design session
  // runs long enough that the URL is usually something else by the end of it. See `captureUtm`.
  captureUtm();

  // Single shared context object. Created empty, passed by reference to every
  // controller, then filled in below. Most ctx.* is read at call-time (inside
  // event handlers) by which point it's fully populated — but `state` is made
  // available up front so a controller that legitimately reads state during
  // construction can't hit `ctx.state === undefined` (that white-screened the
  // whole app once — a construction-time read the pure-logic tests can't catch).
  const ctx = {};
  /* ── EVERY PLATE'S RECOLOURED LOGO, KEPT (Ryan 2026-08-10) ──────────────────────────────────────
   * The quote form draws a card per configuration, and a configuration's logo is not its artwork —
   * it is the artwork RECOLOURED for that plate (the Wallabies design: yellow bands on the green
   * plate, green bands on the yellow one). That recolour is a canvas job, so a synchronous thumbnail
   * can never produce it on demand and the card fell back to the raw upload — which on the yellow
   * plate meant yellow-on-yellow, i.e. a logo nobody could see.
   *
   * ⭐ IT IS ALREADY BUILT, AND WAS BEING THROWN AWAY. `syncLogoPeel` composites exactly this raster
   * for whichever plate is live, every time the buyer visits one — so by the time they reach the
   * quote form they have paid for every configuration they designed. Keeping the result costs a map
   * entry and buys the card the real picture with no second implementation of the peel.
   * ⚠ KEYED BY INK CONTEXT, the same axis the ink itself is remembered on (`savedInk`) — a plate
   * colour, not a product. Two configurations dyed the same colour share one raster, correctly.
   * ⚠ A MISS IS NOT AN ERROR. A resumed design whose other configuration was never opened this
   * session simply has no raster, and the card falls back to the plain artwork exactly as before.
   */
  const logoRasterByInk = new Map();
  /* ⛔ FINGERPRINTED AGAINST THE SOURCE ART, or a cached raster outlives the logo it was made from —
     upload a new mark and every stored composite is of the OLD one, on a card that looks convincing.
     Same length-plus-tail fingerprint `syncLogoPeel`'s own key uses, and for the same reason: these
     are base64 strings that run to megabytes. */
  const logoArtKey = () => {
    const el = (state.designProfile.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
    const d = el && el.logo.dataUrl;
    return typeof d === 'string' && d ? `${d.length}:${d.slice(-16)}` : '-';
  };

  ctx.state = state;

  // 3. skeleton --------------------------------------------------------------
  // Gym-Designer dark workspace shell (Ryan 2026-07-11 crossover): a CENTERED
  // folder-tab step row sits ABOVE one framed canvas container. Editing now
  // lives in a LEFT slide-out overlay (was a static right panel); select / hand
  // / undo / redo / zoom float as a top-right tool cluster; a powered-by badge
  // sits bottom-right; the status pill floats top-center. All the absolute
  // chrome mounts INSIDE the canvas frame (preview.el = the shell `.ds-canvas`).
  // Glass law unchanged: the slide-out + tools are frosted chrome; the visible
  // stage stays the one clean, unfrosted region. Shell pieces are WD-agnostic
  // factories from ./ui/shell/* (see DESIGNER-SHELL-UI-KIT.md).
  const status = createStatusBar();
  const preview = createPreview(ctx);

  const toolMode = 'orbit'; // the ONLY interaction — drag-to-orbit (select/hand tools removed, QW-3) — applied to the 3D on mount

  // Left slide-out editing pane — now the primary editing surface (hosts each
  // step's controller element; goToStep swaps the body + title). Its open/min/
  // close state is mirrored onto the stage so the plate slides to stay centered
  // in the visible canvas (Ryan 2026-07-13 — see .wd-pane-* in weight-designer.css).
  // How far the plate shifts to clear the pane, per state (desktop only — on a phone
  // the pane is a bottom sheet, no horizontal shift). The 3D plate follows via a CAMERA
  // lens-shift (reveal.setPaneShift), NOT a CSS canvas-translate — that translate left a
  // dead strip on the roll-in (Ryan 2026-07-17). The flat SVG fallback still uses the CSS
  // `--wd-plate-x` (no roll-in to spoil). `.wd-pane-*` on the stage drives the SVG + the
  // pane-following chrome; setPaneShift drives the 3D.
  const PANE_SHIFT_PX = { 'wd-pane-open': 160, 'wd-pane-min': 24 };
  const isDesktopPane = () =>
    (typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 481px)').matches : true);
  const paneShiftFor = (cls) => (cls && isDesktopPane() ? PANE_SHIFT_PX[cls] || 0 : 0);
  /* ── A CONTROL THAT SLIDES UNDER YOUR CURSOR MUST NOT ACT ON IT (Ryan 2026-08-07) ──────────────
   * *"when you click the ‹ button to fully retract the pane, due to the location of the save and
   * share buttons, they will usually slide under your mouse, which results in the pop-up from one
   * of them sliding down as the pane is retracting."*
   *
   * The Save/Share cluster rides the pane edge (`--wd-pane-w`, a 0.32s `left` transition), and its
   * cards open on `mouseenter`. The collapse chevron sits almost exactly where the cluster is
   * heading, so the pointer does not move at all — the BUTTON arrives under it, the browser fires a
   * perfectly genuine `mouseenter`, and a card drops open on a gesture nobody made.
   *
   * ⚠ THE BUTTONS GO INERT, NOT THE CLUSTER. An already-open card slides along with it and stays
   * usable; only the hover targets stop answering, which is precisely the input that is bogus here.
   * Killing the whole cluster would take a card the buyer deliberately opened offline mid-slide.
   *
   * ⚠ AND ONLY WHEN IT IS ACTUALLY MOVING. `animate: false` is the first paint and a resize — there
   * is no travel, so there is no phantom hover, and blocking clicks for a third of a second there
   * would be a bug of its own. */
  const PANE_FOLLOW_SLIDE_MS = 320;   // `.ds-canvas .ds-actions-cluster`'s own `left` transition
  let paneFollowMoveTimer = 0;
  const holdPaneFollowHover = () => {
    preview.el.classList.add('wd-pane-moving');
    clearTimeout(paneFollowMoveTimer);
    // Timer rather than `transitionend`: that never fires for a transition interrupted by a second
    // click (collapse, expand, collapse), and a cluster left inert would be a dead control forever.
    paneFollowMoveTimer = setTimeout(() => preview.el.classList.remove('wd-pane-moving'), PANE_FOLLOW_SLIDE_MS + 60);
  };

  const syncPanePlate = (cls, { animate = true } = {}) => {
    preview.el.classList.remove('wd-pane-open', 'wd-pane-min');
    if (cls) preview.el.classList.add(cls);
    if (animate) holdPaneFollowHover();
    /* ⚠ A FULL-WIDTH PANE HAS NO REMAINING STAGE TO CENTRE ON. `wd-pane-open` slides the plate
       160px right so it sits in the half of the frame the 320px pane leaves; with the pane
       covering the frame there is no such half, and shifting anyway would animate the plate
       sideways behind a surface nobody can see through — burning a lens-shift, and leaving it
       parked off-centre for the moment the pane comes back. */
    if (preview.el.classList.contains('wd-pane-wide')) {
      if (reveal && reveal.setPaneShift) reveal.setPaneShift(0, { animate });
      syncBandShift({ animate });
      return;
    }
    /* ⛔ AND A SHARED PRESENTATION HAS NO PANE TO CLEAR EITHER (Ryan 2026-08-16: *"when you click the
       next arrow button, the next plate appears off-center but stays off-center"*).
         A flip is a real product switch, and `switchProduct` funnels through here — so every arrow
       press re-derived the shift from the pane's classes and put the plate back out at 160px, where
       it stayed, because nothing re-zeroes it per flip. Zeroing it in the presentation's ENTER
       handshake was only ever going to hold until the next thing asked this function.
         So the rule lives at the seam every pane change already comes through, in the same shape as
       the full-width guard above: while a presentation owns the screen the pane is off it, and the
       plate belongs in the middle. Any future path that moves the pane inherits this instead of
       having to remember it. */
    if (presentationActive()) {
      if (reveal && reveal.setPaneShift) reveal.setPaneShift(0, { animate });
      syncBandShift({ animate });
      return;
    }
    if (reveal && reveal.setPaneShift) reveal.setPaneShift(paneShiftFor(cls), { animate });
    // Every open/minimise/close funnels through here, so the phone's re-frame hangs off the same
    // one call rather than being repeated in the three sidePanel callbacks.
    syncBandShift({ animate });
  };
  // ── PHONE: step navigation is a bar in the canvas's bottom band (Ryan 2026-07-28) ────────────
  // "remove the POWERED BY on mobile, move the next tab button into that position, make it a clear
  // glass button, add NEXT: to the beginning, then on the other side add a previous button."
  //
  // It began life in the sheet header (entry 107) to answer the real problem: the GATE navigates with
  // Back/Next at the bottom, so relocating navigation to numbered tabs at the top left buyers with no
  // taught way forward. The bottom band says the same thing in the gate's own language and has room
  // for BOTH directions, which the header did not.
  //
  // RIDES THE SHEET rather than sitting at a fixed offset. The powered-by badge it replaces is pinned
  // clear of the minimised header and is deliberately covered once the sheet expands — fine for a
  // credit, wrong for the control that teaches the flow, which would vanish exactly when someone is
  // deepest in a step. `--wd-sheet-h` is written by the same ResizeObserver that re-frames the 3D, so
  // at rest this lands precisely where the badge was and never gets buried.
  // TWO BARS, NOT AN SVG CHEVRON — because it has to MORPH (Ryan 2026-07-28: "when you slide the
  // menu up, have the chevron slide into that slot and become the line… when you slide the menu back
  // down again, have the chevron slide out and go from straight line back to chevron again").
  //   An SVG's `points` cannot be transitioned in CSS, so a polyline could only ever cross-fade with
  // a second shape. A chevron IS two straight bars pivoted at the middle: rotate them to ±16° and it
  // is a chevron, rotate them to 0 and it is one continuous line. That makes the whole morph a
  // transform transition — composited, interruptible, and reversible halfway through.
  //   Three nested elements because three things move independently and one element gets ONE
  // transform: the button carries the slide into the slot, the glyph carries the bounce, and the
  // bars carry the fold. Stacking them on one node is how the bounce would fight the slide.
  const hintBars = () => h('span', { class: 'wd-sheet-hint__glyph', 'aria-hidden': 'true' },
    h('span', { class: 'wd-sheet-hint__bar wd-sheet-hint__bar--l' }),
    h('span', { class: 'wd-sheet-hint__bar wd-sheet-hint__bar--r' }));
  const stepNavBtn = (dir) => h('button', {
    type: 'button',
    // NOT `.wd-flat-glass` (Ryan 2026-07-28: "instead of the next and prev buttons being glass card,
    // make them pills with outlines and clear/see-through fill colors"). The class is dropped rather
    // than overridden — it supplies fill, rim, directional light AND drop shadow as one recipe, and
    // an outline pill is a different tier, not a tuned glass card. Everything these need now lives in
    // `.wd-stepnav__btn`.
    class: 'wd-stepnav__btn' + (dir < 0 ? ' wd-stepnav__btn--prev' : ' wd-stepnav__btn--next'),
    // LEAVE THE SHEET AS IT WAS (Ryan 2026-07-28: "if the slide up is collapsed and someone presses a
    // next or previous button, don't auto expand"). `goToStep` opens the pane by default, which is
    // right for a TAB tap — that is a request to go and work on that step. These buttons are used
    // from the canvas, often to look at the plate rather than to edit it, so forcing the sheet up
    // covers the very thing the buyer just navigated to see.
    //   Passing the CURRENT state rather than a flat `false`: `open: false` only declines to expand,
    // it never collapses, so an already-open sheet stays open and a minimised one stays minimised.
    // The step still changes underneath either way.
    //
    // ⚠ IT ASKS THE MODEL RATHER THAN ADDING ±1 (2026-08-06). On Customize Design a press is now a
    // move through the buyer's PICKED PRODUCTS, not a step — so `currentStep + dir` was the wrong
    // arithmetic in exactly the place the sequence lives. `navNow` is the same call the desktop
    // pane footer makes, and re-read at CLICK time rather than closed over, so an edit to the
    // picked set under a painted button can never fire a stale destination.
    onClick: (e) => {
      e.stopPropagation();
      const stop = navNow()[dir < 0 ? 'back' : 'forward'];
      if (stop) goToStop(stop, { open: sidePanel.isOpen() });
    },
  });
  const prevStepBtn = stepNavBtn(-1);
  const nextStepBtn = stepNavBtn(1);
  const stepNav = h('div', { class: 'wd-stepnav', role: 'group', 'aria-label': 'Step navigation' }, prevStepBtn, nextStepBtn);

  /* ── THE FORWARD NUDGE, PHONE EDITION (Ryan 2026-08-08) ─────────────────────────────────────────
   * *"once the design my weights button is pressed, should someone go 5 seconds without navigating
   * away from this page, have the next button to go to the CD tab, begin pulsing with the white
   * outline."*
   *
   * ⛔ THE DESKTOP NUDGE COULD NEVER HAVE REACHED A PHONE, and that is why this looks like a
   * duplicate and is not. `ui/step-nav.js` owns the hint for the pane FOOTER's forward button —
   * and `createPaneNav` is built behind `if (!isPhone())` a few hundred lines below, so `paneNav`
   * is null on a phone and `ctx.armForwardHint()` has always been a silent no-op there. The phone's
   * forward control is THIS pill, in the sheet header, which is a different node in a different
   * surface. Two surfaces, so two arms; what they share is the CSS rule and the 5s constant.
   *
   * ⚠ A DELAY, NOT AN IDLE TIMER — the same divergence as `armSheetHint` above, for the same
   * reason. Ryan named the cancel as NAVIGATING AWAY, not stillness, and on a phone the first thing
   * anyone does with a freshly built plate is spin it: a drag is a stream of `pointerdown`s, so an
   * idle timer would reset forever for precisely the buyer who has not noticed there is a next step.
   *
   * ⚠ NO VISIBILITY WAIT NEEDED HERE, unlike the footer's. This pill lives in the sheet HEADER
   * specifically so it survives the sheet being collapsed — that is the whole reason the phone has
   * its own nav surface. It only hides when there is nowhere forward, which the fire-time check
   * covers.
   */
  const STEPNAV_HINT_MS = 5000;
  let stepNavHintTimer = null;
  let stepNavHintSpent = false;
  let stepNavHintStep = null;
  function endStepNavHint() {
    if (stepNavHintTimer) { window.clearTimeout(stepNavHintTimer); stepNavHintTimer = null; }
    stepNavHintStep = null;
    nextStepBtn.classList.remove('is-hinting');
  }
  function armStepNavHint() {
    if (stepNavHintSpent || !isPhone()) return;
    stepNavHintStep = Number(state.ui.currentStep) || null;
    if (!stepNavHintStep) return;
    stepNavHintSpent = true;   // one-shot for the session, like the footer's `spent`
    if (stepNavHintTimer) window.clearTimeout(stepNavHintTimer);
    stepNavHintTimer = window.setTimeout(() => {
      stepNavHintTimer = null;
      /* Re-checked at FIRE time, not just at arm time. Five seconds is long enough for the buyer to
         have moved on, and `syncStepNav` hides this pill outright when there is nowhere forward —
         a ring on a `display: none` button is a nudge nobody receives. */
      if (Number(state.ui.currentStep) !== stepNavHintStep) return;
      if (nextStepBtn.hidden || !nextStepBtn.getClientRects().length) return;
      nextStepBtn.classList.add('is-hinting');
    }, STEPNAV_HINT_MS);
  }
  // The DESKTOP half of the same idea — the pane's persistent footer. Built below (it needs
  // `goToStop`, which needs `sidePanel`); null on a phone, where `stepNav` above does this job.
  let paneNav = null;
  /**
   * WHERE BACK AND FORWARD POINT — asked in exactly one place, by both nav surfaces.
   *
   * The label tables and the picked set both live in this file, so the model is called from here
   * and handed its inputs rather than importing them: `pickedProducts()` is the SAME list the
   * down-tab bar cycles, in the same order, which is what keeps the footer's "Next: …" and the
   * bar's › from ever disagreeing about which product comes next.
   */
  const navNow = () => navTargets(state, {
    stepLabels: STEP_LABELS,
    shortLabels: STEP_NAV_LABELS,
    products: pickedProducts(),
  });

  // The "there is more up here" nudge, shown only while the sheet is collapsed. It sits on the CANVAS
  // rather than inside the sheet because `.ds-side-panel` is `overflow: hidden`, so anything above its
  // top edge would be clipped away.
  //   AND IT IS A BUTTON, not a decoration. With the chevron control gone and header taps deliberately
  // inert, dragging would otherwise be the only way to open the sheet — and an affordance that looks
  // pressable but ignores presses is exactly the bug the grab line was (entry 107). Tapping it opens;
  // the drag still works and is what the bounce is pointing at.
  const sheetHint = h('button', {
    type: 'button',
    class: 'wd-sheet-hint',
    'aria-label': 'Expand panel',
    onClick: (e) => { e.stopPropagation(); sidePanel.open(); },
  }, hintBars());

  const STEP_ADD_LOGO = 2;    // STEP_LABELS[1] — 'Add Logo', where a first design begins
  const syncStepNav = () => {
    if (!isPhone()) return;
    // Gating reads the same fact as the tabs (`refreshControls` → `stepTabs.setDisabled`): 3 and 4
    // need a logo. One rule, not a second copy that can drift out of step with it — `stepGate`.
    //
    // ⚠ HIDDEN HERE, DISABLED-WITH-A-REASON IN THE PANE FOOTER, and the split is deliberate rather
    // than an oversight. The pill is one of three things sharing a phone's sheet header; there is
    // no room beside it for the sentence that would make a greyed button honest, and a dead pill
    // with nothing explaining it is the very thing the footer's reason line exists to avoid. Same
    // predicate, two presentations — which is why the predicate is a function and not a boolean.
    const { back, forward } = navNow();
    const set = (btn, stop, word) => {
      const show = !!stop && stepGate(state, stop.step).ok;
      btn.hidden = !show;
      if (!show) return;
      // ── AN ARROW, NOT THE WORD (Ryan 2026-07-28) ────────────────────────────────────────────
      // "instead of PREV: and NEXT:, do you think simply writing the tab name with a forward or back
      // arrow would be more effective? This might also let us increase the font size."
      //   It would, and the room is the smaller half of it. DIRECTION was being said three times over
      // — the word, the arrow's slot in the `1fr auto 1fr` grid (left is back, right is forward), and
      // the presence of a twin opposite it — while the DESTINATION, the only part a buyer cannot
      // already infer, was the smallest text on the button. Dropping the word removes a redundancy
      // and promotes what is actually informative.
      //   And it pays for itself: `NEXT: Customize` → `Customize ›` is 15 characters down to 11, a
      // 27% cut, against 10.5px → 12px which costs 14% per character. Still ~17% narrower than it was,
      // at the bigger size.
      //   The aria label is untouched and still says "Next step: <full name>" — a screen reader is not
      // fighting for horizontal room, and it should not have to infer direction from a glyph either.
      //   ⚠ AN SVG, NOT `‹` / `›` (Ryan 2026-07-28: "arrows need to be taller, like as tall as the
      // letters"). Those are single guillemets — PUNCTUATION, drawn at about x-height and centred on
      // the lowercase, so no font-size will lift them to cap height without thickening the stroke and
      // throwing off the pill's optical weight with it. A stroked path is sized and weighted
      // independently: `1em` tall against a `2.2` stroke, so it grows with the label and stays a
      // hairline. It is also the same chevron the carousel arrows and the gate use.
      //   ⚠ THE STOP CARRIES ITS OWN SHORT FORM. A step shortens through `STEP_NAV_LABELS`
      // ("Customize Design" → "Customize"); a PRODUCT shortens by dropping the category, the same
      // segment the down-tab bar's own <900px rule drops first. Both decisions live on the stop
      // (ui/step-nav.js), so this button reads a name rather than re-deriving one.
      const name = stop.short;
      const chev = h('span', {
        class: 'wd-stepnav__chev', 'aria-hidden': 'true',
        html: word === 'NEXT' ? STEPNAV_CHEVRON_R : STEPNAV_CHEVRON_L,
      });
      // ⚠ The label is a SPAN, not a bare text node. This button is a flex row now, and a loose text
      // node in a flex container becomes an anonymous item that `text-overflow` cannot reach — so the
      // ellipsis the pill has always used for a long step name would have quietly stopped working.
      /* ⚠ AND THE TEXT GETS ITS OWN SPAN INSIDE THAT ONE (Ryan 2026-08-09: *"when they contain some
         long text like 'Training Bumpers', the text will truncate with '…'. Instead, can you have the
         text slowly scroll on these buttons so you can read them in full?"*).
           The clipping box and the moving box cannot be the same element. This label is a flex item
         that SHRINKS — that is what produces the ellipsis in the first place — so translating it
         would slide the text out over the chevron sitting beside it rather than under a clip. The
         outer span stays the window; the inner one is what travels. See `syncNavMarquee`. */
      const label = h('span', { class: 'wd-stepnav__label' },
        h('span', { class: 'wd-stepnav__labeltext' }, name));
      btn.textContent = '';
      if (word === 'NEXT') btn.append(label, chev); else btn.append(chev, label);
      // The SPOKEN label is the pane footer's full phrase — "Back to Pick Weights", "Next: 55 lb".
      // A screen reader is not fighting for horizontal room, so it gets the unshortened
      // destination; and it is `navPhrase`, the one the footer prints, so the two surfaces can
      // never describe the same button differently.
      btn.setAttribute('aria-label', navPhrase(stop, word === 'NEXT' ? 1 : -1));
    };
    set(prevStepBtn, back, 'PREV');
    set(nextStepBtn, forward, 'NEXT');
    /* ── THE FIRST-RUN HEADER IS EMPTY, AND THAT IS THE POINT (Ryan 2026-08-15) ──────────────────
     * *"on mobile, when you first start a design, don't show the 'Pick Weights/back' button on the
     * header of the slide-up pane (less distractions)… Once the logo is uploaded, the pick
     * weights/back pill/button can be visible on the header again."*
     *
     * On Add Logo with no logo yet, `set()` above has already hidden NEXT (3 is logo-gated) and shown
     * PREV — so the header carries exactly one pill, "‹ Pick Weights", pointing BACKWARDS out of the
     * only screen that matters. The buyer's whole job here is to open the sheet and upload.
     *
     * 🔑 IT ALSO WIDENS THE TAP TARGET, which is the half that is not about clutter. `side-panel.js`
     * excludes the chevron and the ACTION SLOT from the header's tap/drag zone — a drag starting on
     * a pill is not a sheet drag. The pill is the only thing in that slot, so hiding it makes the
     * entire header minus the chevron one tap-to-open surface, which is what the 5s pulse around the
     * collapsed sheet (`armSheetHint`) has been pointing at all along.
     *
     * ⚠ `state.ui.hasLogo` IS THE SAME FACT `refreshControls` GATES TABS 3/4 ON, read here rather
     * than re-derived — and `refreshControls` → `syncNav` → here is what puts the pill back the
     * instant the logo lands, with no second hook to remember.
     */
    const firstRunClear = isPhone() && state.ui.currentStep === STEP_ADD_LOGO && !state.ui.hasLogo;
    // Hidden at both ends → the bar is nothing but empty space; collapse it so it cannot eat taps or
    // reserve room in the band the 3D is fitted around.
    stepNav.hidden = (prevStepBtn.hidden && nextStepBtn.hidden) || firstRunClear;
    /* Measured on the NEXT frame, never here: the labels were written microseconds ago and the flex
       row has not been laid out yet, so `clientWidth` would still describe the previous step's
       button — which reads as "the marquee is off on the one step that needs it". */
    requestAnimationFrame(syncNavMarquee);
  };

  /* ── THE STEP-NAV MARQUEE (Ryan 2026-08-09) ────────────────────────────────────────────────────
   * *"Can be on a 3 second timer for the one on the right and 6 second for the one on the left or
   * something? So they sort of alternate?"*
   *
   * His two numbers ARE the design, and they fall out of one 6s cycle: with `infinite`, a 3s delay
   * puts the right button's runs at 3s, 9s, 15s… and a 6s delay puts the left's at 6s, 12s, 18s… —
   * permanently half a cycle apart, so one is always resting while the other reads. Two independent
   * timers would drift into unison eventually; sharing the period means they cannot.
   *
   * ⚠ ONLY WHEN IT ACTUALLY OVERFLOWS. A label that fits must not wander — motion on this row is
   * meant to say "there is more text here", so a button that moves while showing its whole name is
   * saying something untrue. `--wd-nav-shift` carries the exact overflow, so the travel stops with
   * the last letter at the edge instead of scrolling into empty space.
   *
   * ⚠ AND THE INNER SPAN IS `inline` UNTIL IT SCROLLS. `text-overflow: ellipsis` works on inline
   * content but skips an atomic inline-level box, so making it `inline-block` up front would have
   * silently killed the ellipsis on every label — including the ones that never scroll. Transforms
   * do not apply to non-replaced inline elements either, so it cannot simply always be a block. The
   * stylesheet flips it with the same class that starts the animation: ellipsis at rest, marquee
   * when there is something to reveal. */
  function syncNavMarquee() {
    for (const btn of [prevStepBtn, nextStepBtn]) {
      syncMarquee(btn.querySelector('.wd-stepnav__label'), btn.hidden);
    }
  }
  /* Re-measured on resize — see `observeMarquee`, which owns the why. */
  observeMarquee(stepNav, syncNavMarquee);

  // ── The Weight tab's close-up (Ryan 2026-07-28) ───────────────────────────────────────────────
  // Declared up here because three different places END it — the sheet minimising (right below), a
  // step change, and a sub-tab change — while only the Weight section starts it. A flag rather than
  // asking the reveal "are you held?": the eyedropper holds the camera through the SAME machinery,
  // so that question cannot tell the two apart and a restore would yank an open eyedropper's view.
  let weightZoomOn = false;
  // ── THE BUYER CAN OVERRULE THE CAMERA (Ryan 2026-07-28) ────────────────────────────────────────
  // "if they manually zoom out or change their zoom with their fingers or by clicking the zoom
  // button, then continue doing the thing that resulted in the zoom in the first place (like
  // selecting fonts), then we shouldn't re-zoom them back to where they just were."
  //
  // Detected by COMPARING, not by listening. The alternative is wiring a signal out of every way a
  // zoom can change — the +/− buttons, a pinch, a two-finger dolly — and any path missed would
  // silently keep overriding the buyer. Instead each auto-zoom records where it landed, and the next
  // one asks whether the camera is still there. If it is not, something else moved it, and that
  // something can only have been the buyer.
  //   The latch resets when the focus session ends, so leaving the tab and coming back is a fresh
  // start rather than a permanent opt-out of a feature they only declined once.
  let autoZoomPct = null;       // where our last auto-zoom actually landed
  let zoomOverridden = false;   // ...and whether they have since moved it themselves
  const ZOOM_DRIFT_TOL = 1.5;   // percentage points; the plate is held still, so any real move exceeds this
  const runFocusZoom = (focus, { refit = false } = {}) => {
    if (!isPhone() || !reveal || !reveal.squareUp) return Promise.resolve();
    /* ⛔ AN OVERRIDE STOPS THE ZOOM, NOT THE AIM (Ryan 2026-08-09: *"the camera isn't tracking the
     * weight number's movement… I thought we discussed this"*).
     *
     * 🔑 THE TWO RULES HE HAS ASKED FOR COLLIDE HERE, and the older one was silently winning. In
     * July: *"if they manually zoom out… we shouldn't re-zoom them back."* Today: keep the number
     * centred as it moves. This returned outright, so the first pinch ended tracking for the rest of
     * the session — and pinching became far easier the same day, when a re-aim stopped taking the
     * controls. He would have hit this the moment both changes were in front of him.
     *   Separating the two settles it without either giving way: a DISTANCE they chose is theirs and
     * is never overwritten, while a re-AIM only pans, which is the whole of what "keep it centred"
     * means. `holdUserDistance` tells the reveal to frame from where the camera actually is. */
    if (zoomOverridden) {
      return reveal.squareUp(100, focus, { keepDistance: true, holdUserDistance: true });
    }
    /* Has a close-up already landed in THIS session? `autoZoomPct` is set by the first one and
       cleared by `endFocusZoom`, so it doubles as the answer — and the two uses agree: the same
       thing that makes a drift comparison possible is the thing that says "we are already in
       close". */
    const already = autoZoomPct != null;
    if (already && typeof reveal.getZoomPercent === 'function'
      && Math.abs(reveal.getZoomPercent() - autoZoomPct) > ZOOM_DRIFT_TOL) {
      zoomOverridden = true;
      return Promise.resolve();
    }
    // A pending pull-back is a camera move this zoom is about to contradict — drop it, or it would
    // land mid-close-up and yank the buyer back out a fraction of a second after they leaned in.
    clearTimeout(focusReturnTimer);
    weightZoomOn = true;
    /* ⛔ THE FIT HAPPENS ONCE PER SESSION; EVERY CLICK AFTER IT ONLY RE-AIMS (Ryan 2026-08-08:
       *"once the logo or weight number is scaled, it should NOT keep scaling every time you press
       the + or − toggle buttons… it's hard to appreciate how much the size is changing by with each
       click"*). A fit frames the mark to a fixed share of the screen, so re-fitting after a size
       change undoes the size change on screen — the camera backs off by exactly the amount the mark
       grew. The reasoning, and why re-aiming must still run, is at `keepDistance` in reveal-3d.
         `endFocusZoom` resets the session, so leaving the tab and coming back fits afresh — which is
       right: that is a buyer arriving, not a buyer mid-adjustment. */
    // 100 = "no magnification": a focus carrying extents replaces the percent outright, so this is
    // only the fallback for a target that could not report a box (framing the whole face, unzoomed,
    // which is the honest answer when we do not know how big the thing is).
    /* ⛔ A NUDGE RE-FITS; A SIZE CHANGE DOES NOT (Ryan 2026-08-09: *"as the logo moves, the camera
     * should also zoom in on the viewable area… as that area grows, the camera zooms out slightly.
     * As it shrinks due to the logo going off the canvas, the camera should zoom in slightly"*).
     *
     * 🔑 THIS LOOKS LIKE IT CONTRADICTS THE FIT-ONCE RULE AND DOES NOT. That rule (08-08) exists
     * because re-fitting after a SIZE press backed the camera off by exactly the amount the mark had
     * grown, so the dial appeared to do nothing — the fit was cancelling the edit. A nudge changes no
     * size at all: the mark is the same, only the share of it that is ON THE PLATE moves, and that
     * share is precisely what the buyer is asking to see. Re-fitting there cancels nothing and is the
     * whole feature.
     *   So the distinction is not "first call vs later call", it is WHICH CONTROL asked. `refit` is
     * passed by the two nudge pads and by nothing else. */
    return reveal.squareUp(100, focus, { keepDistance: already && !refit }).then(() => {
      // Read it back rather than assuming: the fit is clamped by `controls.minDistance`, so what we
      // asked for and what we got are not always the same number — and it is what we GOT that a
      // later comparison has to be against.
      if (reveal && typeof reveal.getZoomPercent === 'function') autoZoomPct = reveal.getZoomPercent();
    });
  };
  const focusWeightZoom = (opts) => {
    // Phone only. The Weight sub-tab is itself a phone construct — on desktop all four sections
    // stack in one pane, so there is no "tab" to be on and no reason for a click to move the camera.
    if (!isPhone()) return Promise.resolve();
    // Recomputed on EVERY click rather than cached at focus time: the controls being clicked change
    // the number's own box — a longer style ("45 POUNDS") is wider and re-runs auto-balance, drifting
    // both side pieces by a few degrees. A cached frame would slowly fall off the thing it framed.
    //   A layout with no weight element at all (logo-only) yields null, and the plain centred
    // square-up is the right answer there — there is nothing else to aim at.
    return runFocusZoom(weightFocusPoint(state.designProfile, modelFor(state.ui.product)), opts);
  };
  // The same move for the Text tab. No percent here either — the arch reports its own box and gets
  // framed by it, which is why a short word now fills the window where a fixed magnification had to
  // be set low enough for the longest possible line.
  // The Add Logo tab's size dial gets the same treatment (Ryan 2026-07-28) — sizing a mark is a
  // judgement you make by looking at it, and the logo is the one thing on the plate that came from
  // the buyer rather than from the geometry.
  const focusLogoZoom = (opts) => {
    if (!isPhone()) return Promise.resolve();
    const focus = logoFocusPoint(state.designProfile, modelFor(state.ui.product));
    if (!focus) return Promise.resolve(); // no logo yet — nothing to lean in on
    /* `logoFocusPoint` reports the extents of the mark's INTERSECTION with the paintable surface, so
       a mark hanging half off arrives here as a half-width box — which is exactly the number a re-fit
       needs to close in on. The camera work is entirely in that one already-correct measurement. */
    return runFocusZoom(focus, opts);
  };
  const focusTextZoom = () => {
    if (!isPhone()) return Promise.resolve();
    const focus = arcTextFocusPoint(state.designProfile, modelFor(state.ui.product));
    // Nothing typed in either arch → no zoom at all, rather than a centred one. A close-up of blank
    // ring says less about a typeface than the whole plate does, and the buyer has not asked to look
    // anywhere in particular yet.
    if (!focus) return Promise.resolve();
    return runFocusZoom(focus); // same latch + override, so the same exits release both
  };
  /* The sheet's own collapse is `max-height 0.55s` (shell.css). A margin on top so the camera starts
     after the last frame of it, not on it. ⚠ Tied to that declaration — slow one, slow both, or the
     camera goes back to sharing frames with the collapse it was moved out of. */
  const SHEET_COLLAPSE_MS = 570;
  let focusReturnTimer = null;
  const endFocusZoom = ({ after = 0 } = {}) => {
    // The override latch clears whether or not a zoom is live: leaving the tab ends the SESSION the
    // buyer overruled, and coming back should offer the close-up again rather than hold the refusal
    // against them for the rest of the visit.
    autoZoomPct = null;
    zoomOverridden = false;
    if (!weightZoomOn) return;
    weightZoomOn = false;
    const pullBack = () => { if (reveal && reveal.returnFromColorPick) reveal.returnFromColorPick(); };
    /* ── THE CAMERA WAITS FOR THE SHEET (Ryan 2026-08-09) ──────────────────────────────────────
     * After the body-height freeze fixed the sheet drag everywhere else, one case survived: closing
     * it *from the Weight tab*, and only closing. That pair is a signature, not a coincidence —
     * `weightZoomOn` is true on exactly one sub-tab, and this is the only thing it gates.
     *   So the collapse was sharing its frames with a camera tween. `returnFromColorPick` drives the
     * Three.js loop at full rate for the length of its ease, on the same main thread that is
     * rastering a sheet the finger is still moving. Neither is wrong on its own; running them
     * together makes the collapse pay for the zoom-out.
     *   Sequencing them costs nothing anyone asked for. Ryan's ask was *"if you slide down the pane…
     * you should smoothly auto-zoom back to regular levels"* — the zoom-out is the ANSWER to the
     * pane closing, and an answer is allowed to arrive after the question has finished being asked.
     *   ⚠ Only the TWEEN waits. The two latches above are cleared on this tick either way: they are
     * bookkeeping about a session that has already ended, and a re-open in the gap must find them
     * already reset or it would compare its first close-up against a stale `autoZoomPct`. */
    if (!after) { pullBack(); return; }
    clearTimeout(focusReturnTimer);
    focusReturnTimer = setTimeout(pullBack, after);
  };

  /* ── THE MINIMISED SHEET'S NUDGE (Ryan 2026-08-07, phone only) ─────────────────────────────────
   * *"upon initially selecting some weights and clicking the start designing button, a model will
   * load and the slide up pane will be slid down. if, after 5 seconds the user has not slid the pane
   * up yet, can you have it start pulsing? the next button on desktop does this, can use a similar
   * UX."*
   *
   * It answers the same dead end the forward-button nudge does (`ui/step-nav.js`) one screen
   * earlier: the buyer arrives on a finished-looking plate with every control parked off-screen as a
   * 48px bar, and nothing on screen says the bar opens.
   *
   * ⚠ A DELAY, NOT AN IDLE TIMER — and that is a deliberate divergence from step-nav's nudge, which
   * restarts on every pointer/key event. There the condition is the BUYER ("has stopped doing
   * anything"), so activity is the right thing to watch. Here Ryan named the condition as the PANE
   * ("has not slid the pane up yet"), and on this screen the difference decides whether it ever
   * fires at all: the first thing anyone does with a fresh model is spin it, which is continuous
   * pointer activity, so an idle timer would stay silent for precisely the buyer who has not found
   * the sheet.
   *
   * ⚠ ARMED FROM `revealChrome`, not from the boot. The sheet is hidden until `.wd-ui-ready`, and
   * the chrome is gated on the weight's roll-in — so counting from boot would start the clock up to
   * ~2s before the bar the buyer is supposed to notice is on screen.
   *
   * ⚠ ONE-SHOT for the session. Opening the sheet spends it, so someone who slides it back down
   * later is never nagged: they have demonstrably found it. Same rule, and the same reasoning, as
   * step-nav's `spent`.
   */
  /* ── ⛔ THE CROSS-SESSION LATCH IS GONE, AND IT IS A REVERSAL (Ryan 2026-08-15: *"maybe we remove
   *      that latch?"*) ────────────────────────────────────────────────────────────────────────────
   * From 2026-08-08 until now this ALSO checked a localStorage flag, `wd:v2:cd-seen`, written the
   * first time you reached Customize Design with the sheet open. Ryan asked for it then — *"This
   * shouldn't happen on resume, unless they genuinely never went into the CD tab… reserved for
   * instances where someone is using the tool for the first time, and is stuck/lost"* — and asked
   * for it removed a week later, once it became clear what it costs.
   *
   * 🔑 WHAT IT COST IS THE REASON TO KEEP THIS NOTE. A first-run nudge latched per BROWSER is
   * invisible to exactly one group: everyone who has used the tool before — which is every person
   * who would ever review it. Ryan asked twice for a pulse that had been shipping for a week,
   * because his own phone had set the flag and no amount of testing on that phone could ever show
   * it to him. A behaviour nobody on the team can see is a behaviour nobody can verify.
   *
   * ⚠ WHAT COMES BACK WITH THE REMOVAL: `sheetHintSpent` is a SESSION latch, so a resumed project —
   * a fresh session — is nudged again. That is the 2026-08-08 complaint, restored. If it needs
   * answering a second time, the narrow fix is to suppress on a RESUME specifically (`resumedNow`,
   * a few hundred lines below) rather than to latch the browser forever: it answers "did this
   * person already work on THIS design" without blinding the whole team to the feature.
   *
   * ⚠ PHONE ONLY, deliberately, and that part is unchanged. What the pulse teaches is the
   * bottom-sheet gesture, which does not exist on desktop — the pane is already open there.
   */
  /* How long the quote step's ROOM is held after the back pill is pressed — the canvas's unclipping
     and stacking, the body's height reserve, and the model's band ceiling. Must outlast the sheet's
     own `max-height` fall (0.55s, shell.css); the 70ms over is slack, not a second number to keep in
     step. It also times the carousel's release, which is the same question asked once. See
     `goToStep`. */
  const QUOTE_FULL_EXIT_MS = 620;
  let quoteFullExitTimer = 0;
  let quoteThumbsTimer = 0;

  const SHEET_HINT_MS = 5000;
  let sheetHintTimer = null;
  let sheetHintSpent = false;
  const stopSheetHint = ({ spend = false } = {}) => {
    if (spend) sheetHintSpent = true;
    if (sheetHintTimer) { window.clearTimeout(sheetHintTimer); sheetHintTimer = null; }
    sidePanel?.el?.classList.remove('is-hinting');
  };
  const armSheetHint = () => {
    if (sheetHintSpent || !isPhone()) return;
    if (sheetHintTimer) window.clearTimeout(sheetHintTimer);
    sheetHintTimer = window.setTimeout(() => {
      sheetHintTimer = null;
      /* RE-CHECKED AT FIRE TIME, not just at arm time (step-nav's rule, for the same reason): five
         seconds is long enough for the sheet to have been opened or for the layout to have left the
         phone breakpoint, and a ring pulsing around a sheet that is already up reads as a bug. */
      if (!isPhone() || !sidePanel.el.classList.contains('is-minimized')) return;
      sheetHintSpent = true;
      sidePanel.el.classList.add('is-hinting');
    }, SHEET_HINT_MS);
  };

  const sidePanel = createSidePanel({
    // Phone = bottom sheet, so the header becomes a drag handle and stops being a tap target.
    // Decided HERE rather than inside the component: `ui/shell/*` is the module-agnostic shell and
    // must not learn what a phone is (see its header) — it takes the behaviour as a flag.
    dragToggle: isPhone(),
    title: STEP_LABELS[0],
    // Opening it is the thing the nudge was asking for — stop, and never ask again this session.
    onOpen: () => { stopSheetHint({ spend: true }); syncPanePlate('wd-pane-open'); },
    // Sliding the sheet DOWN is one of the three ways out of the Weight tab's close-up (Ryan
    // 2026-07-28) — you have stopped editing and gone back to looking at the whole product.
    onMinimize: () => { endFocusZoom({ after: SHEET_COLLAPSE_MS }); syncPanePlate('wd-pane-min'); },
    onClose: () => { endFocusZoom(); syncPanePlate(null); },
  });

  /* Top-right tool cluster: undo · redo / zoom · help. The select/hand tools are gone
     (Fable QW-3 §6.5 — nothing on the stage is selectable; drag-to-orbit is the
     permanent interaction). The plate always orbits on drag via setToolMode('orbit').

     ⚠ CREATION ORDER IS THE LAYOUT (Ryan 2026-08-07: *"top left is undo, top right is redo, bottom
     left is zoom, bottom right is help"*). The cluster is an 86px-wide wrapping flex row of two
     40px columns, so the four buttons fill it reading-order: 1–2 on the top row, 3–4 on the bottom.
     Zoom used to be created first and therefore sat top-left.
       Done by moving the CALLS rather than with CSS `order`, deliberately. The cluster's roving
     tabindex walks `allBtns` in creation order, so `order` would have left the arrow keys stepping
     through a sequence that no longer matched what is on screen — and the single Tab stop is
     whichever button is built first, which should be the one in the top-left corner. Reordering the
     DOM keeps visual, Tab and arrow order as one thing instead of three. */
  const toolCluster = createToolCluster({});
  const undoBtn = toolCluster.addAction({
    id: 'undo', title: 'Undo (Ctrl+Z)', icon: ICON_UNDO, onClick: () => doUndo(), disabled: true,
  });
  const redoBtn = toolCluster.addAction({
    id: 'redo', title: 'Redo (Ctrl+Shift+Z)', icon: ICON_REDO, onClick: () => doRedo(), disabled: true,
  });
  const zoomBtn = toolCluster.addPopoverButton({ id: 'zoom', title: 'Zoom', icon: ICON_ZOOM });

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — the in-tool AI help assistant (2026-08-01).
     PUBLIC edition only: buyers ask about any feature and get a device-aware answer
     from the WD help corpus (chatbot-worker topic:'weight-designer'). Desktop entry
     is this 4th cluster button (it rides the chrome idle-fade like its neighbours);
     the phone's entry is a hamburger row below — the top row is already full there,
     so the launcher button is CSS-hidden on phones (see .wd-help-launcher).
     Rep mode gets neither: rep demos don't need buyer help. See ui/help-assist.js. */
  const helpAssist = isRepMode() ? null : createHelpAssist({
    getStep: () => state.ui.currentStep || 1,
    getFamilies: () => pickedProducts(),
    isFullscreen: () => !!document.fullscreenElement,
    /* The design that goes WITH the stuck-user hand-off (Q10). `warmShareLink` is the same
       idempotent, envelope-keyed creator Copy link and Share via… use, so a buyer who has already
       shared pays nothing here — and one who has not gets a real, permanent URL the rep can open.
       Called at SUBMIT, never when the offer appears: see the note in ui/help-assist.js. */
    getShareLink: () => warmShareLink(ctx),
    onToggle: (open) => {
      if (!helpBtn) return;
      helpBtn.classList.toggle('is-active', open);
      /* THE WHITE PIN DOT, same class the zoom toggle and the Save/Share buttons use (Ryan
         2026-08-09: *"when someone on desktop clicks on the help button, add the white dot to it
         like we have on the zoom menu"*). It is the cluster's one vocabulary for "this button's
         panel is open and stays open", and the help panel is a pinned panel in every sense — it
         does not peek on hover and it does not retract on mouse-out, which is exactly the state
         the dot exists to distinguish from the blue of a momentary press.
         ⚠ SET WITH `is-active`, NOT INSTEAD OF IT. On the Save/Share cluster the blue means
         "pinned" and the dot means "pinned" — they are deliberately the same event there because
         a hover-peek gets neither (see setPinned in ui/shell/canvas-actions.js). Splitting them
         here would invent a third state the cluster has no meaning for. */
      helpBtn.classList.toggle('is-pinned', open);
      helpBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* Closing must also drop DOM FOCUS, or the button keeps reading as pressed: clearing the two
         classes removes the blue and the dot, but Escape has already flipped the browser into
         keyboard mode, so `.ds-tool-btn:focus-visible` paints a ring over a closed panel. Same
         fix, same reason, as `closeAll`'s blur in canvas-actions.js (Ryan 2026-07-25). */
      if (!open && document.activeElement === helpBtn) helpBtn.blur();
    },
  });
  const helpBtn = helpAssist
    ? toolCluster.addPopoverButton({ id: 'help', title: 'Help — ask about any feature', icon: ICON_HELP })
    : null;
  if (helpBtn) {
    helpBtn.classList.add('wd-help-launcher');
    helpBtn.addEventListener('click', () => helpAssist.toggle());
  }

  // ± steps the READOUT by 5 percentage points, not the distance by a factor (Ryan 2026-07-27:
  // "make the increments of zoom in/out when the +/- buttons are pressed 5%"). It was `zoomBy(1.3)`,
  // a 30% multiplicative dolly — and a factor can't produce even steps on the percent scale anyway:
  // `getZoomPercent` is `cameraDist / dist`, so 1.05 would read 100 → 105 → 110.25 → 115.76.
  //   So the step is computed FROM the current percent, and SNAPPED to the 5% grid rather than added
  // to whatever is there. Snapping is the part that matters: a pinch leaves you on 103.7%, and
  // +5-from-there walks the grid off by that remainder forever. Pressing + at 103.7% goes to 105,
  // not 108.7. zoomBy still owns the min/max clamp, so the ends look after themselves.
  const ZOOM_STEP_PCT = 5;
  const zoomStep = (dir) => {
    if (!reveal) return;
    const cur = reveal.getZoomPercent() || 100;
    const rungs = cur / ZOOM_STEP_PCT;
    // floor+1 / ceil-1 (not round) so a press always MOVES, including when `cur` is already on a rung.
    const next = Math.max(ZOOM_STEP_PCT, (dir > 0 ? Math.floor(rungs) + 1 : Math.ceil(rungs) - 1) * ZOOM_STEP_PCT);
    reveal.zoomBy(next / cur);
  };

  // Zoom popover (in / out / fit / fullscreen — NO rotate).
  //
  // FULLSCREEN IS ON BOTH NOW (Ryan 2026-07-28: "we have the Fullscreen option in the zoom pop-out
  // menu on mobile, but not on desktop — need it on desktop too"). It was phone-only from
  // 2026-07-27, on the reasoning that "on desktop, we don't need the full screen button, the app is
  // already built to be full screen by default" — which is true of the LAYOUT and turns out not to be
  // the whole question. Filling the viewport is not the same as filling the SCREEN: the browser's
  // tab strip, address bar and bookmarks are still there, and on a design tool where the buyer is
  // inspecting a plate up close that band is worth reclaiming on a desktop just as much as on a
  // phone. Same reasoning that kept it on mobile, applied to the other side.
  //
  // `createZoomPopover` omits the button entirely when `onFullscreen` isn't a function, so passing it
  // unconditionally is the whole change — the row sizes itself around whatever it is given.
  const zoomPopover = createZoomPopover({
    onZoomOut: () => zoomStep(-1),
    onZoomIn: () => zoomStep(1),
    onFit: () => { if (reveal) reveal.resetView(); },
    onFullscreen: () => toggleFullscreen(),
    getPercent: () => (reveal ? reveal.getZoomPercent() : 100),
  });
  toolCluster.mount(zoomPopover.el);
  // Link the zoom toggle to the popover it controls for assistive tech (WD-37).
  if (!zoomPopover.el.id) zoomPopover.el.id = 'wd-zoom-popover';
  zoomBtn.setAttribute('aria-controls', zoomPopover.el.id);
  wireZoomToggle();

  // Centered GD folder-tab steps.
  const stepTabs = createStepTabs({ steps: STEP_LABELS, onSelect: (n) => goToStep(n) });

  const poweredBy = createPoweredBy('Blue Sky Fitness Supply');
  // Bottom-left "3D · click to rotate" hint (mirrors powered-by; rides the pane).
  // Teach-then-vanish (Fable QW-5 §6.6): once the buyer has orbited (ever), the
  // hint stays gone — start it hidden for a returning visitor, and dismiss it on
  // their first orbit below.
  // The product down-tab (2026-07-17) — one tab hanging DOWN from the underside
  // of the canvas's top rim, the mirror image of the four step tabs standing up
  // off it. It names the live product and its ‹ › arrows scroll THE BUYER'S PICKS
  // (Ryan 2026-07-15) — every category has geometry, so there's no
  // designable/quote-only split to encode here. Switching swaps the stage AND the
  // quote-step category (switchProduct owns both).
  //
  // It replaced the top-left `.wd-product-switch` glass pill, which rode
  // `.wd-pane-follow` — it slid away under the left pane, so with the pane
  // collapsed there was no product control on screen at all. This one re-centres
  // over the visible stage as the pane moves rather than fleeing it.
  const productDownTab = createProductDownTab({
    products: pickedProducts(),
    onSelect: (key) => switchProduct(key),
  });
  // Vertical centring of the 3D model between whatever occupies the TOP of the stage
  // (the presentation-mode header — org name + dots) and the BOTTOM (the switcher
  // bar, present with 2+ picks). A camera LENS shift (reveal.setBandShift), the
  // vertical twin of the pane-follow (the CSS-translate try was reverted in changelog
  // 44 for a floor seam). The net shift centres the model in the free region:
  //   up = (bottomOccupied − topOccupied) / 2
  // so the bar alone lifts it UP, the header alone drops it DOWN, and both together
  // (presentation + 2+ picks) net to ~centre — which is the overlap Ryan hit once the
  // window got shorter (2026-07-18): the bar was lifting the model straight into the
  // header. BAND = half the bar band (12px bottom offset + 40px pill); HEADER = half
  // the header band (14px top + ~34px caption/dots). The flat-SVG fallback never shows
  // the parade (showcase needs the 3D stage), so its `--wd-plate-y` stays bar-only.
  //
  // PHONE: NO BAR LIFT (2026-07-27). The bar moved out of the bottom band and into the
  // top row beside the zoom/undo/redo cluster, so it no longer sits under the model —
  // and it takes no vertical room the always-present cluster wasn't taking already, so
  // the free region doesn't change with its visibility. Lifting here would shove the
  // plate off centre for the one reason that stopped being true. The flat-SVG twin of
  // this switch is the `--wd-plate-y: 0px` override in weight-designer.css's phone
  // top-row block; flip the two together.
  const BAND_SHIFT_PX = 26;   // bar → lift UP
  /* How much room the parade's header needs to be given, as a SAFE AREA rather than a lens shift.
     A shift moves the model without resizing it, which is why the plate's top edge used to reach into
     the caption and dots; an inset makes reveal-3d fit the model into the band that's left and centre
     it there, so the overlap becomes impossible rather than avoided by a tuned number.
       ⚠ THIS IS NOT THE HEADER'S HEIGHT (~56px). The plate never reached the canvas top to begin
     with — there was already slack — so an inset of the full header over-corrected badly: measured
     48px of clearance, and Ryan's reply was *"you made the weight too small now… it only needed to
     get a tiny bit smaller to avoid the dots."* What the inset has to cover is only the DIFFERENCE
     between the slack and the overlap. 20px lands ~11px of clearance and costs the model about 3% of
     its size, which is the "fraction smaller" that was asked for.
       If the header ever grows a line, this goes up by roughly that line's height — and
     `verify-showcase.mjs` measures the real gap, so the number can be re-derived instead of guessed. */
  /* NO SHRINK AT ALL any more — and the reason is worth keeping, because it took three attempts.
     The plate was underlapping the parade's dots, so I inset the top and shrank the model to fit. That
     treated a symptom. Measured with the switcher bar in play:
         1 product picked → bar hidden → +14px clearance
         2 products       → bar VISIBLE → −12px, still underlapping even with a 20px inset
     The cause is `BAND_SHIFT_PX`: the bar's presence lifts the model 26px, straight at the dots. Ryan
     spotted it ("the weight is programmed to move down slightly when there's only one model loaded")
     and drew the right conclusion — the switcher has no job during a presentation, so the parade hides
     it, the lift goes with it, and no shrinkage is necessary. See `paradeOn` below. */
  /* The last 4px, taken from POSITION rather than size. With the switcher's 26px lift gone the plate
     still measured 4px into the dots, so it drops a touch during the parade — a lens shift, which
     moves the model without resizing it, so Ryan's "no shrinkage" holds. 10px lands ~6px of
     clearance. This is a much smaller cousin of the old HEADER_SHIFT_PX (24px), which had to
     compensate for the lift as well. */
  const SHOWCASE_DROP_PX = 10;
  /* ⭐ A SHARED PRESENTATION DROPS FURTHER, BECAUSE ITS HEADER IS A ROW TALLER (Ryan 2026-08-16:
     *"the weights sometimes slightly underlap [the dots]"*).
       The parade's header is a caption and dots. This one is a title, a MODEL SUB-HEADER, and dots
     — and `SHOWCASE_DROP_PX` was tuned to land ~6px of clearance under the parade's, so inheriting
     it put the plate ~13px into this one. Exactly the case the note above anticipated: *"if the
     header ever grows a line, this goes up by roughly that line's height."*
       Derived, not dialled in. Against the canvas top, with the tightened CSS:
         parade       10 (top 14 + caption 19.2 + gap 8 + dots 9  = 50.2) → ~6px clear
         presentation    (top 10 + title  21.25 + gap 4 + model 15 + gap 4 + dots 9 = 63.25)
       so 63.25 − 50.2 ≈ 13 more, and 10 + 13 = 23 reproduces the parade's clearance under a taller
       header. Every line-height in that sum is written explicitly in the stylesheet for this
       reason; `normal` is font-dependent and would make the arithmetic a guess.
     ⚠ AND IT APPLIES ON A PHONE, where the parade's drop is deliberately zero. The parade never
     ran there in this form; a shared link's first audience is a phone, and its header is the same
     three rows at that viewport. */
  const PRESENT_DROP_PX = 23;
  /* ⚠ A ONE-MODEL SHARE HIDES THE SUB-HEADER **AND** THE DOTS (`paintHeader`), so its header is a
     lone title — SHORTER than the parade's, with nothing below it to underlap. Dropping it would
     push a centred plate off-centre to clear chrome that is not on screen. There is no third case:
     the header has exactly two shapes and this is the other one. */
  const PRESENT_DROP_SOLO_PX = 0;
  // How long the parade's move takes. The sheet's 300ms default is the wrong cadence for a one-shot
  // change with nothing to stay locked to (Ryan: "really smoothly and slowly").
  const SHOWCASE_RESIZE_MS = 700;
  /* ⭐ …EXCEPT LEAVING A PHONE PRESENTATION, WHICH HAS SOMETHING TO STAY LOCKED TO AGAIN (Ryan
     2026-08-12: *"it's the slide-up of the menu that gives the appearance of pushing the weight up
     into its upper/smaller slot… just like when you slide the pane up or down mid project"*).
       Coming back, the sheet is on screen and moving: shell.css slides it over `transform 0.3s`, and
     the model has to read as being pushed up BY it. 700ms would leave the plate still shrinking a
     third of a second after the sheet had arrived, which is the opposite of one motion. Going the
     other way there is nothing to track — the sheet is gone by 320ms and the plate has the whole
     screen to grow into — so entry keeps its slow, deliberate cadence. */
  const SHOWCASE_EXIT_MS = 300; // = shell.css's `.ds-side-panel { transition: transform 0.3s … }`
  // PHONE: how much of the canvas the bottom sheet is covering. Read from the DOM rather than from
  // the CSS percentages (58% / 92% / 48px) because the sheet has three states and a snap variant,
  // and duplicating those numbers here is how they drift. `getBoundingClientRect` is the live truth
  // for all of them at once. Clamped to 70%: below ~30% of the canvas there is not enough band left
  // for the plate to be worth showing, and `setSafeArea` floors the fit anyway.
  // The top button row's BOTTOM EDGE: 12px inset + a 40px button. Matches `--wd-topbar-inset` +
  // `--wd-tool-btn` in weight-designer.css's phone top-row block; if that row is resized, this follows.
  //   Deliberately NO air added on top of it (Ryan 2026-07-28: "I want the gap below the bottom of
  // the weight to mirror the minimal gap you've got above it"). The air comes from FILL_FRACTION's
  // margin, which the fit splits EVENLY between the two ends of the band — so if the band starts at
  // the buttons and ends at the sheet, the gap above and the gap below are the same number by
  // construction. Padding this value would have made the top gap that much larger than the bottom.
  //   Applied ONLY while the sheet is open (`cover ? … : 0`). With the full canvas the model is small
  // enough relative to it that it never reaches the buttons, and declaring a permanent top inset
  // would shift and shrink the default framing — a surface that is already signed off.
  const TOP_CHROME_PX = 52;
  // ONE PRODUCT → A SHORTER TOP INSET (Ryan 2026-07-28: "when someone only has 1 weight selected,
  // the big button at the top for clicking left and right and changing models isn't there, which
  // means we can slightly reduce the padding at the top… allow the top of the model to extend as far
  // up as the bottom of the zoom/undo/redo buttons").
  //   The row's HEIGHT does not change — the switcher and the cluster share one 40px row (entry 104),
  // so 52px is the row's bottom either way. What changes is what is in it HORIZONTALLY: with the
  // switcher gone the row is empty from the left edge to x≈238 on a 390px screen, and the model is
  // centred, so the only thing left for it to clear is a cluster pinned to the far right.
  //   36px is not a taste value, it is where his bound lands. In the tightest state — sheet open, so
  // the vertical band is the limiting axis — the model is FILL_FRACTION (0.82) of the band and centred
  // in it, which puts its top edge at `0.91·T + k`. On a 506px canvas under a ~294px sheet that gives
  // 66px at T=52 and exactly 52px — the cluster's bottom — at T=36. Every looser state (sheet
  // minimised, shorter content) is aspect-limited instead, so the model is smaller than its band and
  // its top sits LOWER: the bound degrades in the safe direction, never past it.
  const TOP_CHROME_SOLO_PX = 36;
  // `el.hidden` is set by `setProducts` as `items.length < 2`, so this is exactly "one weight picked"
  // — the same fact the row itself uses, rather than a second count that could disagree with it.
  const topChrome = () => (productDownTab.el.hidden ? TOP_CHROME_SOLO_PX : TOP_CHROME_PX);
  /** The band the sheet was taking when the quote step claimed the screen. See `sheetCover`. */
  let quoteCoverCeiling = Infinity;
  /* FULL PRESENTATION MODE IS RUNNING — the phone half of what `.wd-presenting` means (the class
     itself goes on at every viewport and the stylesheet's `@media (max-width: 768px) and
     (pointer: coarse)` block, the same query `isPhone()` asks, is what makes it visible). Two
     things read it: `sheetCover` (the sheet is off screen, so it covers nothing) and
     `syncBandShift` (this is a discrete toggle, so its re-frame animates). */
  /* ⚠ TWO THINGS CAN BE PRESENTING, AND THE STAGE MUST NOT KNOW THE DIFFERENCE. The parade (an
     intro, for a blank stage) and presentation mode (a shared `?d=` design) both take the screen
     the same way, so every measurement that asks "is a presentation running" has to answer for
     both — otherwise a shared design gets the chrome cleared and the camera framed as though the
     sheet were still there. Declared here rather than beside `createPresentation` so it exists
     before this predicate closes over it. */
  let presentation = null;
  const paradeActive = () => !!(showcase && showcase.isActive && showcase.isActive());
  const presentationActive = () => !!(presentation && presentation.isActive());
  /* Is a presentation the thing on screen, or about to be? Asked by the 3D mount, which has to know
     where the plate's resting spot is BEFORE it rolls it in, and by the boot's step change, which
     would otherwise open a pane behind it.

     ⚠ TWO TERMS, AND BOTH ARE LOAD-BEARING — one alone is false at exactly the wrong moment.
       · `presentationActive()` is false at BOOT (the presentation cannot open until the stage is
         ready), which is when the roll-in resting spot is decided.
       · `isSharedView` is false during a FLIP: `applyEnvelope` clears it, correctly, and the
         presentation puts it back after the await — so for the whole model rebuild, which is when
         the shift is re-derived, the flag reads false.
     ⛔ It deliberately does NOT ask `canPresent()`. That needs the 3D chunk warm, which at boot it is
     not, so gating on it made this quietly false for the entire boot — the pane opened, the plate
     rolled to its offset, and the presentation then dragged it back. Cleared instead when the
     presentation is left (`present.js` `leave`), which is the moment it stops being true. */
  const sharedPresentationBoot = () => presentationActive() || !!state.ui.isSharedView;
  const presentingNow = () => isPhone() && (paradeActive() || presentationActive());
  /* The same question as `presentingNow`, asked by the CAMERA — and it has to be true one beat
     earlier (Ryan 2026-08-16: *"it needs to roll in at this height on page load. can't start higher
     then jump down"*).
       On a phone the framing comes from the sheet's safe area, not from the desktop lens drop, and
     `presentingNow()` cannot be true until `enter()` runs — which is strictly after the roll-in has
     chosen where to rest. So the plate was framed for a sheet that a shared boot has already taken
     off screen, rolled in high and small, and travelled 22px the moment the presentation opened.
     ⛔ DELIBERATELY NOT FOLDED INTO `presentingNow()` ITSELF. That answer is also the handshake's
     "how long does the stage need before a set-piece may start" (~700ms of re-frame). Widening it
     would promise a wait for a re-frame that, thanks to this very fix, no longer happens — a dead
     pause in front of the one moment the presentation is built around. Two questions that happen to
     share a phrase; only the framing one moves. */
  const presentingFraming = () => isPhone() && (paradeActive() || sharedPresentationBoot());
  const sheetCover = () => {
    if (!isPhone()) return 0;
    const canvasH = preview.el.clientHeight || 0;
    const el = sidePanel.el;
    if (!canvasH) return 0;
    // Off-screen (`translateY(100%)`) occludes nothing, and it still has a height while it is there.
    const shown = el.classList.contains('is-open') || el.classList.contains('is-minimized');
    if (!shown) return 0;
    /* ⛔ AND PRESENTATION MODE IS OFF-SCREEN TOO — IT JUST DOES NOT LOOK IT FROM HERE (Ryan
       2026-08-12: *"the whole point is that you're giving more room for the model… right now it's
       staying small at the top"*).
         Full presentation mode takes the sheet away with `transform: translateY(110%)`, and a
       transformed element still reports its untransformed height AND keeps `is-open` — so every
       measurement below happily returns a ~380px cover for a sheet occupying no pixels, and the
       model stays framed in the band above a sheet that is not there. Exactly the trap the arrows
       already carry a note about ("a sheet moved by transform still reports its old height"), one
       floor down: they ride `--wd-sheet-h`, this rides the same measurement.
         ⚠ ASKED AS A FACT, NOT READ OFF THE CLASS. `wd-presenting` goes on the root at every
       viewport and the stylesheet gates it to phones; `presentingFraming()` states that same pair
       (`isPhone()` + a presentation has the screen) in one place, so the camera and the stylesheet
       cannot answer differently — and on a SHARED boot it says so from the first frame, which is
       what stops the plate rolling in framed for a sheet that is already gone. */
    if (presentingFraming()) return 0;
    // MEASURED, NOT PREDICTED (Ryan 2026-07-28: "when I expand up the 'Add Logo' slider… this one
    // naturally isn't that tall… can the weight model detect this and dynamically be a little
    // bigger?"). This used to compute the CAP from the CSS snap fractions, because at the moment a
    // state change fires the sheet is mid-transition and a measurement reports the height it is
    // LEAVING. But the sheet is `height: auto` — a short tab like Add Logo produces a short sheet —
    // so fitting to the cap shrank the model for room the sheet was never going to take.
    //   Predicting it properly would mean deriving the content height too, and that changes with the
    // tab, the accordion, and anything added later. So the prediction is gone: a ResizeObserver
    // below re-reads this as the sheet actually moves, which is correct for every one of those cases
    // without knowing about any of them. The CSS cap needs no mirroring here either — it is already
    // in the number being measured.
    // THE FULL HEIGHT, not the part above the minimised header. Subtracting the 48px header put the
    // band's bottom edge 48px BELOW where the sheet actually starts — so the model was fitted and
    // centred into a band that ran under the sheet, and the visible gap beneath it came out 48px
    // tighter than the gap above. The band has to end exactly where the sheet begins for the two
    // gaps to mirror.
    //   It costs nothing at rest: minimised, this is 48px of a ~623px canvas, and on a portrait
    // phone `aspect` (0.63) is still the smaller limit in fitCamera — so the resting framing is
    // untouched and the insets only start to bind once the sheet is genuinely tall. It also keeps
    // the collapse smooth, because the number now eases all the way down with the sheet instead of
    // stepping to zero when a class flips.
    // NOTE THE NAME. This was briefly `h`, which does not throw — `h` is the DOM element-builder
    // imported at the top of this file, so `Math.min(h, …)` quietly evaluated to NaN, and
    // `setSafeArea`'s `Number(NaN) || 0` turned that into a perfectly valid inset of ZERO. The
    // sheet re-framing appeared to be dead with no error anywhere. Keep it `sheetH`.
    const sheetH = el.getBoundingClientRect().height || 0;
    const cover = Math.min(sheetH, canvasH * 0.7);
    const px = Number.isFinite(cover) && cover > 0 ? cover : 0;
    /* ── THE FULL-SCREEN QUOTE STEP DOES NOT ASK THE MODEL FOR ROOM. IT COVERS IT. (Ryan 2026-08-09)
     * *"I want it to expand right up and over the 3D model, as opposed to the 3D model thinking it
     * needs to change size/shape to accommodate the incoming taller menu… this should reduce a lot of
     * the flickering I'm seeing when it expands, and then again when you minimize it or go back."*
     *
     * 🔑 EVERY OTHER SHEET STATE IS A NEGOTIATION; THIS ONE IS NOT. The band exists so a model stays
     * READABLE above a sheet that has taken part of the screen — re-fit as the sheet moves and the
     * plate keeps its whole self in what is left. A sheet going to 100dvh leaves nothing, so there is
     * no framing to solve: every pixel of re-fit is work done to arrange something that is about to
     * be, and for the whole time it matters IS, completely hidden. What it buys instead is a model
     * resizing under a rising sheet, and then again under a falling one — which is the flicker.
     *
     * ⚠ A CEILING, NOT A FREEZE, and the difference is the way OUT. A held number would still be held
     * while the sheet falls back to Customize Design, and would have to be released at some moment —
     * which is a moment for the model to jump in. `Math.min` is inert while the sheet is taller than
     * it was (the whole takeover) and tracks it exactly once it comes back below (the fall), so the
     * band re-opens continuously and there is no release to time.
     *   ⚠ THE CEILING IS TAKEN FROM AN OPEN SHEET ONLY, and that is what makes it the right number
     * rather than a nearby one. The sheet is locked open on step 4 and its one exit lands on Customize
     * Design with the sheet open (`setDragLocked`, `goToStep`), so the band the model must be sitting
     * in when it is uncovered is the band an OPEN sheet takes — which is exactly what is captured
     * here. Recording a collapsed 48px on the way in would frame the plate for a screen it is never
     * going to be shown on, and the correction would land the instant the takeover ended.
     *   ⛔ AND IT IS SAFE ONLY BECAUSE THE CANVAS NO LONGER MOVES — memory
     * `wd-cannot-freeze-apparent-size`: you cannot hold a camera still against a box that is really
     * changing size, because the held model shrinks with it and then snaps out on release. The
     * canvas is the same height before, during and after this state (see `is-quote-full`,
     * weight-designer.css), so the value being held is still the true one when it is let go.
     *   Un-clamped until the first open sheet has been seen — which is every session that can reach
     * this step, since a logo has to be uploaded from an open sheet to unlock it. The fallback is
     * today's behaviour, not a guess. */
    if (el.classList.contains('is-quote-full') || el.classList.contains('is-quote-leaving')) {
      return Math.min(px, quoteCoverCeiling);
    }
    if (el.classList.contains('is-open')) quoteCoverCeiling = px;
    return px;
  };
  // Whether the parade's inset is currently applied. Only a CHANGE to it should animate — see below.
  let paradeInsetOn = false;
  // …and whether that inset was the PHONE's presentation mode, which is the only one whose return
  // has a moving sheet to keep step with (SHOWCASE_EXIT_MS). Kept beside the flag it qualifies.
  let phonePresentationInset = false;
  const syncBandShift = ({ animate = true } = {}) => {
    if (!reveal || !reveal.setBandShift) return;
    /* ⛔ THE FULL-SCREEN QUOTE STEP RE-FRAMES NOTHING, AT ALL (Ryan 2026-08-09: *"as the finalize tab
     * expands up, I can see the 3D model start to shrink as it encroaches on its territory. This
     * shouldn't happen when it's going to get covered anyway."*)
     *
     * `sheetCover`'s ceiling below already holds the number still through this state, and this is the
     * same decision stated where it cannot be got wrong: while the sheet is on its way to 100dvh there
     * is no framing question to answer, so the answer is not recomputed. Two reasons to prefer the
     * hard stop over relying on the clamp alone:
     *   · the clamp depends on a value CAPTURED earlier (the last open sheet's band). That is the
     *     right number and it is also a thing that can be wrong for reasons far from here — a step
     *     reached without the sheet ever settling open, a future caller. This line does not care what
     *     the ceiling holds.
     *   · it is also the per-frame WORK. The observer fires on every frame of the rise, and each call
     *     re-derives insets for a model that is being covered.
     * ⚠ NOT `is-quote-leaving`. The fall is exactly when the band has to re-open, and it has to do it
     * continuously as the sheet uncovers the plate — that is the clamp's job and it must keep running.
     */
    if (sidePanel.el.classList.contains('is-quote-full')) return;
    // The sheet's re-frame is declared as a SAFE AREA and reveal-3d does both halves of it — fit the
    // model into the band and centre it there. main.js used to compute the lift itself (`cover / 2`)
    // and that was the bug Ryan hit: half the BOTTOM inset centres the model in the band above the
    // sheet, and that band still contains the top button row, so the plate rose under the buttons and
    // left the slack beneath it. Naming the top inset too makes the centring fall out of the same
    // two numbers the fit uses, instead of being a second calculation that can disagree with it.
    const cover = sheetCover();
    // `animate: false` ALWAYS for the safe area: the ResizeObserver below re-reads the sheet's real
    // height as it moves, so the sheet's own CSS transition IS the animation and the camera tracks it
    // 1:1. A 300ms JS tween on top would be a second animation racing the first — which is what the
    // easing mismatch was. Nothing to keep in sync now, because there is only one thing moving.
    //   `top` ramps in with the cover rather than switching on at the first non-zero pixel, so the
    // model doesn't jolt down 62px the instant the sheet starts to move. It converges on
    // TOP_CHROME_PX within the first 62px of travel and holds there.
    // The nav lives IN the sheet header now, so the sheet's own height already accounts for it —
    // there is no separate band to reserve.
    // Desktop parade only: the phone shows the swipe pager, whose framing is tuned separately and
    // must not inherit a desktop header's inset.
    /* ⚠ EITHER PRESENTATION — the parade or a shared `?d=` design. Both put a header above the model
       and hide the switcher bar, so both owe the same inset and the same drop; asking only about the
       parade would leave a presented design framed for chrome that is no longer on screen. */
    /* ⛔ …AND IT ANSWERS FROM BOOT, NOT FROM `enter()` (Ryan 2026-08-16: *"the new height of the
       weight means it needs to roll in at this height on page load. can't start higher then jump
       down"*).
         `presentationActive()` cannot be true until the stage is ready, which is strictly AFTER the
       roll-in has chosen where to rest. So the plate rolled to the ordinary framing and the
       presentation re-framed it a beat later — a drop the visitor watches, on the one screen this
       feature exists to make composed. It is the same trap 319 fixed for the horizontal pane shift,
       one axis over, and it takes the same predicate. */
    const paradeOn = !isPhone() && (paradeActive() || sharedPresentationBoot());
    // No parade inset: hiding the switcher (below) removes the lift that caused the overlap.
    const topInset = Math.min(topChrome(), cover);
    if (reveal.setSafeArea) {
      /* ANIMATE ONLY WHEN THE PARADE TOGGLES (Ryan 2026-07-29: *"when it shrinks/grows, make it
         smooth, not jumpy and snappy"*). The `animate: false` here was right for the reason the
         comment above gives — the phone sheet drives this from a ResizeObserver every frame of its
         own CSS transition, and a JS tween on top would be a second animation racing the first. The
         parade is the opposite case: one discrete change, no observer, so `false` meant the model
         jumped to its new size in a single frame. Animating only the toggle keeps both correct. */
      /* ⚠ THE PHONE'S PRESENTATION MODE IS THE SAME KIND OF EVENT and needs the same treatment.
         `paradeOn` is deliberately desktop-only (the phone has no header inset to reserve), but the
         sheet leaving on a phone moves the bottom inset from ~380px to 0 in one discrete step with
         no ResizeObserver behind it — the transform doesn't change the sheet's size, so nothing
         fires. Left un-animated that is the single-frame jump this block exists to prevent, in the
         one state where the model travels furthest. */
      const insetOn = paradeOn || presentingFraming();
      const paradeChanged = insetOn !== paradeInsetOn;
      // Which DIRECTION this toggle is going, because the two have different partners to keep step
      // with — see SHOWCASE_EXIT_MS. Read before `paradeInsetOn` is overwritten.
      const leavingPhonePresentation = paradeChanged && !insetOn && phonePresentationInset;
      paradeInsetOn = insetOn;
      phonePresentationInset = insetOn && isPhone();
      /* 700ms for the parade, against the pane's default 300ms. The resize is only ~3% of the
         model, and a small change over a short clock reads as a snap no matter how good the easing
         is — Ryan asked for it "really smoothly and slowly", both directions. The sheet keeps the
         default: its camera has to stay locked to a CSS transition it doesn't control. */
      const changeMs = leavingPhonePresentation ? SHOWCASE_EXIT_MS : SHOWCASE_RESIZE_MS;
      reveal.setSafeArea({ top: topInset, bottom: cover }, { animate: paradeChanged, ms: paradeChanged ? changeMs : undefined });
    }
    /* THE PARADE HIDES THE SWITCHER, so it must not reserve room for it either. The 26px lift exists
       to clear a bar that is fading out — keeping it would hold the model up against the dots for the
       whole presentation, which is the underlap this all started with. */
    const barUp = (productDownTab.el.hidden || isPhone() || paradeOn) ? 0 : BAND_SHIFT_PX;
    /* HOW FAR THE PLATE SITS BELOW CENTRE — see `PRESENT_DROP_PX` for why a shared presentation's is
       nearly double the parade's, and why it is the only one that applies at every viewport. */
    const drop = sharedPresentationBoot()
      ? ((state.ui.sharedSet || []).length > 1 ? PRESENT_DROP_PX : PRESENT_DROP_SOLO_PX)
      : (paradeOn ? SHOWCASE_DROP_PX : 0);
    /* The arrows flank the plate, and CSS cannot see a camera lens shift — so the number is
       published for `.wd-present__arrow` to read rather than restated in the stylesheet. */
    try { target.style.setProperty('--wd-present-drop', `${drop}px`); } catch { /* older browsers */ }
    // No header lens-shift any more — the inset above both shrinks and re-centres. Keeping both
    // would move the model down twice.
    reveal.setBandShift(barUp - drop, { animate });
  };
  // THE SHEET DRIVES THE CAMERA, frame by frame, off its own real geometry. A ResizeObserver fires on
  // every frame of the `max-height` transition, so this covers things no state-change hook could:
  // a short tab (Add Logo) never asking for the room a tall one needs, switching tabs while open,
  // and expanding an accordion inside tab 1 — none of which are "the sheet opened".
  //   Phone only: on desktop the panel is a left pane and occludes nothing vertically.
  // ── THE KEYBOARD TRANSITION, AND WHY "HOLDING" WAS THE WRONG IDEA TWICE ────────────────────────
  // Ryan 2026-07-28, three times: the plate shrinks / shifts up / snaps back when a field is focused.
  //
  // Focusing changes two things a beat apart — the sheet collapses (the band above it grows) and the
  // keyboard shrinks the layout viewport (the canvas gets shorter). Fit to each as it lands and the
  // model makes a visible round trip, even though the two END states are within ~10px of each other.
  //
  // Two attempts at FREEZING the camera through it both made it worse, for the same reason: you
  // cannot freeze apparent size. The canvas element really does get shorter, so a held camera's model
  // shrinks along with it — and then the release snaps it back out. Holding buys nothing and adds the
  // snap. What actually matters is not that the camera stays still, but that it is never fitted
  // against a HALF-UPDATED world: a shrunken canvas paired with the old tall sheet frames a band of
  // almost nothing, which is the "really small and way up" frame.
  //
  // So: hold only until the keyboard has actually landed, then let normal continuous fitting resume.
  // `visualViewport`'s resize is the keyboard, reported by the browser instead of guessed at — the
  // timer is only the fallback for a focus that never raises one (a hardware keyboard, a desktop-ish
  // device). After the release the sheet is usually still mid-ease, so the remaining movement is
  // tracked frame by frame by the ResizeObserver and reads as one smooth settle rather than a jump.
  /**
   * Run `apply` at the moment the on-screen keyboard finishes changing the viewport — or after
   * `ms` if no keyboard turns up (a hardware keyboard, a desktop-ish device).
   *
   * `visualViewport`'s resize IS the keyboard, reported rather than guessed at. It can fire several
   * times while the keyboard animates; only the first matters, so the listener removes itself.
   */
  let kbWaitTimer = null;
  let kbWaitHandler = null;
  const whenKeyboardSettles = (apply, ms = 400) => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const run = () => {
      clearTimeout(kbWaitTimer);
      if (vv && kbWaitHandler) { vv.removeEventListener('resize', kbWaitHandler); kbWaitHandler = null; }
      apply();
    };
    clearTimeout(kbWaitTimer);
    if (vv && kbWaitHandler) vv.removeEventListener('resize', kbWaitHandler);
    kbWaitTimer = setTimeout(run, ms);
    if (vv) { kbWaitHandler = run; vv.addEventListener('resize', kbWaitHandler); }
  };
  if (isPhone() && typeof ResizeObserver === 'function') {
    const sheetRO = new ResizeObserver(() => {
      // `--wd-sheet-h` keeps updating either way — the hint chevron rides the sheet's edge and must
      // not lag behind it just because the camera is being held.
      syncBandShift({ animate: false });
      // Republish the sheet's live height for CSS (Ryan 2026-07-28 — the hint chevron rides the
      // sheet's TOP EDGE so it can settle into the header's grab-line slot). `--wd-sheet-lift` was
      // retired in entry 111 when the step-nav moved inside the header and nothing needed the height
      // any more; this is the same idea under its own name, and it comes from the same measurement
      // the camera uses rather than a second one that could disagree.
      const px = sidePanel.el.getBoundingClientRect().height;
      if (Number.isFinite(px) && px > 0) preview.el.style.setProperty('--wd-sheet-h', `${px}px`);
    });
    sheetRO.observe(sidePanel.el);
  }

  /* ⭐ WAIT FOR THE SHEET TO ACTUALLY LAND, NOT FOR A CLOCK (Ryan 2026-08-12, the parade exit
     snapping). Two things must not touch the sheet while it is sliding back up: the step change
     (`goToStep` → `easeAround` pins `transition: none` and would CANCEL the rise) and the plate
     restore (a full-size raster that blocks the thread the rise is being painted on).
       A timer for either is a race, and the measurement says so: with the main thread busy, the
     sheet's `transitionrun` did not fire until 313ms after the tap, so a 360ms timer landed 160ms
     INTO a 300ms animation and cancelled it. The event cannot be early by construction.
       ⚠ The fallback is not optional. `transitionend` never fires for a transition that never
     starts — reduced motion, a desktop where nothing moves, an interrupted re-entry — and the
     follow-on work must happen regardless. */
  ctx.afterSheetSettles = (fn, fallbackMs = 900) => {
    const el = sidePanel.el;
    if (!el || typeof el.addEventListener !== 'function') { fn(); return; }
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
      fn();
    };
    // `e.target === el` because the sheet's children transition constantly (the header, the pills).
    const onEnd = (e) => { if (e.target === el && e.propertyName === 'transform') fire(); };
    el.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(fire, fallbackMs);
  };

  // Assemble: step tabs above one framed canvas. The tabs are the canvas's
  // SIBLING (they stick up past its rim); every other chrome piece — the down-tab
  // included, since it hangs from that rim's inner face — lives INSIDE the canvas,
  // absolute-positioned by shell.css / weight-designer.css.
  /* ── NAVIGATION LIVES IN ONE PLACE PER FORM FACTOR (Ryan 2026-08-06) ─────────────────────────
   * Phone → the ‹ prev | Title | next › pills in the sheet HEADER, which is the one part of the
   * sheet that stays on screen when it is collapsed. Desktop → a permanent footer pinned below the
   * scrolling body. Deliberately NOT both: a desktop footer on a phone would sit inside the part
   * of the sheet that slides away, so it would vanish exactly when a buyer collapsed the sheet to
   * look at the plate — which is the regression that moved the phone's nav out of the sheet body
   * in the first place (changelog 107). Two nav systems, one of them invisible half the time, is
   * also two things that can disagree; they share `navTargets` instead.
   *   Decided ONCE at boot, like `setHeaderAction` beside it. */
  if (isPhone()) sidePanel.setHeaderAction(stepNav);
  preview.el.append(sidePanel.el, toolCluster.el, poweredBy.el, productDownTab.el, sheetHint, status.el);
  const frame = h('div', { class: 'wd-frame' }, stepTabs.el, preview.el);
  target.append(frame);

  // --- step controllers (share the same ctx reference) ---
  // The in-tool "Pick Weights" tab. NOT the gate's square-card component (that's
  // sized for the wide intro stage and collapsed to unusable in the pane — Ryan
  // 2026-07-16); this is the pane-native accordion. It reads/writes the same
  // state.ui.selectedProducts, so picks made here or at the gate agree.
  const pickWeightsPanel = createPickWeightsPanel(ctx, {
    onChange: () => {
      /* ⭐ A PICK CHANGE IS AN UNDOABLE EDIT (Ryan 2026-08-09: un-picking a product with five
         designed configurations *"should"* be undoable, and was not — `serializeSnapshot` only ever
         carried the design). `recordNow` rather than `scheduleRecord`: this is a discrete
         structural edit, exactly what that split exists for, and debouncing it would fold two
         deliberate picks into one undo step. It runs BEFORE the stage work below because the state
         has already changed — the history model pushes the NEW state as the new top. */
      recordNow();
      syncPicksToStage();
    },
  });

  /** Bring the switcher and the stage in line with `state.ui.selectedProducts`.
   *
   * Shared by the Pick Weights tab and by undo/redo — the two ways the picked set can change under
   * a live stage. Extracted when history learned about picks: an undo that restored five products
   * without this left them in the state, absent from the switcher, and invisible on screen. */
  function syncPicksToStage() {
    // The picks changed → the down-tab's set is those picks, so it has to follow. Keep the stage on
    // the current product if it survived the edit; otherwise fall to the left-most remaining one
    // (same rule the gate seeds by).
    const picks = pickedProducts();
    notePickAdditions(picks);
    productDownTab.setProducts(picks);
    if (!picks.length) { clearStage(); return; }          // deselected all → blank canvas
    if (!reveal) { mountFreshProduct(picks[0].key); return; } // coming back from blank
    productDownTab.setActive(state.ui.product);
    if (!picks.some((p) => p.key === state.ui.product)) switchProduct(picks[0].key);
    // Crossing 1↔2 picks shows/hides the switcher bar — ease the model to its
    // matching resting height. (The switch/rebuild paths above pre-set it
    // themselves inside buildReveal; this covers the reveal that stays put.)
    syncBandShift();
  }
  /* Where an "added" ghost lands. Hung on ctx rather than passed down, matching how `ctx.logoStep`
     below is shared — and as a FUNCTION rather than a node, because the bar un-hides and re-renders
     its layers as the picked set changes, so the answer is only valid at the moment it is asked. */
  ctx.switcherFlyTarget = () => productDownTab.flyTarget();
  const logoStep = createLogoStep(ctx);
  // The Customize Design tab BORROWS the logo size dial + the "Upload different logo" tile out of
  // this step (2026-07-29) — hung on ctx rather than passed as an argument, matching how the rest of
  // ctx is assembled. `createDesignPanel` reads it lazily inside its own logoSection(), so the order
  // of these two lines is a convenience, not a dependency.
  ctx.logoStep = logoStep;
  const designPanel = createDesignPanel(ctx);
  const productStep = createProductStep(ctx);
  const steps = { 1: pickWeightsPanel, 2: logoStep, 3: designPanel, 4: productStep };

  /* The desktop pane footer, built HERE rather than up with the other chrome because its forward
     slot borrows `productStep`'s own Submit button on the last step — and a `const` read before its
     declaration is a TDZ throw, not an `undefined`. The arrow defers the read until `sync()`, which
     runs long after this line, but relying on that is a landmine for whoever next adds a
     `syncNav()` call in between. Declared after its dependency instead. */
  if (!isPhone()) {
    paneNav = createPaneNav({
      state,
      targets: navNow,
      onGo: (stop) => goToStop(stop),
      // Nothing is forward of the last step, so it lends the slot its own Submit rather than leaving
      // Back to stretch across the pane (Ryan 2026-08-07). `productStep` still owns that button
      // entirely — its gate, its "Sending your request…" label, its POST.
      forwardSlot: (n) => (n === QUOTE_STEP ? productStep.footerAction : null),
    });
    sidePanel.setFooter(paneNav.el);
  }

  // Zoom button: click pins the popover open/closed; on desktop, hover reveals
  // it too (GD parity) without fighting the pin.
  function wireZoomToggle() {
    let pinned = false;
    let hideTimer = null;
    const clearHide = () => clearTimeout(hideTimer);
    const scheduleHide = () => {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        if (!pinned) {
          zoomPopover.hide();
          zoomBtn.setAttribute('aria-expanded', 'false'); // keep AT state honest on hover-out (WD-26)
        }
      }, 220);
    };
    const setPinned = (v) => {
      pinned = v;
      // Pinning the zoom popover lights the button blue (is-active) AND drops the
      // GLD "pinned" dot on it (is-pinned) — matching the GLD's popout behavior.
      // It also "takes over as the blue button": the select tool's highlight is
      // suppressed while zoom is pinned (GLD overlay.js), so only one is blue.
      zoomBtn.classList.toggle('is-active', v);
      zoomBtn.classList.toggle('is-pinned', v);
      zoomBtn.setAttribute('aria-expanded', v ? 'true' : 'false');
      toolCluster.setActiveSuppressed(v);
    };
    zoomBtn.addEventListener('click', () => {
      clearHide();
      // Pin open / unpin+hide. (A plain toggle would fight the hover-show that
      // fires on the way to the click — leaving it closed.)
      if (pinned) {
        setPinned(false);
        zoomPopover.hide();
      } else {
        setPinned(true);
        zoomPopover.show();
      }
    });
    // Attach hover-show unconditionally but gate at call-time on the breakpoint,
    // so rotating/resizing a tablet across the mobile threshold re-evaluates it
    // instead of being frozen to the mount-time state (WD-38).
    zoomBtn.addEventListener('mouseenter', () => {
      if (media(MOBILE_MQ).matches) return;
      clearHide();
      if (!pinned) { zoomPopover.show(); zoomBtn.setAttribute('aria-expanded', 'true'); } // (WD-26)
    });
    zoomBtn.addEventListener('mouseleave', scheduleHide);
    zoomPopover.el.addEventListener('mouseenter', clearHide);
    zoomPopover.el.addEventListener('mouseleave', scheduleHide);
    // Escape closes a PINNED zoom popover (Ryan 2026-07-25). It had no keyboard dismissal at all —
    // the only way out was a second click on the button, which is a trap for anyone on the keyboard.
    // Matches the canvas Save/Share cards.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !pinned) return;
      setPinned(false);
      zoomPopover.hide();
      // …and un-press it visually. Clearing is-active/is-pinned drops the blue and the dot, but the
      // button keeps DOM focus and Escape puts the browser in keyboard mode, so :focus-visible then
      // paints a ring — it still looks pressed. Blur restores the pre-click appearance.
      if (document.activeElement === zoomBtn) zoomBtn.blur();
    });
  }

  // Fullscreen the whole designer. Fullscreen the ROOT mount (not the inner
  // .wd-frame) so modals — which mount to the root (ctx.el) — render inside the
  // fullscreen subtree instead of being invisible behind it (WD-12).
  function toggleFullscreen() {
    try {
      if (!document.fullscreenElement) {
        if (target.requestFullscreen) target.requestFullscreen();
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    } catch {
      /* fullscreen denied (permissions / unsupported) — non-fatal */
    }
  }

  // 4. shared context --------------------------------------------------------
  // Fill the shared ctx now that every piece exists. Function declarations below
  // are hoisted, so referencing them here is safe.
  Object.assign(ctx, {
    state,
    el: target,
    status,
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-26) — WHEN THE MODEL HAS ACTUALLY STOPPED MOVING.

       ⛔ `switchProduct` / `applyEnvelope` RESOLVE ABOUT A SECOND BEFORE THE PLATE SETTLES.
       `playHandoffEnter` is deliberately fired UN-AWAITED (see the note at the tail of
       `switchProduct`) and runs on for ~1s, so anything sequenced off the restore's promise lands
       mid-spin. That is the whole of Ryan's report: *"there's a little lag when loading designs in
       presentation mode… the new product title/name having to load and the new dot having to get
       coloured in blue at the same time as us trying to load a new 3D model."*

       ⚠️ HIS TIMING IS RIGHT AND HIS MECHANISM IS NOT, which is worth writing down because the fix
       differs. Setting `textContent` and toggling a class cannot perturb a WebGL rebuild — they are
       microseconds against a texture pipeline. Nothing is CONTENDING; the chrome is simply arriving
       a second early, while the plate is still spinning, and a label that changes under a moving
       model reads as the model glitching. So the cure is sequence, not load-shedding.

       This is the same promise the deferred panel refresh and the thumbnail drain already wait on —
       a fourth consumer of one beat, not a fourth schedule that would have to be kept in step. */
    /* ⚠️ try/catch, not `|| Promise.resolve()`: `handoffEnterDone` is a `let` declared FURTHER DOWN
       this function body than the object literal it is read from, so a call made between those two
       points would hit the temporal dead zone and THROW rather than fall through the `||`. Nothing
       calls it that early today (the presentation is built ~2,500 lines later), and this makes that
       stay true for the next caller too. */
    settled: () => { try { return handoffEnterDone || Promise.resolve(); } catch { return Promise.resolve(); } },
    announce: status.announce,
    // Run a change that alters what the bottom sheet CONTAINS, easing its height across it rather
    // than letting it jump (Ryan 2026-07-28: switching between the Layout/Colors/Text/Weight pills
    // "should smoothly slide up or slide down to adjust to the height change — no snapping").
    //   The panel owns the measure/pin/animate dance; this is only the door into it, because the
    // thing that changes the height — a `data-msection` swap on the design panel's own root — happens
    // inside a component that knows nothing about the sheet it is sitting in. Safe everywhere: on
    // desktop, and while the sheet is minimised, `easeContentChange` simply runs the callback.
    easeSheet: (fn) => sidePanel.easeContentChange(fn),
    // What a share link carries (Phase D2). Deliberately the SAME string localStorage holds
    // for Resume, so a shared design and a resumed one restore through one code path
    // (`fromAutosave`) and there is no share-only schema to keep in step with the profile.
    // Lives here because this module owns `toAutosave`; ui/share.js only posts it.
    /* The buyer's PICKED SET, for the contact sheet (Ryan 2026-08-07). Same list the pane nav and
       the down-tab bar walk, so the sheet is in the order they have been looking at all session. */
    pickedProducts,
    /* The pane nav owns the nudge (it owns the button); logo-step decides WHEN, because only it
       knows the build just finished and left the buyer standing still. Optional-chained on the ctx
       so a build predating the footer cannot throw. */
    /* BOTH SURFACES, because only one of them exists on any given device — the footer on desktop,
       the header pill on a phone. Calling both is what makes this one call site correct everywhere;
       each arm no-ops on the device it does not belong to. */
    armForwardHint: () => {
      try { paneNav?.armForwardHint(); } catch { /* a hint is never worth a throw */ }
      try { armStepNavHint(); } catch { /* ditto */ }
    },
    shareEnvelope: () => {
      try { return toAutosave(state); } catch { return null; }
    },
    /* The rung `key` is designed at — the gate's circle, or the Size dial's last move on it — for
       ui/project-push.js, which seeds each staged configuration's chips with it (WD 460). The same
       answer the stage gives on a hop, by construction: it IS `weightForVisit`. */
    weightForProduct: (key) => weightForVisit(key),
    /* Every product this session has DESIGNED and then left, oldest capture first. The live
       product is deliberately NOT in here — it has not been left, so the caller reads it off the
       stage with `shareEnvelope()` and gets something fresher than any snapshot could be. See
       `captureLeavingDesign`; ui/showcase-admin.js is the caller. */
    capturedDesigns: () => Object.values(designByProduct).sort((a, b) => a.seq - b.seq),
    /** The stage's own design in the same shape, or null when there is not one to publish. */
    liveDesign: () => captureOf(),
    /* Put a different MODEL on the stage wearing the design that is already loaded — the app's own
       product switch, which is what "one identity re-renders across the family" means in code.
       Presentation mode uses it for a model the sender picked but never opened: there is no
       per-product envelope to restore, and re-rendering the shared design on it is not a fallback,
       it is what the tool does natively when a buyer changes product. */
    switchProduct: (key) => switchProduct(key),
    /* Rebuild `logo.renderUrl` — the recoloured / peeled / ringed composite — on a profile that
       did NOT come off the live stage. Anything that restores an envelope and then rasters it
       itself must call this first, or the plate renders the logo's ORIGINAL artwork while every
       other element takes the buyer's colours (Ryan 2026-07-31; the reasoning is on
       `syncLogoRaster`). `plate-batch` is the caller today; `showcase/remote` has the same hole
       and the same one-line cure. */
    rebuildLogoComposite: (profile) => syncLogoRaster(profile).catch(() => false),
    /* The READ half of `shareEnvelope`, for the rep's My Designs menu (ui/designs-menu.js).
       Mid-session wholesale restore is not the boot path: at boot `fromAutosave` runs BEFORE the
       shell exists, so building it from state is the sync. Here the shell is already up, so this
       re-syncs it the same way an undo/redo does — `afterHistoryNav` — which is the one place that
       already knows a replaced profile needs the logo raster rebuilt, not just a flat repaint. */
    applyEnvelope: async (raw) => {
      if (!raw) return false;
      const before = state.ui.product;
      let ok = false;
      try { ok = fromAutosave(raw, state).restored; } catch { ok = false; }
      if (!ok) return false;
      /* WD 506 - BEFORE `switchProduct` below: its arrival cascade (`arriveOnConfiguration` ->
         `syncLogoContrastRing`) reads the recolour rules, and a design saved before the census rode the
         envelope would hand them nothing - the ringed navy logo Ryan saw paging a share link. */
      try { await ensureLogoCensus(state); } catch { /* the design still opens */ }
      state.ui.isDemo = false;
      state.ui.isSharedView = false;
      /* AND IT STARTS AS NOBODY (Ryan 2026-07-31). A wholesale envelope swap replaces the artwork,
         so the `WEIGHT-` ref the app was carrying no longer describes what is on screen — and a ref
         attached to the wrong artwork files a stranger's plate under a customer's reference number.
         Forgetting is the fail-safe default and it costs nothing: the ONE caller that legitimately
         knows the new identity (designs-menu's `load`) re-adopts on the very next line via
         `setCurrent(rec)`. The other caller — showcase-admin's "Open in the designer" — has no
         design record at all (a showcase entry is its own thing), so forgetting is the correct and
         complete answer there. Any future caller inherits the safe behaviour without knowing this
         module exists.
           BOTH halves: the stored key AND the panel's in-memory `current`, or the push dialog
         would still read the stale id out of `getCurrent()` for the rest of this session — the
         same bug the reload used to cause, just with the reload removed. */
      clearDesignIdentity();
      /* AND THE RETAINED ORIGINAL GOES WITH IT, for exactly that reason one step further on. The
         artwork on screen is now someone else's; the file this session happened to upload earlier is
         not its original, and leaving it held would attach customer A's logo to customer B's quote —
         the same "stranger's plate under a customer's reference" failure, except the evidence is a
         file a rep would send to production. Nothing re-adopts it: a held file only ever comes from a
         live upload, so forgetting is the complete answer for every caller. See ui/artwork-hold.js. */
      artworkHold.clear();
      ctx.adoptSavedDesign?.(null);
      /* ⛔ AND EVERY CAPTURED PER-PRODUCT DESIGN, for exactly the reason the two lines above exist.
         The models this session designed earlier belong to the identity being replaced; keeping
         them would let one publish ship customer A's bumper and grip beside customer B's dumbbell
         under B's name. Forgetting is complete rather than defensive — re-visiting a model
         re-captures it through `adoptProduct`, which is the only door. */
      clearCapturedDesigns();
      // The opened design starts its own history. Without this, undo walks back into the design
      // that was on screen a moment ago and mixes two customers' work in one stack.
      resetHistory(state);
      /* If the saved design is a DIFFERENT product, the 3D model has to be rebuilt — and
         `switchProduct` early-returns when the key already matches `state.ui.product`, which
         `fromAutosave` has just set. So hand it back the old key first and let it do the real
         transition. Looks redundant; without it the stage keeps the previous product's geometry
         wearing the new design's texture. */
      const wanted = state.ui.product;
      /* ⛔ AND FROM A BLANK STAGE IT HAS TO MOUNT, NOT SWITCH (Ryan 2026-08-02). `switchProduct`
         early-returns on `!reveal` — it only adopts the state and leaves, because historically the
         only way to reach it with no reveal was before `initPersistent3D` had run, and that was
         about to mount the model anyway. The portal's rep now BOOTS with a blank stage, so the
         headline flow this whole change exists for — open the tool, open My Designs, load a design
         — went through that early return and restored the design onto nothing. Correct panel,
         correct colours, no model.
           `mountFreshProduct` is the seam for exactly this and it is shared, not copied (see its
         header). None of the old-key hand-back the `else` branch below needs: it deliberately has
         no same-key guard, and handing it the OLD key would make `adoptProduct` stamp the design we
         just restored into the outgoing product's memory and then overwrite it with whatever the
         incoming one remembered — losing the envelope's own weight, ink and layout. With state
         already naming `wanted`, adoption is the documented round-trip no-op. */
      if (!reveal) {
        await mountFreshProduct(wanted);
      } else if (wanted !== before) {
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 507, Ryan 2026-11-04) - THE ENVELOPE'S OWN VALUES ARE STAMPED
           UNDER ITS OWN KEY FIRST. The hop's arrival half (`arriveOnConfiguration`) restores the plate
           colour, the ink and the number's position from the per-configuration MEMORY - and that memory is
           written only when a configuration is LEFT. A design saved while standing on a plate that had just
           been re-dyed carries the new colour as `bodyColor` and the old one in `savedBodyColor[wanted]`
           (WEIGHT-1030-A: #3 live yellow, memory white), so every restore that hops onto it - a share link's
           slide, My Designs - painted the stale colour over the live one. Ryan: *"I see a white 10, white
           25, and blue 45.. why isn't the 25 yellow?"* `openConfiguration` (ui/state.js) already does exactly
           this stamp before it moves; this is the same seam for the live app. WD 468's `restoring` skips the
           LEAVING stamps because they would file the incoming design under the outgoing key - this files it
           under its own, which is the one key those stamps were always meant to describe. */
        rememberBodyColor(state, wanted);
        rememberInk(state, inkContextKey(state));
        rememberWeightNudge(state, weightNudgeKey(state));
        rememberWeight();
        state.ui.product = before;
        // `restoring`: the state on screen is the design just restored, not `before`'s — see adoptProduct.
        await switchProduct(wanted, { restoring: true });
      }
      /* ⛔ RE-HYDRATE THE ADD LOGO PANE. Ryan 2026-07-31, opening WEIGHT-1005-A from My Designs:
         *"the 3D model looks good, but the 'Add Logo' tab shows 'Click to upload the logo' as
         opposed to importing the one that was in there when I saved… so it's therefore also not
         showing any color swatches on that tab, but when I go to the Customize Design tab, it's
         preserved all the color swatches and selections."*

         NOTHING WAS LOST — the saved record is complete (both logo elements carry the 85,886-char
         dataUrl, and `savedLogo` carries it again). The three symptoms are one cause: this pane's
         DOM is NOT derived from state on each paint. It is the record of a choreography that ran
         once — the thumbnail, the cleanup reveal, the extracted tone chips, the identity fields —
         which is exactly what `startNewDesign` documents when it calls `logoStep.reset()`. The
         Customize Design tab repaints from state, which is why it looked right; the 3D re-renders
         from state, which is why the plate looked right; and this pane just kept showing whatever
         it had, which in a fresh session is the bare dropzone.

         `hydrate()` exists for precisely this and says so ("empty dropzone instead of the design
         the buyer is resuming — logo + colours gone"). It was wired to the BOOT resume path only
         (`if (resumedNow)`), and opening a saved design mid-session is the same restore with the
         shell already up. It falls back to the dropzone when a design genuinely has no logo, so it
         is safe on every design.

         ⚠ AFTER `switchProduct`, not before: a product hop re-slots the logo element, and hydrating
         first would show the mark from the layout being left behind. */
      try { logoStep.hydrate?.(); } catch { /* never block opening a design on one pane */ }

      /* ⛔ AND THE PRODUCT SWITCHER BAR. Ryan 2026-07-31, same design: *"there was a bar at the
         bottom of the window to scroll through the other product models that I originally had
         included… but on load, it only loaded the bumpers"* — then, checking: *"if I go to the
         'Pick weights' tab, it shows me the 5 highlighted there."*

         That pair of observations IS the diagnosis. `fromAutosave` restores
         `state.ui.selectedProducts`, so the Pick Weights tab — which reads state on each paint —
         showed all five. The bar does not read state: it is told, via `setProducts()`, and the ONLY
         caller was `pickWeightsPanel`'s `onChange`. Opening a design is the other way the picks can
         change, and nothing here said so. The bar therefore kept the set it was built with, and
         `setProducts` hides it below two products (`el.hidden = items.length < 2`) — so on a fresh
         session that had never picked anything, it stayed hidden entirely. It reads as "my other
         products didn't load" when they had.

         ⚠ `syncBandShift()` IS NOT OPTIONAL HERE. The 3D model is nudged UP to sit optically centred
         above this bar, and the lift keys off `el.hidden` — so a bar that appears without the
         re-sync leaves the model sitting low, overlapping it. Same three calls, same order, as the
         onChange path above; the product switch has already happened, so this only re-syncs. */
      const picks = pickedProducts();
      productDownTab.setProducts(picks);
      productDownTab.setActive(state.ui.product);
      syncBandShift();

      afterHistoryNav('Design opened.');
      syncHistoryButtons();
      return true;
    },
    /**
     * "+ New" — a blank plate, its own history, and no stale autosave to resurrect.
     *
     * ⚠ CLEARING THE STATE IS NOT ENOUGH, which is what the first version got wrong (Ryan
     * 2026-07-29: *"a fresh design which still has remnants on the 'Add Logo' tab from the previous
     * design"*). The Add Logo step keeps its own DOM — the uploaded thumbnail, the cleanup reveal,
     * the extracted colour chips, the identity fields, the unlocked size dial — and none of that is
     * derived from `state` on each paint, because it is the record of a choreography that ran once.
     * `logoStep.reset()` exists for exactly this and nothing was calling it.
     *   And it LANDS ON PICK WEIGHTS (step 1), because that is where a new design starts: dropping
     * the buyer on Add Logo with an empty dropzone reads as a half-finished version of what they
     * were just doing, rather than the beginning of something.
     */
    startNewDesign: () => {
      state.designProfile = createDefaultProfile();
      state.ui.hasLogo = false;
      state.ui.isDemo = false;
      state.ui.isSharedView = false;
      state.ui.palette = null;   // the extracted "Your colors" chips
      resetHistory(state);
      try { localStorage.removeItem(AUTOSAVE_KEY); } catch { /* private mode */ }
      /* ⛔ AND THE DESIGN'S IDENTITY, or "+ New" inherits the ref (Ryan 2026-07-31). This is the
         mirror image of the bug design-identity.js exists to fix, and it is the worse half: a
         forgotten ref costs one extra reference number and announces itself, while an INHERITED
         one silently files a brand-new school's plates under the previous school's WEIGHT- number,
         in a customer-facing proposal, with no symptom until someone reads the packet.
           Belt and braces: the only caller today (designs-menu's `startNew`) already runs
         `setCurrent(null)` before it gets here, but a reset that clears the autosave and leaves
         the identity pointing at it is a trap laid for the next caller. */
      clearDesignIdentity();
      ctx.adoptSavedDesign?.(null);
      /* ⛔ AND THE STAGED SET, for the same reason and at the same seam (Ryan 2026-07-31). A saved
         design now HOLDS its staged plates, so leaving them behind would carry one school's
         weights into the next school's reference number. The "+ New" dialog names the count before
         it gets here, so nothing disappears unannounced — see `startNew` in ui/designs-menu.js. */
      clearTray();
      // …and the captured per-product designs, at the same seam and for the same reason: they are
      // the previous school's other two models. See `clearCapturedDesigns`.
      clearCapturedDesigns();
      // The logo pane's own DOM, back to the bare dropzone.
      try { logoStep.reset?.(); } catch { /* never block the reset on a pane */ }
      afterHistoryNav('Started a new design.');
      syncHistoryButtons();
      // The PICKED PRODUCTS deliberately survive: they are the set this buyer is shopping, not part
      // of the design, and making someone re-pick their products to start a second plate would be a
      // worse annoyance than the one being fixed.
      goToStep(1);
    },
    // Every design mutation fans out to BOTH surfaces: the cheap flat SVG (kept
    // current as the instant first-frame + the WebGL-unsupported fallback + the
    // specs strip) and a debounced 3D texture rebuild that feeds the live plate.
    requestRender: () => {
      /* Rebuild the plate to the denomination's real thickness (no-op if unchanged).
         ⛔ …EXCEPT while adopting the next product, when this weight belongs to a model that is not
         on the stage yet and the one that IS there is about to be disposed. See `adoptingProduct`.
         The incoming plate gets its own denomination from `switchProduct`'s own call, after
         `buildReveal` — so nothing is skipped, it is only stopped from landing on the wrong mesh. */
      if (reveal && reveal.setPlateForWeight && !adoptingProduct) reveal.setPlateForWeight(state.ui.weight, 'lb');
      // ONE ATOMIC VISUAL COMMIT (Ryan 2026-07-24). The logo's composited raster is built FIRST, then
      // the stage tint, the flat SVG and the 3D texture all land together.
      //   Before: updateStageTint() fired synchronously from the caller, the texture rebuild landed
      // ~180ms later, and syncLogoRaster — being async — kicked off its OWN second texture rebuild when
      // it finished. So a plate dye visibly arrived in four steps: the surround, then the plate face,
      // then the text, then the logo. Now there is exactly one paint per render request.
      //   Cheap in the common case: syncLogoRaster early-outs on an unchanged composite key, so this is
      // a resolved-promise microtask rather than a real wait. `paint` is passed as BOTH handlers so a
      // raster failure still repaints — a canvas error must never freeze the render pipeline.
      //   The panel's layout THUMBNAILS draw the same composite (Ryan 2026-07-24). They are built
      // synchronously inside structuralCommit()'s render(), i.e. before this async raster lands, so
      // they showed the previous composite until the NEXT edit rebuilt them — the 2D preview sitting
      // one step behind the 3D model. Repainting them here closes that gap; gated on the raster
      // actually having changed so an ordinary edit doesn't redraw 4 thumbnails for nothing.
      const paint = (rasterChanged) => {
        updateStageTint();
        /* The switcher's plate-colour dot follows the dye. Here rather than at the five dye call
           sites in the design panel because this is the one atomic visual commit every one of them
           already goes through — and because the panel's `preview` handler comes through here too,
           so the dot tracks a hue-bar drag live instead of jumping when the buyer lets go. One
           property write on one node; a no-op on any set without a product picked twice. */
        productDownTab.setSwatch?.(state.ui.product, bodyColorFor(state, state.ui.product));
        preview.requestRender();
        scheduleTextureUpdate();
        if (rasterChanged && designPanel && designPanel.refreshThumbs) designPanel.refreshThumbs();
      };
      /* ⚠ RETURNED, so a caller can WAIT for the paint (2026-08-07). This is async — the raster is
         composited on a canvas and `paint` runs after it — and `switchProduct` used to fire it and
         then start a 420ms animation, so the flat-SVG rebuild, the texture schedule and the
         thumbnail redraw all landed in the animation's opening frames. Nothing about the work was
         wrong; WHEN it ran was. The hand-off now awaits this before it spins. */
      return syncLogoRaster().then(paint, () => paint(false));
    },
    recordHistory: scheduleRecord,
    recordHistoryNow: recordNow, // immediate snapshot for discrete structural edits (WD-19)
    goToStep,
    refreshControls,
    exitDemo,
    updateStageTint,
    // Eyedropper opened → square the 3D plate to the nearest face, level it, zoom
    // to 110%, and hold it still so the logos are easy to sample. When the
    // eyedropper closes → slowly zoom back to 100% and resume the sway (Ryan
    // 2026-07-13). Both no-op until the 3D stage is mounted / in WebGL fallback.
    squareUpForColorPick: () => (reveal && reveal.squareUp ? reveal.squareUp(110) : Promise.resolve()),
    endColorPick: () => { if (reveal && reveal.returnFromColorPick) reveal.returnFromColorPick(); },
    // ── WEIGHT TAB: lean in while you work, pull back when you leave ─────────────────────────────
    // Ryan 2026-07-28: "any button click on this tab should result in a close-up zoom of the visible
    // weight… then, if you slide down the pane, or click to go to another tab or sub-tab, you should
    // smoothly auto-zoom back to regular levels."
    //   Same tween pair the eyedropper uses, at a different percent — it already squares the plate
    // face-on (which is what you want when the thing being edited is printed ON that face), holds it
    // still so it is not swaying while you compare number styles, and returns to 100% releasing the
    // hold. Building a second zoom tween beside it would be two clocks on one camera, which is the
    // mistake changelog 106 and 109 each cost a round.
    //   150%, not more. The weight number sits at roughly 0.7 of the plate's radius, and squareUp
    // centres on the plate's MIDDLE — so past about 170% the number itself starts leaving the frame
    // at the top and bottom. 150 fills the view with the ring band while keeping the whole number on
    // screen, including inside the shorter band left above an open sheet.
    //   The flag is what keeps the two callers of `returnFromColorPick` from stepping on each other:
    // the eyedropper's hold uses the same machinery, so an unguarded restore fired by a step change
    // would pull the camera out from under an open eyedropper. Only a zoom this feature started is a
    // zoom this feature is allowed to end.
    // The Add Logo tab's controls edit the design from outside the design panel; this is how they
    // ask its thumbnails to catch up. See `scheduleThumbRefresh` in ui/design-panel.js.
    refreshDesignThumbs: () => designPanel.scheduleThumbRefresh?.(),
    focusWeightZoom,
    focusTextZoom,
    focusLogoZoom,
    endFocusZoom,
    /* Chrome-free PNG for Download image, the design sheet and the quote modal (§5.4 / §5.7).
       `portraitPNG`, NOT `snapshotPNG` (Ryan 2026-07-29). The latter photographs the canvas as it
       stands — the right thing for the mobile pager's stills and the category-card generator, which
       both stage their own pose and framing first, and the wrong thing for anything a buyer keeps:
       the idle sway meant every download came out at a different angle, at viewport resolution,
       framed on the whole studio. `portraitPNG` stages a fixed pose, renders square at 3072, crops
       to the product's alpha and puts the live stage back. Falls through to the old shot if a build
       predates it, so a stale reveal can never make this path return nothing. */
    snapshotPNG: (opts) => (reveal && (reveal.portraitPNG || reveal.snapshotPNG)
      ? (reveal.portraitPNG ? reveal.portraitPNG(opts) : reveal.snapshotPNG())
      : Promise.resolve(null)),
    // Park the 3D while a full-screen overlay covers it (the View-all layout picker). The stage's
    // own off-screen check can't see this case — an overlay leaves it "intersecting" — so the loop
    // kept drawing at full rate behind an opaque panel and fought the overlay's scrolling.
    suspend3D: (on) => { if (reveal && reveal.setSuspended) reveal.setSuspended(on); },
    /* The side pane, lent to a full-width surface inside the current step (the View-all layout
       picker on desktop). `canTakeOverPane` is asked FIRST so a caller can build the modal instead
       without having already handed its content over — see design-panel's `openLayoutViewAll`. */
    canTakeOverPane: () => canTakeOverPane(),
    openPaneTakeover: (o) => openPaneTakeover(o),
    closePaneTakeover: (work) => closePaneTakeover(work),
    switchProduct,
    changeWeight,
    /** The recoloured logo raster for an ink context, or null when we have never built one. */
    logoRasterFor: (inkKey) => {
      const hit = logoRasterByInk.get(inkKey);
      return hit && hit.art === logoArtKey() ? hit : null;
    },
    /**
     * Which rungs of the LIVE product's ladder another configuration of it is sitting on.
     * `Map<weight, {key, color}>` — empty for every product that cannot be picked twice, which is
     * every product but the competition bumpers.
     *
     * ⚠ THE DESIGNER MARKS, IT DOES NOT ENFORCE, and the two surfaces answer deliberately different
     * questions. Here a rung is "taken" because a sibling is PARKED on it — which is exactly what
     * the staggered opening denominations (45, then 35, then 25) arrange, so the marking is already
     * true the moment a second configuration is opened and there is nothing to explain. On the
     * QUOTE FORM a rung is taken because a sibling has a QUANTITY against it, and there it is
     * genuinely blocked, because that is the surface where a double-count costs money. A buyer may
     * still look at a white 45 and a green 45 side by side; they may not order both.
     *
     * ⚠ `weightForVisit`, NOT the sibling's last-seen weight. A configuration the buyer has not
     * opened yet has no remembered denomination — but it HAS an opening one, and that is the rung it
     * is going to claim. Reading the memory alone would leave the ladder unmarked until every
     * configuration had been visited, i.e. exactly while the marking is most useful.
     */
    siblingRungs: () => {
      const live = state.ui.product;
      const out = new Map();
      if (!productSupportsMultiConfig(live)) return out;
      const base = baseProductKey(live);
      for (const p of pickedProducts()) {
        if (p.key === live || baseProductKey(p.key) !== base) continue;
        const w = Number(weightForVisit(p.key));
        if (Number.isFinite(w)) out.set(w, { key: p.key, color: bodyColorFor(state, p.key) });
      }
      return out;
    },
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 496, 2026-11-01) — THE SIZE ROW ADDS AND REMOVES PLATES. Ryan, UX run 3: the
       Customize tab's 10/25/35/45/55 tiles on a Competition Urethane bumper were confusing - a free one MOVED your plate,
       one another set held was refused with *"The 45lb plate is already assigned. To edit that plate instead, click the
       left/right arrow buttons…"*, and adding a plate meant a trip to Pick Weights, *"an extra confusing step people new to
       the tool won't pick up"*. His design: the plate you are on wears an X (click it = remove), a weight in your lineup
       switches to it, a weight not added ADDS it (copying the plate you are on, colour included - his pick) and goes to
       it; the last plate has no X; a removed weight clicked again comes back with the design it had. The arrows under the
       plate are unchanged. Only for products that can be picked more than once (`productSupportsMultiConfig`). */
    lineup: {
      /** How many plates of the live product are in the lineup (0 for a product that cannot hold two). */
      count: () => {
        const live = state.ui.product;
        if (!productSupportsMultiConfig(live)) return 0;
        const base = baseProductKey(live);
        return pickedProducts().filter((p) => baseProductKey(p.key) === base).length;
      },
      /** Add a plate at rung `w` and go to it. Its colour: what this rung wore when it was last removed, else the live one. */
      add: (w) => {
        const live = state.ui.product;
        if (!productSupportsMultiConfig(live)) return false;
        const { category, kind } = parseProductKey(live);
        const n = addProductInstance(state, category, kind);
        if (n == null) return false;
        const pick = state.ui.selectedProducts.find((q) => q.category === category && q.kind === kind && (q.instance || 1) === n);
        if (pick) pick.weight = Number(w);
        const key = productKey(category, kind, n);
        /* WD 502 (Ryan 11-02: 45 alone, + 35, remove it, + 55 -> "loaded a 35 lb instead ... It won't let me load the 55 lb
           now"). A removed plate's slot is handed out again (`addProductInstance` re-uses the free instance number), and its
           session memory of the last rung shown there still said 35 - which `weightForVisit` lets win over the pick, by
           design, for a slot the buyer moved themselves. A new add is a new plate: the rung asked for is the memory now. */
        lastWeightByProduct[key] = { w: Number(w) };
        const hex = lineupRemoved.get(Number(w)) || state.designProfile.bodyColor;
        const p = state.designProfile;
        p.savedBodyColor = { ...(p.savedBodyColor && typeof p.savedBodyColor === 'object' ? p.savedBodyColor : {}), [key]: hex };
        markBodyColorUserSet(state, key); // the buyer's copy, not a colour for the deal to re-pick
        lineupRemoved.delete(Number(w));
        recordNow();
        syncPicksToStage();
        void switchProduct(key);
        return true;
      },
      /** True when rung `w` was removed this session and clicking it brings that plate back. */
      wasRemoved: (w) => lineupRemoved.has(Number(w)),
      /** Remove the plate on the stage (never the last one); the stage falls to the remaining first one. */
      removeCurrent: () => {
        const live = state.ui.product;
        if (!productSupportsMultiConfig(live)) return false;
        const base = baseProductKey(live);
        if (pickedProducts().filter((p) => baseProductKey(p.key) === base).length < 2) return false;
        const { category, kind, instance } = parseProductKey(live);
        lineupRemoved.set(Number(state.ui.weight), state.designProfile.bodyColor);
        if (!removeProductInstance(state, category, kind, instance || 1)) return false;
        recordNow();
        syncPicksToStage();
        return true;
      },
    },
    rememberLayoutChoice,
  });
  /* WD 496: what each removed rung was wearing, so clicking it again brings that plate back as it was. Session-scoped. */
  const lineupRemoved = new Map();

  // --- persistent LIVE 3D stage (Phase E ↔ D1) -----------------------------
  // The always-on preview is the code-gen 3D plate, resting face-on and updating
  // as the buyer edits (Ryan 2026-07-11). Three.js + the texture pipeline are
  // still DYNAMICALLY imported so the FLAT SVG paints first and the 2D entry
  // bundle ships no Three.js (V2 §4 latency discipline) — the flat frame is what
  // the buyer sees for the ~fraction of a second the chunk takes to load, and it
  // stays underneath as the permanent WebGL-unsupported fallback.
  let revealChunk = null;
  let reveal = null; // the persistent createReveal() controller (null until mounted)
  let revealMount = null;
  // Chrome entrance is gated on the weight's roll-in (Ryan 2026-07-17). revealChrome()
  // is assigned near the end of mount(); buildReveal calls it once the first roll-in
  // settles (or the no-WebGL path calls it straight away). Idempotent via chromeRevealed.
  let revealChrome = () => {};
  let chromeRevealed = false;
  // The mount-time safety net's handle. It has to be CANCELLABLE (Ryan 2026-07-29: "I want the roll
  // to happen BEFORE the menus/buttons/settings all slide out — right now it's happening after").
  //   That net is armed when the shell mounts and fires blind 4.5s later. Entry 116 added an awaited
  // logo composite before the first texture, which on a resumed design pushed the reveal past it — so
  // the net fired while the plate was still being prepared, the chrome slid in on its own, and the
  // weight rolled in afterwards. The order inverted without anything about the order changing.
  //   So it is cancelled the moment a roll is armed, and replaced by one sized to the roll. A blind
  // timer cannot be the thing that decides this sequence; the roll is.
  let chromeFallbackTimer = 0;
  /** One-shot latch for the rep's "unsaved work" offer — `revealChrome` can run more than once. */
  let repRestoreOffered = false;

  // ── "PREPARING YOUR DESIGN…" WHILE THE PLATE IS STILL OFF-STAGE (Ryan 2026-07-29) ─────────────
  // "Should there be a 'Loading' with three dots enlarging down the line while we wait, that way when
  // it loads you're not seeing a weight roll in with some old colours and some new colours."
  //   The colour half of that is fixed at the source (see buildReveal — the composite is now awaited
  // before the first texture). This is the other half: waiting for it is honest work — the reveal
  // chunk, the logo composite and a 4096 raster — and on a resumed design that is a real pause on a
  // bare studio with nothing to explain it.
  //   Built the same way the share-link note is (`.wd-share-loading`): straight into the bare mount,
  // carrying its own centring, because no chrome exists yet to hang it on.
  //
  // ── AND ON A COLD START TOO, BUT ONLY IF IT IS SLOW (Ryan 2026-08-10) ─────────────────────────
  // *"On a really slow device, when you click Start designing it'll take 10+ seconds for the weight
  // to roll out. Meanwhile you're just staring at a white screen wondering if the tool is broken.
  // Whereas if you click Resume design you get a 'Preparing your design…'. We should have something
  // similar for folks starting from scratch."*
  //   This used to bail outright on a cold start, on the reasoning that a blank plate mounts fast so a
  // loader there would be "a flash of nothing, worse than no loader at all". Both halves of that are
  // true and they are not in conflict — it is a claim about DURATION, not about who is waiting. A
  // cold start still imports Three.js and the texture pipeline, builds geometry and rasters a face,
  // and on a slow phone that is the same ten seconds a resume takes, on a stage where the flat SVG is
  // hidden behind `wd-3d-primary` and there is nothing at all to look at.
  //   So the ANSWER to the flash is a delay, not a refusal: a cold start arms the message and only
  // paints it if the mount is still running 400ms later. Fast device → the timer is cleared by
  // `hideBootLoader` on the way past and nothing is ever shown, exactly as before. Slow device → the
  // words are on screen well inside the ~1s where a wait starts reading as a fault.
  //   ⚠ A RESUME STILL PAINTS IMMEDIATELY. It is already known to be real work (a logo composite and
  // a 4096 raster on top of everything the cold start does), and it is the behaviour Ryan approved.
  const BOOT_LOADER_COLD_DELAY_MS = 400;
  let bootLoader = null;
  let bootLoaderTimer = 0;
  /**
   * @param {{immediate?: boolean}} [opts] paint NOW instead of arming the 400ms delay. For a
   *   re-mount from an empty stage, where arming does not work — see `mountFreshProduct`.
   */
  function showBootLoader({ immediate = false } = {}) {
    if (bootLoader || bootLoaderTimer) return;
    // The two entrances the buyer can arrive through, named by the one thing that tells them apart.
    // Same pill, same dots, same ink — only the words differ, because "preparing your design" is a
    // lie to someone who does not have one yet.
    const resuming = isPopulated(state);
    // ⚠ THE TRAILING "…" IS THE ANIMATION, not punctuation — `labelWithDots` swaps it for the
    // shared cycling dots and returns text without one untouched. Ryan asked for "Weights
    // loading"; dropping the ellipsis here would silently drop the thing that proves the tool
    // has not frozen, which is the entire point of the message.
    const label = resuming ? 'Preparing your design…' : 'Weights loading…';
    if (resuming || immediate) { paintBootLoader(label); return; }
    bootLoaderTimer = window.setTimeout(() => {
      bootLoaderTimer = 0;
      paintBootLoader(label);
    }, BOOT_LOADER_COLD_DELAY_MS);
  }
  function paintBootLoader(label) {
    if (bootLoader) return;
    // Dressed as the tool's own busy language rather than bare text on the stage (Ryan 2026-07-29:
    // *"class up the PREPARING YOUR DESIGN message… more similar to the 'Enhancing your logo' text in
    // terms of style"*). Same glass pill, same shared `.wd-dots` ellipsis as the canvas busy pill, so
    // there is ONE way this tool says "working" instead of three.
    //   NO PILL, NO GLASS — plain text on the bare stage (Ryan 2026-07-29: *"don't need the pill at all,
    // just the text on the plain background with the ... is fine"*). What carries over from the styling
    // pass is only the ELLIPSIS: the shared `.wd-dots` from ui/busy-dots.js, so this animates the same
    // way "Enhancing your logo…" does instead of owning a second dot implementation.
    //   ⚠ And never put `.wd-flat-glass` on `.wd-share-loading`: it is a full-bleed centring layer
    // (`position:absolute; inset:0`), so a fill on it paints the ENTIRE stage — that was the "massive
    // black blob" (changelog 145).
    bootLoader = h('div', { class: 'wd-share-loading wd-boot-loading' },
      h('span', {}, labelWithDots(label)));
    bootLoader.setAttribute('role', 'status');
    preview.el.appendChild(bootLoader);
  }
  function hideBootLoader() {
    // ⛔ THE PENDING TIMER FIRST, AND UNCONDITIONALLY. On a fast cold start this is the ONLY thing
    // hide has to do — the message has not been built yet, and an early `return` on `!bootLoader`
    // would leave the timer armed to paint "Loading your weights…" onto a studio that is already
    // showing the plate, with nothing left to ever take it down again.
    if (bootLoaderTimer) { window.clearTimeout(bootLoaderTimer); bootLoaderTimer = 0; }
    if (!bootLoader) return;
    const el = bootLoader;
    bootLoader = null;
    el.classList.add('is-out');
    window.setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
  }
  let pipelineMod = null; // memoized texture-pipeline module
  let currentTexture = null; // the design texture the plate is showing — WE own its disposal
  let textureTimer = null;
  let buildToken = 0; // in-flight guard: only the newest build may swap the texture
  let lastLayoutPreset = null; // tracked so the FIRST dressing plays the reveal spin
  // §6.2 spin discipline: the dramatic wind-up→whip→settle reveal is reserved for
  // TWO set-pieces — design-initiation (a blank plate gets its first design, or a
  // saved design resumes) and product hand-off (Phase-A, N/A yet). Layout FLIPS and
  // every other edit are a micro crossfade (setTexture, no rotation). `dressed`
  // flips false→true on the first real design and back to false on "start fresh".
  let dressed = isPopulated(state);

  const TEXTURE_DEBOUNCE_MS = 180; // heavier than the 60ms flat paint — coalesce rapid edits
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function warmRevealChunk() {
    if (!revealChunk) {
      revealChunk = Promise.all([import('./reveal-3d.js'), import('./texture-pipeline.js')]);
    }
    return revealChunk;
  }

  // Build a texture from the CURRENT design so the 3D face is pixel-identical to
  // the flat preview (color fidelity is a trust issue — V2 §4/§8). Design-only
  // layer (chrome:false — the 3D geometry provides the hub/bolts/ring). Rasters
  // at 4096 on desktop / 1024 on small-touch. Desktop uses 4096 (not 2048) so a
  // small-slot logo gets ~626px of texture (was ~313px) and fine detail — laces,
  // thin lines — survives at the smaller sizes; phones stay at 1024 for memory.
  // Plate texture raster size — 4096 desktop / 1024 small-touch. Color-picker drags
  // don't rebuild the plate at all until release (see design-panel), so there's no
  // separate low-res "live" pass — every rebuild is the full crisp one.
  // 2048 on a phone, not 1024 (Ryan 2026-07-28: "why do uploaded logos look so rough around the edges
  // and blurry relative to everything else on mobile only?"). The logo is the ONLY thing on the plate
  // that comes from an uploaded raster — the body, rings, knurl and metal are shaded geometry, which
  // is why they stayed crisp while the mark alone went soft, and why it reads as a logo problem
  // rather than a resolution one.
  //   The arithmetic: `prepareForDisplay` caps an upload at 1024px, and a logo occupies roughly a
  // quarter of the plate texture's width. At 4096 that quarter is ~1024 texels — the source lands
  // about 1:1. At 1024 it is ~256, so every upload was being thrown away at 4x linear before it ever
  // reached the plate, and zooming in had nothing left to show.
  //   2048 rather than matching desktop's 4096: an RGBA texture is 4 bytes a texel, so this is 16MB
  // against 64MB, and phone GPUs are exactly where that matters. It doubles linear resolution — the
  // quarter becomes ~512 texels — which is the difference between "rough" and "clean" at the sizes a
  // phone actually displays. Going to 4096 for a marginal further gain is how you get an evicted
  // context, which this tool has already been bitten by (changelog 101/102).
  const fullTexSize = () => (media(MOBILE_MQ).matches ? 2048 : 4096);
  // The body colour actually shown for the CURRENT product: the buyer's dye ONLY on a
  // dyeable product (competition bumper), else black. reveal.setBodyColor dyes rings A/C
  // directly, so it must be gated too — else switching to the training bumper / grip /
  // dumbbell / barbell would carry the competition dye across (Ryan 2026-07-22). The design
  // PROFILE keeps its dye, so a switch back to the competition bumper renders dyed again.
  /* ⚠ "PLAIN" MEANS BLACK TOO, not just undressed. The rings and the ground are dyed separately from
     the texture, so a shared boot that rendered a stock FACE while `setBodyColor` still applied the
     buyer's navy would roll in a navy blank — half-revealed, and worse than either end state. Asked
     here, at the one function every dye path already goes through, so the texture and the rings
     cannot disagree about what "plain" is. */
  const plateBodyColor = () => (
    sharedRevealPending || !productSupportsBodyColor(state.ui.product)
      ? DEFAULT_BODY_COLOR
      : state.designProfile.bodyColor
  );
  /* ⛔ THE PLATE ROLLS IN PLAIN, AND THE SPIN IS WHAT DRESSES IT (Ryan 2026-08-16: *"the first weight
     to roll into a presentation should be plain/black.. then when it spins, that's when it reveals
     its design"*).
       On an ordinary boot that is already true by accident: the stage mounts on a blank design and
     the first dressing plays the reveal. A SHARED boot breaks it, because `fromAutosave` restores the
     design before the 3D exists — so the plate rolled in already wearing the artwork and the spin
     revealed something the viewer had been looking at for a second already, which throws away the one
     moment the whole presentation is built around.
       While this is set, every texture build renders a STOCK plate instead. The presentation clears
     it as it plays its reveal (`ctx.sharedRevealDone`), and the refusal path clears it too — a flag
     that outlived its reveal would leave a shared design permanently blank, which is the worst
     failure available here.
     ⚠ The `let` itself is declared at the TOP of `mount`, not here: the `?d=` boot block sets it
     hundreds of lines earlier, and a `let` assigned before its declaration has been evaluated is a
     TDZ ReferenceError — which killed the whole mount, silently, and read as "the presentation
     stopped opening". */
  ctx.sharedRevealDone = () => { sharedRevealPending = false; };

  /* WHICH PROFILE THE PLATE WEARS RIGHT NOW — the design, or the stock plate it rolls in on.
     Asked here, beside `plateBodyColor`, for the same reason: the FACE and the RELIEF are built by
     two different functions and a hold that only one of them honours is a half-held plate. */
  const plateProfile = () => (sharedRevealPending ? stockProfile() : state.designProfile);

  async function buildCurrentTexture(pipeline, size) {
    const sz = size || fullTexSize();
    const profile = plateProfile();
    const svg = renderDesignToSVGString(profile, {
      weight: state.ui.weight,
      size: sz,
      uid: 'wd-3d',
      chrome: false,
      // WHERE the slots land on this product's face. The grip plate's are the gaps
      // between its hand cutouts; everything else takes the bumper's ring.
      product: modelFor(state.ui.product),
      // Force the ground fill black for a product that can't dye, regardless of
      // what the buyer's bumper is dyed — the design PROFILE stays dyed (a
      // switch back to the bumper renders it correctly again).
      // …and black while the reveal is pending, for the reason on `plateBodyColor`.
      bodyColorOverride: (sharedRevealPending || !productSupportsBodyColor(state.ui.product)) ? DEFAULT_BODY_COLOR : undefined,
    });
    const raster = await pipeline.rasterizeSVG(svg, { size: sz });
    return pipeline.createThreeTextureFromRaster(raster);
  }

  // The branding HEIGHT map (Ryan 2026-07-17): the SAME layout as buildCurrentTexture
  // but ink-as-white on black (renderDesignToSVGString heightMap), at a lighter res
  // (a bump needs less). It drives ~⅓mm of fake relief on the brand face — zero
  // geometry, ~free per frame. Ground override is irrelevant here (height mode forces
  // black), so it isn't passed.
  const bumpTexSize = () => (media(MOBILE_MQ).matches ? 1024 : 2048);
  async function buildCurrentBumpTexture(pipeline) {
    const sz = bumpTexSize();
    /* ⚠ HELD TOO, or a "plain" plate rolls in with the design EMBOSSED on it. This map is ink-as-
       relief: with the face gone stock but the relief still carrying the buyer's mark, the logo and
       the arctext would read as ~⅓mm of raised black-on-black — a ghost of exactly the thing the
       spin is supposed to reveal. Same question, same answer, one function (project CLAUDE.md §0b). */
    const svg = renderDesignToSVGString(plateProfile(), {
      weight: state.ui.weight,
      size: sz,
      uid: 'wd-3d-ht',
      chrome: false,
      product: modelFor(state.ui.product),
      heightMap: true,
    });
    const raster = await pipeline.rasterizeSVG(svg, { size: sz });
    return pipeline.createThreeTextureFromRaster(raster);
  }
  // Rebuild + push the relief for the CURRENT design. Fire-and-forget AFTER the colour
  // update lands — it never blocks the design, and `token` drops it if a newer edit
  // superseded. Skipped while the parade is up (its examples are flat in v1).
  async function refreshBump(token) {
    if (!reveal || !pipelineMod || !reveal.setBumpTexture) return;
    if (showcase?.isActive?.()) return;
    try {
      const bump = await buildCurrentBumpTexture(pipelineMod);
      if (token !== buildToken || !reveal) { bump.dispose(); return; }
      reveal.setBumpTexture(bump);
    } catch { /* the relief is a nicety — never fail the design over it */ }
  }

  // TEMP QA-only preview hooks (Ryan 2026-07-14):
  //   ?economy=1  → the Economy plate variant (no zinc hub/bolts, rubber + bore flange)
  //   &ure=1      → urethane instead of rubber, when the dumbbell is showing
  //   ?product=   → seeds the INITIAL product for QA links. It now takes a real
  //                 products.js KEY ("grip|Premium Urethane"), and the legacy
  //                 'dumbbell'/'plate' words still resolve so old QA links live.
  //                 Switching afterward goes through switchProduct(), not the URL.
  // These override whatever the gate seeded, which is the point of a QA link.
  let platePreview = {};
  let dumbbellPreview = {};
  try {
    const qs = new URLSearchParams(location.search);
    if (qs.get('economy') === '1') platePreview = { economy: true, thickness: 0.0739 };
    const p = qs.get('product');
    if (p) {
      const LEGACY = { dumbbell: 'dumbbell|Round Rubber', plate: 'bumper|Competition Urethane' };
      const key = PRODUCTS[p] ? p : LEGACY[p];
      if (key) {
        state.ui.product = key;
        const { category, kind } = parseProductKey(key);
        state.ui.productCategory = category;
        state.ui.productKind = kind;
        const fam = layoutFamilyFor(key);
        if (layoutPresetProduct(getLayoutPreset(state.designProfile.layoutPreset)) !== fam) {
          setLayoutPreset(state, FAMILY_DEFAULT_PRESET[fam]);
          seedBlank(state); // keep the QA-seeded canvas artless too (see the gate path above)
        }
      }
    }
    // ONLY when the hook is actually present. This read `{ rubber: qs.get('ure')
    // !== '1' }` unconditionally, which was right when the dumbbell had one kind
    // and rubber was its default — but it now layers OVER the registry's per-kind
    // `rubber`, so an absent ?ure would have forced Round Urethane to render as
    // rubber (a raised centre boss instead of a flat face). An absent hook must
    // say nothing.
    if (qs.has('ure')) dumbbellPreview = { rubber: qs.get('ure') !== '1' };
  } catch { /* no location */ }

  // Build (or rebuild) the persistent 3D plate for a given product KEY. Shared by
  // the first mount and by switchProduct() — a product swap needs a fresh reveal
  // instance (material-slot count, dye support, and framing radii all differ
  // between models; there's no in-place path).
  // Returns true if WebGL is supported and the reveal mounted, else false.
  async function buildReveal(key, { first = false, handoff = false } = {}) {
    const [revealMod, pipeline] = await warmRevealChunk();
    pipelineMod = pipeline;
    // ── COMPOSITE THE LOGO BEFORE THE FIRST TEXTURE, NOT AFTER (Ryan 2026-07-29) ────────────────
    // "the roll-in on resume is still very slow and glitchy IF THE LOGO COLOURS WERE CHANGED from
    // their uploaded colours… you're seeing a weight roll in with some old colours and some new
    // colours as it glitches along."
    //   The `if` in that sentence is the whole diagnosis, and the reason was already written down 700
    // lines below: `logo.renderUrl` — the recoloured / peeled / ringed composite — is a TRANSIENT that
    // `serializeLogo` deliberately drops, "so every snapshot restore lands with it null and the plate
    // falls back to the ORIGINAL art". A RESUME IS A SNAPSHOT RESTORE. So the first texture was built
    // from the uploaded artwork, the roll began on it, `syncLogoRaster` finished a beat later, and the
    // recoloured texture crossfaded in mid-roll — which is both the glitch and, because a 4096 raster
    // rebuild is not free, the "slow".
    //   A logo whose colours were never touched has no composite to wait for, which is exactly why
    // only the recoloured case shows it.
    //   Awaited unconditionally rather than on `first`: `syncLogoRaster` early-outs on an unchanged
    // composite key, so a product switch pays nothing, and there is one rule — the texture is built
    // from the art the design is actually wearing.
    const rasterChanged = await syncLogoRaster().catch(() => false);
    /* ⚠ AND THE FLAT SURFACES HAVE TO FOLLOW THE WINNER OF THAT RACE (Ryan 2026-08-01: *"on hard
       refresh, the 2d layout preview shows the older/original logo color while the 3D model retains
       the custom one… this does NOT happen on the bumper"*).
         Two GUARDED syncs run at boot: the resume path fires `ctx.requestRender()` (which takes
       `logoPeelJob` #1 and paints when it resolves), then `initPersistent3D` reaches this line a tick
       later and takes #2. When #1's async raster work resumes it is superseded, so it returns early
       WITHOUT writing `renderUrl` — but its `.then(paint)` still runs, and `drawLogo` falls back to
       `logo.dataUrl`, the ORIGINAL upload. #2 then writes the composite and bakes it into the texture,
       and nothing repaints the flat SVG. 3D right, 2D wrong, and only for whichever product loses the
       race — which is why it showed on the grip and not the bumper rather than everywhere.
         Gated on `rasterChanged` so the ordinary case pays nothing: when this call early-outs, the
       sync that DID write the composite is the one whose paint already ran correctly. */
    if (rasterChanged) {
      preview.requestRender();
      designPanel?.refreshThumbs?.(); // the layout thumbnails draw the same composite
    }
    const texture = await buildCurrentTexture(pipeline);

    revealMount = h('div', { class: 'wd-reveal-mount' });
    preview.el.appendChild(revealMount);

    // The registry says WHICH model and WHAT differs from its defaults (economy,
    // rubber, bar kind). The QA query hooks layer on top, so a ?economy=1 link
    // still overrides — they're the same shape by design.
    const model = modelFor(key);
    const geom = geomOptsFor(key);
    reveal = revealMod.createReveal({
      mountEl: revealMount,
      persistent: true,
      texture,
      prefersReducedMotion,
      plate: model === 'plate' ? { ...geom, ...platePreview } : {},
      product: model,
      // Dumbbell AND barbell: both go through the shared head generator, so both
      // read this. `rubber` comes from the registry (Round Urethane vs Round
      // Rubber is a real geometry difference — a flat face vs a raised boss);
      // dumbbellPreview's ?ure= hook still wins for QA links.
      dumbbell: model === 'dumbbell' || model === 'barbell' ? { ...geom, ...dumbbellPreview } : {},
      grip: model === 'grip' ? { ...geom, weight: state.ui.weight } : {},
      // Only surface the 3D hint once the buyer has engaged — on first load the
      // primary coaching is "drop your logo," which this must not clobber (WD-31).
      onReady: () => { if (state.ui.hasLogo) status.announce('Your plate is shown in 3D. Drag to spin it.'); },
      // The stage went blank because the GPU took the context away — rebuild it. Without this the
      // designer had no recovery at all; see `recoverLostContext` for the whole diagnosis.
      onContextLost: recoverLostContext,
      // Keep the zoom readout honest after the user orbits/zooms with the mouse,
      // and retire the rotate hint on the first orbit (teach-then-vanish, QW-5).
      onInteractChange: () => { zoomPopover.updatePercent(); },
      // PHONE: 40% off the side margins (Ryan 2026-07-28). On a portrait phone the WIDTH is the
      // limiting axis at rest — measured, the plate spans 82.9% of a 386px canvas, i.e. exactly
      // FILL_FRACTION, leaving 9% either side. 40% off that is 5.4%, so the model has to reach 89.2%
      // of the width: 0.892 / 0.82 = 1.088. Phone only, because a desktop canvas is wide enough that
      // `vh` is the limiting term and this would never bind anyway — passing it there would only be
      // a dial waiting to surprise someone on a narrow window.
      sideFill: isPhone() ? 1.088 : 1,
      // A thumb travels far less than a mouse, so the phone gets a higher orbit base (Ryan
      // 2026-08-08: *"more like a 1.5:1 ratio"*). Same `isPhone()` gate as `sideFill` — deliberately
      // NOT a bare `pointer: coarse` test, which would speed up Ryan's touchscreen laptop too.
      touchOrbit: isPhone(),
    });

    // Graceful WebGL fallback (V2 §8): keep the flat SVG as the primary stage.
    if (!reveal.isSupported) {
      if (revealMount.parentNode) revealMount.parentNode.removeChild(revealMount);
      revealMount = null;
      reveal = null;
      texture.dispose();
      /* ⛔ AND TAKE THE LOADER DOWN, which this branch never did. Every other exit from this
         function hides it (the success path below, `fallBackToFlat` for a throw) — a machine with
         no WebGL got the flat SVG with "Weights loading…" pinned over it and nothing left to ever
         clear it, because the only other clearer runs on the path that isn't taken. Latent since
         the cold-start loader landed; reachable from a second entrance now that
         `mountFreshProduct` arms it too. */
      hideBootLoader();
      preview.el.classList.remove('wd-3d-primary'); // no WebGL → reveal the flat 2D
      // The flat SVG can't orbit / zoom — hide the zoom tool so it isn't a
      // live-but-dead button; undo/redo stay (they still work) (WD-23).
      toolCluster.el
        .querySelectorAll('[data-tool="zoom"]')
        .forEach((b) => { b.style.display = 'none'; });
      zoomPopover.hide();
      preview.el.classList.add('wd-no-3d'); // no plate to rotate → hide the rotate hint (WD)
      // No 3D → no roll-in to gate the chrome on, so bring it in now (after the same
      // short beat the roll would have opened with) rather than waiting out the fallback.
      if (first) setTimeout(revealChrome, UI_REVEAL_PAUSE_MS);
      return false; // flat SVG is already painted and stays visible — no scary message
    }

    currentTexture = texture; // now owned by us for disposal on the next edit
    lastLayoutPreset = state.designProfile.layoutPreset; // baseline — a change triggers the reveal spin
    hideBootLoader(); // the stage is about to be revealed with the plate parked in the wing
    reveal.open();
    reveal.setToolMode(toolMode); // honour the active tool (select → orbit)
    if (reveal.setBodyColor) reveal.setBodyColor(plateBodyColor()); // B6 §4 initial dye (dyeable products only)
    if (reveal.setPlateForWeight) reveal.setPlateForWeight(state.ui.weight, 'lb'); // thickness for the initial denomination
    // Pre-set the pane-follow lens shift to the CURRENT pane state, INSTANTLY, so the
    // weight rolls STRAIGHT to its final resting spot (offset for the open pane) in one
    // motion — no roll-to-centre-then-shift when the pane appears (Ryan 2026-07-17).
    if (reveal.setPaneShift) {
      const paneCls = preview.el.classList.contains('wd-pane-open') ? 'wd-pane-open'
        : preview.el.classList.contains('wd-pane-min') ? 'wd-pane-min' : null;
      /* ⛔ …AND A SHARED PRESENTATION HAS NO PANE TO CLEAR (Ryan 2026-08-16: *"When the weight rolls
         in, it rolls past the mark, then snaps back to center."*).
           This is the 2026-07-17 bug above, arriving from the other direction. The plate rolled
         straight to the OPEN-PANE resting spot — correct, per this pre-set — and then presentation
         mode zeroed the lens shift, which is a camera change requested while a reveal still OWNS the
         camera. `reveal-3d` defers every refit until the reveal releases it, so the correction did
         not ease: it landed in one step at the end. That is the snap.
           The cure is not to animate the correction better, it is to have nothing to correct. A
         share presentation hides the pane completely, so the resting spot was always centre — ask
         for centre before the roll starts and the plate rolls to the mark and stops. */
      const shift = sharedPresentationBoot() ? 0 : paneShiftFor(paneCls);
      reveal.setPaneShift(shift, { animate: false });
    }
    // Same pre-set for the vertical band lift: if the switcher bar is up (2+ picks),
    // the weight must roll/hand off straight to its LIFTED resting spot — never
    // land centred and then hop up.
    syncBandShift({ animate: false });
    /* ── A PROBE SEAM FOR THE CAMERA, DEV BUILDS ONLY ─────────────────────────────────────────────
       The framing is a CAMERA fit, not a DOM box, so a `ResizeObserver` log — which is what
       `probe-quote-refit.mjs` had — reports "nothing moved" for a model that visibly shrank
       (2026-08-09, twice). The only observable that answers "what re-framed the plate and when" is
       the call into `setSafeArea`, and a probe cannot wrap a closure it has no handle on.
         ⚠ `quoteCoverCeiling` is EXPOSED, `sheetCover()` IS NOT. Calling it captures the ceiling as
       a side effect, so a probe that read it would be changing the thing it is measuring.
       Stripped from the production bundle by the `DEV` guard. */
    if (import.meta.env && import.meta.env.DEV) {
      window.__wdDbg = { reveal: () => reveal, ceiling: () => quoteCoverCeiling };
    }
    // A fresh reveal host paints the default pale studio — re-wash it with the
    // design's tint so a product swap can't flash the backdrop back to neutral
    // once colors are chosen (updateStageTint targets live nodes at call time).
    updateStageTint();
    // Pre-warm the GPU NOW, while the canvas is still opacity 0 (open() only schedules
    // the `is-revealed` fade for next frame): compile the shader + upload the colour map
    // synchronously here, so those one-time stalls don't land on the first frames of the
    // hand-off spin and make it snap (the spin is time-based — a stall lets it jump). WD-18.
    if (reveal.prewarm) reveal.prewarm();
    // The ⅓mm relief. On a SWITCH, hold it until the entrance spin has settled: assigning
    // the bump map recompiles the shader, and mid-spin that second stall is the other half
    // of the jitter (Ryan 2026-07-17). Deferred onto a resting plate, its micro-stall is
    // invisible. The token guard makes a late apply no-op if another switch raced past.
    //   ⚠ THE FIRST MOUNT NEEDS THE SAME PROTECTION, and the comment that said otherwise went stale
    // (Ryan 2026-07-29: on RESUME "the tabs/buttons/menus all slide in prior to the weight finishing
    // its grand roll-in… seems a bit laggy", while a new design "seems bigger [a gap], and there's no
    // lag"). It used to read "first mount rolls in later; no spin to protect" — true when the chrome
    // came first and the weight rolled in LAST, and false since the 2026-07-17 re-sequencing made the
    // roll the OPENING beat. It now starts 200ms after this line and runs for 2s, so the shader
    // recompile lands squarely inside it.
    //   Why only a RESUMED design feels it: a new design mounts a BLANK plate, whose bump texture is
    // trivial and whose recompile is invisible. A resumed one mounts a finished design — logo raster,
    // text, outlines — and that is a real texture and a real stall.
    //   And why the stall shows up as the CHROME arriving early rather than as a stutter: the roll is
    // time-based (`handoffStart + handoffDur`, advanced from the frame loop), so its promise resolves
    // on the clock whether or not the frames kept up. Stall the frames and the promise still fires on
    // time — the chrome slides in on schedule while the plate is visibly still catching up.
    //   Deferred onto the roll's OWN promise rather than a second timer, so it cannot drift from the
    // animation it is waiting for.
    if (handoff) {
      const bumpTok = buildToken;
      window.setTimeout(() => { if (bumpTok === buildToken) void refreshBump(bumpTok); }, HANDOFF_SETTLE_MS);
    } else if (!(first && reveal.playRollIn)) {
      void refreshBump(buildToken); // nothing is animating — apply it now
    }
    zoomPopover.updatePercent();
    // §6.2 hand-off half 2 — the fresh reveal starts mid-spin and settles face-on
    // (open() already put it to rest; this immediately overrides that with the
    // "arrives already dressed, still spinning" pose before the first paint).
    //
    // ⚠ DELIBERATELY NOT AWAITED — the chrome must arrive around the weight rather than after a 1s
    // settle. The promise is captured instead, so `switchProduct` can hand the frames back to the
    // thumbnail drain when the settle really ends rather than when `buildReveal` merely resolved.
    /* HELD DEAD STILL FROM THE MOMENT IT IS BUILT (Ryan 2026-08-02). The resting sway keeps the
       WebGL loop running at 60fps, and the window right after a hand-off is exactly when the panel
       is rebuilding and the layout thumbnails are being drawn — decorative motion bidding for the
       same frames as the work the buyer is waiting on. Released once BOTH are done, which is also
       what starts the wind-up. See reveal-3d's `idleHeld`. */
    if (handoff && reveal.setIdleHold) reveal.setIdleHold(true);
    handoffEnterDone = (handoff && reveal.playHandoffEnter)
      ? (reveal.playHandoffEnter() || Promise.resolve())
      : Promise.resolve();
    // The first product ROLLS ON — now the OPENING beat (Ryan 2026-07-17, re-sequenced
    // from "the last beat, after the chrome"). Called synchronously with open() above,
    // NOT on a timer: open() reveals the canvas on the very next frame, so the plate
    // must already be parked off-stage by then. playRollIn parks it now and holds it in
    // the wing for `delay` — a short beat on the bare studio — then rolls it in slowly.
    // When it settles, its promise resolves and we bring the chrome in AROUND the weight,
    // so the tabs/panes/tools arrive only once the weight is home. (revealChrome is
    // idempotent — the fallback timer in mount() covers a stalled/slow roll.)
    if (first && reveal.playRollIn) {
      // The roll owns the sequence from here — retire the blind mount-time net and arm one that
      // only outlasts THIS animation, so a slow boot can never let the chrome go first again.
      if (chromeFallbackTimer) { window.clearTimeout(chromeFallbackTimer); chromeFallbackTimer = 0; }
      chromeFallbackTimer = window.setTimeout(revealChrome, UI_ROLL_CHROME_FALLBACK_MS);
      const bumpTok = buildToken;
      const rolled = Promise.resolve(reveal.playRollIn({ delay: UI_REVEAL_PAUSE_MS }));
      rolled.then(revealChrome, revealChrome);
      // …and the ⅓mm relief lands on the RESTED plate (see the note above). Its own token guard makes
      // a late apply a no-op if another build raced past while the weight was rolling.
      rolled.then(() => { if (bumpTok === buildToken) void refreshBump(bumpTok); }, () => {});
    } else if (first) {
      revealChrome(); // no roll-in available but 3D is up — don't strand the chrome
    }
    if (first) showcase.onStageReady(); // 3D is live → arm + preload the intro parade (once)
    return true;
  }

  // One-time mount of the persistent 3D plate. Kicked off AFTER the first flat
  // paint so the flat frame renders instantly, then the plate fades in over it.
  async function initPersistent3D() {
    try {
      productDownTab.setActive(state.ui.product);
      showBootLoader(); // immediate on a resume; armed-but-deferred on a cold start (see above)
      await buildReveal(state.ui.product, { first: true });
      // The 3D plate is really on screen. Fired HERE rather than on the "See it in 3D" intent,
      // because a WebGL failure leaves the flat SVG in place and the catch below owns that path —
      // counting intent would report a reveal that the buyer never saw.
      wdTrack(WD_EVENTS.REVEAL_3D);
    } catch (err) {
      fallBackToFlat(err);
    }
  }

  /**
   * A 3D mount THREW — show the flat SVG instead of nothing.
   *
   * ⚠ TWO CALLERS, DELIBERATELY (CLAUDE.md §0b). `buildReveal` handles the graceful
   * no-WebGL case itself (it removes `wd-3d-primary` and returns false), so this covers the
   * other one: the chunk never arrives, or the build throws part-way. That used to be
   * `initPersistent3D`'s private catch, which was sufficient for as long as the FIRST mount
   * was always its own — but the portal's rep boots with a blank stage, so the first mount is
   * now whichever `mountFreshProduct` runs first, on a cold cache, with `wd-3d-primary` still
   * hiding the flat plate. Uncaught there, a failed chunk leaves a permanently empty stage
   * with no message and no fallback — which reads as "the portal version is broken".
   */
  function fallBackToFlat(err) {
    hideBootLoader(); // a failed mount must not leave the buyer reading "preparing" forever
    // Any failure leaves the flat SVG in place (it's already painted).
    preview.el.classList.remove('wd-3d-primary');
    // eslint-disable-next-line no-console
    console.error('[weight-designer] 3D preview unavailable, flat preview left in place:', err);
  }

  /* ── RECOVERING A LOST WebGL CONTEXT (Ryan 2026-08-08) ─────────────────────────────────────────
   * *"the weight will roll in, then it'll often just randomly disappear and all I can see is a white
   * window with no weight model in it. if I then close/minimize the app and reopen it, the weight
   * will be back."*
   *
   * `reveal-3d`'s `webglcontextlost` handler stops the loop and removes `is-revealed` — it takes the
   * studio backdrop OUT OF THE WAY on purpose, so that whatever the CALLER has underneath can show
   * through (on the mobile pager that is the still, a real picture of the same product). It then
   * hands off to `onContextLost` so the caller can rebuild.
   *
   * ⛔ THE DESIGNER PASSED NEITHER. `mobile-kind-pager.js` was the only caller in the codebase that
   * supplied `onContextLost`, so here there was nothing underneath to reveal and nobody to rebuild:
   * the stage simply stayed empty. Recovery depended entirely on `webglcontextrestored`, which
   * reveal-3d's own note says an over-limit eviction never sends — and backgrounding the app is
   * exactly when the browser hands a context back, which is why that "fixed" it.
   *
   * ⚠ SHARED SEAM, NOT A COPY. `clearStage` + `mountFreshProduct` is the existing rebuild path;
   * adoption is a no-op for the product we are already on (see `mountFreshProduct`'s header), so the
   * design, the layout and the denomination all survive the rebuild untouched.
   *
   * ⚠ BOUNDED, because the usual cause is memory pressure and an unbounded rebuild loop feeds it.
   * The budget refills after a quiet spell — a phone that drops one context an hour is not the
   * runaway this guards against — and once it is spent we land on the FLAT SVG, which is a real
   * fallback the buyer can keep designing on, rather than an empty stage.
   */
  const CTX_LOSS_MAX_REBUILDS = 3;
  const CTX_LOSS_REBUILD_DELAY_MS = 250;
  const CTX_LOSS_BUDGET_RESET_MS = 60000;
  let ctxLossCount = 0;
  let ctxLossLastAt = 0;
  let ctxLossRebuilding = false;
  function recoverLostContext() {
    if (ctxLossRebuilding) return;
    const now = Date.now();
    // A loss that is not part of a burst starts the count over.
    if (now - ctxLossLastAt > CTX_LOSS_BUDGET_RESET_MS) ctxLossCount = 0;
    ctxLossLastAt = now;
    if (ctxLossCount >= CTX_LOSS_MAX_REBUILDS) {
      fallBackToFlat(new Error('WebGL context lost repeatedly — staying on the flat preview'));
      return;
    }
    ctxLossCount += 1;
    ctxLossRebuilding = true;
    const key = state.ui.product;
    // A beat before rebuilding: the context is torn down asynchronously, and asking for a new one in
    // the same task is the request most likely to be refused.
    window.setTimeout(async () => {
      try {
        clearStage();          // dispose the dead reveal + drop its mount; `reveal` is still set here
        await mountFreshProduct(key);
      } catch (err) {
        fallBackToFlat(err);
      } finally {
        ctxLossRebuilding = false;
      }
    }, CTX_LOSS_REBUILD_DELAY_MS);
  }

  // Phase 2 (2026-07-14): swap the live 3D product. §6.2 product hand-off — the
  // outgoing model is torn down and the incoming one arrives already dressed
  // (buildReveal's fresh open() + setBodyColor/setPlateForWeight stand in for
  // "arrives already dressed"; a true crossfade between two live reveals is a
  // later polish pass, not required for the swap to read cleanly).
  //
  // Takes a products.js KEY. It used to take 'plate' | 'dumbbell' and reject
  // everything else outright — which is why a grip plate could never reach the
  // stage no matter what the buyer picked.
  let switchingProduct = false;
  /** Resolves when the hand-off's ENTER settle has finished — set by `buildReveal`, read by
   *  `switchProduct` to decide when the main thread is free again. Never null: an unresolved
   *  promise here would strand the thumbnail drain permanently. */
  let handoffEnterDone = Promise.resolve();
  /** The panel rebuild a product switch owes, held back so it cannot starve the hand-off spin.
   *  See `switchProduct`. Always drained through `runDeferredPanelRefresh`, which is idempotent. */
  let deferredPanelRefresh = null;
  function runDeferredPanelRefresh() {
    const fn = deferredPanelRefresh;
    deferredPanelRefresh = null;   // cleared FIRST — a throw inside must not re-arm it forever
    /* The fade lifts HERE, not in `switchProduct`'s `finally`, because this is the one place the new
       content actually lands — and the whole point is that the buyer never sees the old panel, the
       rebuild, or the gap between them. Cleared unconditionally, including on the paths with nothing
       to rebuild (a same-family hop, an early return, a failed build): a panel left faded out is a
       dead pane, so this must not depend on `fn` existing. */
    if (designPanel.el) designPanel.el.classList.remove('is-handoff');
    /* The pane comes back HERE, because this is where the new product's controls actually appear —
       see `setBodyBusy(true)` in switchProduct. Unconditional: this runs on every path out of a
       switch (early return, success, and the post-spin hand-off), and a body left parked out would
       be an invisible panel with no way back. */
    sidePanel.setBodyBusy?.(false);
    // Returns the panel's own "the height tween has finished" promise, so the caller can hold the
    // NEXT beat (the layout thumbnails) until the menu has stopped moving. Always a promise.
    return fn ? (fn() || Promise.resolve()) : Promise.resolve();
  }
  // §4.3 — the remap toast is shown at most once per (target product) per
  // session ("the toast never reappears for that product this session").
  const remapToastShownFor = new Set();
  // §9.1 — the layout families are mutually exclusive (the bumper's 15 ring/arc
  // presets, the dumbbell's flatter 3, the grip's own). Remember each family's
  // last-used preset so hopping products round-trips cleanly instead of dumping
  // the buyer on a default every time. Keyed by FAMILY, not by product: the three
  // bumper kinds share one face, so designing on Competition and hopping to
  // Training should keep your layout, not reset it.
  /* ── THE DENOMINATION IS PER-PRODUCT, AND IT SURVIVES A HOP (Ryan 2026-08-02) ──────────────────
     *"if you set a fixed barbell to 110 lb, then use the arrow to move to another model like a
     bumper plate, then go back to the fixed barbell, it would have switched from the 110 lb version
     to the 60 lb version on you. Should stay on the 110."*

     `switchProduct` has always reset to the category's sample weight, and that was RIGHT for as
     long as the sample was the only denomination anyone could see — there was nothing to lose. The
     Size ladder turns the same line into data loss: it throws away a choice the buyer made, on a
     control whose entire job is letting them make it.

     Keyed by product KEY, not by category or family, and that distinction is load-bearing: the
     competition bumper offers 55/45/35/25 while the training bumper adds 15 and 10, so a memory
     shared across a category would hand Competition a 10 lb it cannot render. Same shape as
     `lastPresetByFace` below — session-only, deliberately: the design's own weight is what
     autosave persists, and a remembered SET of denominations is not part of the design.

     The UNIT travels with the memory. A remembered 110 means 110 lb; restoring it into a kg
     session would silently invent a 110 kg bar. Mismatched unit → fall through to the default,
     which knows both. */
  const lastWeightByProduct = Object.create(null);
  function rememberWeight() {
    const key = state.ui.product;
    if (!key) return;
    /* ⚠ NO UNIT ON THE RECORD (2026-08-02). It used to store one and `weightForVisit` discarded the
       memory whenever it disagreed — correct while the number MEANT something different per unit,
       and wrong the moment it stopped: a buyer who set a bumper to 25, hopped away and flipped the
       toggle would come back to the 45. A denomination is pounds now, whatever the plate says. */
    lastWeightByProduct[key] = { w: Number(state.ui.weight) };
  }
  /** The rung the GATE says this configuration holds — its own choice where the circles made one,
   *  and `defaultWeightFor`'s derived rung where they did not. See ui/state.js `claimDenomination`. */
  function claimedWeightFor(key) {
    const { category, kind, instance } = parseProductKey(key);
    if (!category || !kind) return defaultWeightFor(key);
    return instanceWeight(state, category, kind, instance);
  }

  /** The denomination to open `key` on: what it was last left at, else the rung it CLAIMS.
   *
   * ⭐ THE ONE PLACE THE GATE'S CIRCLES REACH THE APP (Ryan 2026-08-10: *"if I add only 25 and 10
   * lbers, then the bumpers on load need to be only the 25 and 10 lb bumpers"*). Every surface that
   * asks "what denomination is this configuration on" already comes through here — the stage on
   * adopt, the switcher, and `siblingRungs`, which is what greys a rung on the Size ladder because a
   * sibling holds it. So changing the FALLBACK from the derived rung to the claimed one moves all of
   * them together, and there is no second place that could still answer 45.
   * ⚠ THE MEMORY STILL WINS, and must: `lastWeightByProduct` is where the buyer's own move on the
   * Size dial lives, and a picker choice made before they opened the plate cannot outrank the change
   * they made after. Absent a memory, the claim is the answer.
   */
  function weightForVisit(key) {
    let fallback = claimedWeightFor(key);
    /* ⭐ ON A SHARED LINK, THE ORDER OUTRANKS THE PICKER'S CLAIM (Ryan 2026-08-30). Arrowing through
       a set has the same failure the boot path had: the second model opens on whatever rung its
       claim implies, which for a dumbbell is the 25 nobody bought. The ordered lines are a better
       claim about the same question, so they replace the fallback rather than sitting beside it.
       ⚠ IT DOES NOT DISTURB THE MEMORY BELOW, which still wins and must: `lastWeightByProduct` is
       the viewer's own move on the Size dial, and a fact about the order cannot outrank a choice
       they made after seeing it. This only improves the answer for a product they have not touched. */
    if (state.ui.isSharedView && Array.isArray(state.ui.sharedOrdered) && state.ui.sharedOrdered.length) {
      fallback = orderedDenomination(state.ui.sharedOrdered, key, fallback, designableWeights(key));
    }
    const seen = lastWeightByProduct[key];
    if (!seen || !Number.isFinite(seen.w) || seen.w <= 0) return fallback;
    // ⚠ RE-VALIDATE AGAINST TODAY'S LADDER rather than trusting the memory. `null` is FREE ENTRY
    // (the fixed barbells have no chart of their own), so it accepts anything — an absent ladder
    // must not be read as "nothing is allowed", which would reset the one product most likely to
    // be sitting on a non-default rung.
    const ladder = designableWeights(key);
    if (ladder && !ladder.some((x) => Number(x) === seen.w)) return fallback;
    return seen.w;
  }

  /* ── ONE LAYOUT MEMORY PER FACE, AND A SHARED LAYOUT WRITES TO EVERY FACE THAT HAS IT ─────────
     Ryan 2026-08-02, on the grip: *"if they change one, it should lock it in on both (2.5 and 5).
     Moreover, if they choose a layout which is also available on the 10/25/35/45, then it should
     affect those too, and vice-versa. If however they choose a layout which is NOT available on
     the other, then they should no longer be in lock-step and become independent… but then if they
     revert back to a layout type which is on both, then both update accordingly."*

     That is not a grip rule, it is a rule about FACES: a set of denominations that offer the same
     layouts is one face, each face remembers what it was last set to, and choosing a layout writes
     it into every face that can wear it. Shared layout → both memories move → lock-step. Exclusive
     layout → only its own memory moves → they diverge. Choose a shared one again → both converge.
     Ryan's four sentences fall out of that one sentence.

     ⚠ THE FACE IS DERIVED FROM THE ELIGIBILITY LIST, not from `isSmallGrip` or any product test.
     Two denominations are the same face **iff they offer the same presets** — which is the actual
     definition, needs no knowledge of grips, collapses to a single face for every family with no
     weight gating (the bumper, dumbbell and barbell offer one list at every rung), and gives the
     grip its two for free. A third face on some future product costs nothing and, more to the
     point, cannot be forgotten: it exists the moment its presets declare a `weights` predicate. */
  /** The `ctx.requestRender()` `adoptProduct` fires, so the hand-off can let it LAND before it
   *  animates. See `switchProduct`'s wait. Null when nothing is in flight. */
  let adoptRenderPending = null;
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-26) — TRUE only while `adoptProduct`'s own render
     is being kicked off, i.e. while the model ON SCREEN is the one being LEFT.

     ⛔ THE GRIP PLATE SNAPPED SMALLER BEFORE IT SPUN AWAY. Ryan: *"on desktop when I arrow off the
     grip plate, it snaps smaller and then fades away. why the snap?"* `adoptProduct` sets
     `state.ui.weight` to the INCOMING product's denomination (`weightForVisit`) and then calls
     `requestRender`, whose first act is `reveal.setPlateForWeight(state.ui.weight)` — and the reveal
     still holding the stage is the OUTGOING one. `switchProduct` then AWAITS that render before
     playing the exit, so the resize is not merely computed, it is displayed.

     ⭐ AND IT IS THE GRIP ALONE, WHICH IS THE TELL. `setPlateForWeight` rebuilds geometry for every
     family, but a bumper is 450 mm at every weight and the dumbbell/barbell deliberately re-fit the
     camera so a head reads one size at any denomination. The grip plate is the documented exception
     — it genuinely changes diameter with weight and skips that re-fit — so it is the one product
     where the wrong denomination is a wrong SIZE rather than a wrong thickness.

     ⚠️ NOT gated on `switchingProduct`: that is set at the top of the `try` in `switchProduct`,
     which is AFTER `adoptProduct` has already returned. A guard has to be true at the moment the
     resize fires, not at the moment the switch is declared.
     ⚠️ Safe as a plain flag because `setPlateForWeight` runs SYNCHRONOUSLY at the top of
     `requestRender`, before its first await — so the window this covers cannot interleave. */
  let adoptingProduct = false;
  const lastPresetByFace = Object.create(null);
  const layoutFaceKey = (key, weight) => {
    const family = layoutFamilyFor(key);
    return `${family}|${layoutPresetsForProduct(family, weight).map((pr) => pr.id).join(',')}`;
  };
  /** Every distinct face this product has, as faceKey → one representative weight. */
  function facesOf(key) {
    const ws = designableWeights(key) || [defaultWeightFor(key)];
    const seen = new Map();
    for (const w of ws) { const k = layoutFaceKey(key, w); if (!seen.has(k)) seen.set(k, w); }
    return seen;
  }
  /** The layout to open a face on: what it was last set to, else the family's flagship. */
  function rememberedLayoutFor(key, weight) {
    const family = layoutFamilyFor(key);
    const seen = lastPresetByFace[layoutFaceKey(key, weight)];
    const wearable = seen && layoutPresetsForProduct(family, weight).some((pr) => pr.id === seen);
    return wearable ? seen : (FAMILY_DEFAULT_PRESET[family] || DEFAULT_LAYOUT_PRESET);
  }
  /* A face the buyer has PERSONALLY set. Until then a face follows along; afterwards it is pinned.
     Ryan 2026-08-02, in two halves that only make sense together:
       *"as soon as you make a manual override to the 2.5 or 5 lb, it stays on that new layout,
       independent of any changes made to the 10/25/35/45 no matter what"*
       *"but if you don't touch the 2.5/5 lb and change the 10/25/35/45 a few times, it CAN affect
       the 2.5/5 depending on what you pick"*
     So propagation is not a property of the LAYOUT (shared vs exclusive), it is a property of the
     DESTINATION: a shared layout reaches a face that has never been set and stops at one that has.
     "Depending on what you pick" is the second condition — an exclusive layout has nowhere to go
     regardless, because the other face cannot wear it. */
  const touchedFace = Object.create(null);
  /**
   * The buyer PICKED a layout, while standing on one particular face.
   *
   * Called from the two places a pick actually happens (the carousel and the View-all grid), NOT
   * from `setLayoutPreset`: healing, `smartStart` and a product hop all go through that function
   * too, and none of them is the buyer expressing a preference worth pinning a face on.
   */
  function rememberLayoutChoice(id) {
    const key = state.ui.product;
    if (!key || !id) return;
    const family = layoutFamilyFor(key);
    const here = layoutFaceKey(key, state.ui.weight);
    touchedFace[here] = true;      // this face is now the buyer's, and stops following
    lastPresetByFace[here] = id;   // the picker only offers what this face can wear
    for (const [faceKey, w] of facesOf(key)) {
      if (faceKey === here || touchedFace[faceKey]) continue; // already set, or pinned by its own choice
      if (layoutPresetsForProduct(family, w).some((pr) => pr.id === id)) lastPresetByFace[faceKey] = id;
    }
  }
  /**
   * ADOPT A PRODUCT — everything a product change means to STATE, with no 3D in it.
   *
   * ⛔ EVERY PATH THAT MOVES `state.ui.product` COMES THROUGH HERE. There were four such paths and
   * only three of them reconciled the design to the incoming product. `mountFreshProduct` — the
   * re-mount after the picked set has been emptied and re-filled — set three fields and nothing
   * else, so a dumbbell picked after a bumper had been designed mounted still wearing the PLATE's
   * layout, its arch text and its denomination (Ryan 2026-08-02: *"dumbbells and fixed barbells, if
   * added after designs have already been started on a plate, starting with a layout with text on
   * one of the arches"*). The layout picker then NAMED one of the cap's own arched presets, because
   * it highlights by composition (`matchPresetToComposition`) rather than by stored id — which is
   * why the symptom read as a wrong default instead of as a missing conversion.
   *   That is CLAUDE.md §0b for the fourth time: two places needed the same answer and one of them
   * was wrong. The fix is this function, not a second copy of the sequence.
   *
   * ⚠ THE ORDER BELOW IS A MOVE, NOT A REWRITE. It has absorbed several separate ordering bugs —
   * the ink restore must land before the record/refresh/paint beat (changelog 197), the logo dial
   * must re-seed before the draw (2026-07-30), `ctx.requestRender` not `preview.requestRender`
   * (2026-08-01). Anything that changes relative position in here is a regression, not a tidy-up.
   *
   * ⚠ THE "LEAVE" HALF RUNS ON THE FRESH-MOUNT PATH TOO, and that is deliberate rather than
   * defensive: `clearStage()` disposes the reveal but leaves `state.ui.product` still naming the
   * bumper, so when the buyer re-picks, the outgoing context is fully present in state and its ink,
   * weight and layout deserve to be remembered exactly as on a normal hop.
   *
   * Safe when `key` already equals `state.ui.product`: the weight and the ink round-trip through
   * their own memories unchanged and the family comparison takes the same-family branch.
   * `switchProduct` still early-returns on that case; `mountFreshProduct` must NOT, because the 3D
   * genuinely has to be rebuilt after a `clearStage`.
   *
   * Leaves the panel rebuild in `deferredPanelRefresh` for the CALLER to drain — a hand-off has an
   * exit spin to protect and a fresh mount does not.
   *
   * @returns {boolean} whether remembered ink was adopted (the contrast remap in `switchProduct`
   *   reads it — a guess must never overwrite a choice).
   */
  /* ── WHERE THE APP HOLDS THREE PRODUCTS' DESIGNS AT ONCE (Ryan 2026-08-10) ────────────────────
   * Before this: NOWHERE, and that is the whole of changelog 267's open bug. There is one
   * `state.designProfile` and `adoptProduct` mutates it in place, so "the bumper's design" is not
   * stored anywhere — it is RECOMPUTED on the hop back, out of memories that each live on a
   * different axis (`savedBodyColor` by product, `savedInk` by plate colour, `savedArcText` by
   * family) plus two session-only closures above that never reach an envelope at all
   * (`lastWeightByProduct`, `lastPresetByFace`). 266 reached for the staging tray because the tray
   * was the only place in the app that genuinely held more than one product's design — and tray
   * items come from an explicit PRIME, not from designing and saving, so Ryan published a set of
   * three and got one slide.
   *
   * 🔑 SO CAPTURE IS A REAL STEP, AND THIS IS THE SEAM FOR IT. `adoptProduct` is the one function
   * every path that moves `state.ui.product` comes through (see the note by `syncNav` below,
   * including `mountFreshProduct`, which is not a switch), and its top already stamps FOUR
   * per-product memories while the outgoing product is still named. This is the fifth stamp at
   * that same seam and the only one that is a whole design.
   *
   * ⛔ RE-DERIVING A FAMILY'S DESIGN HEADLESSLY WAS THE ALTERNATIVE AND IS THE TRAP. The restore
   * half below is ~90 lines whose ORDER has already absorbed several separate silent bugs (the
   * plate colour before the ink; the ink before the record/refresh/paint beat; the logo dial before
   * the draw). A second copy of that sequence for the publish dialog is CLAUDE.md §0b with a loaded
   * gun. A snapshot taken here is, by construction, exactly the design the buyer approved.
   *
   * ⚠ GATED THREE WAYS, because a wrong capture ships a stranger's plate to the storefront:
   *   • POPULATED only — a stock plate nobody has touched is not a design, and capturing one would
   *     let the publish dialog claim a family Ryan never authored.
   *   • THE DESIGN'S OWN FAMILY MUST MATCH THE PRODUCT BEING LEFT. `applyEnvelope` deliberately
   *     hands `switchProduct` the OLD key after `fromAutosave` has already installed the new
   *     design (see its comment), so at this seam state can name the bumper while the profile on
   *     screen is the dumbbell that was just opened. Comparing the layout preset's family against
   *     the key's is what refuses that, and it costs nothing on every honest hop.
   *   • An unknown preset id resolves to NO family rather than to the bumper — the same rule the
   *     rebuild test below uses, for the same reason (project CLAUDE.md §4).
   *
   * ⚠ `seq` IS THE TIE-BREAK, NOT INSERTION ORDER. Two competition-bumper configurations are two
   * products in ONE family and only one can be the family's slide; the publish dialog folds by
   * family and takes the highest `seq`, i.e. the one designed most recently. Object key order
   * cannot answer that — re-visiting a product overwrites in place and does not re-order.
   */
  const designByProduct = Object.create(null);
  let captureSeq = 0;
  /**
   * The design on screen RIGHT NOW as a capture record, or null when it is not one.
   *
   * ⭐ SHARED WITH THE LIVE READ ON PURPOSE (`ctx.liveDesign`). The publish dialog needs the same
   * question answered about the product being left and about the one still on the stage, and the
   * three gates above are the answer to both. A second copy in the dialog would be the one that
   * eventually disagrees — and it would disagree by publishing a stock plate nobody designed.
   */
  function captureOf() {
    const key = state.ui.product;
    if (!key || !isPopulated(state)) return null;
    const id = state.designProfile.layoutPreset;
    const family = LAYOUT_PRESET_IDS.includes(id) ? layoutPresetProduct(getLayoutPreset(id)) : null;
    if (!family || family !== layoutFamilyFor(key)) return null;
    let env = null;
    try { env = toAutosave(state); } catch { env = null; }
    if (!env) return null;
    return {
      env,
      product: key,
      family,
      /* ⚠ THE STORED ID, NOT THE DERIVED ONE. The dialog used to name the live layout through
         `matchPresetToComposition` — right for TELLING someone what they are looking at, wrong for
         rebuilding it, because this id is handed straight back to `applyLayoutPreset` when the
         parade loads the slide. Re-slotting to a derived neighbour would show an arrangement the
         admin never picked, which is the exact fault 266 exists to remove. */
      layoutPreset: id,
      // The rung on screen. `state.ui.weight` is the live one; the profile's own value is the
      // fallback for a product whose ladder never moved it.
      weight: Number(state.ui.weight) || Number(state.designProfile.weightValue) || 0,
      /* WHO it was for at the moment it was captured. The publish dialog compares this against the
         live design to catch the one way a snapshot can go wrong: swap the logo after leaving the
         bumper and the stored bumper still wears the old mark, so a "set" would ship two different
         identities across three models. See `identityKey` — it is deliberately NOT the artwork
         fingerprint, which differs per family by construction. */
      idKey: identityKey(state.designProfile),
    };
  }
  function captureLeavingDesign() {
    const cap = captureOf();
    if (cap) designByProduct[cap.product] = { ...cap, seq: ++captureSeq };
  }
  /** Forget every captured design. The artwork on screen is now somebody else's — same reasoning
   *  as `clearDesignIdentity` / `clearTray`, and the same failure if it is skipped: a set published
   *  after opening a second customer's design would carry the first customer's other two models. */
  function clearCapturedDesigns() {
    for (const k of Object.keys(designByProduct)) delete designByProduct[k];
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-28) — ⛔ `restoring`: THE STATE ON SCREEN IS NOT
     THE PRODUCT BEING LEFT. `applyEnvelope` restores a whole design (weight, plate colour, ink,
     layout) with `fromAutosave` and only THEN hands `switchProduct` the old key so the model
     rebuilds. Every "leaving" stamp below reads `state` — which by then is the INCOMING design — and
     files it under the OUTGOING product. Ryan, walking a shared set of bumpers at 10/25/35/45 on
     WEIGHT-1025-A: the first pass was right, and wrapping round gave *"25 … 35 … then the next two were
     both 45lb"*: each bumper wore the NEXT slide's weight, because that is what its memory had been
     stamped with. Colour, ink, nudge and layout were stamped the same way (invisible there only
     because every plate was navy). So a restore skips the leaving stamps entirely; the arrival half
     is untouched. */
  function adoptProduct(key, { restoring = false } = {}) {
    // The FACE being left, resolved while both the product and its weight still name it — the
    // stamp below has to land on the memory this design actually belongs to, and one line further
    // down neither is true any more.
    const prevFaceKey = layoutFaceKey(state.ui.product, state.ui.weight);
    if (!restoring) {
    // Stamp the ink belonging to the plate we are leaving, while `state.ui.product` still names it.
    // A hop is a plate-colour change whenever the design is dyed — a yellow competition bumper and a
    // black dumbbell are two contexts — and `migrateElementStyle` deliberately carries style across a
    // family hop, so without this the outgoing ink would simply follow and overwrite the incoming.
    /* ⭐ THE PLATE COLOUR, FIRST OF ALL THE STAMPS (Ryan 2026-08-09). Two configurations of one
       competition bumper differ by NOTHING except this, so a hop that does not carry it away is a
       hop that loses the buyer's white/green split entirely.
         ⚠ BEFORE `rememberInk`, not after. The ink is bucketed BY plate colour, so both calls read
       `bodyColor` — and while stamping cannot disturb it, the pair is mirrored by the restores
       below, where the order genuinely bites. Keeping the two seams in the same order is what makes
       that reviewable at a glance instead of by tracing `inkContextKey` twice. */
    rememberBodyColor(state, state.ui.product);
    rememberInk(state, inkContextKey(state));
    /* ⛔ AND THE NUMBER'S POSITION, at the same seam and for exactly the same reason (Ryan
       2026-08-09). Stamped while `state.ui.product` still names the plate being left, because that
       is the key it belongs under. `migrateElementStyle` carries the nudge across a family hop —
       right for a LAYOUT change, wrong for a PRODUCT one — so without this stamp the outgoing
       position would follow the buyer onto the next product and overwrite its own. See
       `rememberWeightNudge`; the restore is beside the ink's, in both hop branches. */
    rememberWeightNudge(state, weightNudgeKey(state));
    // ⛔ AND THE DENOMINATION, at the same seam and for the same reason (Ryan 2026-08-02: *"set a
    // fixed barbell to 110 lb, arrow over to a bumper, come back — it switched to the 60 lb"*).
    // Stamped while `state.ui.product` still names the plate being left. See rememberWeight.
    rememberWeight();
    /* ⭐ AND THE WHOLE DESIGN, at the same seam and for the same reason as the four stamps above
       (Ryan 2026-08-10). Taken while `state.ui.product` still names the plate being left, because
       that is the product this design belongs to; one line down neither the product nor its weight
       is true any more. See `captureLeavingDesign` for why the publish dialog cannot re-derive
       this instead. */
    captureLeavingDesign();
    } // end of the leaving stamps (skipped when `restoring`, see above)
    state.ui.product = key;
    productDownTab.setActive(key);
    // …and the pane nav, which names the picks either side of this one. Here rather than in
    // `switchProduct` because THIS is the function every path that moves `state.ui.product` comes
    // through (see the ⛔ above it) — including `mountFreshProduct`, which is not a switch.
    syncNav();
    // The down-tab IS the buyer's picked set, so a switch is always a real product
    // choice — carry it into the quote step verbatim. (This used to guess a
    // category from the 3D kind and deliberately skip grip/barbell picks so it
    // couldn't clobber them; now the key names the exact kind, so there's nothing
    // to guess and nothing to protect.)
    const { category, kind } = parseProductKey(key);
    state.ui.productCategory = category;
    state.ui.productKind = kind;
    // Back to the denomination this product was LAST LEFT ON, or its sample weight on a first
    // visit (45 lb plate / 50 lb dumbbell / 60 lb bar). See weightForVisit.
    setWeight(state, weightForVisit(key));
    if (productStep.refresh) productStep.refresh();
    const family = layoutFamilyFor(key);
    // ⚠ AN ID THIS BUILD DOESN'T KNOW MEANS "UNKNOWN FAMILY", NOT "THE BUMPER" (Ryan 2026-07-30).
    // `getLayoutPreset` falls back to `LAYOUT_PRESETS[0]` for anything unrecognised, which is a PLATE
    // preset — so a design still naming a deleted grip layout answered this question with `plate`,
    // the families matched, and the rebuild below was skipped entirely. The bumper then rendered the
    // grip's arrangement while the panel highlighted Varsity Arch and "View all" highlighted nothing.
    //   Resolving to `null` instead makes an unknown id fail the comparison against every real
    // family, which forces the rebuild — the safe direction, and the honest one. `applySnapshot` also
    // heals the id on the way in, so this is the second line of defence for a design that reached
    // this state some other way.
    const currentId = state.designProfile.layoutPreset;
    const currentFamily = LAYOUT_PRESET_IDS.includes(currentId)
      ? layoutPresetProduct(getLayoutPreset(currentId)) : null;
    /* ⛔ THE INK RESTORE HAPPENS INSIDE BOTH BRANCHES BELOW, NOT AFTER THEM (Ryan 2026-08-01).
       It shipped after the whole if/else and was wrong in three ways at once, because everything that
       READS the design runs in that block: `recordNow()` snapshotted and PERSISTED the pre-restore
       colours (so the adopted ink was absent from the undo stack and from the save — a reload reverted
       it silently), `refreshControls()`/`designPanel.refresh()` rebuilt the swatch row from them (so the
       panel highlighted the old colour while the 3D texture — built later, in `buildReveal` — showed the
       new one), and the flat SVG painted from them too.
         Ryan saw exactly that split: white ink on the grip, but the swatches highlighting black. The
       restore was correct; it was simply the last thing to run and nothing re-read it. */
    let adoptedInk = false;
    if (currentFamily !== family) {
      // Only remember an id that can actually be applied later — `setLayoutPreset` refuses an unknown
      // one and silently leaves the design alone, which would strand the buyer on the hop BACK.
      if (currentFamily && !restoring) lastPresetByFace[prevFaceKey] = currentId;
      /* Resolved against the FACE the restored denomination lands on, and re-validated there:
         `rememberedLayoutFor` refuses an id that face cannot wear and falls back to the flagship.
         That guard is not decoration — "an id this face cannot wear" is a failure this project has
         already paid for once (CLAUDE.md §4: a dangling id made a bumper render the grip's
         arrangement), and here two independent memories (the weight and the layout) are being
         restored together with nothing forcing them to agree. */
      setLayoutPreset(state, rememberedLayoutFor(key, state.ui.weight));
      // Keep a still-artless canvas artless across the hop. setLayoutPreset seeds the
      // family preset's weight/text elements (e.g. the grip's two "45 -LB-"); with no
      // real logo/text those must not appear — NOR persist and then leak onto the next
      // product on a further hop, which `preserve` would carry over (Ryan 2026-07-16).
      // A populated design (a real logo/text, which setLayoutPreset preserves) is still
      // carried across as intended — that's the "same identity across the set" moment.
      if (!isPopulated(state)) { state.designProfile.elements = []; state.ui.selectedElementId = null; }
      // ⛔ RE-SEED THE LOGO DIAL ON A PRODUCT HOP, BEFORE THE DRAW (Ryan 2026-07-30: *"by the time I
      // had circled back to the grip plate, the logo looked to have shrunk, and the size now read
      // 60%… I didn't change it from 75 to 60"*).
      //   The two size dials are rebuilt on completely different schedules, and that is the whole
      // bug. The design panel is torn down and rebuilt by `refreshControls`, so the WEIGHT stepper's
      // `seedDefault` re-fires on every hop and re-forces 75%. The Add Logo tab's dial PERSISTS, and
      // `logoStep.refresh()` only re-displays — so the logo silently inherited whatever
      // `setLayoutPreset`'s `seedDefaultSizes` had just written, which on the grip carries
      // `openShareFactorFor` (65/80) and reads ~60%. Hop away and back and the mark shrank.
      //   Seeding HERE rather than in `refreshControls` is deliberate: seeding commits and therefore
      // draws, and the flicker note on `syncLogoSize` records why it must not fire on a mere tab
      // visit. A product switch is already a deliberate re-render — the `requestRender` on the next
      // line — so this is the same "one and only draw" beat the upload flow uses.
      // The elements exist now, so the ink they should be wearing can land — and it must land before
      // the three lines under it, each of which reads the design and none of which runs again.
      /* ⭐ ONE ARRIVAL, ONE HOME (WD 460, 2026-09-22). The plate-colour restore, the ink restore, the
         re-balance, the ring re-derive and the outline assignment used to be written out here AND in
         the same-family branch below, word for word — and the packet renderer needed the identical
         sequence to draw a configuration the rep is not standing on (the Hartnell push rendered every
         denomination in the live plate's colour). A third copy of an order that has absorbed as many
         silent bugs as this one was the wrong answer, so the block moved to `arriveOnConfiguration` in
         ui/state.js with every note it carried. The order there is the order that was here. */
      adoptedInk = arriveOnConfiguration(state, key).adoptedInk;
      logoStep.refresh({ seed: true, force: true });
      recordNow();
      // ⚠ `ctx.requestRender()`, NOT `preview.requestRender()` (Ryan 2026-08-01). The flat SVG draws
      // `logo.renderUrl` — the recoloured composite — and falls back to `logo.dataUrl`, the ORIGINAL
      // uploaded artwork, when that is null. `syncLogoRaster` NULLS it whenever the composite collapses
      // to nothing on the product being left, and on this path it is not rebuilt until `buildReveal`
      // hundreds of ms later. So the flat frame painted the raw upload and nothing ever repainted it:
      // the 3D showed a white mark and the 2D showed a black one, on the same design. `requestRender`
      // is the one-atomic-commit door that awaits the raster first — the same rule `afterHistoryNav`
      // already follows for a wholesale restore. Its debounced texture build is harmless here; the
      // teardown below clears that timer a few lines later.
      adoptingProduct = true;
      try { adoptRenderPending = ctx.requestRender(); } finally { adoptingProduct = false; }
      /* ⛔ THE PANEL REBUILD IS DEFERRED PAST THE HAND-OFF (Ryan 2026-08-02, measured).
         `refreshControls()` used to run right here, synchronously, and a long-task probe across the
         switch found FOUR tasks totalling ~500ms packed into +0…+509ms — i.e. the entire 420ms exit
         spin was starved before it rendered a frame. It rebuilds the whole design panel, the logo
         step, the product step and the pick-weights pane; none of that is on screen while the model
         is spinning, and all of it reads state that is already committed above.
           ⚠ IT IS A DEFERRAL, NOT A SKIP. Every caller of `refreshControls` outside this function is
         untouched, and the `finally` below runs this on every path — including a failed build — so
         the panel can never be left describing the product the buyer just left. */
      /* Wrapped so the SIZE ladder's height eases across the rebuild instead of snapping — the
         products carry very different racks (a bumper's six rungs against the dumbbell's
         thirty-five), and after a switch this is the section whose height actually moves. */
      deferredPanelRefresh = () => designPanel.easeSizeAcross(refreshControls);
    } else {
      // Same reason as the branch above: a same-family hop can still change the plate colour
      // (Competition ↔ Training bumper — one dyes, one never does), and the refresh below reads it.
      /* ⭐ ONE ARRIVAL, ONE HOME (WD 460, 2026-09-22). The plate-colour restore, the ink restore, the
         re-balance, the ring re-derive and the outline assignment used to be written out here AND in
         the same-family branch below, word for word — and the packet renderer needed the identical
         sequence to draw a configuration the rep is not standing on (the Hartnell push rendered every
         denomination in the live plate's colour). A third copy of an order that has absorbed as many
         silent bugs as this one was the wrong answer, so the block moved to `arriveOnConfiguration` in
         ui/state.js with every note it carried. The order there is the order that was here. */
      adoptedInk = arriveOnConfiguration(state, key).adoptedInk;
      // Same layout FAMILY, different product (e.g. Competition Urethane ↔ Training Rubber,
      // both 'plate'). The block above is skipped, so nothing refreshed the design panel —
      // but per-PRODUCT panel content (e.g. the "Plate color" row, offered only on the
      // competition bumpers) would go stale (Ryan 2026-07-20). Rebuild it here.
      // …and deferred past the hand-off for the same reason as the branch above — this rebuild is
      // smaller but it lands in the same 420ms of animation.
      deferredPanelRefresh = () => designPanel.easeSizeAcross(() => {
        designPanel.refresh();
        // Same-family, different CATEGORY still moves the open header — the dumbbell and the
        // barbell share the 'dumbbell' family, so a dumbbell↔barbell hop skips the block above
        // and the pane never followed (Ryan 2026-07-16). Refresh it so its open/close animates.
        if (pickWeightsPanel.refresh) pickWeightsPanel.refresh({ animate: true });
      });
    }
    /* ⭐ EVERY configuration's dot, at the one point where every colour is finally authoritative.
     *
     * ⚠ NOT LEFT TO THE PAINT HOOK, which is where this obviously belongs and does not work. That
     * hook updates only the LIVE product, and it hangs off `ctx.requestRender()` — which the
     * same-family branch above never calls. A white-set → green-set hop is same-family by
     * definition (they are the same product), so the one journey this feature exists for is exactly
     * the one the paint hook does not cover. The hook stays for the live hue-bar drag, which this
     * cannot serve; the two answer different questions.
     *
     * ⚠ AND IT IS AFTER BOTH BRANCHES. `restoreBodyColor` runs inside each of them, so asking any
     * earlier would paint the incoming configuration's dot with the outgoing one's colour. */
    for (const p of pickedProducts()) {
      if (p.swatch) productDownTab.setSwatch?.(p.key, p.swatch);
    }
    return adoptedInk;
  }

  async function switchProduct(key, { restoring = false } = {}) {
    /* ⛔ `productSpec`, NOT `PRODUCTS[key]` (2026-08-09). The registry is keyed by the BASE key, so
       a raw lookup answers `undefined` for every SECOND configuration — and this guard then
       silently refused to switch to one. The pill was in the bar, it highlighted on hover, and
       pressing it did nothing at all: no error, no console, just a dead control, which is the
       hardest possible shape of bug to see. `productSpec` strips the instance, and it is the one
       door into the registry precisely so this question has a single answer. Found by
       `probe-config-adder`, which measures `state.ui.product` after a hop rather than trusting it. */
    if (!productSpec(key)) return; // not designable → nothing to put on the stage
    if (switchingProduct || key === state.ui.product) return;
    /* ⛔ CLAIM THE FRAMES BEFORE ANYTHING REBUILDS THE PANEL (Ryan 2026-08-02: *"whenever you change
       models, things are a little glitchy and laggy"*). This has to come BEFORE `adoptProduct`,
       not inside the `try`: a family change arms a `refreshControls()` that rebuilds all ~16
       carousel slides, and the IntersectionObserver queues them for drawing on the very next frame
       — well before the exit spin starts. Pausing after that point would be pausing a drain
       already several full SVG builds deep into the animation it is supposed to yield to.
         Released in the `finally`, and on the early return below. See design-panel's setThumbsBusy. */
    designPanel.setThumbsBusy?.(true);
    /* …and the PANE goes with them (Ryan 2026-08-07). The panel's rebuild is already deferred past
       the spin, so for that whole window the strip on screen belongs to the product being LEFT and
       then swaps in one frame — which is the "glitchy and laggy" the machinery above was chasing,
       still visible because it was happening in plain sight. Parking the body out means the swap
       lands behind a faded pane and arrives on the same motion a tab change uses.
         Released in `runDeferredPanelRefresh`, which is the ONE place the new content actually
       lands — not here in a `finally`, which would fade back in before the rebuild it is covering. */
    /* ⛔ ONLY WHEN THE DESIGN PANEL IS THE THING ON SCREEN (Ryan 2026-08-09, phone AND desktop):
       *"if you de-select the one which also just so happens to be the one in the preview pane, all
       the category headers/buttons disappear, all the sub-category/product buttons disappear, then
       it takes a second for them all to reappear."*
         This fade is body-WIDE but every word of the reasoning above it is about the DESIGN PANEL —
       its layout strip belongs to the outgoing product and swaps in one frame, so the fade covers
       the swap. On Pick Weights there is nothing to cover: four category cards and their pills are
       product-agnostic and identical before and after. So the tab was being blanked for the whole
       exit spin plus rebuild to hide a change that was never going to happen to it, and the buyer
       who had just deselected a product watched the menu they were using vanish and come back.
         `isConnected` is the honest test rather than a step number: `setBody` swaps content with
       `replaceChildren`, so the design panel is detached whenever another step is mounted, and the
       question this fade answers is literally "is there a design panel on screen to cover". A step
       index would need updating the day a fifth step arrives; this cannot go stale.
         The release in `runDeferredPanelRefresh` stays unconditional — it is idempotent, and a
       release that has to re-derive the same condition is a second place to get it wrong. */
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 497, 2026-11-01) — A HOP BETWEEN TWO PLATES OF THE SAME PRODUCT KEEPS THE PANE.
       Ryan, UX run 3: *"when you switch between a urethane bumper plate of one weight to another, the menu items are
       identical… think we can shorten the load time … by not clearing then reloading the left side slide-out pane"*.
       Two configurations of one product share every section (layouts, colours, size row); only the values differ, and
       `designPanel.refresh()` puts those in place after the spin. So the pane stays on screen (still inert during the
       spin, for the reason below), and the 160 ms wait for its fade-out is skipped - the model starts turning at once. */
    const sameProductHop = baseProductKey(state.ui.product) === baseProductKey(key);
    if (designPanel.el?.isConnected && !sameProductHop) sidePanel.setBodyBusy?.(true);
    /* ⛔ AND THE PANEL GOES INERT FOR THE DURATION — the cost of deferring its rebuild.
       Holding the rebuild back means the strip on screen during the spin belongs to the product
       being LEFT, and a layout card is the one control that is family-specific: clicking a bumper
       arch while a dumbbell is spinning in would apply a plate preset to a cap face. (Colours and
       sizes are product-agnostic, so they were never the exposure; layouts and the Size rungs are.)
       `inert` blocks pointer AND keyboard reach in one attribute and reverses cleanly — and for the
       1.4s of a set-piece the buyer is watching the model, not reaching past it. */
    if (designPanel.el) designPanel.el.inert = true;
    /* ── AND IT FADES, RATHER THAN SITTING THERE AND THEN POPPING (Ryan 2026-08-07) ──────────────
     * *"on exit, the weight rotates away AS the left pane contents fade away? Whereas when the new
     * weight enters, it enters, AND THEN AFTER THAT the side panes contents populate?"*
     *
     * The fade did not exist — `inert` blocks reach and has no look — so what actually happened was
     * worse than the asymmetry described: the pane stayed fully visible showing the OUTGOING
     * product's controls for the whole exit spin, then swapped in one pop once the enter settled.
     *
     * The SEQUENCING is not the thing to fix. The rebuild cannot move earlier: a family change
     * rebuilds ~16 carousel slides, measured at ~500ms of long tasks, and running it against the
     * 420ms exit spin is the "glitchy and laggy" this whole deferral exists to answer (2026-08-02).
     * So the swap stays where it is and the fade covers it — out with the exit, in after the settle.
     * Both sides then read as ONE move, and the pop has nowhere to be seen. */
    if (designPanel.el && !sameProductHop) designPanel.el.classList.add('is-handoff'); // WD 497: not between two of one product
    // Re-derived here rather than returned from `adoptProduct`: the only thing left in this
    // function that wants it is the "Now showing" announcement at the end of the hand-off.
    const { kind } = parseProductKey(key);
    const adoptedInk = adoptProduct(key, { restoring });
    // 3D not mounted yet — the choice above is all that's needed; initPersistent3D reads it.
    // Nothing is going to animate, so the panel and its thumbnails may have the thread back now.
    if (!reveal) {
      runDeferredPanelRefresh();
      designPanel.setThumbsBusy?.(false);
      if (designPanel.el) designPanel.el.inert = false;
      return;
    }
    switchingProduct = true;
    try {
      // §4.3 cross-product contrast remap — BEFORE the new texture builds, so
      // the incoming product's first frame already reads correctly (no pop).
      // Only fires hopping FROM a colored bumper body TO an always-black product.
      const targetAlwaysBlack = !productSupportsBodyColor(key);
      const hadColoredBody = !isBlackBody(state.designProfile.bodyColor);
      // ⚠ AND ONLY WHEN THE BUYER HAS NOT ALREADY ANSWERED THIS (Ryan 2026-08-01). The remap guesses
      // an ink that reads on black; `restoreInk` above knows the one they CHOSE for black. Both firing
      // means the guess overwrites the choice, and the toast then offers to undo a change the buyer
      // cannot see. This stays for the first visit to black, which is the case it was written for.
      if (targetAlwaysBlack && hadColoredBody && !adoptedInk && !remapToastShownFor.has(key)) {
        const { changed, snapshot } = applyCrossProductContrastRemap(state);
        if (changed) {
          remapToastShownFor.add(key);
          status.info('Adjusted your colors to pop on black.', {
            ms: 8000,
            actionLabel: 'Undo',
            onAction: () => {
              restoreSnapshot(state, snapshot);
              refreshControls();
              // Same wholesale-restore rule as afterHistoryNav: ctx.requestRender() so the logo's
              // transient composite is rebuilt, not just the flat SVG + texture (Ryan 2026-07-24).
              ctx.requestRender();
              status.announce('Colors restored.');
            },
          });
        }
      }

      buildToken++; // invalidate any in-flight texture build against the old reveal
      /* ⛔ LET `adoptProduct`'s RENDER LAND BEFORE ANYTHING ELSE HAPPENS (Ryan 2026-08-07). It is
         async — a logo raster composited on a canvas, then a `paint` that rebuilds the flat SVG,
         schedules a texture and redraws the layout thumbnails — and it was resolving into the first
         frames of the exit spin. That is why the exit stutters when a drag does not: a drag runs
         against an idle main thread, and this ran against one still finishing the switch.
           ⚠ AND `clearTimeout(textureTimer)` MOVED BELOW IT. `paint` calls `scheduleTextureUpdate`,
         which ARMS that timer — so clearing it first and awaiting second left the debounce re-armed
         and a full texture build landing mid-spin. Order matters more than the call did. */
      if (adoptRenderPending) { await adoptRenderPending.catch(() => {}); adoptRenderPending = null; }
      clearTimeout(textureTimer);

      /* §6.2 hand-off half 1 — spin the OUTGOING reveal away + fade before tearing it down
         (playHandoffExit degrades to an instant fade under reduced-motion).
         ⚠ THE PANE EMPTIES FIRST, AND IN FULL (Ryan 2026-08-07: *"on scroll, the left side pane
         contents should disappear, THEN the model should rotate out, THEN the new model rotates in,
         THEN the left side pane contents repopulate. Whereas right now, it looks like the left side
         pane contents disappear at the same time as the model rotating out — which is causing
         jumpiness and lag"*).
           The panel's `is-handoff` fade was armed at the top of this function and runs for 160ms,
         but the exit spin used to start one frame later — so nine of those ten frames had a CSS
         opacity animation and a 420ms 3D animation running against each other. Waiting the fade out
         makes the four beats strictly sequential, which is what Ryan is describing.
           ⚠ IT ALSO REPLACES A ONE-FRAME "DAYLIGHT" PAUSE THAT WAS NEVER ENOUGH. Everything above
         this line runs synchronously in one task — `adoptProduct`, which by the note at the top of
         this function arms a `refreshControls()` that rebuilds ~16 carousel slides; the contrast
         remap; a possible toast; the panel's own state flips. One rAF gave the browser a single
         frame to flush all of that before a 420ms animation began on top of it; ten frames actually
         clears it. The fade is not just cosmetic here — it is the window that work now happens in.
           The ENTER half never had this problem because it begins after `await buildReveal(...)` —
         an async boundary — and is explicitly protected (thumbnails held, panel rebuild deferred). */
      await new Promise((r) => setTimeout(r, (reducedMotion() || sameProductHop) ? 0 : PANEL_HANDOFF_FADE_MS)); // WD 497: no fade to wait out
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (reveal.playHandoffExit) await reveal.playHandoffExit();

      const oldMount = revealMount;
      const oldTexture = currentTexture;
      const oldReveal = reveal;
      reveal = null;
      currentTexture = null;
      if (oldReveal) oldReveal.dispose();
      if (oldTexture) oldTexture.dispose();
      if (oldMount && oldMount.parentNode) oldMount.parentNode.removeChild(oldMount);
      const ok = await buildReveal(key, { first: false, handoff: true });
      if (ok) status.announce(`Now showing: ${kind}.`);
      // If the showcase parade is open, the new reveal was built from the user's real
      // (blank) design — re-paint the current hypothetical onto it so switching products
      // mid-parade shows the SAME example on the new product, not a blank plate (Ryan
      // 2026-07-16).
      if (ok && showcase.isActive?.()) showcase.reapply();
    } finally {
      switchingProduct = false;
      /* ⚠ THE THUMBNAILS COME BACK WHEN THE SETTLE ENDS, NOT WHEN `buildReveal` RESOLVED.
         `playHandoffEnter` is fired un-awaited and runs for a further second, so releasing here
         would put sixteen SVG builds straight into the tail of the animation — the same collision
         moved one beat later, which is exactly the "new model spinning in" half of what Ryan saw.
           `.finally` on a promise that is already resolved (reduced motion, no reveal, a failed
         build) still defers by a microtask, so the release can never run before this block ends.
         Errors are swallowed by construction — `finally` ignores the rejection reason — and a
         thumbnail drain must never be the thing that surfaces a 3D failure.
           ⚠ KNOWN AND ACCEPTED: `refreshBump` is on its own `HANDOFF_SETTLE_MS` (1150ms) timer, so
         its shader recompile lands ~150ms after this releases — two or three thumbs in. That is ONE
         event against the sixteen this pause removes from the animation, and moving it would mean a
         third schedule tracking the other two. Left measured rather than guessed at: if a hitch
         survives at the tail of the settle, this is the first place to look. */
      const settled = handoffEnterDone;
      void settled.catch(() => {}).then(() => {
        // Only if no NEW switch has started in the meantime; that one owns the pause now and will
        // release it itself. Without this, arrowing twice quickly would un-pause mid-second-hop.
        if (switchingProduct) return;
        /* ── THREE BEATS, NEVER TWO AT ONCE (Ryan 2026-08-02) ──────────────────────────────────
           1. the model finishes its settle          (awaited above)
           2. the panel rebuilds and the Size ladder SLIDES to its new height
           3. …and only when it has stopped moving do the layout thumbnails redraw.
           Beats 2 and 3 used to start on the same frame: a 320ms height tween running while a
           queue of full SVG builds drained one per frame, which is a tween being animated by a
           thread that is not available to animate it. The panel comes out of `inert` with beat 2
           because it is truthful again by then — waiting for the thumbnails would leave a
           correct, readable panel refusing clicks for no reason a buyer could see. */
        const slid = runDeferredPanelRefresh();
        if (designPanel.el) designPanel.el.inert = false;
        void slid.catch(() => {}).then(() => {
          if (switchingProduct) return;   // a newer switch owns the beats now
          designPanel.setThumbsBusy?.(false);
          /* ── AND ONLY NOW DOES THE PLATE START BREATHING AGAIN (Ryan 2026-08-02) ──────────────
           * The last beat. `setIdleHold(false)` releases the render LOOP and arms reveal-3d's
           * wind-up: still for 3s, then the sway's speed eases 0 → full over 5s — behaviour that
           * until now only the very first roll-in could reach.
           *   ⚠ ARROWING QUICKLY LEAVES IT SUPPRESSED FOR THE WHOLE RUN, which is the point. Each
           * switch re-holds before this fires, and `setIdleHold` no-ops when the state is
           * unchanged, so a burst of presses never restarts a 3-second countdown per press — the
           * sway simply stays off until the buyer settles on something. */
          reveal?.setIdleHold?.(false);
        });
      });
    }
  }

  /**
   * SHOW A DIFFERENT DENOMINATION OF THE PRODUCT ALREADY ON THE STAGE (Ryan 2026-08-02).
   *
   * Driven by the "Size" chips at the top of Customize Design. The buyer's question is *"does the
   * 25 lb look like the 45?"*, and until now the tool could not answer it — the designer opens on
   * one representative sample per category and the denomination picker was removed on 2026-07-20.
   *
   * ⚠ IT DISSOLVES — IT DOES NOT SPIN, AND IT USED TO (Ryan 2026-08-02).
   *
   * The first build ran the product hand-off set-piece here, at Ryan's own request: *"it should
   * animate in the same way it animates if you use the scroll buttons at the bottom to move from
   * one product to another."* Using it told him otherwise — *"it still feels like there's some lag…
   * the spinning weight model might be overkill"* — and the reason is frequency, not taste. A
   * product switch is a rare, deliberate act, so 420ms of wind-up plus a 1s settle reads as
   * ceremony. A denomination change is a BROWSE: six of them in a row, comparing. The same
   * choreography that dignifies the rare act obstructs the frequent one.
   *
   * So this now does what §6.2 already prescribed for everything that is not one of the two
   * set-pieces (design-initiation, product hand-off): a micro crossfade and no rotation.
   * `runTextureUpdate` → `reveal.setTexture` dissolves the old face into the new over 0.7s in the
   * shader (uMix / uMap2), which is the same transition a colour edit has always used. The plate
   * never stops being draggable, the camera is never taken away, and there is no window in which
   * the buyer is waiting on a performance rather than looking at a plate.
   *
   * ⚠ A SNAP WAS THE OTHER OPTION AND IS WORSE, specifically on the bumper. That face is 450 mm at
   * every denomination, so the ONLY visible difference is one numeral and an edge seen end-on —
   * replace it in a single frame and the honest answer ("they're identical") is indistinguishable
   * from a dead control. The dissolve is what puts the eye on the thing that moved.
   *
   * ⚠ HEAL THE LAYOUT FIRST. On the grip family a denomination is not just a size: 2.5 and 5 lb
   * are the CUTOUT-FREE plates — a plain annulus with `PLATE_ANGLES` and no gaps to arch a word
   * along — so `layouts.js` gates six of the family's presets by weight. Land on a denomination
   * that cannot wear the current preset and the design would paint text into a place that does not
   * exist on that face. `grip-classic` (the family default) carries no `weights` predicate and is
   * therefore valid at every denomination, which is what makes it a safe backstop rather than
   * another guess.
   *
   * ⚠ AND IT NO LONGER REBUILDS THE DESIGN PANEL (Ryan 2026-08-02: *"every click results in all the
   * 2D renders on the layout cards disappearing, then all reappearing at once when they reload"*).
   *
   * He is describing `refreshControls()`, which this used to call. That tears down and rebuilds the
   * whole panel — including all ~16 layout cards — and their thumbnails are drawn LAZILY, one per
   * frame, by an IntersectionObserver. So every rung click blanked the carousel and trickled it
   * back, to change one numeral inside each tile.
   *   `designPanel.refreshThumbs()` is the tool that already existed for exactly this: it swaps the
   * SVG inside each surviving `[data-wd-thumb]` tile and, in its own words, "touches no control, no
   * carousel transform, and no drag handler". Paired with `refreshSizeTiles()` for the ladder's own
   * selected state and `logoStep.refresh()` to re-probe the logo dial, that covers everything a
   * denomination actually changes — and leaves the ~16 cards, the swatch rows, the font list and
   * the scroll position exactly where the buyer left them.
   */
  /* ⛔ A CLICK DURING A CHANGE IS REMEMBERED, NOT DROPPED (the layout picker's lesson, again).
     The hand-off holds this path for the 420ms exit spin plus the texture build, and a bare
     `if (busy) return` spends that window throwing rungs away — so scanning 55 → 45 → 35 → 25 by
     tapping four times lands on 45 and the control reads as DEAD rather than slow. That is the same
     complaint the layout carousel got ("it should scrub at the rate you click", changelog 76-82).
       Coalesced rather than queued: only the LAST rung asked for is worth painting, because the
     ones in between are denominations nobody looked at. So the buyer always ends on what they last
     clicked, having waited for at most two changes rather than one per click. */
  let changingWeight = false;
  let queuedWeight = null;
  async function changeWeight(w) {
    const next = Number(w);
    if (!Number.isFinite(next) || next <= 0) return;
    if (switchingProduct) return;
    if (changingWeight) { queuedWeight = next; return; }
    if (next === Number(state.ui.weight)) return;
    changingWeight = true;
    try {
      const prevWeight = Number(state.ui.weight);
      setWeight(state, next);
      const family = layoutFamilyFor(state.ui.product);
      const eligible = layoutPresetsForProduct(family, next);
      /* ⛔ CROSSING A FACE RESTORES THAT FACE'S MEMORY — ALWAYS, not only when forced.
         This was `if (!eligible.includes(current))`, i.e. restore only when the layout literally
         cannot be worn, and Ryan caught the hole immediately: *"I clicked the 5 lb, changed the
         layout to one ONLY available on the 5 lb, went back to the 10 lb (good, no change), then
         back to the 5 lb and it adopted the layout from the 10 lb since that one is available on
         both."* Coming back, Grip Classic was perfectly WEARABLE on the small face, so nothing
         forced a restore and his Fieldhouse never returned. Eligibility was answering "may this
         layout stay?" when the question is "what does this face wear?".
           The `!wearable` half is kept as a floor: a face must never be left in a layout it cannot
         render, whatever the memory says. */
      const crossed = layoutFaceKey(state.ui.product, prevWeight) !== layoutFaceKey(state.ui.product, next);
      const wearable = eligible.some((p) => p.id === state.designProfile.layoutPreset);
      const beforeId = state.designProfile.layoutPreset;
      if (crossed || !wearable) setLayoutPreset(state, rememberedLayoutFor(state.ui.product, next));
      // Only a layout that ACTUALLY moved needs the heavy refresh — the lock-step case restores the
      // id it already had, and rebuilding the carousel to change nothing is the blanking Ryan saw.
      const healed = state.designProfile.layoutPreset !== beforeId;
      /* ⛔ THE HEAVY REFRESH IS FOR THE HEAL, AND ONLY THE HEAL.
         `refreshControls()` rebuilds the design panel wholesale, which destroys ~16 layout cards and
         re-draws their thumbnails one per frame — the blanking Ryan saw. It is the right tool when
         the LAYOUT SELECTION moved (a grip dropping onto a denomination its preset cannot wear),
         because the carousel's chosen card genuinely changed. It is a sledgehammer for a numeral.
           Both paths run BEFORE the draw, not after: a panel rebuild re-fires the weight number's
         `seedDefault` and a seed COMMITS, so running it afterwards let the plate land wearing the
         OUTGOING denomination's share and pop to the right size a beat later (measured on the grip:
         a 45 lb number back at 48.85px, correcting to 44.77px one interaction on). Refreshing first
         folds that commit into the single texture build below — the same "the requestRender on the
         next line is the one and only draw" rule the upload flow already follows. It is also why
         `textureTimer` is cleared AFTER: a debounce already in flight must not repaint over it. */
      if (healed) refreshControls();
      else { designPanel.refreshForWeight(); logoStep.refresh(); }
      // ── re-dress, in the order the face is assembled ──────────────────────────────────────
      /* ⛔ THE GEOMETRY NO LONGER SWAPS AHEAD OF THE ARTWORK (Ryan 2026-08-05: *"the new weight will
         spin in and look good at first, then the logo/text/number on it will flash… sometimes when it
         reappears it's bigger"*).
           `setPlateForWeight` used to run HERE, two awaits before the texture that belongs to it. So
         the plate took the new denomination's shape while still wearing the previous one's face —
         and because the texture is UV-mapped, the old artwork simply scaled onto the new plate. On a
         GRIP that is glaring: it is the one product whose face physically changes size per
         denomination (10.63″→17.72″), so the 45's face on a 2.5's disc renders the number small, and
         the correct face then lands with the number re-pegged to a bigger share of a smaller plate.
         "It spun in at the incorrect size" was exactly right — it was wearing the wrong plate's clothes.
           The swap is now handed to `runTextureUpdate` as `beforeApply`, which fires it after the
         build and immediately before `setTexture`. Shape and face change in the same beat.
           ⚠ Measured first, not guessed: the instrumented run showed the seeded `sizeScale` (1.013)
         was already correct at the geometry swap AND at both builds, which ruled out the re-peg —
         the fault was never the number's size, it was which texture the plate had on. */
      updateStageTint();
      preview.requestRender(); // the flat SVG — the no-WebGL fallback must move too
      clearTimeout(textureTimer);
      try { await syncLogoRaster(); } catch { /* a canvas failure must never strand the dissolve */ }
      // The dissolve IS this call: `runTextureUpdate` hands the new face to `reveal.setTexture`,
      // which crossfades it over the old one in the shader. No rotation, no camera, nothing locked.
      await runTextureUpdate(fullTexSize(), 1, () => {
        if (reveal && reveal.setPlateForWeight) reveal.setPlateForWeight(next, 'lb');
      });
      recordNow();
      status.announce(`Now showing the ${displayWeightText(next, state.ui.unit)} ${state.ui.unit} ${categoryLabelOne(parseProductKey(state.ui.product).category)}.`);
    } finally {
      changingWeight = false;
      // Drain whatever was clicked while that ran. `null` FIRST, or a queued rung equal to where we
      // already landed would leave the slot armed forever and the next real click would find it
      // stale. Recursion is bounded by the clicking: each drain consumes the slot and only a fresh
      // click can refill it, so this is at most one extra pass per burst.
      const q = queuedWeight;
      queuedWeight = null;
      if (q != null && q !== Number(state.ui.weight)) void changeWeight(q);
    }
  }

  // Deselecting EVERY weight leaves nothing to show → tear the reveal down to a blank
  // stage (Ryan 2026-07-16). The reveal renders with scene.background = null, so once
  // its mount is gone the CSS stage gradient IS the blank canvas — nothing else to draw.
  function clearStage() {
    if (!reveal) return;
    buildToken++; // invalidate any in-flight texture build against the dying reveal
    clearTimeout(textureTimer);
    const oldMount = revealMount;
    const oldTexture = currentTexture;
    const oldReveal = reveal;
    reveal = null;
    currentTexture = null;
    oldReveal.dispose();
    if (oldTexture) oldTexture.dispose();
    if (oldMount && oldMount.parentNode) oldMount.parentNode.removeChild(oldMount);
    productDownTab.setActive(null);
  }

  /**
   * Re-mount from a blank stage: the left-most remaining pick becomes the live product.
   *
   * ⛔ IT ADOPTS THE PRODUCT PROPERLY — it used to set three fields and build (Ryan 2026-08-02).
   * `clearStage` disposes the reveal but leaves the DESIGN alone, so a bumper's Varsity Arch (arch
   * text and all) was still on the profile when the buyer re-picked, and this path did nothing to
   * reconcile it: the dumbbell mounted wearing a plate layout at the bumper's denomination, with
   * stale ink, a stale logo dial and no undo entry. `adoptProduct` is the whole of that sequence
   * and it is shared with `switchProduct` — see its header for why this must never be a copy.
   *
   * ⚠ NO `key === state.ui.product` GUARD, unlike `switchProduct`. Clearing every pick and then
   * re-picking the SAME product is a real case and the 3D genuinely has to be rebuilt; adoption is
   * a no-op on that path by construction.
   *
   * The deferred panel rebuild is drained IMMEDIATELY rather than after the build, because there is
   * no outgoing model to spin away and therefore no animation for it to starve — the same call
   * `switchProduct` makes on its own `!reveal` early return, which is the identical situation.
   */
  async function mountFreshProduct(key) {
    // `productSpec`, not a raw registry lookup — see the note on the identical guard in
    // `switchProduct`. This is the path a buyer takes back from an empty stage, so the same bare
    // lookup here would have made a set whose ONLY pick is a second configuration unmountable.
    if (!productSpec(key)) return;
    adoptProduct(key);
    runDeferredPanelRefresh();
    /* ⭐ AND IT SAYS SO WHILE IT WORKS (Ryan 2026-08-12: *"if you have no weights selected but you go
       to the Pick Weights tab and you click on one, it takes a little bit to load… can you add the
       'Loading weights…' message"*).
         This is the SAME wait as a cold start — the reveal chunk, the geometry, a full-size raster —
       on the same bare studio, and it was the one entrance to a blank stage that never said anything.
       So it is the same call, not a second loader: `showBootLoader` already picks the wording from
       the state (a blank design gets "Weights loading…", one with work in it gets "Preparing your
       design…"). `buildReveal` takes it down on the way past; `fallBackToFlat` covers the throw.

       ⛔ `immediate`, AND THEN A FRAME — THE 400ms ARM DOES NOT WORK ON THIS PATH, AND WHY IS THE
       WHOLE FIX. Armed exactly as the cold start does it, the message never appeared at all:
       `hideBootLoader` ran with the timer still PENDING and an id that had never fired, 4.9s after
       it was set (measured under 6× CPU throttling — and at full speed too, where the mount takes
       1.5s). Nothing on this path yields: the geometry build and the raster are synchronous
       main-thread work, so a deferred callback cannot run, and even if it did the browser could not
       paint it. A cold start has real async gaps in it (the chunk import, the fetches), which is
       exactly why arming works THERE and is dead here.
         So paint synchronously, then hand the browser two frames to composite it before the thread
       is blocked. `requestAnimationFrame` TWICE, not once: the first fires before the paint of the
       frame it is scheduled in, so a single one still lets the block land on an unpainted message. */
    showBootLoader({ immediate: true });
    await new Promise((r) => {
      if (typeof requestAnimationFrame !== 'function') { r(); return; }
      requestAnimationFrame(() => requestAnimationFrame(() => r()));
    });
    // Caught, because on the portal's blank boot this IS the first mount — see `fallBackToFlat`.
    try {
      await buildReveal(key, { first: false });
    } catch (err) {
      fallBackToFlat(err);
    }
  }

  // Debounced live update: rebuild the texture from the current design and swap
  // it onto the plate, disposing the previous one (the reveal never disposes a
  // caller-owned texture — see reveal-3d.js dispose()). An in-flight token means
  // a slow build can never clobber a newer design.
  function scheduleTextureUpdate() {
    if (!reveal || !pipelineMod) return; // 3D not mounted (still loading, or fallback)
    clearTimeout(textureTimer);
    textureTimer = setTimeout(() => runTextureUpdate(fullTexSize()), TEXTURE_DEBOUNCE_MS);
  }

  // ── logo-outline raster (A auto-conflict peel · B manual subtract · C add) ───
  // Regenerate the logo's composited raster to match the effective outline edits:
  // first PEEL `max(auto-conflict, manual)` outer bands (outside-in), then ADD a fresh
  // outer ring if the buyer asked for one (C — definition on a matching plate). The
  // original raster is never mutated — `renderUrl` is the transient result (null =
  // original), so un-dyeing / toggling back restores it exactly; `renderAspect` carries
  // the padded aspect when an added ring grew the canvas. Async (canvas); a job counter
  // drops a superseded result. Cheap no-op when the composite key is unchanged.
  //
  // EVERY LOGO SLOT, not just the first (Ryan 2026-07-24 bug). This used to `.find()` one logo
  // element, so on a 2-logo layout ("Four Corners" = logo top + logo bottom) only the TOP logo was
  // ever composited: the bottom kept whatever `renderUrl` it was given when the layout was applied
  // and was never rebuilt OR cleared again. On a yellow plate that showed as a top logo correctly
  // recoloured for contrast beside a bottom logo still in the plate's own colour — invisible.
  // The panel is single-logo by design too (it edits `elements.find(type==='logo')`), and layouts.js
  // seeds every empty logo slot from ONE `savedLogo` — "a plate shares ONE logo across all its
  // slots" — so the first element's styling IS the design's logo styling, and every slot showing
  // that same source art gets the same composite. A slot carrying DIFFERENT art (no UI produces one
  // today) is left alone rather than being handed someone else's raster.
  // Returns true when any slot's composite changed, so the caller can repaint surfaces that draw it.
  let logoPeelJob = 0;
  /**
   * @param {object} [profileArg] rebuild the composite on THIS profile instead of the live one.
   *
   * ── WHY THIS TAKES AN ARGUMENT (Ryan 2026-07-31) ─────────────────────────────────────────
   * Ryan: *"on the grip plates, I added a custom color to the palette… made the logo and weight
   * numbers that color, and sent them to the project. In the project, the logo did not adopt the
   * custom color. The weight numbers did."*
   *
   * `logo.renderUrl` — the composite this function builds — is a TRANSIENT that `serializeLogo`
   * deliberately drops (see the note in `afterHistoryNav`). Every restore therefore lands with it
   * null and `render-engine.js` falls back to `logo.dataUrl`, the ORIGINAL artwork. On screen
   * that never showed, because `ctx.requestRender()` re-runs this. Off screen it always showed:
   * `plate-batch` restores a staged envelope into its OWN state and rasters it directly, so the
   * plate went into a customer's packet wearing the logo's original colours while the weight
   * numbers — plain SVG fills — took the new one. Exactly the split he saw.
   *
   * Parameterised rather than extracted to a module: this reads nothing but the profile and its
   * module imports, so an argument is the whole change. Moving 135 lines of a pipeline Ryan has
   * tuned across a dozen changelog entries to achieve the same thing would be all risk, no gain.
   *
   * ⚠ THE SUPERSEDE COUNTER IS SKIPPED FOR AN EXPLICIT PROFILE. `logoPeelJob` exists so a newer
   * live edit abandons an older in-flight raster. An off-screen batch is not competing for that
   * — and if it bumped the counter it would CANCEL whatever the live UI was mid-way through.
   */
  async function syncLogoRaster(profileArg) {
    const guarded = !profileArg;
    const p = profileArg || state.designProfile;
    /* ⚠ THE PLATE THIS COMPOSITE IS BEING BUILT AGAINST — not `p.bodyColor` (Ryan 2026-08-01).
       The text path has been product-gated since 2026-07-22 (`plateBodyColor`, and
       `bodyColorOverride` on every render call), so on a product that cannot be dyed the numbers
       resolve against BLACK while the design keeps its dye. This function read the stored dye
       directly, so the logo raster was still peeling bands and running the contrast rescue for a
       yellow plate on a black dumbbell: text and logo disagreed about which plate they were on,
       and only the logo was wrong.
         On the LIVE path the answer is the current product's effective body. On an explicit
       profile it is the profile's own dye — an off-screen caller renders a product the live UI is
       not showing, so `state.ui.product` is the wrong question there; the caller states the plate
       ON the profile it hands over (see `buildJobProfile`, which is what makes that true). */
    const body = guarded ? plateBodyColor() : (p.bodyColor || DEFAULT_BODY_COLOR);
    const logoEls = (p.elements || []).filter((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    const logoEl = logoEls[0];
    if (!logoEl) return false;
    const twins = logoEls.filter((e) => e.logo.dataUrl === logoEl.logo.dataUrl).map((e) => e.logo);
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-26) — THE INK HULL, measured once per artwork
       and BEFORE the paint this sync is awaited by, so the logo dial's new stop (ink meets the band,
       `LOGO_SCALE_REACH` in render-engine) is known on the first frame. Keyed on the same source
       fingerprint as the composite below. Transient like `renderUrl`: never serialised, so a
       resumed or shared design re-measures here. A failed read is recorded (empty `pts`) so it is
       not retried on every paint, and the engine then keeps the old box stop. */
    {
      const hk = inkHullKey(logoEl.logo.dataUrl);
      if (hk && !(logoEl.logo.inkHull && logoEl.logo.inkHull.key === hk)) {
        const hull = await measureInkHull(logoEl.logo.dataUrl, resolveLogoAspect(logoEl.logo)).catch(() => null);
        for (const t of twins) t.inkHull = hull || { key: hk, aspect: 0, pts: [] };
      }
    }
    const bands = p.logoStructure && Array.isArray(p.logoStructure.bands) ? p.logoStructure.bands : [];
    // Per-band colour overrides (the buyer recoloured an outline band in the Colors panel).
    // Computed FIRST so the auto-conflict peel below compares the plate against each band's
    // CURRENT colour, not its original — else recolouring the outer band (orange→pink) then
    // dyeing the plate that same pink wouldn't drop the now-redundant pink-on-pink ring
    // (Ryan 2026-07-22). Kept only for in-range bands; a disabled band ignores its override.
    const bandColors = {};
    if (logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') {
      for (const k of Object.keys(logoEl.logoBandColors)) {
        const idx = k | 0, hx = normalizeHex(logoEl.logoBandColors[k]);
        if (idx >= 0 && idx < bands.length && hx) bandColors[idx] = hx;
      }
    }
    // The bands as they CURRENTLY render (override if recoloured, else the detected colour).
    const effBands = bands.map((c, i) => bandColors[i] || normalizeHex(c) || c);
    // Effective disabled bands = the buyer's MANUAL set. The remap peels outermost-disabled bands and
    // recolors a disabled inner band to its outer neighbour.
    const disabled = new Set((Array.isArray(logoEl.logoBandsOff) ? logoEl.logoBandsOff : []).map((i) => i | 0));
    // ── A MERGING RING IS REPAINTED, NOT REMOVED (Ryan 2026-07-28) ───────────────────────────────
    // "the navy plate makes the logo mostly orange and white — the blue gets removed for more orange
    // — looks like crap."
    //   The peel was written for a DECORATIVE outline: a keyline that happens to match the plate reads
    // as a phantom edge, and deleting it lets the mark sit cleanly on the dye. That reasoning holds
    // for a ring that is trim. It fails completely for a ring that is STRUCTURE — the Denver Broncos
    // badge carries its wordmark in the outer navy ring, so peeling it on a navy plate deletes a third
    // of the design rather than tidying an edge, and nothing in a band's geometry says which kind it
    // is.
    //   Repainting answers both: a decorative keyline in the standout colour is still a keyline, and a
    // structural ring keeps its lettering. The mark's SHAPE is preserved either way, which is the part
    // the tool has no right to change on the buyer's behalf.
    //   The target comes from `fillContrastRemap` — the same rule the band-less fill swap uses — so
    // there is one definition of "the colour that stands off this plate" rather than a second one
    // here that could disagree with it.
    //   ⚠ DERIVED, NEVER STORED. This writes the LOCAL `bandColors` used to build this raster, not
    // `logoEl.logoBandColors`. Un-dyeing the plate restores the original ring with nothing to undo,
    // which is the same contract the fill swap and the peel already keep.
    /* ⭐ `allowDark` + `avoid` (Ryan 2026-08-10, the Cherry Valley crest) now live inside
       `bandConflictView`, along with the peel-vs-repaint choice — extracted 2026-08-12 so the
       Customize-Design swatch can show the SAME answer instead of re-deriving it beside the chip.
       See that function's header for why each option is opt-in. */
    // Roles AND the bench, `avoid`, the touch table, the detached-keyline keep and `ringed` — all
    // five-and-one built by `bandViewOptions`, the one place that knows the shape.
    const bandView = bandConflictView(effBands, body, bandViewOptions(p));
    Object.assign(bandColors, bandView.repaint);
    for (const i of bandView.peel) disabled.add(i);
    const off = [...disabled].filter((i) => i >= 0 && i < bands.length).sort((a, b) => a - b);
    // ⚠ COMPUTED AFTER the rescue above, not before. These describe what the raster will be built
    // with, and the rescue adds entries to `bandColors` — read earlier, a repainted ring would not be
    // in the cache key (so a plate change could serve a stale raster) and `hasBandColors` could be
    // false while band overrides existed (so the remap would skip them entirely).
    const bcKey = Object.keys(bandColors).sort((a, b) => a - b).map((i) => i + ':' + bandColors[i]).join(',');
    const hasBandColors = bcKey.length > 0;
    const center = normalizeHex(logoEl.logoCenterColor) || null; // flatten the logo interior
    // Contextual-contrast fill swap (Ryan 2026-07-23): for an OUTLINE-LESS logo, a brand color
    // that merges with the dyed plate is repainted to the standout brand color (green→yellow on a
    // green plate). Only when there are no outline bands (a banded logo's interior is protected by
    // its kept inner ring, and the band peel already handles the outer conflict) and no manual
    // flatten. Reversible: derived from bodyColor each sync, un-dye restores the original raster.
    // ⚠ AND NOT ON A COLOUR-BORNE MARK (Ryan 2026-07-28: "it still changes the logo to the solid
    // orange or blue ones despite those swatches not even being visible"). THIS is what was doing it —
    // not the flatten. The swap is gated on `!bands.length`, which reads as "a plain mark with no
    // rings to protect it"; a concentric badge reports no bands either, for the opposite reason, so it
    // sailed straight through and had its whole orange field repainted navy.
    //   The swap is right for the mark it was written for: repaint the ONE tone that merges with the
    // plate, on a logo whose tones sit apart. On a mark whose tones all touch, repainting one tone to
    // another that is already present MERGES two adjacent regions — the ring and the field become one
    // shape — which is the same destruction as the flatten, arrived at a different way.
    // Gate + derivation both live in `contextualFillSwap` now (extracted 2026-08-12, same reason as
    // `bandConflictView` above) — the swatch has to be able to ask the identical question.
    const autoRemap = contextualFillSwap(p, body);
    // The buyer's OWN tone swaps (Ryan 2026-07-26) — a colour they edited on the Add Logo tab's
    // "Your colors", repainted in the artwork. Unlike the contextual remap above these are NOT gated
    // on the logo being band-less: a banded logo's interior is just as much theirs to recolour, and
    // the band pixels are handled separately through `bandColors`, so the two can't fight over the
    // same pixel. Theirs go FIRST — the pixel pass takes the first match and stops, so a deliberate
    // choice outranks a derived contrast swap on the same tone.
    //   COMPOSED with the contrast remap, not merely ordered ahead of it (Ryan 2026-07-26: "should
    // the logo color have a new color from the Add Logo tab, then when I click on the CD tab to
    // change the plate color and it cascades down to affect the logo, then it should change in
    // accordance with the new colors given to it"). The pixel pass takes the FIRST matching swap and
    // stops, so a pixel rewritten by `{originalBlue → red}` can never also be caught by the
    // contrast remap's `{red → standout}` — a logo recoloured red, on a plate later dyed red, would
    // sit there invisible with the rescue mechanism looking straight past it. Running the buyer's
    // target through the contrast map here collapses the two hops into the one the pass can apply.
    //   The plate cascade therefore judges the logo by the colours it is WEARING, which is the whole
    // of his point: the band peel already did (it reads `logoBandColors`), and `applyVanishingLogoDefaults`
    // already did (it reads the palette roles `recolorEverywhere` rewrote). The interior was the gap.
    const autoMap = new Map(autoRemap.map((s) => [normalizeHex(s.from), normalizeHex(s.to)]));
    const userSwaps = (Array.isArray(logoEl.logoToneSwaps) ? logoEl.logoToneSwaps : [])
      .map((s) => ({ from: normalizeHex(s && s.from), to: normalizeHex(s && s.to) }))
      .filter((s) => s.from && s.to)
      .map((s) => ({ from: s.from, to: autoMap.get(s.to) || s.to }));
    // The auto entries still trail the buyer's: they cover ink nobody has touched. Any that duplicate
    // a `from` already claimed above is unreachable and harmless — the first match wins.
    /* ⭐ …AND THE PIECE-SCOPED RESCUE, LAST (WD 455, Ryan's carrolllynx card). A last resort by
       construction — `ghostedPieceSwaps` returns nothing unless every other remedy is silent on
       this plate — so its position states the precedence and costs nothing. Kept OUT of
       `contextualFillSwap` on purpose: half a dozen consumers read that as "this tone was
       rescued", and moving one component has not rescued the tone. */
    const pieceSwaps = ghostedPieceSwaps(p, body, { ringed: !!(logoEl && logoEl.logoAddOutline) })
      .map((sw) => ({ ...sw, tones: (p.logoStructure && p.logoStructure.traceTones) || p.logoTraceTones || null }));
    const fillRemap = [...userSwaps, ...autoRemap, ...pieceSwaps];
    const hasFillRemap = fillRemap.length > 0;
    /* ⚠ THE CACHE KEY MUST SEE THE SCOPE. Two swaps with the same from>to but different scope are
       different pictures; keyed on the pair alone, a piece swap would serve the global swap's
       cached raster. */
    const frKey = hasFillRemap ? fillRemap.map((s) => s.from + '>' + s.to + (s.pieceOnly ? '#piece' : '')).join(',') : '-';
    // Up to TWO added outer rings (Ryan 2026-07-23), inner→outer. Legacy single-object saves are
    // wrapped into a 1-element array so old designs still render.
    const aoRaw = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
    const adds = aoRaw.filter((a) => a && normalizeHex(a.color) && a.thickness > 0).slice(0, OUTLINES_MAX);
    const addKey = adds.length ? adds.map((a) => normalizeHex(a.color) + ':' + a.thickness).join(';') : '-';
    const lg = logoEl.logo;
    /* ⛔ THE KEY HAS TO NAME THE SOURCE ART, NOT JUST THE STYLING (Ryan 2026-08-08: flipping "Let
     * weight show through enclosed areas" before the build left the 3D showing white pockets while
     * the pane thumbnail showed them cut).
     *
     * Every other term here describes how the art is DRESSED — peeled bands, centre colour, added
     * outlines, band recolours, the fill remap. `renderUrl` is derived from the artwork too, and the
     * holes toggle changes exactly that: same styling, different `dataUrl`. The key was therefore
     * identical across the flip, `already in sync` returned false, and the composite built from the
     * PRE-FLIP artwork stayed on the element and kept being baked into the texture.
     *
     * ⚠ FIFTH BUG OF THIS SHAPE (memory: `wd-renderurl-transient-repeat-offender`). The others were
     * all "something restored/replaced the art and nothing rebuilt the composite"; this is the same
     * fault reached from the other side — the guard that decides whether to rebuild could not see
     * the art. Naming the source here closes the class rather than this one route into it.
     *
     * ⚠ A FINGERPRINT, NOT THE URL. These are base64 rasters and can run to megabytes; length plus
     * the tail is enough to tell two keyed variants of the same logo apart, costs no allocation of
     * the whole string, and a false MATCH would need identical length AND identical final 16 chars. */
    const srcKey = lg && typeof lg.dataUrl === 'string' && lg.dataUrl
      ? `${lg.dataUrl.length}:${lg.dataUrl.slice(-16)}`
      : '-';
    const key = `${off.join(',')}|${center || '-'}|${addKey}|${bcKey || '-'}|${frKey}|${srcKey}`;
    // NOTE: this function no longer paints (Ryan 2026-07-24) — it only updates the transient composite
    // and returns. ctx.requestRender() awaits it and does ONE paint for everything, so the logo can't
    // arrive a debounce cycle after the plate and text.
    if (!off.length && !center && !adds.length && !hasBandColors && !hasFillRemap) {
      // Clear EVERY slot — a stale composite left on one is exactly how the bottom logo kept
      // showing a recolour that no longer applied.
      let cleared = false;
      for (const t of twins) {
        if (t.renderUrl || t._rasterKey) { t.renderUrl = null; t.renderAspect = null; t._rasterKey = ''; cleared = true; }
      }
      // ⚠ RECORDED, not merely skipped: "this plate needs no composite" is an answer, and leaving a
      // previous one in the map would hand the card a recolour the design no longer has.
      // ⛔ LIVE PATH ONLY — see the note at the other `logoRasterByInk.set`.
      if (guarded) logoRasterByInk.set(inkContextKey(state), { url: null, aspect: null, art: logoArtKey() });
      return cleared;
    }
    if (twins.every((t) => t._rasterKey === key && t.renderUrl)) return false; // already in sync
    const job = guarded ? ++logoPeelJob : 0;
    let url = lg.dataUrl, aspect = null;
    if (off.length || center || hasBandColors || hasFillRemap) url = await remapLogoDataUrl(url, bands, off, center, hasBandColors ? bandColors : null, hasFillRemap ? fillRemap : null, (p.logoStructure && p.logoStructure.fillHex) || null).catch(() => url);
    for (const a of adds) { const r = await addOutlineDataUrl(url, a.color, a.thickness).catch(() => null); if (r && r.url) { url = r.url; aspect = r.aspect; } if (guarded && job !== logoPeelJob) return false; }
    if (guarded && job !== logoPeelJob) return false; // superseded by a newer sync
    for (const t of twins) { t.renderUrl = url || null; t.renderAspect = aspect; t._rasterKey = key; }
    /* ⛔ THE MAP IS THE LIVE DESIGN'S, SO ONLY THE LIVE PATH MAY WRITE IT. Both the key
       (`inkContextKey(state)`) and the stamp (`logoArtKey()`) are read off `state.designProfile`,
       while the composite just built belongs to `profileArg` — so an explicit-profile caller files
       another design's raster under the buyer's own art. `logoRasterFor`'s `hit.art === logoArtKey()`
       guard looks like it covers that and does not: with a BLANK live design both sides are '-', so
       they match and the cache hands `product-step` a composite from a design the buyer never made.
       A blank live design is exactly the state the showcase parade runs in.
         The composite still reaches its caller the same way it always has — on the element, via
       `t.renderUrl` above — so nothing that uses an explicit profile loses anything. */
    if (guarded) logoRasterByInk.set(inkContextKey(state), { url: url || null, aspect, art: logoArtKey() });
    return true;
  }
  /**
   * @param {number} size
   * @param {number} [retriesLeft]
   * @param {() => void} [beforeApply] run AFTER the build, immediately BEFORE the texture lands —
   *   the seam where a geometry change must happen so the plate never wears the previous face.
   */
  async function runTextureUpdate(size, retriesLeft = 1, beforeApply = null) {
    if (!reveal || !pipelineMod) return;
    /* ⛔ AN EXPLICIT BUILD CANCELS A QUEUED ONE (Ryan 2026-08-05, measured). A weight change logged
       TWO `runTextureUpdate` calls with identical inputs — same weight, same `sizeScale`, same 4096 —
       so the second built a pixel-for-pixel copy of the first and crossfaded it over itself. That is
       the second flash.
         The handler already called `clearTimeout(textureTimer)` to prevent exactly this, and lost a
       race: `preview.requestRender()` schedules its debounce on a LATER TICK, so the guard ran and
       then the thing it was guarding against arrived. Clearing HERE cannot lose that race — whenever
       a build actually starts, any queued one is redundant by definition, no matter who asked first.
       Ordering guards are fragile; this one is positional. */
    clearTimeout(textureTimer);
    const token = ++buildToken;
    try {
      const next = await buildCurrentTexture(pipelineMod, size);
      if (token !== buildToken || !reveal) {
        next.dispose(); // superseded by a newer edit (or torn down) — drop it
        return;
      }
      // The geometry swap, if one was handed to us — see the weight handler.
      if (typeof beforeApply === 'function') { try { beforeApply(); } catch { /* never strand the texture */ } }
      // §6.2 — the dramatic reveal spin fires ONLY on design-initiation (a blank
      // plate gets dressed / a saved design resumes). Layout flips + every other
      // edit are a micro crossfade (no rotation). reveal-3d owns disposing the
      // previous texture in both paths, so we never dispose it here.
      const populatedNow = isPopulated(state);
      const firstDressing = !dressed && populatedNow;
      dressed = populatedNow;
      lastLayoutPreset = state.designProfile.layoutPreset;
      if (firstDressing && reveal.playLayoutReveal) reveal.playLayoutReveal(next);
      else reveal.setTexture(next);
      currentTexture = next;
      void refreshBump(token); // ⅓mm relief for the new design (async, non-blocking)
      // Dye rings A/C to match (ring-B ground is already dyed in the texture) — B6 §4.
      // Gated to dyeable products so a non-dyeable one shows black rings, not the leftover dye.
      if (reveal.setBodyColor) reveal.setBodyColor(plateBodyColor());
    } catch {
      // Transient raster failure. The raster canvas is a shared singleton the
      // showcase preload also uses, so a rebuild right after the parade can lose the
      // race and throw — which used to silently KEEP the last texture, i.e. leave the
      // showcase design + dyed body stuck on a blank Logo-tab plate (Ryan 2026-07-16).
      // Retry once so the real design (usually blank) reliably lands. If nothing newer
      // was queued and the retry also fails, keep the last good texture as before.
      if (retriesLeft > 0 && token === buildToken && reveal) {
        setTimeout(() => { if (token === buildToken) runTextureUpdate(size, retriesLeft - 1, beforeApply); }, 160);
      }
    }
  }

  // --- showcase stage API + the intro parade (Fable §3.1, batch B5) ----------
  // The showcase drives the SAME persistent plate through these three calls, so
  // the user's real (blank) design underneath is never mutated — exit restores it.
  // Rasters are cached at a lighter size than the 4096 editor texture (the parade
  // isn't zoom-inspected) to keep 4 preloaded textures cheap.
  const showcaseTexSize = () => (media(MOBILE_MQ).matches ? 1024 : 2048);
  ctx.stage = {
    prefersReducedMotion,
    ready: () => !!(reveal && reveal.isSupported && pipelineMod),
    // Render a profile → rasterized canvas (cache it; mint a fresh texture per show).
    /* ⛔ RASTERED FOR THE MODEL IT WILL BE DRAWN ON — the same two options `runTextureUpdate` passes
       for the buyer's own design, and for the same reasons (Ryan 2026-08-10).
         `product` picks the ZONE: where each slot lands on this face. Omitted reads as the BUMPER
       (see renderDesignToSVGString's docs), so every parade slide has been laid out on the bumper's
       ring since the feed learned about other families — which put the grip plate's number and logo
       out on ring-B instead of in the lanes between its hand cutouts, and would do the same to a
       dumbbell's cap. It was invisible while every published example was a bumper.
         `bodyColorOverride` forces the ground the ink is balanced against, so a design authored on a
       pink comp bumper is re-peeled against black before it is drawn on a training bumper.
         ⚠ BOTH READ THE LIVE PRODUCT, not the profile's. The parade rasters onto whatever model is
       on the stage, which is exactly why `showcase.js` has to rebuild when that model changes. */
    async buildRaster(profile, weight) {
      const pipeline = pipelineMod || (await warmRevealChunk())[1];
      const sz = showcaseTexSize();
      const dyeable = productSupportsBodyColor(state.ui.product);
      /* ⛔ THE LOGO HAS TO BE RE-COMPOSITED FOR THIS PLATE, NOT JUST DRAWN ON IT (Ryan 2026-08-12:
         Cherry Valley looked right on the comp bumper and showed a black keyline on the BLACK
         training bumper — *"the presentation looks sloppy"*).
           `bodyColorOverride` repaints the ground and reaches the TEXT, because text contrast is a
         render-time view (`resolveStyle` takes `bodyColor`). It cannot reach the LOGO: `drawLogo` is
         never passed a plate colour at all. Every plate-aware decision about a mark — the band peel,
         the band rescue, the contextual fill swap — is baked into a COMPOSITE RASTER by
         `syncLogoRaster`, and the composite lives on `logo.renderUrl`, which is transient and never
         serialized (memory `wd-renderurl-transient-repeat-offender`). A restored showcase envelope
         therefore arrives with the ORIGINAL artwork and no composite, and nothing rebuilt one.
           So the parade drew every slide's mark in its authored colours whatever it was standing on.
         It only ever looked right because each example was authored on the plate it was first shown
         on. Cherry Valley's crest is a #C38F82 field inside a #020202 keyline: on the white comp
         bumper `conflictPeelCount` is 0 and the mark is correct, on black it is 1 and the rescue
         repaints the keyline #FFFFFF — the white/rose mark Ryan expected. That rescue's `allowDark`
         and `avoid` options were added FOR this crest (2026-08-10, see `syncLogoRaster`); the logic
         was already right and simply never ran here.
           `profile.bodyColor` is set rather than passed because that IS `syncLogoRaster`'s contract
         for an explicit profile — *"the caller states the plate ON the profile it hands over"* — the
         same contract `buildJobProfile` keeps. Restored in `finally` so a slide is never left
         claiming a dye it does not have: the parade redraws on whatever model the buyer picks up
         next, and `showcase.js` already rebuilds its rasters when that changes. */
      const storedBody = profile.bodyColor;
      try {
        if (!dyeable) profile.bodyColor = DEFAULT_BODY_COLOR;
        await syncLogoRaster(profile);
        const svg = renderDesignToSVGString(profile, {
          weight: weight ?? profile.weightValue ?? 45,
          size: sz,
          uid: 'wd-showcase',
          chrome: false,
          product: modelFor(state.ui.product),
          bodyColorOverride: dyeable ? undefined : DEFAULT_BODY_COLOR,
        });
        return await pipeline.rasterizeSVG(svg, { size: sz });
      } finally {
        profile.bodyColor = storedBody;
      }
    },
    // Push a cached raster onto the plate. `dramatic` plays the reveal spin (entry);
    // otherwise a crossfade (flips). reveal owns disposing the OUTGOING texture, so
    // we only track the newest for teardown.
    show(raster, { dramatic = false, bodyColor = null } = {}) {
      if (!reveal || !pipelineMod || !raster) return;
      const tex = pipelineMod.createThreeTextureFromRaster(raster);
      if (dramatic && reveal.playLayoutReveal) reveal.playLayoutReveal(tex);
      else reveal.setTexture(tex);
      currentTexture = tex;
      if (reveal.setBumpTexture) reveal.setBumpTexture(null); // parade examples are flat (v1)
      /* Dye rings A/C to the showcase plate's body (dyed-body identities, B6 §4).
         ⛔ ONLY ON A MODEL THAT CAN ACTUALLY WEAR A COLOUR (Ryan 2026-08-10: *"its plate color is
         pink, but these can't be anything but pink — training bumper should revert to black no
         matter what"*). This is the SAME rule `effectivePlateHex` applies to the buyer's own
         design, and the parade was the one path that never asked it. Two ways it went wrong here,
         and the gate closes both: the example's own stored colour (every envelope in a set carries
         the buyer's bumper dye, because `bodyColor` is one field and "never dyed" means INHERIT),
         and the `||` fallback, which reaches for the BUYER's live colour when a slide has none. */
      if (reveal.setBodyColor) {
        reveal.setBodyColor(productSupportsBodyColor(state.ui.product)
          ? (bodyColor || state.designProfile.bodyColor)
          : DEFAULT_BODY_COLOR);
      }
    },
    // Rebuild the user's live design (the blank plate) back onto the stage — this
    // also restores the user's own body color via runTextureUpdate's setBodyColor.
    restore() { runTextureUpdate(fullTexSize()); },
  };
  /* ── THE STAGE-CHROME HANDSHAKE — ONE COPY, TWO PRESENTATIONS ─────────────────────────────────
     Presentation open → clear the top-right corner (the undo/redo/zoom cluster slides out);
     closed → the cluster slides back (Ryan 2026-07-16).

     ⚠ SHARED BY THE PARADE AND BY PRESENTATION MODE (`ui/present.js`), deliberately. Everything
     below — the two-frame arming, the class pair, the band re-frame, and the settle time it answers
     with — is a property of THE STAGE, not of which feature is using it. A second copy would be a
     second set of timings to keep in step with `reveal-3d`, and the project's own rule is that when
     two places need the same answer, one of them will be wrong. `stillActive` is the only thing
     that differs: each caller knows whether IT is still the one presenting.

     ⛔ `extraClass` IS WHY THE PARADE AND A SHARED DESIGN ARE NOT THE SAME CLASS ON DESKTOP. Both
     dress the stage down, but the parade's ONLY way out on desktop is the "Design your own" pill,
     and that pill lives INSIDE the left pane — so hiding the pane on desktop for `.wd-presenting`
     would turn the parade into a screen with no door, which is the one thing this app refuses to
     build. A shared design has its own always-visible way out, so it can afford to clear the pane
     and the step tabs completely, and it says so with a class of its own.
     @param {() => boolean} stillActive
     @param {string} [extraClass] a second root class, added and removed with `wd-presenting`
     @returns {number} ms the STAGE needs before a set-piece should start on it — see the ⭐ below. */
  const stageChromeHandshake = (activeNow, stillActive, extraClass) => {
    /* ⛔ THE CHROME'S EXIT IS ARMED ONE FRAME BEFORE IT IS ASKED FOR (Ryan 2026-08-12: *"they just
       snap disappear"*).
         Both classes below move properties that the entrance animations PIN with `fill-mode: both`,
       so both rules have to cancel those animations — and a property whose before-change value
       comes from an animation being cancelled in the same style change does not start a transition,
       it is just set. Measured with `transitionrun`: the tab strip animated only its transform (its
       opacity was cut in one frame, which is what "disappear" looked like) and the tool cluster
       created no transition at all.
         So `wd-presenting-arm` releases the pins on their own, and the classes that MOVE things
       land two frames later, by which time every property has an ordinary value to leave from.
       Two `requestAnimationFrame`s, not one: the first runs before the paint of the frame it is
       scheduled in, so a single one can still put both changes in one style recalculation.
         ⚠ ENTRY ONLY. On the way out both classes come off together and the entrance animations are
       restored — which REPLAYS them, and that replay is why the return already reads as the chrome
       sliding back in (Ryan: *"pretty smooth"*). Arming the exit would replace a good animation
       with a worse one. */
    if (activeNow) {
      target.classList.add('wd-presenting-arm');
      const armed = () => {
        if (!stillActive()) return; // left again before the frame landed
        preview.el.classList.add('wd-showcase-active');
        /* ⛔ A SECOND CLASS, ON THE ROOT, AND IT IS NOT REDUNDANT (Ryan 2026-08-12 — full
           presentation mode on phones). `wd-showcase-active` sits on the `.ds-canvas` SHELL, which
           is enough for everything inside it (the tool clusters, the product row) and reaches
           nothing outside it — and the step-tab strip is a SIBLING of `.ds-canvas`, not a child
           (see side-panel.js's note), with the slide-up sheet and its hint chevron alongside. So
           the chrome Ryan wants gone during a presentation is precisely the chrome that class
           cannot address.
             Phone-only in CSS, not here: the class is the FACT ("a presentation is running"), and
           which viewport dresses down for it is a presentation decision for the stylesheet. */
        target.classList.add('wd-presenting');
        if (extraClass) target.classList.add(extraClass);
      };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(armed));
      else armed();
    } else {
      preview.el.classList.remove('wd-showcase-active');
      target.classList.remove('wd-presenting', 'wd-presenting-arm');
      if (extraClass) target.classList.remove(extraClass);
    }
    // Re-entering the parade → stop the dropzone flashing; they're browsing
    // examples now, not being cued to upload (Ryan 2026-07-16).
    if (activeNow) logoStep.stopPulse();
    // The presentation header now occupies the top of the stage → re-centre the
    // model in the free region (drops it clear of the header, nets to ~centre when
    // the bottom switcher bar is also up). Reverses on exit (Ryan 2026-07-18).
    //   ⚠ IMMEDIATE, not deferred with the classes above: the plate's slide-and-grow IS the beat
    // the chrome is leaving on, and holding it back two frames would stagger the one motion.
    //   ⚠ Read the flag BEFORE the call — `syncBandShift` is what clears it.
    /* ⛔ AND THE PLATE HAS TO COME BACK TO THE MIDDLE (Ryan 2026-08-16: *"the weight/header needs to
       be centered horizontally within the viewable window. Right now they're right aligned as if the
       left slide out pane was out, when it wasn't"*).
         He is describing two mechanisms that both say "pane". The CHROME rides `--wd-pane-half`, a
       CSS var set on `.wd-stage-region` — a DESCENDANT, which is why an override on the root did
       not win. The PLATE rides a camera lens-shift (`setPaneShift`, 160px for an open pane) that no
       stylesheet can reach at all. A share presentation takes the pane fully off screen, so both go
       to zero, and only one of them was ever a CSS problem.
         ⚠ SHARE ONLY. The parade keeps its pane — its desktop exit lives inside it — so its plate
       must go on clearing it. */
    if (extraClass === 'wd-presenting-share' && reveal && reveal.setPaneShift) {
      const paneCls = preview.el.classList.contains('wd-pane-open')
        ? 'wd-pane-open'
        : (preview.el.classList.contains('wd-pane-min') ? 'wd-pane-min' : null);
      reveal.setPaneShift(activeNow ? 0 : paneShiftFor(paneCls), { animate: true });
    }
    const wasPhonePresentation = phonePresentationInset;
    syncBandShift();
    /* ⭐ AND THE STAGE TELLS THE PARADE HOW LONG IT NEEDS (Ryan 2026-08-12: *"it should enlarge to
       be full width first… then only once it's done sliding down and gotten its smooth width dims
       set, then it can spin"*).
         Entering presentation mode moves the camera's bottom inset from the sheet's full height to
       zero, which is the plate sliding down AND growing — a 700ms eased re-frame. The dramatic spin
       used to start one frame into it, and `reveal-3d` defers every refit while a reveal owns the
       camera, so the growth was swallowed by the spin and applied in ONE step at the end: the
       plate span the wrong size and then snapped to the right one (measured: flat at 322px for the
       whole spin, then +37px in a single sample).
         Returning the number rather than exporting a constant keeps the decision here, where the
       re-frame is actually scheduled — the parade only has to know that the stage is busy. 0 when
       nothing is re-framing (desktop, reduced motion), which is the old behaviour exactly. */
    if (prefersReducedMotion) return 0;
    /* Entry: how long the STAGE needs before a set-piece should start on it. Exit: how long the
       CHROME needs before follow-on work may touch the sheet — `startDesign`'s note has the
       measurement, and it is the sheet's own 300ms because that is what it is waiting for.
       ⚠ `presentingNow()` is already FALSE by the time this runs on the way out (`exit()` clears
       `active` before calling), so the exit case is answered from the flag captured above. */
    if (activeNow) return presentingNow() ? SHOWCASE_RESIZE_MS : 0;
    return wasPhonePresentation ? SHOWCASE_EXIT_MS : 0;
  };

  const showcase = createShowcase(ctx, {
    onActiveChange: (activeNow) => stageChromeHandshake(activeNow, paradeActive),
    // "Design your own" → land on the Logo step (open the panel) and pulse the
    // dropzone so it's obvious where to start (Ryan 2026-07-16).
    /* ⚠ AFTER THE SHEET HAS LANDED, not with it. `goToStep(…, { open: true })` runs the sheet's own
       `easeAround`, whose first act is to pin `transition: none` — mid-rise that cancels the very
       animation this hand-off is supposed to read as. See `afterSheetSettles`; on every viewport
       where nothing is sliding, the fallback runs it immediately enough that nobody can tell. */
    onStartDesign: () => {
      ctx.afterSheetSettles(() => { goToStep(2, { open: true }); logoStep.pulseDropzone(); }, 900);
    },
  });
  // The parade overlay is a full-canvas dialog → stays floating on the stage. The
  // "Show me what's possible" CHIP, though, now lives IN the Logo pane, right above
  // the "Drop your logo to start" dropzone (Ryan 2026-07-17: get it off the bottom
  // band so the product bar owns that space, and put the nudge where the eye
  // already is). showcase.refresh()/dismiss() just toggle the node, so relocating
  // it is safe — it shows on the Add Logo step during the blank intro, and fades
  // the moment a logo lands, same as before.
  preview.el.append(showcase.overlayEl);
  /* PRESENTATION MODE — what a shared `?d=` link opens on (Phase 1 of PRESENTATION-MODE-PLAN.md).
     Same stage and the same chrome handshake as the parade; a different header, and a door that is
     always on screen rather than phone-only. Built unconditionally (it is inert until `enter()`)
     because whether this boot is a shared one is already known by now, but the OVERLAY has to exist
     in the canvas either way for the mount order to stay boring. */
  presentation = createPresentation(ctx, {
    onActiveChange: (activeNow) => stageChromeHandshake(activeNow, presentationActive, 'wd-presenting-share'),
    /* The guidance the arrival toast used to carry, moved to the moment it is actually actionable:
       on arrival they are being SHOWN something, and telling someone they may edit is noise until
       they have asked to. */
    onEdit: () => {
      /* ⛔ THE EXIT FROM THE PRESENTATION IS ALSO THE EXIT FROM THE LOCK, and that is the whole
         reason the desktop lock needs no control of its own. Release it here rather than inside
         `present.js`: the lock is the HOST's (it owns `target` and the page around it), and a view
         that could leave the page locked behind it would be a screen with no door wearing a
         different hat. No-op on a phone and on any boot that never locked. */
      closeViewportLock(target);
      /* ⛔ AND THE PANE IS OPENED HERE, BY THIS CLICK — Ryan's *"the only way to get it to slide out
         is by clicking 'Edit this design'"*, taken literally.
           It used to be open the whole time and merely translated off screen, so removing the class
         revealed it. That is what made the plate roll past its mark and snap back: an open pane
         carries a 160px lens shift the presentation then had to undo. With the pane no longer opened
         at boot, "bring it back" stops being a CSS class coming off and becomes a real open — which
         is also the honest reading of what he asked for. */
      if (!isPhone()) goToStep(state.ui.currentStep || 2, { open: true });
      status.info('This design was shared with you — change anything you like, or request a quote.');
    },
  });
  preview.el.append(presentation.overlayEl);
  // The logo above the window and the rep's contact line below it live OUTSIDE the canvas, on the
  // root, because the canvas clips to its rounded window (see `surroundEls` in ui/present.js).
  target.append(...presentation.surroundEls);
  logoStep.introSlot.append(showcase.chipEl);
  // The logo step fades the intro chip away the moment the design process begins.
  ctx.dismissShowcase = showcase.dismiss;
  // …and tears the parade DOWN (overlay: renderings, arrows, top label) the instant
  // the buyer starts their own logo — dismiss() only fades the pane chip and no-ops
  // while the parade is open, so without this the examples stayed on stage through
  // the upload (Ryan 2026-07-18). exit() self-guards when the parade isn't active.
  ctx.exitShowcase = showcase.exit;
  // Committing a logo (handleFile) RETIRES the chip permanently — once they've chosen
  // to design their own, the "what's possible" invite shouldn't return (Ryan 2026-07-18).
  ctx.retireShowcase = showcase.retire;
  // "Show me what's possible" from the landing gate auto-enters the parade the
  // moment its assets are warm — no polling, no need to wait on initPersistent3D
  // (2026-07-14). Guarded on still being on the (logo-less) intro.
  if (startAction === 'showcase') {
    showcase.whenReady().then(() => { if (!state.ui.hasLogo) showcase.enter(); });
  }

  // Save · Share now live on the CANVAS, top-left, as two square glass buttons that slide a card
  // down — the Gym Layout Designer's cluster, in WD's glass (Ryan 2026-07-25). The pane's footer
  // button is gone: it was frozen at a fixed width and only faded when the pane collapsed, so it
  // never travelled with it. See ui/shell/canvas-actions.js.
  // PHONE: the same actions, in the hamburger instead of on the canvas (Ryan 2026-07-27). Declared
  // once and used twice rather than written out per surface — two lists drift, and the day they do
  // it is the phone's copy that quietly falls behind.
  /* ⚠ A FUNCTION, NOT A CONST ARRAY (Ryan 2026-08-07). A `const SAVE_ITEMS = [...]` evaluates its
     contents once at boot and then reads like a live list forever — which is how "Download all 2 on
     one image" survived a buyer adding a third model. Building it on demand means the only way to
     get a stale count is to cache the RESULT, and `onSaveOpen` deliberately does not. */
  const saveItems = (ctx) => {
  /* Read once: `showcaseItems` is admin-gated and returns [] for everyone else, which is what
     decides whether this card shows one full-width Download row or a two-up. */
  const _showcase = showcaseItems(ctx);
  return [
    /* ⚠ "A clean, chrome-free PNG" until 2026-08-03. Ryan: *"What does that even mean."* — and he
       is right: "chrome" is developer vocabulary for the app's own furniture (tabs, panels, buttons),
       so the line explained the picture in terms of a word only we use. His replacement, verbatim. */
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — DOWNLOAD AND SHOWCASE SHARE A LINE when
       both are present, to buy back height on a card that was running past the fold. Ryan: *"we can
       preserve more space by positioning the 'Download image' button and the 'Show me what's
       possible…' button side-by-side... We can call one just 'Download' and the other 'Show me…'
       with a little sub-text under each and the emoji/icon preserved."*

       ⚠️ THE SHORTER LABELS BELONG TO THE PAIRED FORM ONLY. Half a card's width does not fit
       "Download image", and a lone full-width row reading just "Download" would be vaguer than the
       row it replaced for no gain. So the label follows the layout rather than the layout following
       a renamed label.

       ⚠️ AND THE SHOWCASE ROW MOVES UP to sit beside it. It is admin-only, so for everybody else
       this list is byte-identical to what it has always been: one full-width "Download image". */
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-30) — IN THE PORTAL, PNG AND JPEG SIDE BY SIDE, for
       everyone signed in (his pick: "everyone"), on plain white for the JPEG ("plain white"). Same
       picture, same size. The showcase row then stands on its own line under them, with its full
       label back. A buyer's card is unchanged: one "Download image", the PNG. */
    ...(repMode
      ? [
        /* WD 509 (Ryan 2026-11-04): no sub-text on the portal's download rows - the labels say it. */
        { icon: ICON_DL, label: 'Download PNG', pair: true, onClick: () => downloadDesignImage(ctx) },
        { icon: ICON_DL, label: 'Download JPEG', onClick: () => downloadDesignImage(ctx, { jpeg: true }) },
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 508, Ryan 2026-11-04) - NO "Show me what's possible" ROW ON THE PORTAL.
           Ryan: *"it results in the drop-down menu hanging too low, so it gets cut off on the bottom on desktop
           anyway."* It was the only entry point (`isShowcaseAdmin` is rep-mode + admin), so publishing a showcase
           example from the app is off until it gets a home that fits; `ui/showcase-admin.js` is untouched. */
      ]
      : _showcase.length
      ? [
        { icon: ICON_DL, label: 'Download', sub: 'PNG rendering file', pair: true, onClick: () => downloadDesignImage(ctx) },
        Object.assign({}, _showcase[0], { label: 'Show me…', sub: 'Publish as example' }),
      ]
      : [{ icon: ICON_DL, label: 'Download image', sub: 'PNG rendering file', onClick: () => downloadDesignImage(ctx) }]),
    /* EVERY PICKED PRODUCT ON ONE PNG (Ryan 2026-08-07) — shown ONLY when there are 2+, because with
       one product it IS the row above and two names for one action is worse than one.
       ⚠ BOTH THE ROW'S EXISTENCE AND ITS COUNT ARE RE-READ EVERY TIME THE CARD OPENS, via
       `onSaveOpen` → `setItems('save', saveItems(ctx))`. Picking products is a different TAB, so the
       buyer who most needs this row is guaranteed to have changed the count since the card was last
       built — and before that hook existed, this said "all 2" to someone looking at three. */
    ...(pickedProducts().length > 1
      ? (repMode
        ? [
          { icon: ICON_GRID, label: `All ${pickedProducts().length} · PNG`, pair: true, onClick: () => downloadAllDesignImages(ctx) }, // WD 509: no sub-text
          { icon: ICON_GRID, label: `All ${pickedProducts().length} · JPEG`, onClick: () => downloadAllDesignImages(ctx, { jpeg: true }) },
        ]
        : [{
          icon: ICON_GRID,
          label: `Download all ${pickedProducts().length} on one image`,
          sub: 'Every weight you picked, one PNG',
          onClick: () => downloadAllDesignImages(ctx),
        }])
      : []),
    /* ADMIN-ONLY, and empty for everyone else: publish this design into the parade behind
       "Show me what's possible" (Ryan 2026-07-29 — the examples are data now, not hardcoded).
       It rides in SAVE_ITEMS so the phone's hamburger gets it for free, the same way the PDF
       rows do. See ui/showcase-admin.js. */
  ];
  };
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ A FUNCTION, FOR THE SAME REASON
     `saveItems` IS ONE, and this list needed it more. As a `const` it was evaluated during mount at
     THIS line, and `ctx.currentDesign` is not assigned until ~200 lines further down — so the
     "Copy link to all versions" row's own guard (`ctx.currentDesign?.()?.id`) read `undefined` on
     the only pass that ever ran, and the row was never added to the card at all. Not "stale":
     absent, for everybody, always.

     ⚠️ THE ROW'S COMMENT SAID IT WAS REBUILT ON EVERY CARD OPEN. That is true of the SAVE card, and
     this row lives on the SHARE card, which had an `onShareOpen` hook that only warmed the share
     link. Ryan: *"I'm not seeing 'Copy link to all versions' — where is that button?"* Two green
     suites and a clean deploy said nothing about whether the row existed on screen, which is the
     same shape as the white-page boot crash the row's other comment records. */
  const shareItems = (ctx) => [
    // ── "Share via…" — the OS share sheet (Ryan 2026-07-30) ──────────────────────────────────────
    // "should we make the 'Email me my design' link a 'Share via' button which then allows people to
    // choose email, WhatsApp, text, etc.?" — yes, and it LEADS the card, because on a phone the real
    // channel for "send this to my AD" is very often not email.
    //   FIRST and CONDITIONAL: `canWebShare()` is a feature test, not a mobile test, so this appears on
    // the desktops that can actually do it (Chrome/Edge on Windows, Safari on macOS) and stays away
    // from the ones that can't (Firefox). Copy link is directly beneath it and always present, which
    // is what keeps every viewport whole — and on a desktop it is the better tool regardless.
    ...(canWebShare()
      ? [{ icon: ICON_SHARE_VIA, label: 'Share via…', sub: 'Email, WhatsApp, text, AirDrop', onClick: () => shareViaSystem(ctx) }]
      : []),
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — *"the sub-text under 'Copy link' button
     should read 'Send your design'."* Shorter because the row now does more: with a chain open it
     asks which versions to send before it copies anything, so naming one audience under it would
     be describing the old, narrower button. */
  { icon: ICON_LINK, label: 'Copy link', sub: 'Send your design', onClick: (_e, btn) => copyDesignerLink(ctx, btn) },
    // ⚠ RELABELLED from "Email me my design / A copy in your inbox" (Ryan 2026-07-30). That promised
    // something this row has never done: it is a `mailto:` with an EMPTY recipient, so it opens a blank
    // compose window the buyer has to address themselves — nothing lands in anyone's inbox unless they
    // type it. The old wording is the right label for the Resend-backed version noted at the top of
    // share.js (which, unlike this, would capture an address and be worth real money); it is reserved
    // for that, not spent here.
    { icon: ICON_MAIL, label: 'Email my design', sub: 'Opens your email app', onClick: () => emailDesign(ctx) },
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — SAVE MY CHANGES, for a buyer who opened
     their own design from a link and edited it. `sharedOrigin` is set only for their own submission
     (boot, and `leave()` in ui/present.js), so this row cannot appear on a design of ours or for
     somebody who arrived at the tool cold.
     ⚠️ REBUILT ON EVERY SHARE-CARD OPEN, which is what lets it appear after they leave presentation
     mode — the list is a function for exactly this reason (see the note on `shareItems`). */
  ...(!isRepMode() && ctx.state?.ui?.sharedOrigin
    ? [{
      icon: ICON_SAVE_BACK,
      label: 'Save my changes',
      sub: 'Your rep will see the update',
      onClick: (_e, btn) => reviseMyDesign(ctx, btn),
    }]
    : []),
  ];
  // Rep-only, and empty for everyone else — see ui/pdf-sheets.js. Declared beside the other
  // two lists so the phone menu and the canvas cluster can never disagree about what a rep
  // can do (the reason SAVE_ITEMS/SHARE_ITEMS were hoisted here in the first place).
  const PDF_ITEMS = pdfItems(ctx);
  // My Designs — null off the portal, and the cluster then never builds the button.
  const designsPanel = createDesignsPanel(ctx);

  /* ⭐ `?designId=` — OPEN A SAVED DESIGN FROM A LINK (Ryan 2026-08-30) ─────────────────────────
   * The Deal Dashboard's LINKED FILES card opens a floor plan at
   * `/pages/rep-gym-designer?designId=…`; a linked WEIGHT design had no equivalent URL at all, so
   * its card was a row with nowhere to go. This is that URL.
   *
   * ⚠ REP MODE ONLY, and that is not a permission check — `isRepMode` explicitly is not one. It is
   * that saved designs live behind the portal's own auth and `/wd-design/{id}` answers 401 to
   * anybody else, so honouring this on the public tool would spend a fetch to display an error to
   * a customer who cannot have been given the link in the first place.
   *
   * ⚠ AND `?d=` OUTRANKS IT. A share id is a frozen design plus its order and drives presentation
   * mode; a `designId` is an editable working copy. A URL carrying both is a mistake either way,
   * and opening the editable one over a presentation would be the more surprising resolution.
   */
  try {
    const _q = new URLSearchParams(location.search);
    const _deepId = _q.get('designId');
    if (_deepId && !sharedId && repMode && designsPanel && designsPanel.openById) {
      const _opening = designsPanel.openById(String(_deepId).slice(0, 8));

      /* ⭐ `?autosheet=vendor` — BUILD THE SHEET AND REPORT BACK (Ryan 2026-09-01) ──────────────
       * The Deal Dashboard opens this page in a popup when a rep generates a PO for a deal whose
       * weight design has no Vendor Design Sheet yet. It cannot build one itself: the sheet needs
       * a WebGL render of every picked product, and neither a Worker nor the dashboard has a plate
       * renderer. This page does. So the DD sends the rep here, this runs the ordinary vendor-sheet
       * path unattended, posts the outcome back to the opener, and the popup closes.
       *
       * ⚠ NOT A NEW RENDERING PATH. It calls the same `downloadDesignSheet` the PDFs menu calls,
       * with the same `allProducts`/`admin` flags, so the document a PO carries is byte-for-byte
       * the document a rep gets by hand. A second implementation here is exactly how the two would
       * drift, and the one that drifted would be the one nobody looks at.
       *
       * ⚠ THE THREE PO FIELDS TRAVEL TOGETHER. They are what makes the sheet FINDABLE — the worker
       * writes the R2 key back against this design so the purchase order can attach it. A sheet
       * built without them is a download and nothing more.
       *
       * ⚠ ADMIN-GATED DOWNSTREAM, not here. `/wd-pdf` re-checks `rep.admin` for the vendor variant;
       * this only asks. A non-admin gets a 401 and the opener is told so.
       */
      const _sheet = String(_q.get('autosheet') || '').trim();
      if (_sheet) {
        const _reply = (msg) => {
          try {
            /* Same-origin by construction — the DD and this page are both portal pages, and the
               bundle being cross-origin does not change the DOCUMENT's origin. Naming it rather
               than posting to '*' means a mis-targeted message is dropped instead of delivered. */
            if (window.opener) window.opener.postMessage({ source: 'weight-designer', ...msg }, window.location.origin);
          } catch { /* opener gone, or origin mismatch — nothing to do but stop */ }
        };
        void _opening
          .then(async (opened) => {
            /* ⛔ THE DESIGN HAS TO HAVE ACTUALLY OPENED. A failed open leaves the BLANK starting
               plate on screen, and building from that would register a sheet of nothing against
               the deal and attach it to a purchase order. `openById` answers this now. */
            if (!opened) { _reply({ type: 'wd-sheet:failed', error: 'That design could not be opened.' }); return; }
            const r = await downloadDesignSheet(ctx, {
              customerName: String(_q.get('customer') || '').slice(0, 120),
              /* ⛔ THE SAME DOCUMENT BY EITHER ROUTE (Ryan 2026-09-02: *"whether this particular PDF
                 is rendered at the time of sending the PO or if it's rendered manually in the WD, I
                 want it to be the same in both instances"*). The PDFs menu asks for the contact in
                 its dialog; this path has no dialog, so the Deal Dashboard passes it on the URL. It
                 was missing here, and the sheet a PO carried therefore named nobody while the one a
                 rep built by hand did.
                 ⚠ THE FULL NAME, as typed on the deal (Ryan 2026-09-02: *"or the full name if
                 rendering from the DD"*). It was briefly initialised to "F. Last" on both routes;
                 that is gone from both, together, because a document that abbreviates on one path
                 and not the other is the drift this block exists to prevent. */
              contactName: String(_q.get('contact') || '').slice(0, 120),
              variant: _sheet.slice(0, 24),
              allProducts: true,
              admin: true,
              openResult: false,
              po: {
                quoteRef: String(_q.get('ref') || '').slice(0, 50),
                designId: String(_deepId).slice(0, 8),
                poVendor: String(_q.get('poVendor') || '').slice(0, 100),
                poNumber: String(_q.get('poNumber') || '').slice(0, 40),
              },
            });
            /* `downloadDesignSheet` answers `false` for every failure it has already narrated on
               the status bar. Nothing is left on screen here to read it, so the opener is told. */
            if (!r) { _reply({ type: 'wd-sheet:failed', error: 'The design sheet could not be built.' }); return; }
            _reply({
              type: 'wd-sheet:done',
              recorded: !!(r && r.recorded),
              filename: (r && r.filename) || '',
              url: (r && r.url) || '',
            });
          })
          .catch((err) => _reply({ type: 'wd-sheet:failed', error: (err && err.message) || 'Unknown error' }));
      }
    }
  /* ⭐ `?d=` IN REP MODE — ADOPT THE CUSTOMER'S DESIGN AS `-A` (Ryan 2026-09-01) ────────────────
   * A buyer's submission is an immutable share. A rep opening it from the deal wants to improve it
   * and send both back, which needs the customer's version to exist as a SAVED design so our edits
   * can be versions OF it — and needs the two told apart forever after.
   *
   * ⛔ THE WORKER BUILDS `-A` FROM THE STORED ENVELOPE, not from this canvas, so the "Customer
   * design" label is true by construction rather than by a dirty-check. Nothing here sends the
   * artwork; it sends the share id and lets the worker read its own copy. A client that could
   * supply the artwork could supply artwork the customer never drew, under a label saying they did.
   *
   * ⚠️ AUTOMATIC ONLY FOR THE DEAL OWNER. Ryan: *"if the rep/admin opening it is also the deal
   * owner, it automatically saves it... If the rep/admin is not the deal owner, they can save it
   * themselves if they want."* So a colleague looking at somebody else's deal writes nothing. The
   * endpoint is idempotent on the share either way, so a second opener never mints a rival chain.
   *
   * ⚠️ THE REF COMES FROM THE DEAL DASHBOARD'S OWN LINK. Without it the worker cannot tell whose
   * list the chain belongs in and would file it under whoever happened to click.
   */
  try {
    const _shareId = state.ui.sharedId;
    const _dealRef = String(new URLSearchParams(location.search).get('ref') || '').trim();
    const _rep = getRep() || {};
    const _me = String(_rep.code || '').toUpperCase();
    const _owner = (_dealRef.split('-')[0] || '').toUpperCase();
    /* ⛔ THE REF IS OPTIONAL. Requiring it tied this to one entry point: the Deal Dashboard's link
       carries `&ref=`, and the share link in the customer's own email — the other obvious way a rep
       opens this — does not, and never will. Ryan opened
       `/pages/rep-weight-designer?d=DxETUzgj` and nothing happened, silently.
         With a ref we can tell whether the viewer owns the deal, so the auto-save stays the deal
       owner's (a colleague browsing somebody else's deal still writes nothing). Without one there
       is no deal to be owner OF, and a signed-in rep who deliberately opened a customer's design
       is exactly who should get it saved. Idempotency makes that safe either way. */
    /* CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-10-30) — never for an external user: adopting reads
       the deal and files the chain under the deal's owner, and rfq-worker refuses him the route. */
    const _mayAuto = isExternalPortalUser() ? false : (_dealRef ? (_me && _me === _owner) : !!_me);
    if (_shareId && repMode && _mayAuto && designsPanel) {
      void portalApi('/wd-design-adopt-share', { method: 'POST', body: { shareId: _shareId, ref: _dealRef } })
        .then((r) => {
          if (!r || !r.ok) return;
          /* Tell the panel this IS the open design, so the rep's next save is a VERSION of it
             rather than a second, unrelated design sitting beside it. */
          ctx.adoptSavedDesign?.({ id: r.id, ref: r.ref, name: r.name || '' });
          if (r.adopted) ctx.status?.info?.('Saved the customer’s design as ' + r.ref + '.');
          /* The Worker built this record from the stored envelope and cannot render a plate, so it
             has no thumbnail. This browser can, once the design is actually on the stage — see
             `backfillThumb`, which waits for that and fails silently if it never arrives. */
          if (r.id) void designsPanel.backfillThumb?.(r.id);
        })
        .catch(() => { /* adopting is a convenience — never let it break opening the design */ });
    }
  } catch { /* same */ }

  } catch { /* a malformed query string is not a reason to fail the boot */ }

  const DESIGN_ITEMS = designsPanel
    ? [{
        icon: ICON_DESIGNS_ROW,
        label: 'My designs',
        sub: 'Name, save, open, delete',
        onClick: () => { void openModal({ mount: ctx.el, title: 'My Designs', body: designsPanel.el, actions: [{ id: 'done', label: 'Done', variant: 'ghost' }] }); },
      }]
    : [];
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-30) — the saved-design handshake between the
     designs menu and the Projects push. `/wd-plate-upload` namespaces a plate PNG by `designId`
     and checks ownership against the stored `wd-design:` record, so pushing plates into a
     proposal needs to know whether THIS design has ever been saved — and, if the push has to
     save it, to tell the menu about the new id. Without the second half, every push would mint
     a fresh design and the rep's own Save would then update a different one.
       Two small functions rather than handing `designsPanel` to project-push: a module that
     could reach the panel could also repaint its list, and that is not a seam anyone meant to
     open. Both are no-ops off the portal, where there is no panel at all. */
  ctx.currentDesign = () => (designsPanel && designsPanel.getCurrent ? designsPanel.getCurrent() : null);
  /* Every version of the open design, for the "Copy link to all versions" row. Behind `ctx` for the
     same reason `currentDesign` is: the row that calls it is built before the panel exists. */
  ctx.chainVersions = () => (designsPanel && designsPanel.chainVersions ? designsPanel.chainVersions() : []);
  /* Is a presentation on screen right now? Asked by the designs panel's thumbnail backfill, which
     photographs the reveal and must not try while the reveal is mid-set-piece. */
  ctx.isPresenting = () => presentationActive();

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ SAVING A VERSION IS THE INTENT; THE LINK
     SHOULD NOT HANG OFF A SECOND CLICK. The two email snippets that offer our work ("the version we
     made for them", "their design plus ours") are gated on a chain share link, and that link only
     existed if the rep also opened the Save card and picked "Copy link to all versions". Ryan saved
     a version on RW-1371-A, saw both rows still greyed, and was right to expect otherwise.

     ⚠️ RE-MINTED ON EVERY VERSION SAVE, which is also a fix: the recorded link is a frozen set, so
     one minted before the newest version would page through everything except the newest version.
     Last one wins (the handler says so), and the older share still resolves for an email already
     sent.

     ⛔ BEST EFFORT, ALWAYS. This runs after the save has already succeeded and been reported. A
     share service that is down, or a set too large for one link (413), must cost the automation and
     never the save — the manual row still works and says what to do. */
  ctx.recordChainShare = async (opts) => {
    try {
      /* THE WHOLE CHAIN, ALWAYS. This is the link the email snippets find when nobody has picked
         anything, so it must carry every version. A hand-picked link comes from the share dialog
         and is deliberately never recorded.
         ⛔ AND BOTH LINKS ARE UPDATED IN PLACE where they already exist, which is the whole of
         "the URL they already have keeps up": a version saved today reaches a link sent last week
         rather than stranding it at the two designs it was minted with. */
      const versions = (await ctx.chainVersions?.()) || [];
      /* OUR VERSION ON ITS OWN, first, because it does not need the chain to exist: a rep who saves
         the first version of a design has something to send before there are two rungs to page
         through. */
      await syncSoloShare(ctx, versions, ctx.currentDesign?.()?.id || '', opts);
      if (versions.length < 2) return;
      await syncChainShare(ctx, versions, opts);
    } catch { /* the design is saved either way */ }
  };
  /* `rec = null` means FORGET — the artwork on screen is no longer a saved design (see
     `applyEnvelope`). `setCurrent` treats it as the empty identity and clears the stored ref with
     it, so this is the one call that both halves of "who am I" listen to. The list refresh is
     skipped in that case: nothing about the rep's designs changed, and re-fetching the index to
     redraw the same rows is a round trip for no pixels. */
  ctx.adoptSavedDesign = (rec) => {
    if (!designsPanel) return;
    designsPanel.setCurrent(rec);
    if (rec) void designsPanel.refresh();
  };
  // Rep-only, like the PDFs list, and declared here for the same reason — one list, read by
  // both the canvas cluster and the phone's hamburger.
  const PROJECT_ITEMS = projectItems(ctx);
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31) — the phone's copy of the LIVE staged count.
     `projectItems` now returns TWO rows, and the second reads "Review & send (3)…". That 3 is read
     at build time and the hamburger's rows are built once, so without a rebuild the phone would show
     whatever the tray held when the shell booted — forever. `setFullScreenMenuItems` already
     re-renders the sheet, so re-deriving the list on open is the whole fix (the desktop cluster does
     the same thing through `onProjectsOpen` + `setItems` below). */
  // CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — the phone's entry to the help assistant
  // (the "?" cluster button is CSS-hidden on phones; this row is the launcher).
  const HELP_ITEMS = helpAssist
    ? [{ icon: ICON_HELP, label: 'Help & questions', sub: 'Ask about any feature', onClick: () => helpAssist.open() }]
    : [];
  const rebuildMenuItems = () => setFullScreenMenuItems(
    [...saveItems(ctx), ...shareItems(ctx), ...HELP_ITEMS, ...PDF_ITEMS, ...projectItems(ctx), ...DESIGN_ITEMS],
  );
  if (media(MOBILE_MQ).matches) rebuildMenuItems();
  // Warm the share link the moment the hamburger opens, so "Share via…" can call `navigator.share()`
  // inside the tap's own activation instead of after an await — the one thing iOS Safari refuses.
  // `warmShareLink` is idempotent and keyed on the design's autosave envelope, so repeated menu opens
  // cost one POST per actual design; the row rebuild beside it is a handful of DOM nodes.
  /* ⚠ REGISTERED UNCONDITIONALLY, and the rebuild is no longer gated on PROJECT_ITEMS (2026-08-07).
     Chasing Ryan's stale "Download all 2 on one image" on the desktop card turned up the same bug on
     the PHONE, in a worse form: the rows were rebuilt only when `PROJECT_ITEMS.length` — which is
     REP-ONLY — so for a buyer the hamburger was built once at boot and never again. A count could go
     stale exactly as it did on desktop, and worse, a buyer who picked their SECOND product after
     boot would never see the row appear at all, because it did not exist when the menu was made.
     The callback guards each half itself, so registering it always costs nothing when neither
     applies. */
  setFullScreenMenuOnOpen(() => {
    if (canWebShare()) void warmShareLink(ctx);
    if (media(MOBILE_MQ).matches) rebuildMenuItems();
  });

  const canvasActions = createCanvasActions({
    // ("Download PDF · Comes with your quote" was REMOVED 2026-07-25 at Ryan's request. It was a
    // greyed, inert row advertising a server endpoint that doesn't exist yet, and a dimmed row you
    // can't click reads as something broken rather than something coming. It comes back as a real
    // row when /share/pdf ships — not as a placeholder.)
    saveItems: saveItems(ctx),   // the boot snapshot; `onSaveOpen` re-reads it on every open
    shareItems: shareItems(ctx),   // the boot snapshot; `onShareOpen` re-reads it on every open
    /* Desktop twin of the hamburger's warm-up above — the Share card opening is the head start
       "Share via…" needs to keep its tap synchronous; the warm is skipped where there is no OS
       sheet to feed. The REBUILD is unconditional and is what puts "Copy link to all versions" on
       the card the moment there is a chain to walk: a design adopted or saved after boot did not
       exist when the boot snapshot was taken. */
    onShareOpen: () => {
      try { canvasActions.setItems('share', shareItems(ctx)); } catch { /* never block the card */ }
      if (canWebShare()) void warmShareLink(ctx);
    },
    pdfItems: PDF_ITEMS,
    projectItems: PROJECT_ITEMS,
    /* The staged-plate count on "Review & send (N)…" has to be LIVE — a rep primes, the card is
       shut, and the next time it drops the number must be right (Ryan 2026-07-31). The card's rows
       are built once and their labels are plain strings, so `setItems` rebuilds that ONE card from
       a freshly-derived list every time it opens. Reads `canvasActions` from its own initialiser,
       which is fine: the arrow doesn't run until a card opens, long after the const is bound. */
    /* ⚠ THE SAVE CARD'S ROWS ARE REBUILT EVERY TIME IT OPENS (Ryan 2026-08-07: *"if I go back to the
       Pick Weights tab and select a 3rd model, the button still reads 'Download all 2 on one
       image'"*). `SAVE_ITEMS` is a `const` evaluated ONCE at boot — the comment on the row claiming
       it was rebuilt per open was simply wrong — so its count froze at whatever was picked then.
       Same fix, same hook, same reason as `onProjectsOpen` directly below; the only surprise is that
       the Save card did not already have one. `saveItems(ctx)` is now a FUNCTION so the count can
       never be captured by accident again. */
    onSaveOpen: () => { try { canvasActions.setItems('save', saveItems(ctx)); } catch { /* never block the card */ } },
    onProjectsOpen: PROJECT_ITEMS.length
      ? () => { try { canvasActions.setItems('projects', projectItems(ctx)); } catch { /* never block the card */ } }
      : null,
    /* Rep-only, and it renders INSIDE the Save card above the download rows — one Save button
       holding both halves (Ryan 2026-07-29). The phone gets the same panel through the hamburger
       (DESIGN_ITEMS above) rather than a second implementation, since the canvas cluster is
       display:none there. One consequence worth knowing: the panel element is MOVED into whichever
       surface opens it, so it can only live in one at a time — fine, because only one of the two
       exists per viewport. */
    designsPanel: media(MOBILE_MQ).matches ? null : (designsPanel ? designsPanel.el : null),
  });
  preview.el.appendChild(canvasActions.el);

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — the portal edition's programmatic seam (2026-07-29).
     The PDF BUTTON itself lives on the canvas beside Save and Share (Ryan 2026-07-29), not
     in the Liquid page — a rep acting on a design should reach for the same corner a buyer
     does. This handle is what remains: a way to drive the same actions without a pointer.
       Its consumer is `tools/verify-portal-gate.mjs`, which asserts the whole path end to
     end on the LIVE page, and it is the hook for any portal chrome that later wants a
     shortcut. Deliberately a few small functions rather than `ctx` — a page that could
     reach into ctx would end up reading state, and then this becomes an API surface nobody
     meant to own.
       The event fires because the two halves boot independently: the section's script runs
     long before the bundle finishes mounting. */
  if (isRepMode()) {
    window.wdPortalApi = {
      hasDesign: () => !!ctx.shareEnvelope?.(),
      orgName: () => { try { return String(state.designProfile?.orgName || ''); } catch { return ''; } },
      pdfTypes: () => PDF_TYPES.map((t) => ({ id: t.id, label: t.label })),
      openPdfDialog: (id) => openPdfDialog(ctx, PDF_TYPES.find((t) => t.id === id) || PDF_TYPES[0]),
      // Straight to the bytes, no dialog — what the verifier drives.
      downloadDesignSheet: (opts) => downloadDesignSheet(ctx, opts || {}),
    };
    try { window.dispatchEvent(new CustomEvent('wd:portal-ready')); } catch { /* older browser */ }
  }

  // §6.5 note (Ryan 2026-07-14): the "↺ Front view" pill was removed — the idle sway
  // is now camera-relative (reveal-3d), so the plate always gravitates back to face
  // the camera on its own; a manual reset affordance is redundant. Double-click the
  // plate still snaps home (re-fits zoom) as an unobtrusive extra.

  // §6.4 clean-shot chrome — the canvas UI auto-fades after 2.5s of pointer
  // inactivity over the stage (buyers screenshot their work constantly), and
  // restores on movement. The downloaded PNG already excludes chrome (snapshotPNG).
  let chromeIdleTimer = null;
  const armChromeIdle = () => {
    preview.el.classList.remove('wd-chrome-idle');
    clearTimeout(chromeIdleTimer);
    chromeIdleTimer = setTimeout(() => preview.el.classList.add('wd-chrome-idle'), 2500);
  };
  preview.el.addEventListener('pointermove', armChromeIdle);
  preview.el.addEventListener('pointerenter', armChromeIdle);
  preview.el.addEventListener('pointerdown', armChromeIdle);
  armChromeIdle();

  // --- history debounce ---
  let recordTimer = null;
  function scheduleRecord() {
    clearTimeout(recordTimer);
    recordTimer = setTimeout(() => {
      record(state);
      persist();
      syncHistoryButtons();
    }, HISTORY_DEBOUNCE_MS);
  }
  // Immediate commit for discrete structural edits so each is individually
  // undoable (the debounce is only right for continuous typing / slider) (WD-19).
  function recordNow() {
    clearTimeout(recordTimer);
    record(state);
    persist();
    syncHistoryButtons();
  }

  function refreshControls() {
    designPanel.refresh();
    // The Add Logo tab holds Logo size now (2026-07-25) and, unlike the design panel, does NOT
    // rebuild itself on commit — so it has to be told when the profile moved under it (undo/redo,
    // "↺ Reset all styling", a resume, a layout switch). Every one of those already lands here.
    if (logoStep.refresh) logoStep.refresh();
    if (productStep.refresh) productStep.refresh();
    if (pickWeightsPanel.refresh) pickWeightsPanel.refresh({ animate: true }); // switcher moved the product → animate the open/close
    syncHistoryButtons();
    showcase.refresh(); // keep the intro chip in sync (gone once a logo/design lands)
    // Design (3) + Product (4) are gated behind a logo (2026-07-14 redesign item 7)
    // — makes the upload's mandatory nature obvious rather than letting a buyer
    // wander into an empty Design step.
    stepTabs.setDisabled(3, !state.ui.hasLogo);
    stepTabs.setDisabled(4, !state.ui.hasLogo);
    syncNav(); // unlocking 3/4 must reveal the nav too, not just the tabs
  }

  /**
   * Repaint whichever navigation surface this device has.
   *
   * ONE function rather than two call sites everywhere, because only one of the two exists at a
   * time and every reason to refresh applies to both: the gate opening, a step change, a product
   * switch (which swaps the ladder under the buttons), a denomination change. Splitting them is how
   * one surface ends up refreshed on a path the other is not.
   */
  function syncNav() {
    syncStepNav();
    if (paneNav) paneNav.sync();
  }

  /**
   * GO TO A STOP — the one door both nav surfaces navigate through.
   *
   * A stop is either a tab or one of the buyer's picked products (ui/step-nav.js), and this is the
   * only place that knows a product means `switchProduct` while a tab means `goToStep`. The buttons
   * hand over a destination and stay ignorant of how it is reached.
   *
   * ⚠ THE SWITCH IS NOT AWAITED, and it is `switchProduct` — the one door every path that moves
   * `state.ui.product` goes through, so the down-tab bar, the ←/→ keys and these buttons all get
   * the same ink stamping, contrast remap and hand-off animation. It guards itself against
   * re-entry (a press landing mid-switch is dropped, exactly as the bar's own arrows are), and it
   * repaints this nav from inside `adoptProduct`, so nothing here has to know when it lands.
   */
  function goToStop(stop, { open = true } = {}) {
    if (!stop) return;
    const sameStep = stop.step === state.ui.currentStep;
    if (!sameStep) goToStep(stop.step, { open });
    if (stop.product && stop.product !== state.ui.product) void switchProduct(stop.product);
    /* ⛔ BACK TO THE TOP OF THE PANE ON EVERY MOVE (Ryan 2026-08-06). Only in the same-step case:
       `setBody` already resets the scroll on a step change, and calling it twice would be a second
       place deciding the same thing. Walking the picked set is the case with no `setBody` behind
       it — the tab does not change, but every control in it now describes a different product, and
       leaving the buyer parked 600px down against the previous plate's Layout section is landing
       them somewhere they did not ask to be. */
    if (sameStep) sidePanel.scrollBodyTop();
    syncNav();
  }

  // Enter a step: load its controller into the slide-out, light its tab, and
  // (on desktop) slide the pane open. `open:false` is used only on first paint so
  // mobile doesn't cover the plate on load — a tab tap always opens it.
  function goToStep(n, { open = true } = {}) {
    if (!steps[n]) return;
    /* ⛔ THE IDLE NUDGE DIES ON ANY NAVIGATION, AND THIS IS THE ONLY PLACE THAT DECIDES IT (Ryan
       2026-08-07: *"once the Add Logo tab is left, there should be no more pulsing of any next
       button on any tab… it's a one time deal that pulses to encourage people to leave the tab, that's
       it."*). It lived in `syncNav` first and compared the current step against the armed one — but
       `syncNav` can run before `state.ui.currentStep` moves, so the comparison bailed, nothing
       cleared the class, and because the forward button is ONE persistent node repainted per step,
       the glow followed the buyer onto Pick Weights and Request Quote.
         Here there is nothing to compare: reaching `goToStep` at all IS the outcome the hint exists
       to cause. Before the early returns below on purpose — a navigation the app declines to make is
       still a buyer who has stopped needing the hint. */
    if (paneNav) paneNav.endForwardHint();
    endStepNavHint();   // the phone's half of the same rule — see `armStepNavHint`
    /* ⛔ ANY NAVIGATION DROPS A LIVE PANE TAKEOVER, and that is the exit guarantee the takeover
       leans on (see `openPaneTakeover`). Teardown only — the fill below IS the restore, so doing it
       here rather than in each caller means every door out of a takeover is the same door. */
    dropPaneTakeover();
    // Bug 9 (Ryan 2026-07-18): the moment a logo lands, Customize Design (3) + Request
    // Quote (4) unlock. Clicking either while the upload build-flow is still live acts
    // like the "Design My Weights" button — commit whatever's typed and build, landing
    // on that tab when it's done. Without this, the tab would open an un-built design.
    if ((n === 3 || n === 4) && logoStep.inBuildFlow && logoStep.inBuildFlow()) {
      logoStep.commitWhoWithDest(n);
      return;
    }
    /* Arriving at the design step: if a pick made since the last visit sits BEHIND where the buyer
       is standing, move to it — see `applyPendingPickJump`. Here rather than in the pane fill so the
       product state is already the new one when the panel is built, and after the build-flow
       interception above, which re-enters this function and would otherwise consume the picks on a
       pass that never reaches the step. */
    if (n === DESIGN_STEP) applyPendingPickJump();
    // Which WAY the step moved, for the header title's swipe (Ryan 2026-07-28: "each time the 'next'
    // button is clicked, have the center text swipe left and fade while the new header text fades in
    // and slides in from the right… do the opposite when we click the PREV button").
    //   Taken from the step NUMBERS rather than from which button was pressed, so a tab tap — the
    // one-tap jump from Request Quote back to Add Logo, which is the tabs' main remaining job —
    // swipes the right way too, and so nothing has to thread a direction through goToStep's callers.
    // Phone only: the shell offers the animation, the host decides when it applies (desktop's title
    // cross-fades with its own vertical rail copy, which this would fight).
    const dir = isPhone() ? Math.sign(n - (state.ui.currentStep || n)) : 0;
    // Leaving the step leaves the Weight tab's close-up behind with it.
    if (n !== state.ui.currentStep) endFocusZoom();
    state.ui.currentStep = n;
    /* ── THE QUOTE STEP TAKES THE WHOLE FRAME (Ryan 2026-08-05) ────────────────────────────
     * *"when they go to this step, on desktop, the right-side pane can slide out to cover the
     * entire window — giving the person plenty of room to read and absorb all the questions in
     * the form, and more easily navigate through the questions and answers."*
     *
     * Driven off the STEP rather than toggled by a control, so it cannot be left on: every path
     * out of the quote step — a tab, Back, Next, a resumed session landing elsewhere — runs
     * through here and un-widens the pane on the way. That is also the exit guarantee. The step
     * tabs live in `.wd-frame` ABOVE `preview.el`, which is what the pane is absolutely
     * positioned inside, so they are never covered however wide it gets.
     *
     * Desktop only: a phone already opens the pane as a full-screen sheet, and `isDesktopPane`
     * is the same 481px line the plate-shift uses, so the two can never disagree about what
     * "desktop" means. */
    const wide = n === QUOTE_STEP && isDesktopPane();
    /* ── AND THE PHONE'S VERSION OF THE SAME DECISION (Ryan 2026-08-08) ─────────────────────────
     * *"should we just make it slide all the way to the very top like it does when you click View
     * all layouts? Then on the header we can have the back/customize pill on the left, and on the
     * right the hamburger."*
     *
     * The same rule as `wide` one line up, on the surface that has no width to give: the quote step
     * is the one step that earns the whole frame, because it is a FORM rather than a control panel —
     * and with the taller inputs he asked for in the same message, a 58% sheet would scroll on
     * nearly every question.
     *
     * 🔑 IT IS A CLASS, NOT A NEW SURFACE, and that is what makes it small. The sheet already slides
     * at the right cadence, already carries the grab line, already closes on a tap or a swipe of its
     * header (`dragToggle`), and its header already puts the PREVIOUS step's pill on the left — which
     * on step 4 reads "Customize Design" and is exactly the back button he described. Re-parenting
     * the step into a new overlay would have rebuilt all of that to arrive at the same place.
     *
     * ⚠ THE HAMBURGER IS NOT MINE TO PLACE. It is body-appended at a near-maximum z-index (see
     * `wd-fullscreen-chrome-tokens-need-root`), so it already sits above this sheet at the top right
     * — which is the layout he asked for, provided nothing here adds `wd-overlay-open`, the class the
     * View-all sheet uses to stand it DOWN. Deliberately not added.
     *
     * ⛔ THE EXIT. Going full-screen covers the step tabs, which were the guaranteed way out — the
     * redundancy `setWide`'s own note warns about spending. What replaces them must therefore be
     * unconditional, and it is: the prev pill is present for every step after the first, the header
     * tap-and-swipe close is always live, and the hamburger is untouched. Three doors, none of them
     * depending on the form's state. */
    /* ⚠ AND LEAVING IT IS A TRANSITION, NOT A CLASS REMOVAL (Ryan 2026-08-08: *"when you're on the
     * full screen Finalize tab then you click the customize/back button, it needs to smoothly slide
     * back down to the CD tab's height, not snap down."*)
     *
     * 🔑 EXACTLY ONE THING IS ALLOWED TO MOVE, AND IT IS THE CAP. The sheet's used height is
     * `min(content, max-height)`; the body's reserve keeps the content taller than the cap at every
     * instant, so `max-height` is the binding term all the way up and all the way down and the whole
     * motion is one CSS transition on one property. Everything else the state changed — the canvas's
     * unclipping and stacking, the reserve itself, the model's band ceiling — is HELD across the exit
     * by `is-quote-leaving` and dropped once the sheet has landed. Re-clip the canvas in frame one and
     * a sheet still taller than it is guillotined at its rim on the way down; release the reserve and
     * the sheet falls to Customize Design's natural height instantly and eases only what is left,
     * which is a jump with a slide attached — the report above, in its original form.
     *
     * ⛔ AND `easeAround` HAS TO STAND DOWN FOR THIS ONE SWAP, which is what `{ ease: false }` says.
     * It pins an inline height across a content change, and an inline height is a CEILING as well as
     * a floor — pinned at 844 while the cap falls to 489, the cap wins in frame one and the ease
     * animates nothing. See its note in side-panel.js. */
    const phoneFull = n === QUOTE_STEP && isPhone();
    const wasFull = sidePanel.el.classList.contains('is-quote-full');
    /** True on 3→4 and on 4→3: the swap where the sheet's CAP, not its content, owns the height. */
    const quoteHeightMove = phoneFull !== wasFull;
    clearTimeout(quoteFullExitTimer);
    /* ── AND IT IS NOT COLLAPSIBLE (Ryan 2026-08-09) ──────────────────────────────────────────────
     * *"the finalize menu on mobile should never be allowed to be fully down where you can only see
     * the header at the bottom of the page… any time you go to the finalize menu, it should default
     * to fully expanding the menu up… then we can also do away with the functionality of sliding
     * down the finalize menu with your finger."*
     *
     * 🔑 IT DELETES A STATE RATHER THAN ADDING A RULE, which is why it is worth doing beyond the ask.
     * "Collapsed on step 4" was a real state with nothing in it: the plate behind the sheet is
     * finished with by the time a buyer is filling in a form, and every edge case in this feature has
     * been some version of "what if the sheet is down here". It also makes the model's framing
     * DECIDABLE — see `sheetCover`'s ceiling. If the only way out is back to Customize Design, then
     * CD's band is the band the model should be sitting in, and that is knowable on the way in
     * instead of guessed at.
     *
     * ⛔ TWO DOORS REMAIN AND BOTH ARE UNCONDITIONAL — the prev pill (present for every step after
     * the first) and the body-appended hamburger. Memory
     * `never-enter-a-screen-whose-exit-is-conditional`: removing the gesture is only safe because
     * neither of those depends on the form's state, and the tabs it also covers were never the
     * guarantee. `setDragLocked` takes the grab line with it, in CSS — see its note. */
    if (isPhone()) sidePanel.setDragLocked(phoneFull);
    /* ⛔ AND THE CAROUSEL IS STOOD DOWN FOR THE WHOLE MOVE (Ryan 2026-08-09: *"going from fully
     * expanded finalize menu to half expanded CD menu jumpy/laggy… a partial snap down followed by a
     * jittery contraction down from there"*).
     *
     * 🔑 THAT IS THE SIGNATURE OF A BLOCKED MAIN THREAD, NOT OF A CSS PROBLEM. `max-height` is a
     * layout property, so its transition is ticked by the main thread — block that thread and the
     * sheet freezes where it is, then jumps to wherever the clock says it should be when the thread
     * comes back. A long block gives the snap; the drain that follows gives the jitter. Both halves
     * of his description, from one cause.
     *   The block is `steps[3].refresh()`, which rebuilds the design panel: sixteen carousel slides
     * all intersect at once and hand the IntersectionObserver sixteen synchronous
     * `renderDesignToSVGString` builds, *"tens of milliseconds"* each by the carousel's own measured
     * note. That has ALWAYS landed in the frames after a step swap — it is only visible now because
     * every other tab caps at the same height, so their swaps animate ~0px and a stalled animation of
     * nothing looks like nothing. This one moves ~384px.
     *
     * ⚠ HELD PAST THE FILL, NOT RELEASED BY IT. `setThumbsBusy(false)` drains one tile per frame,
     * which is the right shape when nothing is moving and exactly the wrong one here: sixteen frames
     * at tens of milliseconds each IS the jitter, merely spread out. So the release is timed to the
     * settle instead — the same `QUOTE_FULL_EXIT_MS` the rest of the takeover's hold-and-drop rides,
     * because it is the same question ("has the sheet landed yet"), asked once.
     *   Same collision, same remedy, third surface: `switchProduct`'s hand-off (2026-08-02) and the
     * design panel's own sub-tab swap (2026-08-08) both claim these frames for the same reason. */
    /** Does this direction put content in the pane while the sheet is still MOVING? Only the rise
     *  does, now that the fall waits the fall out (`settling` below) — and three separate decisions
     *  turn on it, so it is one named fact rather than three re-derivations of `quoteHeightMove`:
     *  whether the thumbnail drain needs a timed release, whether `easeAround` may pin a height
     *  across the content swap, and (by its absence) whether `fillPane` may release the drain. */
    const fillsDuringMove = quoteHeightMove && phoneFull;
    if (quoteHeightMove) {
      designPanel.setThumbsBusy?.(true);
      clearTimeout(quoteThumbsTimer);
      if (fillsDuringMove) {
        quoteThumbsTimer = setTimeout(() => designPanel.setThumbsBusy?.(false), QUOTE_FULL_EXIT_MS);
      }
    }
    if (phoneFull) {
      sidePanel.el.classList.remove('is-quote-leaving');
      sidePanel.el.classList.add('is-quote-full');
    } else if (wasFull || sidePanel.el.classList.contains('is-quote-leaving')) {
      /* ⚠ THE SECOND HALF OF THAT TEST RE-ARMS A FALL THAT WAS INTERRUPTED. `quoteFullExitTimer` is
         cleared at the top of every `goToStep`, so tapping another tab while the sheet was still
         coming down used to strand `is-quote-leaving` on the panel for the rest of the session —
         holding the body's 100dvh reserve, the canvas unclipped and the band ceiling, none of which
         is visible until something else goes wrong because of it. `wasFull` alone cannot catch that
         case: `is-quote-full` was already removed by the pass that started the fall. */
      sidePanel.el.classList.remove('is-quote-full');
      sidePanel.el.classList.add('is-quote-leaving');
      quoteFullExitTimer = setTimeout(
        () => sidePanel.el.classList.remove('is-quote-leaving'),
        QUOTE_FULL_EXIT_MS,
      );
    }
    /* ⛔ HOLD THE LAYOUT THUMBNAILS ACROSS A PANE-WIDTH CHANGE (Ryan 2026-08-07: *"going back from
     * Request Quote to Customize Design — it's like the SVGs try to load too quickly, and it causes
     * lag and visual issues"*) ───────────────────────────────────────────────────────────────────
     *
     * Two expensive things were landing in the same frames. `setWide(false)` starts a 300ms WIDTH
     * transition on the pane, and the `setBody` + `refresh` right below it rebuild the design panel
     * — which rebuilds ~16 carousel slides and hands their SVGs to the IntersectionObserver, whose
     * first observation is delivered whether or not anything moved. So sixteen `renderDesignToSVGString`
     * builds start draining into the opening frames of an animation of the very box they sit in,
     * and each one re-measures against a width that is still moving.
     *
     * `setThumbsBusy` already exists for exactly this collision — `switchProduct` claims the frames
     * before its hand-off for the identical reason (2026-08-02). It was simply never wired to a STEP
     * change, because until the quote step went full-width no step change animated the pane's width.
     *
     * ⚠ GATED ON THE WIDTH ACTUALLY CHANGING. Entering Customize Design from Add Logo rebuilds the
     * same sixteen slides and is fine, because nothing is animating underneath them — holding there
     * would only make the carousel arrive late for no reason. `wide !== wasWide` is the honest test:
     * it is true on 3→4 and 4→3 and false everywhere else.
     *   Released on a timer rather than `transitionend`: that event never fires for a transition
     * that gets interrupted (a second tab click mid-slide) and a permanently-held drain is a
     * carousel of blank cards forever. The release drains ONE PER FRAME on its own — see
     * design-panel's `setThumbsBusy`. */
    const wasWide = sidePanel.el.classList.contains('is-wide');
    const resizing = wide !== wasWide;
    /* ── AND THE PHONE'S HEIGHT MOVE EARNS THE SAME TREATMENT (Ryan 2026-08-09) ────────────────────
     * *"if when you click the back button, the contents on the finalize menu fade away and then it
     * smoothly slides down and the contents of the CD menu don't fade/slide/animate in until the
     * slide-up menu is fully in position, then that in and of itself could reduce a lot of the
     * loading burden."*
     *
     * 🔑 THE CHOREOGRAPHY BELOW ALREADY DOES EXACTLY THAT, AND THE PHONE NEVER GOT IT — because it
     * is keyed to `resizing`, a WIDTH change, and `wide` is `isDesktopPane()`-only. So on the phone
     * `resizing` is false, `fillPane()` runs SYNCHRONOUSLY, and the entire design-panel rebuild lands
     * in frame one of a 550ms `max-height` transition. Measured by `tools/probe-quote-camera.mjs`:
     * a 371ms main-thread block DURING the fall and 3.2s of stalls across the whole move.
     *
     * ⛔ AND THAT BLOCK IS ALSO THE "MODEL SHRANK" REPORT, which is why nothing here re-frames the
     * camera. *"The 3D model… is pushed up towards the top of the display very small… it needs to
     * resize as the menu slides down so it can go back to its regular size."* The same probe wraps
     * `setSafeArea` and samples `fitRadius / camDist` — the plate's true apparent size — every 16ms:
     * `setSafeArea` is not called ONCE on the way up, is handed the identical `36 / 460` at every
     * tick on the way down, and the apparent size reads 1.000 for the entire cycle. The plate is
     * never resized. A WebGL canvas whose thread is blocked for 371ms is a FROZEN one, and a frozen
     * frame that catches up in one step is what a resize looks like. One cause, both reports.
     *
     * ⚠ THE FALL ONLY, and that is not timidity. The rise fills in frame one and Ryan calls it
     * *"seemingly unaffected, no problem"* — because step 4 is a FORM, and building it costs nothing
     * next to the design panel's sixteen `renderDesignToSVGString` carousel slides. Deferring it
     * would mean a sheet rising for 550ms with the step you just left still in it, which is a
     * regression paid for nothing. Same reasoning, opposite answer, because the payloads differ. */
    const leavingFull = wasFull && !phoneFull;
    /** Does the pane travel EMPTY and get filled on arrival? Width on desktop, height on the phone. */
    const settling = resizing || leavingFull;

    /** Put the step's content in the pane. Deferred behind a width change — see below. */
    const fillPane = () => {
      /* ⛔ ANYTHING QUEUED FOR THE FAR SIDE OF THE SLIDE RUNS HERE, AND FIRST (Ryan 2026-08-07:
         *"the pane goes to slide shut, it gets 80% there, then it freezes and stalls b/c something
         must be loading simultaneously to slow it down, then it finishes closing. Sloppy UX"*).
           `width` is a MAIN-THREAD transition. The View-all dialog got away with committing a
         layout two frames after asking to close, because ITS close is `transform` + `opacity` and
         lives on the compositor — once handed over, main-thread work cannot touch it. Nothing about
         that reasoning survives the move to the pane, and the 80% stall is precisely a 300ms width
         animation meeting a `structuralCommit` (panel rebuild + 3D texture) at frame two.
           FIRST, before `setBody`, so the commit has already moved the design by the time the step's
         controls are attached and refreshed — one rebuild, not a rebuild plus a correction. */
      if (paneRestoreWork) {
        const work = paneRestoreWork;
        paneRestoreWork = null;
        try { work(); } catch (_) { /* a failed commit must never leave the pane empty */ }
      }
      /* ⚠ `easeAround` STANDS DOWN ONLY WHILE THE CAP IS STILL MOVING — the rise. Its note: an inline
         height is a CEILING as well as a floor, so pinning 844 while the cap falls to 462 lets the cap
         win in frame one and the ease animates nothing. On the way DOWN this line now runs after the
         sheet has landed, where nothing is competing for the height and the ease is simply right: it
         is what carries the last few px between the cap and Customize Design's natural height,
         instead of stepping them. Was `!quoteHeightMove`, which stood it down in both directions. */
      sidePanel.setBody(steps[n].el, { ease: !fillsDuringMove });
      if (steps[n].refresh) steps[n].refresh();
      /* ⛔ THE FOOTER SWAPS HERE TOO, NOT AT THE TOP OF `goToStep` (Ryan 2026-08-07: *"the footer
         buttons seem to jump around a bit going from Request Quote to CD and vice versa"*). It is a
         SIBLING of the body, so the settle-then-fill above never covered it: its buttons changed in
         the first frame of the slide and then rode the whole width animation. That is a real jump
         and not a small one — the forward cell swaps a two-line "Next: <product>" for a one-line
         Submit, so the ROW's height changes mid-travel, and `is-solo-*` can toggle underneath it at
         the same time.
           Moved in here, the whole footer changes in the one frame the content does, behind the same
         fade. Everything else `syncNav` serves (the gate opening, a product hop) still calls it
         directly and is unaffected. */
      syncNav();
      /* The wide BODY layout — padding + reading column — lands WITH the content, never with the
         width. Held apart from `is-wide` (which drives the width and must flip in frame one)
         because applying them together is what made the outgoing content jump: the instant the pane
         began growing, the old step's cards were re-flowed into a 760px centred column they were
         about to leave. Toggled here, it is simply always true of whatever is on screen. */
      sidePanel.el.classList.toggle('is-wide-body', wide);
      /* A takeover's chrome leaves WITH the content, for exactly the reason `is-wide-body` arrives
         with it: dropping the layout in frame one would re-flow the cards into a 320px column they
         are in the middle of leaving. The header action is desktop-only furniture — on a phone the
         slot holds the step nav, hung there once at boot and never ours to clear. */
      sidePanel.el.classList.remove('is-takeover-body');
      if (!isPhone()) sidePanel.setHeaderAction(null);
      sidePanel.releaseBodyWidth();
      sidePanel.el.classList.remove('is-restepping');
      /* ⛔ NO ENTRANCE OF ITS OWN. `setBody` above already runs `playBodyEnter()` — the shell's own
         "new content just landed" motion, shared with every ordinary tab change. This used to add a
         SECOND keyframe on the same element on top of it: one movement too many, and a second
         transform on the body, which makes `position: fixed` descendants relative to it rather than
         to the viewport (a trap the colour picker already has history with). One vocabulary for
         arriving, owned by the component that owns the body. */
      /* ⚠ EXCEPT ON THE WAY UP TO THE FULL-SCREEN QUOTE STEP, which is the one direction that still
         fills in frame one — see the note beside `quoteThumbsTimer`. Releasing there would start
         sixteen tens-of-ms builds one per frame into an animation that has another ~500ms to run.
         The way DOWN reaches this line only after the sheet has landed, so there is nothing left to
         protect and the ordinary one-per-frame drain is exactly right. */
      if (!fillsDuringMove) designPanel.setThumbsBusy?.(false);   // drains one per frame on its own
      /* THE STAGE COMES BACK LAST, and only if a takeover parked it. Resuming at the top of the
         collapse — which is where the picker used to do it — puts a full-rate WebGL loop alongside
         the same main-thread width animation everything else here is being kept away from, for the
         sake of an idle sway nobody is looking at behind a pane. Owned here rather than by the
         caller because a takeover can end without the caller hearing about it (a step tab), and a
         stage left suspended is a dead 3D view for the rest of the session. */
      if (paneTakeoverSuspended3D) { paneTakeoverSuspended3D = false; ctx.suspend3D?.(false); }
    };

    /* ⛔ PIN THE BODY'S WIDTH BEFORE THE PANE MOVES (Ryan 2026-08-07: *"as all the buttons/words/
       toggles/headers are fading away, the slide-out pane is also growing, and while it's growing
       and things are fading, they're also repositioning... that's not necessary"*).
         The body is full-width, so it tracked the pane all the way out and re-laid-out its whole
       contents on the journey — at 0 opacity, which is work nobody could even see. Frozen at its
       starting width it simply fades where it stands. See `freezeBodyWidth`; released in `fillPane`.
         MEASURED FIRST, before `setWide` — a line later and this pins the destination. */
    // Width only. The phone's move changes the sheet's HEIGHT, and its body is already the pane's
    // full width at both ends of it, so there is nothing to pin.
    if (resizing) sidePanel.freezeBodyWidth();
    sidePanel.setWide(wide);
    preview.el.classList.toggle('wd-pane-wide', wide);
    // The TITLE moves with the tab, not with the content. It names where you are going, and a header
    // still reading "Request Quote" over a pane that has started travelling would be the one part of
    // this that lies about what is happening.
    sidePanel.setTitle(STEP_LABELS[n - 1], { dir });

    clearTimeout(paneStepCommitTimer);
    if (!settling) fillPane();
    else {
      /* ── LET THE PANE ARRIVE BEFORE ANYTHING IS PUT IN IT (Ryan 2026-08-07) ────────────────────
       * *"I need the slide-out pane to slide back in, settle on its position correct for the
       * Customize Design tab, THEN have the contents of that tab populate."*
       *
       * The quote step is the only one that changes the pane's WIDTH, and everything used to land in
       * the same frame the 300ms width transition began: the outgoing quote form — laid out for a
       * 760px reading column — got squeezed to 320px in real time, while the incoming design panel
       * rebuilt ~16 carousel SVGs and measured each one against a width that was still moving.
       * Neither was wrong on its own; running them inside the animation of the box they both live in
       * is what made it read as lag.
       *
       * So the pane travels EMPTY. The body fades out fast (`is-restepping`, 0.14s — gone in the
       * first fifth of the slide, so nothing is ever seen mid-squeeze), the width animates against
       * nothing, and the content lands once it has arrived. Applied to BOTH directions rather than
       * just the way back: they are the same transition and a rule that only holds one way round is
       * a rule someone has to remember.
       *
       * ⚠ THE THUMBNAIL HOLD IS STILL HERE and is no longer belt-and-braces in the way it looks. The
       * rebuild now happens after the slide, so the collision it was added for cannot occur — but
       * `refresh` is called from inside `fillPane`, and releasing the hold in the same breath is what
       * keeps the carousel's own one-per-frame drain from starting mid-swap.
       *
       * ⚠ CANCELLED ON RE-ENTRY. A second tab click inside the 300ms must not have a stale timer
       * fill the pane with the step you have already left — `paneStepCommitTimer` is cleared above,
       * and `fillPane` closes over THIS call's `n`. */
      designPanel.setThumbsBusy?.(true);
      sidePanel.el.classList.add('is-restepping');
      /* ⚠ TWO DISTANCES, ONE QUESTION. Both branches ask "has the pane arrived yet"; they differ only
         in what is moving. The desktop's is a 300ms width, the phone's is the sheet's `max-height`
         fall — and that one is already timed by `QUOTE_FULL_EXIT_MS`, the same constant the reserve,
         the unclipping and the band ceiling are all released on. Reusing it rather than adding a
         fourth number keeps "the sheet has landed" a single fact.
         ⚠ IT MUST NOT BE SHORTER THAN THE CSS FALL (0.55s). 620ms is 70ms of slack, and the slack is
         now real: the rebuild that used to overrun the transition no longer runs inside it. */
      paneStepCommitTimer = setTimeout(fillPane, leavingFull ? QUOTE_FULL_EXIT_MS : PANE_WIDTH_MS);
    }

    stepTabs.setActive(n);   // the tab strip lives OUTSIDE the pane and lights immediately
    /* ⚠ THE QUOTE STEP OVERRIDES THE CALLER (Ryan 2026-08-09: *"any time you go to the finalize
       menu, it should default to fully expanding the menu up"*). Every other step honours `open`,
       which is how a resumed session and the guided stops arrive without throwing the sheet up in
       your face. This one cannot: `setDragLocked` above has just taken away the gesture that would
       raise it, so a collapsed arrival here would be a 48px bar with no way to open it. The override
       and the lock are one decision and have to stay together. */
    if (open || (n === QUOTE_STEP && isPhone())) sidePanel.open();
    persist();
  }

  /** `.wd-design-panel.is-handoff`'s fade-out (weight-designer.css). `switchProduct` waits it out
   *  before starting the exit spin, so the pane is empty BEFORE the model moves. Keep in step. */
  const PANEL_HANDOFF_FADE_MS = 160;
  const reducedMotion = () => media('(prefers-reduced-motion: reduce)').matches;

  /** `.ds-side-panel`'s own `width` transition (shell.css). `goToStep` waits it out before filling
   *  the pane — see its note. Must stay in step with that CSS value. */
  const PANE_WIDTH_MS = 300;
  let paneStepCommitTimer = 0;

  /* ══ THE PANE, LENT OUT — a full-width takeover inside the step you are already on ═════════════
   * Ryan 2026-08-07, on "View all N layouts": *"should the left side pane slide out fully like it
   * does when you go to the Request Quote tab, and then all the existing stuff on it gets dropped
   * for the layout cards instead… the 'x' button… results in the cards being removed and it sliding
   * back to its smaller size and the old buttons/toggles/cards re-rendering back on it again."*
   *
   * So this is not a new mechanism: it is `goToStep`'s width choreography with a different payload,
   * and it is written as thinly as it is on purpose. Everything that made the quote step's slide
   * behave — the body frozen at its starting width so it fades where it stands, the 300ms travelled
   * EMPTY, the thumbnail drain held across it — is the same code, because it is the same movement.
   * A mirror-image "restore" would be a second copy of that reasoning free to drift; instead
   * `closePaneTakeover` simply re-enters the step, which already knows how to arrive.
   *
   * The takeover deliberately does NOT wear `is-wide-body`. That class carries the quote form's
   * 760px reading column, which is right for a column of questions and wrong for a grid whose whole
   * value is fitting six cards across — see `is-takeover-body` in shell.css, which also documents
   * why the takeover keeps the pane's ordinary 12px gutter where the quote step re-insets to 24.
   */
  let paneTakeover = null;
  /** Did a takeover park the 3D stage? Un-parked in `fillPane` — see the note there for why the
   *  host owns this rather than the surface that asked for it. */
  let paneTakeoverSuspended3D = false;
  /** Work queued for the FAR SIDE of the pane's width animation (`fillPane` runs it). */
  let paneRestoreWork = null;

  /**
   * Is there a desktop-style pane here at all? Both halves matter. `isDesktopPane()` is the same
   * 481px line the full-width quote step uses, so a takeover can never be offered where the pane
   * cannot widen — and `!isPhone()` covers what sits between them: a coarse-pointer 600px tablet,
   * where the pane still widens but the header's action slot is already spoken for by the step nav
   * (`setHeaderAction(stepNav)`, hung there once at boot), and clearing it would be permanent.
   */
  const canTakeOverPane = () => isDesktopPane() && !isPhone();

  /**
   * End a takeover WITHOUT restoring anything — for a caller that is about to fill the pane itself.
   * Fires the owner's teardown so it can stop rendering into a body it no longer holds; the visible
   * chrome leaves later, with the content, in `fillPane`.
   */
  function dropPaneTakeover() {
    if (!paneTakeover) return;
    const { onClose } = paneTakeover;
    paneTakeover = null;
    try { onClose?.(); } catch (_) { /* a broken teardown must never strand the pane wide */ }
  }

  /**
   * @param {object} o
   * @param {string} o.title          - the pane header, swapped with the tab (never with the content).
   * @param {HTMLElement} o.body      - what replaces the step's controls.
   * @param {HTMLElement} [o.headerAction] - the exit, dropped into the header's action slot.
   * @param {()=>void} [o.onClose]    - teardown, fired however the takeover ends.
   * @param {()=>void} [o.onShown]    - the body has landed (post-slide) — focus goes here.
   * @returns {boolean} whether the pane was actually taken over.
   */
  function openPaneTakeover({ title = '', body, headerAction = null, onClose, onShown } = {}) {
    if (!canTakeOverPane() || !body) return false;
    dropPaneTakeover();                  // a second open REPLACES the first; takeovers never stack
    paneTakeover = { onClose };
    designPanel.setThumbsBusy?.(true);   // nothing renders a thumbnail inside a moving box
    // The pane covers the stage completely at full width, so a full-rate render loop behind it is
    // pure cost — and worse, it is main-thread cost competing with a main-thread width animation.
    ctx.suspend3D?.(true);
    paneTakeoverSuspended3D = true;
    sidePanel.freezeBodyWidth();         // ⚠ MEASURED FIRST, before the width class — see `setWide`
    sidePanel.setWide(true);
    preview.el.classList.toggle('wd-pane-wide', true);
    sidePanel.setTitle(title);
    sidePanel.el.classList.add('is-restepping');
    clearTimeout(paneStepCommitTimer);
    const mine = paneTakeover;
    paneStepCommitTimer = setTimeout(() => {
      // Dropped mid-slide (a tab click inside the 300ms) — that navigation owns the pane now, and
      // its own fill is already scheduled. `goToStep` clears this timer too; this is the belt.
      if (paneTakeover !== mine) return;
      sidePanel.setBody(body);
      sidePanel.setHeaderAction(headerAction);
      sidePanel.el.classList.add('is-takeover-body');
      sidePanel.el.classList.remove('is-wide-body');
      sidePanel.releaseBodyWidth();
      sidePanel.el.classList.remove('is-restepping');
      designPanel.setThumbsBusy?.(false);  // drains one per frame on its own
      try { onShown?.(); } catch (_) { /* focus is a nicety, never a blocker */ }
    }, PANE_WIDTH_MS);
    sidePanel.open();
    return true;
  }

  /**
   * Give the pane back to the step it was borrowed from — which is just re-entering that step.
   *
   * @param {()=>void} [work] something to run once the pane has FINISHED retracting (the layout
   *   commit behind a picked card). Queued rather than run here because the retraction is a `width`
   *   transition and therefore main-thread: see `fillPane`, which is where it lands.
   */
  function closePaneTakeover(work) {
    if (!paneTakeover) return;
    dropPaneTakeover();
    if (typeof work === 'function') paneRestoreWork = work;
    goToStep(state.ui.currentStep);
  }

  function exitDemo() {
    if (!state.ui.isDemo) return;
    // Real upload lands in the logo element (setLogo) + palette extraction sets
    // colors; keep the rest of the seeded layout as a starting point.
    state.ui.isDemo = false;
    designPanel.refresh();
  }

  // Wash the LIGHT studio backdrop toward the design's SECONDARY color, as a pale
  // tint (Ryan 2026-07-13 — light backdrop so dark weights pop; keep the secondary
  // tint, just light). Called only when colors are actually chosen (a swatch pick
  // or logo palette extraction) — so the stage stays pale-neutral on load and
  // transitions (CSS 0.7s) once the buyer picks colors.
  const STAGE_BASE = [229, 232, 237]; // #e5e8ed — matches --dt-color-stage-bg
  function updateStageTint() {
    const color = washedStageTint(state.designProfile && state.designProfile.palette && state.designProfile.palette.secondary);
    preview.el.querySelectorAll('.wd-reveal-3d, .wd-canvas-stage').forEach((n) => {
      n.style.backgroundColor = color;
    });
    // …and PUBLISH it, so chrome that has to read as continuous with the stage can track it
    // (Ryan 2026-07-28: the phone's folder tabs were "a slightly different color to the background").
    //   The tint is not a constant: it starts at `--dt-color-stage-bg` and is washed toward the
    // design's secondary here, so anything hard-coded to the base hex is correct only on a design
    // with no secondary and drifts on every other one. A custom property on the root is the one way
    // a stylesheet can follow a colour JavaScript decides — set it beside the elements it is being
    // applied to, so the two cannot fall out of step.
    if (target && target.style) target.style.setProperty('--wd-stage-tint', color);
  }
  function washedStageTint(hex) {
    const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(String(hex || '').trim());
    if (!m) return `rgb(${STAGE_BASE.join(', ')})`;
    const rgb = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
    const lum = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114; // desaturate toward gray
    const desat = 0.5; // wash the color halfway to its own gray
    const mix = 0.16; // then blend just ~⅙ of that into the PALE base → a subtle light tint (Ryan 2026-07-13)
    const out = rgb.map((c, i) => {
      const washed = c * (1 - desat) + lum * desat;
      return Math.round(STAGE_BASE[i] * (1 - mix) + washed * mix);
    });
    return `rgb(${out[0]}, ${out[1]}, ${out[2]})`;
  }

  function doUndo() {
    if (undoState(state)) afterHistoryNav('Undid last change.');
  }
  function doRedo() {
    if (redoState(state)) afterHistoryNav('Redid change.');
  }
  function afterHistoryNav(msg) {
    // ctx.requestRender(), NOT preview.requestRender() (Ryan 2026-07-24 bug): a history nav REPLACES
    // the profile wholesale, and `preview.requestRender()` only repaints the flat SVG fallback. The
    // logo's composited raster (`logo.renderUrl` — the flatten / band peel / added ring) is a TRANSIENT
    // that serializeLogo deliberately drops, so every snapshot restore lands with it null and the plate
    // falls back to the ORIGINAL art. Only ctx.requestRender() re-runs syncLogoRaster() to rebuild it
    // (and rebuilds the 3D texture, which this also used to skip).
    ctx.requestRender();
    refreshControls();
    /* ⭐ AND THE PICKED SET COMES BACK WITH IT (2026-08-09). `applySnapshot` now restores
       `selectedProducts`, but restoring STATE is only half of it: without these two the products
       would exist in memory while the switcher still showed the old set and the Pick Weights pills
       still read un-picked — an undo that appears to have done nothing. */
    syncPicksToStage();
    pickWeightsPanel.refresh({ animate: false });
    persist();
    status.success(msg);
    status.announce(`${msg} Preview updated.`);
  }

  function syncHistoryButtons() {
    undoBtn.disabled = !canUndo(state);
    redoBtn.disabled = !canRedo(state);
  }

  // Autosave WRITE (Fable §3.2) — persists the design so a returning visitor can
  // resume. LKG guard: NEVER overwrite a good save with an empty/blank plate, so a
  // stray blank load can't wipe yesterday's design.
  function persist() {
    if (!isPopulated(state)) return;
    try {
      localStorage.setItem(AUTOSAVE_KEY, toAutosave(state));
    } catch {
      /* private mode / storage full — nothing we can do; the design stays in memory */
    }
  }

  // 5. chrome sync + keyboard + lifecycle ------------------------------------
  // Global undo/redo shortcuts (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z or Ctrl+Y).
  target.addEventListener('keydown', (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    // Let editable fields keep their native undo/redo — don't hijack Ctrl+Z while
    // the buyer is typing in a text/number input (WD-24).
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      doUndo();
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      doRedo();
    }
  });

  /* ── ← / → SCROLL THE PICKED SET (Ryan 2026-08-02) ───────────────────────────────────────────
   * *"if you have more than 1 model loaded (bottom bar appears when this is the case), then the
   * keys will scroll through models for you."* Same wrap-around the bar's own ‹ › run, because it
   * IS them — `stepBy` calls the arrows' `step`.
   *
   * ⚠ ON `document`, NOT ON `target` LIKE UNDO/REDO ABOVE. A listener on the mount only fires once
   * focus is INSIDE it, and on a freshly-loaded page focus is on `<body>` — the keys would do
   * nothing until you happened to click the app first, which reads as broken rather than as
   * unfocused. The `activeElement` test below buys back the containment that costs.
   *
   * THE GUARDS, and why each one is load-bearing — this app has thirteen other arrow-key consumers:
   *
   *   1. `defaultPrevented` — every in-app consumer (the steppers, the step tabs, the tool cluster,
   *      the layout carousel, the Advanced position ring, the showcase parade) calls
   *      `preventDefault()` when it eats an arrow, and every one is bound to an element INSIDE the
   *      mount, so by the time the event bubbles to `document` the flag is already set. This is the
   *      guard that maintains itself: a consumer added tomorrow excludes itself for free.
   *   2. Native text/value controls — an `<input>`/`<select>`/`<textarea>` does NOT preventDefault
   *      an arrow; the browser moves a caret or a slider. Invisible to guard 1, so it is stated.
   *   3. An open dialog — one selector retires the whole list (resume gate, quote + saved modals,
   *      the View-all layout grid, the help assistant, the parade, the colour picker). Switching
   *      the product behind a modal is never what an arrow means.
   *   4. `canStep()` — the literal ask: only while the bar is up.
   *   5. Modifiers — `Alt+←` is browser Back and must stay browser Back.
   *   6. `e.repeat` — one press, one product. `switchProduct` is re-entrancy-guarded, so a held key
   *      cannot stack builds, but it WOULD march through the set at rebuild speed while each hop
   *      runs a dissolve. A browse action should not run away from the hand on it.
   *
   * ⚠ AND IT POKES `armChromeIdle`. The clean-shot fade (§6.4) is wired to pointer events ONLY, so
   * after 2.5s idle a keyboard-driven switch would change the model with the bar invisible — a new
   * product and nothing on screen saying which. This is the one line that stops this feature
   * shipping its own bug.
   */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.defaultPrevented || e.repeat) return;
    if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
    if (!productDownTab.canStep()) return;
    const ae = document.activeElement;
    // Outside the designer entirely (the Help & Guide below it on the storefront page) → not ours.
    if (ae && ae !== document.body && !target.contains(ae)) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (target.querySelector('[role="dialog"]:not([hidden])')) return;
    /* Reason 3 again, for a surface that is deliberately NOT a dialog. A pane takeover covers the
       stage and the down-tab bar it would be stepping, and the layout grid it is showing is scoped
       to the live product — so an arrow here would swap the product out from under a list that
       cannot know it happened. Same rule as the modal, different shape. */
    if (paneTakeover) return;
    e.preventDefault();
    armChromeIdle();
    productDownTab.stepBy(e.key === 'ArrowRight' ? 1 : -1);
  });

  // Periodic + unload autosave (crossover §5). The periodic tick SKIPS while a logo
  // upload choreography is mid-flight (state.ui.choreographing) so it can't persist a
  // transient/incomplete design over the last finished one (Ryan 2026-07-18, bugs
  // 5/6). The explicit saves — finishBeat and beforeunload below — call persist()
  // directly and are NOT gated, so the finished design is always captured.
  //   `previewing` joins it for the same reason (Ryan 2026-07-28 audit). The colour picker's live
  // preview is a REAL mutation of the design — that is how the 3D can show it — so a timer landing
  // mid-drag would file a colour nobody confirmed as the buyer's saved design.
  const interval = setInterval(() => {
    if (!state.ui.choreographing && !state.ui.previewing) persist();
  }, AUTOSAVE_INTERVAL_MS);
  // ⚠ THE UNLOAD SAVE IS NOT SKIPPED, IT IS CLEANED UP FIRST. Skipping it would lose real work for
  // anyone who closes the tab with a picker open; saving as-is would keep a provisional colour
  // forever, and on the resume it would be indistinguishable from a choice. Reverting first gives
  // the one honest answer: the design as they last committed it.
  window.addEventListener('beforeunload', () => { ctx.endLivePreview?.(); persist(); });
  window.addEventListener(
    'pagehide',
    () => {
      clearInterval(interval);
      clearTimeout(textureTimer);
      buildToken++; // invalidate any in-flight texture build
      if (reveal) reveal.dispose();
      if (currentTexture) currentTexture.dispose();
      reveal = null;
      currentTexture = null;
    },
    { once: true },
  );

  // Responsive class hook (CSS does the reflow; JS just reflects the state).
  const mq = media(MOBILE_MQ);
  const applyMobile = () => target.classList.toggle('is-mobile', mq.matches);
  applyMobile();
  if (mq.addEventListener) mq.addEventListener('change', applyMobile);

  // ── TYPING MODE: the sheet gets OUT OF THE WAY (Ryan 2026-07-28) ──────────────────────────────
  // "the keyboard + the height of the slide-up menu means the weight preview is no longer visible,
  // so you can't see the weight update in real time as you type… I want the entire menu to collapse
  // so that it only includes the header, the field you're typing into, the other counterpart text
  // field if there is one, and the keyboard."
  //
  // This REPLACES §7.1's `is-snap-full`, which did the exact opposite: it raised the sheet to its
  // 92% snap on focus, on the reasoning that the keyboard must not bury the FIELD. True as far as it
  // went — but the keyboard takes the bottom half and a 92% sheet takes what is left, so between
  // them the plate had nowhere to be. Shrinking the sheet to just the field being typed in serves
  // both: the field clears the keyboard because there is almost nothing else in the sheet, and the
  // plate keeps the band above it.
  //   The sheet is content-sized, so hiding the rest of the pane IS the collapse — no snap height to
  // choose, and the 3D re-frames itself through the same ResizeObserver that handles every other
  // height change. Which rows survive is declared in the markup (`.wd-keep-typing`), so the two
  // screens that own text fields say for themselves what a field's counterpart is.
  //
  // ⚠ WHICH ROWS SURVIVE IS DECIDED BY THE MARKUP, NOT BY A SELECTOR THAT GUESSES THE SHAPE OF IT
  // (Ryan 2026-07-28: "on the Add Logo tab, clicking into the text fields does nothing"). The first
  // version hid `.wd-step > *:not(.wd-reveal-identity)` — which reads as "keep the identity block"
  // and is not what it does: that block is nested inside `.wd-reveal`, so the rule hid the wrapper
  // holding the very field being focused. The field vanished mid-tap, focus went with it, the
  // keyboard closed and focusout took the class straight back off. Nothing happened, visibly.
  //
  // So the keeper declares ITSELF (`.wd-keep-typing`) and JS marks the chain of ancestors between it
  // and the pane (`.wd-typing-path`). CSS then hides, at every level of that chain, the children that
  // are on neither list — which needs to know nothing about how deep a field sits or what wraps it.
  // Siblings that are ALSO `.wd-keep-typing` survive, and that is what "the other counterpart text
  // field" means without naming either one.
  //   A field with no `.wd-keep-typing` ancestor does not enter typing mode AT ALL. The font search
  // is the case that matters: collapsing the pane around it would hide the list it filters. Opting in
  // per field is what keeps a screen this component has never seen from being broken by default.
  /* ⚠ `wd-typing-path` AND ITS TWO HELPERS WERE DELETED 2026-08-09 with the collapse they served.
     `.wd-keep-typing` SURVIVES and is still the opt-in: a field with no such ancestor does not
     enter typing mode at all, which is what keeps the font search out of it — scrolling that box
     to the top would carry the list it filters off the screen. Opting in per field is still what
     stops a screen this component has never seen from being broken by default. */
  /**
   * The field typing mode is FOR, right now, or null.
   *
   * ⚠ ASKED OF THE DOCUMENT, NEVER CAPTURED. The keyboard watcher below outlives any one focus
   * event — a keyboard can close and reopen against a field that never blurred — so a `t` closed
   * over at focus time would go stale the moment the buyer moved to the counterpart field, and the
   * scroll would re-aim at the box they just left.
   */
  function typingField() {
    const a = document.activeElement;
    if (!a || (a.tagName !== 'INPUT' && a.tagName !== 'TEXTAREA')) return null;
    if (!sidePanel.el.contains(a) || !a.closest('.wd-keep-typing')) return null;
    return a;
  }

  /** Live keyboard subscription — held only while a `.wd-keep-typing` field has focus. */
  let kbWatch = null;
  function stopKeyboardWatch() {
    if (!kbWatch) return;
    try { kbWatch.stop(); } catch { /* already gone */ }
    kbWatch = null;
  }
  function startKeyboardWatch() {
    if (kbWatch) return;
    kbWatch = watchKeyboardOpen(({ open }) => {
      if (open) {
        // The keyboard has ACTUALLY arrived. Re-aim: the 400ms fallback may have already scrolled
        // against a scrollport that was not its final size, and this is the same scroll to the same
        // target, so a redundant run costs nothing.
        if (typingField()) scrollTypingFieldToTop();
        return;
      }
      // ⛔ THE HALF `focusout` CANNOT SEE. Back-to-dismiss leaves the field focused, so this is the
      // only signal that the room is no longer needed. `leaveTypingMode` is the identical body the
      // blur path runs — one restore, two triggers, so they can never drift apart.
      leaveTypingMode();
      if (!typingField()) stopKeyboardWatch();   // blurred as well → nothing left to watch for
    });
  }

  /* ── THE PANE HOLDS STILL AND THE FIELD COMES TO THE TOP (Ryan 2026-08-09) ────────────────────
   * *"Instead of everything going away, just ensure the total height of the slide-up menu doesn't
   * grow any taller or shorter, and then just have the field you're typing into position itself
   * (smoothly via sliding if possible) at the very top of the viewable slide-up pane above the
   * keyboard."* … *"just wanting a cleaner UX."*
   *
   * 🔑 EVERY MECHANISM THIS REPLACES EXISTED TO MANAGE A MOVEMENT THE FEATURE ITSELF CAUSED.
   * Hiding rows shrank a content-sized sheet, so the collapse needed `easeContentChange` to stop it
   * snapping, `whenKeyboardSettles` to stop the 3D swelling into the gap and dropping back, and a
   * marked ancestor chain to decide what to hide. Take away the height change and all three stop
   * having a job: nothing to ease, nothing to cancel against the keyboard, nothing to mark.
   *
   * ⚠ HOISTED OUT OF THE `focusin` LISTENER 2026-08-10. It used to close over the focus event's
   * target, which made it callable exactly once per focus — and the keyboard watcher needs to run
   * the same scroll on an event that is not a focus at all (the keyboard finally arriving). It now
   * reads the live field, which is also the honest thing: what to scroll to is a question about the
   * present, not about the event that started this.
   * ⚠ `block: 'start'` IS THE ASK, LITERALLY — the top of the scrollport, not `nearest`, which
   * would leave a field that is already visible exactly where it is. */
  function scrollTypingFieldToTop(attempt = 0) {
    sidePanel.el.classList.add('is-typing');   // smooth scrolling only — it changes NO geometry
    // One frame for the class to flush, then scroll. Nothing resizes in between any more, so this
    // is belt-and-braces rather than load-bearing — kept because `scroll-behavior` arrives with it.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const t = typingField();
      if (!t) return;   // focus moved on while we waited
      {
        /* ⚠ SCROLL THE FIELD'S BLOCK, NOT THE BARE INPUT (Ryan 2026-08-09: *"still scrolling too
           high… top of field is touching top of slide-up pane"*).
             Putting the INPUT's top on the scrollport's top edge is doing exactly what was asked and
           still looks wrong, because an input is not the whole control: its label sits ABOVE it, so
           landing the input at the top scrolls the label out of the pane entirely. What you get is a
           box hard against the rim with no idea what it is for.
             `.wd-keep-typing` is already the element that means "this field and what belongs to it"
           — it is the opt-in marker every participating field carries — so scrolling THAT to the top
           puts the label at the top and the input just below it, with `scroll-padding-top`'s 12px of
           air above. That is "towards the top of the viewable pane" rather than jammed into it, and
           it needs no tuned offset: the gap is whatever the label actually is. */
        /* ⛔ AND IT SCROLLS THE PANE EXPLICITLY, NOT VIA `scrollIntoView` (Ryan 2026-08-09: *"the
           header on the slide-up pane is actually being removed/hidden"*).
             Nothing hides it. `scrollIntoView` walks EVERY scrollable ancestor — the pane, then the
           document — so the browser satisfied the request partly by scrolling the page, which carried
           the whole sheet up and took its header off the top of the screen with it. It looks like the
           header was removed; it was moved.
             Scrolling the one box by hand cannot do that: `scrollTo` on the pane touches the pane and
           nothing above it, so the sheet, its header and the plate behind it all stay exactly where
           they are. The target is the same one `block: 'start'` would have computed — the keeper's
           offset within the scroller, less the 12px `scroll-padding-top` gap — so the landing
           position is unchanged, only the collateral movement is gone.
           ⚠ The 12px is stated here AND as `scroll-padding-top` in the CSS: that property still earns
           its place because the browser's own caret-visibility scroll reads it, and this line does
           not govern that pass. */
        const target = t.closest('.wd-keep-typing') || t;
        const sc = target.closest('.wd-dsection-scroll, .ds-side-panel-body');
        if (!sc) return;
        const top = sc.scrollTop + (target.getBoundingClientRect().top - sc.getBoundingClientRect().top) - 12;
        sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        /* ⛔ AND THEN CHECK THAT IT ACTUALLY LANDED (Ryan 2026-08-10) ────────────────────────────
         * *"first time you click into either one of those fields to type something AFTER UPLOADING
         * A LOGO, they stay hidden behind the keyboard."*
         *
         * 🔑 THAT IS THE ONE MOMENT THE PANE IS STILL GROWING. Uploading un-hides
         * `.wd-reveal-identity` — the two fields themselves — and brings the borrowed size and nudge
         * blocks in with it, over a 0.28s `wtfRise`. A scroll computed against a scrollport that is
         * still gaining content lands at an offset that is correct for one frame and wrong by the
         * next, and nothing afterwards re-runs it: every LATER tap takes the `is-typing` fast path,
         * which is why the same field works fine a moment later and the bug reads as "only the first
         * time".
         *   The keyboard subscription below re-aims when the keyboard genuinely arrives, and that
         * fixes the slow-IME half of this. This covers the other half, which no keyboard event can
         * see: the room under the field changed after we scrolled. Measure where the field actually
         * ended up, and if it is not near the top, do it once more.
         *
         * ⚠ ONE RETRY, MEASURED — not a poll and not a timer chain. `attempt` makes the second pass
         * incapable of scheduling a third, so this can never turn into a scroll that fights a buyer
         * who has started dragging the pane themselves. */
        if (attempt) return;
        setTimeout(() => {
          const live = typingField();
          if (!live || !live.isConnected) return;
          const block = live.closest('.wd-keep-typing') || live;
          const drift = block.getBoundingClientRect().top - sc.getBoundingClientRect().top;
          // 12px is where it was aimed; 24px of slack absorbs a smooth scroll still easing in.
          if (Math.abs(drift - 12) > 24) scrollTypingFieldToTop(1);
        }, 380);
      }
    }));
  }

  sidePanel.el.addEventListener('focusin', (e) => {
    if (!mq.matches) return;
    const t = e.target;
    if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA')) return;
    if (!t.closest('.wd-keep-typing')) return;
    const toTop = scrollTypingFieldToTop;
    /* ── AND THE KEYBOARD ITSELF IS NOW THE TRIGGER, NOT A GUESS AT IT (Ryan 2026-08-10) ─────────
     * Two reports, one cause: this pair of handlers has never actually known where the keyboard is.
     *
     *   *"when you first start your project… should you click onto any text field, it doesn't move
     *   up when the keyboard does, so it stays hidden behind the keyboard… but if I've already been
     *   working on a project then I click on the Add Logo tab to go there and adjust the text, then
     *   it behaves properly."*
     *   *"if I click back on my phone to close the keyboard, the keyboard closes but the window /
     *   slide-up menu / text fields don't go back to their proper positions… things only return to
     *   normal when I click outside of the text field. But the trigger should actually be when the
     *   keyboard retracts/appears."*
     *
     * 🔑 THE SECOND ONE NAMES THE FAULT IN BOTH. **Android's Back gesture dismisses the keyboard
     * without blurring the field**, so `focusout` — the only thing that has ever undone typing mode
     * — never fires, and the pane keeps its 60vh of trailing room with the keyboard gone. And the
     * entry side is the same omission from the other end: `whenKeyboardSettles` waits a flat 400ms
     * and then scrolls whether or not anything has come up. A session's FIRST focus is exactly when
     * that is least true — the IME process is cold and the keyboard animates up well past 400ms —
     * which is the shape of "the first time doesn't work, later on the same tab does". (The later
     * taps also take the `is-typing` fast path below, so they never wait on the timer at all.)
     *
     * So: subscribe to the real keyboard for as long as a field is focused. OPEN re-runs the scroll
     * with the keyboard actually up; CLOSE runs the same restore `focusout` runs. `watchKeyboardOpen`
     * changes no viewport — see its note, and see `observeKeyboard` beside it for the one that does
     * and why that one is only safe under a full-screen panel.
     *
     * ⚠ THE 400ms TIMER STAYS, and deleting it would be the classic version of this bug. Neither
     * sensor exists on a plain-HTTP LAN dev server, so silence is not the same as "no keyboard" —
     * the timer is what keeps the behaviour identical where nothing can answer. When a sensor DOES
     * answer, the timer fires first and the keyboard event re-runs the same scroll to the same
     * target, which is idempotent. */
    startKeyboardWatch();
    // Already typing and moving to the next field: the keyboard is up and the room is open, so go
    // straight there rather than waiting on a settle that has already happened.
    if (sidePanel.el.classList.contains('is-typing')) { toTop(); return; }
    // ⚠ WAIT FOR THE KEYBOARD. Collapsing on focus is what made the plate move: the sheet shrinks
    // while the canvas is still full height, so the band nearly DOUBLES and the model swells — and
    // then the keyboard lands, the canvas shrinks, and it drops back. Both end states are within
    // ~10px of each other; the excursion between them was the whole of what Ryan kept seeing.
    //   Applying the collapse in the SAME frame the viewport shrinks means the two changes cancel
    //   as they land, and the model is fitted once, to a band that barely moved.
    //
    // ⚠ AND IT GOES THROUGH `easeContentChange`, which is the reason that function exists — it was
    // written for this and never wired up (Ryan 2026-07-28: the sheet "snaps up"; the 3D "flashes").
    // Both are the same omission. The pane is content-sized, so a raw class flip changes its height
    // between one frame and the next: nothing to transition, and the ResizeObserver that drives the
    // camera gets handed a height the sheet was never really at. easeAround pins the height BEFORE
    // the mutation and drives it explicitly, so the sheet eases and the camera tracks it frame by
    // frame instead of being refitted to a transient and back.
    whenKeyboardSettles(toTop);
  });
  /**
   * Give the trailing room back and ease the pane to where it will sit without it.
   *
   * ⚠ TWO TRIGGERS, ONE BODY (2026-08-10). A blur runs this, and so does the keyboard retracting —
   * which is NOT the same event on Android, where Back dismisses the keyboard and leaves the field
   * focused. Extracted from the `focusout` handler the day the second trigger arrived, because the
   * alternative was a second copy of the ease-then-drop dance that would eventually disagree with
   * this one about the order of its two steps. Idempotent: with `is-typing` already off it finds
   * nothing to give back and returns.
   */
  function leaveTypingMode() {
    if (!sidePanel.el.classList.contains('is-typing')) return;
    /* ⚠ THE WAIT IS STILL LOAD-BEARING, AND NOT FOR THE OLD REASON (Ryan 2026-08-09: *"when you
         click off the keyboard, this should result in less jumping up/down"*). Dropping the trailing
         room removes 80vh of scrollable space, so the scroller's position is clamped back into range
         the instant the class goes — do that while the keyboard is still on screen and the content
         lurches under it, which is exactly the jump he is describing, arriving from the other side.
         Held until the keyboard has gone and the scrollport is its full height again, the clamp is
         either a no-op or a short slide the smooth scrolling carries.
         No `easeContentChange` any more: the pane's height does not change at either end of this. */
      /* ── EASE BACK, DO NOT CLAMP (Ryan 2026-08-09: *"on release, it should smoothly slide back
         down again, not snap"*) ────────────────────────────────────────────────────────────────
         ⛔ A CLAMP IS NOT A SCROLL, so `scroll-behavior: smooth` cannot soften it. Dropping the class
         removes 60vh of trailing room in one frame; if the scroller is parked inside that room the
         browser simply CLIPS `scrollTop` back into the new range, instantly, with no scroll event and
         nothing for the CSS to animate. That is the snap, and it is invisible to every smooth-scroll
         setting there is.
           So the scroll is done FIRST, while the room still exists — an ordinary smooth scroll to
         exactly where the clamp would have put it — and the padding is dropped once it has arrived.
         The position never changes at the moment the class does, so there is nothing left to clip.
         ⚠ THE TARGET IS COMPUTED FROM THE PADDING ITSELF rather than assumed to be 0. A pane that was
         already scrollable without the extra room has a real resting maximum partway down, and
         sending it to the top would scroll past content the buyer was looking at. */
    whenKeyboardSettles(() => {
      const sc = sidePanel.el.querySelector('.wd-dsection-scroll')
        || sidePanel.el.querySelector('.ds-side-panel-body');
      const pad = sc ? parseFloat(getComputedStyle(sc).paddingBottom) || 0 : 0;
      const maxAfter = sc ? Math.max(0, sc.scrollHeight - pad - sc.clientHeight) : 0;
      if (!sc || sc.scrollTop <= maxAfter + 1) {
        sidePanel.el.classList.remove('is-typing');   // nothing to give back
        return;
      }
      sc.scrollTo({ top: maxAfter, behavior: 'smooth' });
      // Timer, not `scrollend`: that event is still not everywhere, and a class left on forever
      // would hold 60vh of dead scroll range. 420ms comfortably covers a smooth scroll of this size.
      setTimeout(() => sidePanel.el.classList.remove('is-typing'), 420);
    });
  }

  sidePanel.el.addEventListener('focusout', () => {
    // Deferred: moving between the two fields fires focusout before the next focusin, and toggling
    // the whole pane back and forth on that gap would flash the layout in and out on every tab.
    setTimeout(() => {
      if (sidePanel.el.querySelector('input:focus, textarea:focus')) return;
      // Nothing is focused any more, so the keyboard cannot come back without a new focus event —
      // drop the subscription before the restore, so a watcher can never outlive the field it is for.
      stopKeyboardWatch();
      leaveTypingMode();
    }, 60);
  });

  /* ── THE APP OWNS ITS OWN KEYBOARD BEHAVIOUR, WHEREVER IT IS EMBEDDED (2026-08-09) ─────────────
   * `interactive-widget=overlays-content` is what stops Android resizing the viewport for the soft
   * keyboard — the root cause of the sheet jumping, the 3D flashing and the model shrinking (see the
   * long note in index.html, and memory `android-keyboard-resizes-viewport`).
   *
   * ⛔ IT HAS TO BE SET AT RUNTIME, BECAUSE THE STATIC TAG NEVER REACHES A BUYER. There are three
   * HTML shells and the one I edited first is the only one nobody ships:
   *   · `app/index.html` — the DEV SERVER harness. `vite.config.js` says so outright ("local dev
   *     harness only, not used by the Shopify page"). This is what Ryan tested on, which is why the
   *     fix looked complete.
   *   · the `pages.dev` harness — EMITTED by `vite.config.js`, with its own hard-coded meta.
   *   · `/pages/weight-designer` and the portal page — the THEME's `snippets/doc-head-core.liquid`,
   *     shared by every page on the storefront.
   * Editing all three would mean one fact in three files on two deploy pipelines, and the third is a
   * site-wide change for one app's benefit. Setting it here instead means the designer carries its
   * own requirement onto whatever page hosts it, and nothing else on the storefront is touched.
   *
   * ⚠ SAFE TO DO LATE. The keyword only matters once a field takes focus, which cannot happen before
   * the UI this function builds exists — so there is no first-paint window where it is missing.
   * ⚠ APPENDS A TAG IF THERE ISN'T ONE, rather than assuming: a future host might not ship a viewport
   * meta at all, and a silent no-op here would be this same bug wearing a different hat. */
  try {
    const KEYBOARD_OVERLAY = 'interactive-widget=overlays-content';
    let vp = document.querySelector('meta[name="viewport"]');
    if (!vp) {
      vp = document.createElement('meta');
      vp.setAttribute('name', 'viewport');
      vp.setAttribute('content', 'width=device-width, initial-scale=1');
      document.head.appendChild(vp);
    }
    if (!/interactive-widget/.test(vp.content)) vp.content = `${vp.content}, ${KEYBOARD_OVERLAY}`;
  } catch (_) { /* a viewport hint is never worth failing the boot for */ }

  // --- first paint ---
  preview.paint();
  // Straight-to-3D (Ryan 2026-07-12): hide the flat 2D plate up front so the load
  // goes empty-studio → 3D instead of flashing the 2D then swapping. The class is
  // removed only if WebGL is unavailable (initPersistent3D failure path), which
  // makes the flat SVG the visible fallback.
  preview.el.classList.add('wd-3d-primary');
  refreshControls();
  // Resume (Ryan 2026-07-18, bug 7): no upload choreography ran, so re-hydrate the
  // Add Logo pane from the restored design — otherwise it shows the empty dropzone
  // instead of the buyer's logo + colours. (The Design-tab text fields already read
  // element.text, so they're covered.) hydrate() falls back to the dropzone if the
  // resumed design somehow has no logo.
  /* ⛔ `sharedNow` TOO, NOT JUST `resumedNow` (Ryan 2026-08-15, opening a WD lead's design from
     the rep email): *"when on the 'Add Logo' tab, there is no logo uploaded on the left side-pane,
     as you can see the 'Click to upload your logo' button."*

     Identical to the 2026-07-31 My Designs report, and for the identical reason — this pane's DOM
     is not derived from state on each paint, it is the record of an upload choreography that never
     ran. That one was fixed at the `openDesign` seam (search `logoStep.hydrate` above); this is the
     THIRD way a complete design arrives without an upload, and it was still gated on the one flag
     that a `?d=` link deliberately does not set. `fromAutosave` restores a share exactly as it
     restores a resume — same envelope, same function, same missing choreography — so the two
     belong on the same line.

     NOTHING IS LOST WITHOUT IT: the stored envelope for the one real WD submission carries the
     logo twice over (a 474KB dataUrl on `savedLogo` and again on the logo element). It is purely
     that this pane never asked.

     ⚠ `ctx.requestRender()` matters MORE on a share than on a resume. `logo.renderUrl` — the
     flatten / band peel / band recolours / added ring — is a transient `serializeLogo` drops on
     purpose, and no design MUTATION happens on either restore path, so the composite is never
     rebuilt. On a resume the buyer usually goes on to touch something; a rep opening a shared
     design to LOOK at it may touch nothing at all, and would be looking at the original art while
     the panel highlighted the swatches of a composite that was never drawn. */
  if (resumedNow || sharedNow) {
    logoStep.hydrate();
    ctx.requestRender();
  }
  // Land on Logo (2) fresh from the gate, or Design (3) for a resumed session
  // that already has a logo — never trust a resumed save's OLD numeric
  // currentStep (the step order shifted when Select Weights became tab 1;
  // an old save's `2` meant "Design", not "Logo" under the new numbering).
  // …or PICK WEIGHTS (1) on the portal, which is the whole point of skipping the intro: the rep
  // arrives on the one tab that leads to both of their opening moves — choose some weights, or
  // open My Designs. Landing them on Add Logo would be the same half-finished-looking screen
  // `startNewDesign` describes, with the added lie that there is a product to put a logo on.
  const startStep = bootBlank ? 1 : (resumedNow && state.ui.hasLogo ? 3 : 2);
  // Auto-open the slide-out on desktop; on mobile leave it closed so the plate
  // stays visible on load (a tab tap opens the bottom sheet).
  /* ⚠ …BUT NEVER BEHIND A PRESENTATION. Opening the pane on a shared boot puts a 320px panel — and
     the 160px plate offset that goes with it — behind a screen whose whole point is that the pane is
     gone. The visible symptom was the plate rolling to the offset spot and being dragged back to
     centre (see the pre-set in the 3D mount); the cause is that it was opened at all. */
  goToStep(startStep, { open: !media(MOBILE_MQ).matches && !sharedPresentationBoot() });
  // PHONE: leave the sheet's HEADER on screen rather than the whole sheet off it. `open: false`
  // above only declines to expand it — the panel then has neither `is-open` nor `is-minimized`, so
  // it rests at `translateY(100%)` and is gone entirely until a tab is tapped. Minimising instead
  // parks the 48px header at the bottom edge, where it arrives with the rest of the chrome and
  // says there is something down there to open (Ryan 2026-07-27: "make the pane on mobile slide up
  // — just the short top header part — on page load"). It is hidden until `.wd-ui-ready` like every
  // other piece, so this changes what the entrance DELIVERS, not when it runs.
  if (media(MOBILE_MQ).matches) sidePanel.minimize();
  syncHistoryButtons();

  status.guide(
    state.ui.hasLogo
      ? 'Welcome back — your design is saved. Keep refining, or request a quote.'
      : 'Drop your logo — PNG with transparency works best.',
  );

  // Entrance animation (2026-07-14, GLD-referenced): the shell was built fully
  // hidden-by-default via CSS; adding `.wd-ui-ready` fires the staged slide-in
  // keyframes so the tool visibly arrives rather than snapping in.
  //
  // SEQUENCE (Ryan 2026-07-17, re-ordered): you land on the bare studio, the WEIGHT
  // rolls in first — slower + deliberate, from off-screen — and ONLY once it has
  // settled do the tabs/panes/tools/powered-by slide in around it. buildReveal's
  // first-mount roll-in calls revealChrome() when its promise resolves; the fallback
  // below covers WebGL-unsupported / a slow chunk so the chrome never fails to appear.
  // (Was the reverse: a fixed 200ms pause → chrome in → weight rolled in LAST.)
  //
  // Two rAFs before adding the class: the hidden state has to actually paint, or the
  // animation runs from a style the browser never rendered and the stagger collapses.
  revealChrome = () => {
    if (chromeRevealed) return;
    chromeRevealed = true;
    /* WD 492 (Ryan 11-01): the curtains open - on a big desktop screen the navy grows out to the screen edges. HERE,
       after the weight's roll-in, not at the gate's hand-off: the window is still being built at the hand-off and
       grows ~2 s later (648 -> 790 px), so centring it there raced the build. Here its size is final. The rules (where
       it runs, how it ends, how it comes back) live in ui/theatre.js. */
    window.setTimeout(() => theatre.enter({ start: true }), 450);
    if (chromeFallbackTimer) { window.clearTimeout(chromeFallbackTimer); chromeFallbackTimer = 0; }
    /* ⛔ ON A SHARED BOOT THE CHROME MUST NEVER ANIMATE IN (Ryan 2026-08-16: *"at the top of the
       white viewable area/window you see something load then in a flash swipe right"*).
         That flash is the tool cluster and the tab strip playing their `.wd-ui-ready` ENTRANCE and
       then being animated straight back out by the presentation a beat later — two full animations
       to arrive at the state the screen should have opened in. `presentation.enter()` cannot run any
       earlier than it does (it needs a stage to present on), so the fix is not to reorder it but to
       put the chrome in its presenting END STATE before the entrance is ever armed.
         Applied directly rather than through `stageChromeHandshake`: that function's two-frame
       arming exists to make a LIVE transition smooth, and there is nothing to transition from here.
       The handshake re-adds the same classes when the presentation actually opens, which is a no-op.
         ⚠ Gated on `canPresent()` so a WebGL-less browser — which will never present — is not left
       wearing a presentation's chrome with no presentation in it. */
    const sharedPresentation = state.ui.isSharedView && presentation.canPresent();
    if (sharedPresentation) {
      preview.el.classList.add('wd-showcase-active');
      target.classList.add('wd-presenting-arm', 'wd-presenting', 'wd-presenting-share');
    }
    requestAnimationFrame(() => requestAnimationFrame(() => target.classList.add('wd-ui-ready')));
    // The phone's minimised sheet starts its 5s nudge clock HERE — the moment the chrome (and so the
    // sheet's 48px bar) actually arrives on screen. See `armSheetHint` for why it is a delay rather
    // than an idle timer. No-op on desktop and once the buyer has opened the sheet.
    armSheetHint();
    // Say what happened with the share link, now that there IS somewhere to say it (Phase D2).
    // Held until here on purpose: both outcomes are decided during the boot, before the status
    // bar exists, and a message that lands before the plate does has nothing to refer to.
    if (state.ui.isSharedView) {
      /* ⭐ A SHARED DESIGN IS PRESENTED, NOT EXPLAINED (PRESENTATION-MODE-PLAN.md, Phase 1). This
         used to be two lines of status text over a bare editor, which asked the recipient to
         understand a tool before they had seen the thing they were sent. They get their plate, on
         the stage, with their name over it — and a door into the editor when they want one.
           ⚠ THE OLD LINES ARE THE FALLBACK, NOT DEAD CODE. `enter()` refuses when the stage cannot
         show a model (WebGL unsupported, or the chunk never arrived), and in exactly that case the
         page IS a bare editor around a flat SVG — so the sentence that explains it is the right
         thing to say, and the only thing we have to say. */
      /* ⛔ PUT THE PRESENTATION IN FRONT OF THEM FIRST (Ryan 2026-08-16: *"it opens with you scrolled
         to the right place on the landing page (the actual presentation)"*).
           The tool is a mount partway down a marketing page. A phone gets the full-screen card path
         and never sees that page; a DESKTOP visitor following a share link landed at the top of the
         hero and had to find the designer themselves — so the first thing a customer saw of the
         design that was sent to them was a banner about something else.
           `block: 'start'` rather than `'center'`: the tool is taller than most viewports, so
         centring it hangs the header off the top. Instant, not smooth — a share link is not a
         same-page anchor and animating a jump the visitor did not ask for reads as the page moving
         under them. No-op on a phone, where the tool already owns the screen. */
      if (!presentation.enter()) {
        /* The presentation refused (no WebGL). Undo everything that was set up for it at boot —
           the window lock and the dressed-down chrome — or the visitor is left locked into a page
           that cannot scroll, wearing a presentation's chrome with no presentation in it. */
        closeViewportLock(target);
        preview.el.classList.remove('wd-showcase-active');
        target.classList.remove('wd-presenting-arm', 'wd-presenting', 'wd-presenting-share');
        /* …and it stops being a shared VIEW, because there is no view — this is the ordinary editor
           now, which means the pane belongs open and the plate belongs offset for it. Both fall out
           of clearing the flag and asking for the pane the way any other boot would. */
        state.ui.isSharedView = false;
        /* ⛔ AND THE PLATE STOPS BEING HELD PLAIN. Nothing is going to spin the design on now, so a
           flag left set would leave a shared design showing a stock plate forever. */
        sharedRevealPending = false;
        ctx.requestRender();
        if (!isPhone()) goToStep(state.ui.currentStep || 2, { open: true });
        status.success('This design was shared with you — edit it freely, or request a quote.');
        status.announce('A shared design has been opened. You can edit it or request a quote.');
      }
    } else if (state.ui.sharedLinkFailed) {
      status.info('That design link has expired or isn’t valid — start a fresh design here.');
    } else if (repResumeRaw && !repRestoreOffered) {
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-02) — THE REP'S UNSAVED WORK, OFFERED
         RATHER THAN DEMANDED. Dropping the resume GATE must not mean dropping the resume: the one
         thing that gate did that nothing else does is hand back work that was never saved to the
         server. So it becomes a line in the status bar with an inline action — the rep who wanted
         a blank picker never has to answer it, and the rep who reloaded mid-design gets their
         afternoon back in one click.
           ONE-SHOT, because `revealChrome` has three callers (the roll-in's resolve, its reject,
         and the fallback timer) and is written to tolerate being run twice — everything else in
         here is idempotent, and a toast is not. */
      repRestoreOffered = true;
      status.info('You have unsaved work from earlier.', {
        ms: 12000,
        actionLabel: 'Restore',
        onAction: async () => {
          const ok = await ctx.applyEnvelope(repResumeRaw);
          if (!ok) { status.error('That work couldn’t be restored.'); return; }
          /* ⚠ RE-ADOPT AFTER, NEVER BEFORE — `applyEnvelope` clears the identity on purpose (a
             wholesale envelope swap usually means the ref no longer describes what is on screen).
             This is the one case where it does: the envelope and the ref came off the same boot,
             from the same browser, describing the same design. Restoring the work without the ref
             would mint a second `WEIGHT-` number for a design that already has one — the exact
             fault `design-identity.js` exists to prevent. Identical two-step to `designs-menu`'s
             own `load`. */
          if (repResumeIdentity && repResumeIdentity.id) ctx.adoptSavedDesign?.(repResumeIdentity);
          status.success('Restored your unsaved work.');
        },
      });
    }
  };
  chromeFallbackTimer = window.setTimeout(revealChrome, UI_REVEAL_CHROME_FALLBACK_MS);

  if (bootBlank) {
    /* Nothing is picked, so there is nothing to build — the first `mountFreshProduct` (a pick, or
       a design opened from My Designs) mounts the stage instead.

       ⚠ BUT THE CHROME IS GATED ON THE ROLL-IN, and skipping the mount skips the roll. Without
       this line the tabs, the pane and the tool cluster wait out `UI_REVEAL_CHROME_FALLBACK_MS`
       — 4.5 SECONDS of blank screen on the one path where there is no model to explain the wait.
       Same call, same beat, as `buildReveal`'s own no-WebGL branch, which is the identical
       situation: no roll-in is coming, so bring the chrome in now. */
    setTimeout(revealChrome, UI_REVEAL_PAUSE_MS);
  } else {
    // Upgrade the flat first-frame to the live 3D plate on the next tick, so the
    // flat SVG is already on screen before the Three.js chunk starts loading.
    setTimeout(() => initPersistent3D(), 0);
  }
}

// ---------------------------------------------------------------------------
// restore / seed helpers
// ---------------------------------------------------------------------------

function tryRestore(state) {
  let raw = null;
  try {
    raw = localStorage.getItem(AUTOSAVE_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  const res = fromAutosave(raw, state);
  if (res.restored) state.ui.isDemo = false;
  return res.restored;
}

/**
 * The share id on the URL, or null. `?d=<8 alphanumerics>` — validated here so a hand-mangled
 * link never becomes a request, and matching the worker's own id pattern exactly.
 */
function readShareId() {
  try {
    const v = new URLSearchParams(location.search).get(SHARE_PARAM);
    return /^[A-Za-z0-9]{8}$/.test(String(v || '')) ? v : null;
  } catch {
    return null; // no location (tests / SSR)
  }
}

function seedDemo(state) {
  const demo = createDemoProfile();
  state.designProfile = demo;
  state.ui.hasLogo = false; // it's an example, not the buyer's logo
  state.ui.isDemo = true;
  resetHistory(state);
}

// Load on a clean, unbranded BLACK plate: no elements at all (no logo, no text,
// no weight number). setLogo() creates its element on upload, and the Design
// step's layout picker rebuilds elements — so the flow stays intact.
function seedBlank(state) {
  state.designProfile.elements = [];
  state.ui.selectedElementId = null;
  state.ui.hasLogo = false;
  state.ui.isDemo = false;
  resetHistory(state);
}

/**
 * The plate the tool calls PLAIN — a throwaway profile wearing nothing at all, used to hold a
 * shared boot on a bare black weight until the presentation spins the design onto it.
 *
 * ⛔ IT IS `createState` **THEN** `seedBlank`, AND THE SECOND HALF IS THE WHOLE POINT (Ryan
 * 2026-08-16: *"it says 'YOUR TEXT GOES HERE' on it, with a plate number too"*). `createState` runs
 * `applyLayoutPreset`, which seeds the default layout's ELEMENTS — a stock weight number and
 * placeholder arctext. That is the state a new project is in for the one tick before the boot path
 * calls `seedBlank` on it, so a "plain" plate taken from `createState` alone is not the plain plate
 * anybody has ever seen; it is the tool's furniture, on a stranger's presentation.
 *   Built from the two real functions rather than hand-cleared, so plain here cannot drift from
 * plain at boot. Fresh each call, deliberately not cached: it is handed straight to the renderer
 * and a shared mutable "constant" is a trap for the next reader.
 */
function stockProfile() {
  const blank = createState();
  seedBlank(blank);
  return blank.designProfile;
}

// True if the design has real content worth saving/resuming (Fable §3.2 LKG).
// "Populated" = the buyer REALLY customised — a logo, or arctext they actually typed.
// The default layout seeds every fresh plate with a stock weight element + PLACEHOLDER
// arctext ("YOUR TEXT" / "GOES HERE"); none of that is customisation, so it must NOT
// count (else a plate the buyer never touched autosaves + offers Resume — Ryan
// 2026-07-16). A weight element only ever accompanies a logo layout, so a real logo
// already covers the "they configured weights" case.
function isPopulated(state) {
  return profileIsCustomized(state && state.designProfile);
}
function profileIsCustomized(p) {
  if (p && p.savedLogo && p.savedLogo.dataUrl) return true;
  const els = (p && p.elements) || [];
  return els.some((e) =>
    (e.type === 'logo' && e.logo && e.logo.dataUrl) ||
    (e.type === 'arctext' && String(e.text || '').trim() && !isPlaceholderText(e.text)));
}

// Read a resumable save WITHOUT applying it (mount stays blank; the logo step
// offers a Resume card). Returns { raw, ts } for a valid + POPULATED save, else null.
function readResume() {
  let raw = null;
  try { raw = localStorage.getItem(AUTOSAVE_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    // Accepts the one MIGRATABLE older version too (see state.js) — this only decides whether to
    // OFFER the Resume card; `fromAutosave` does the actual v2→v3 weight conversion on accept.
    if (!data || !isReadableEnvelopeVersion(data.v) || !data.design) return null;
    // Same strict "really customised" test as isPopulated — a save that's only the
    // default seeded elements (stock weight + placeholder text) must NOT offer Resume.
    // This also retires any older loosely-saved autosave a returning buyer still holds.
    if (!profileIsCustomized(data.design.profile || {})) return null;
    /* THE LOGO, FOR THE GATE'S RESUME PILL (2026-08-09) — pulled from the envelope this function has
       already parsed. `savedLogo` is the fallback rather than an afterthought: it is where the mark
       lives whenever the active layout is TEXT-ONLY, which is exactly the same reason
       `profileIsCustomized` two lines above has to check it. Null is a fine answer — the pill draws
       a plate glyph instead. */
    const prof = data.design.profile || {};
    const logoEl = (Array.isArray(prof.elements) ? prof.elements : [])
      .find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
    const logoUrl = (logoEl && logoEl.logo.dataUrl)
      || (prof.savedLogo && prof.savedLogo.dataUrl)
      || null;
    return { raw, ts: Number.isFinite(data.ts) ? data.ts : null, logoUrl };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// tool-cluster icons (GD icon set — 20px strokes on the 40px .ds-tool-btn)
// ---------------------------------------------------------------------------

const ICON_ZOOM =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>';
const ICON_UNDO =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h10a5 5 0 0 1 0 10h-1"/><path d="M3 10l4-4M3 10l4 4"/></svg>';
const ICON_REDO =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M21 10H11a5 5 0 0 0 0 10h1"/><path d="M21 10l-4-4M21 10l-4 4"/></svg>';
const ICON_SHARE =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" ' +
  'stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>';

boot();

export { boot };
